import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { db } from "../../backend/firebaseConfig";
import Calendar from "react-calendar";
import { doc, onSnapshot, updateDoc, addDoc, getDoc, collection, getDocs, deleteField, setDoc, deleteDoc, writeBatch } from "firebase/firestore";
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import Modal from "react-modal";
import ViewInsurance from "../../components/UI/organisms/insurance/ViewInsurance";
import ServicesList from "../../components/UI/organisms/list/ServicesList";
import { addNotification } from "../../components/functions/notifyComp";
import {
  applyCompressibleTimelineShiftsAndNotify,
  sendPendingAppointmentTimeAdjustmentNotificationsBatch,
} from "../../utils/appointmentTimeAdjustmentNotifications";
import {
  sendSecondaryDentistAssignmentNotifications,
  sendSecondaryDentistRemovalNotifications,
} from "../../utils/secondaryDentistAssignmentNotifications";
import {
  CHAIR_SETTINGS_DOC_PATH,
  CHAIR_SWAPS_COLLECTION,
  DEFAULT_CHAIR_SETTINGS,
  subscribeToChairSettings,
  isChairCapacityConfigured,
  isDentistOnRoster,
  isSlotEffectivelyUnavailableForDentist,
  countAvailableDentistsForSlot,
  validateChairCapacity,
  findAppointmentConflicts,
  applyProposedChangesWithChairSemantics,
  shouldDefaultUnavailableByLegacySetup,
  normalizeChairSwaps,
  getBenchPartnerRosterUidsForDay,
  getRosterPartnerBenchUidsForDay,
  parseSlotLabelToRange as parseChairSlotLabelToRange,
  rosterGapOverlapsLinkedBenchInheritedExclusive,
  rosterPatientSlotUnavailableDueToBenchMirror,
  getLinkedBenchForRosterSlot,
  getLinkedRosterForBenchSlot,
  getBenchSwapSlots,
  getBenchSwapCoverageRanges,
  getRosterSwapAwayCoverageRanges,
  getRosterAssignablePatientCoverageRanges,
  minuteRangeFullyContainedInUnion,
  upsertSwapForSlot,
  removeSwapForSlot,
  parseAppointmentRangeMinutes,
  buildStandardHalfHourSlotLabels,
  isBlockingAppointmentStatus,
} from "../../utils/chairScheduling";
import {
  dentistHasPlottedSchedule,
  doesRangeViolateDentistPlottedSchedule,
  filterSlotLabelsToPlottedSchedule,
  findPendingEditImmovableConflict,
  findEditChairCascadeScheduleViolation,
  simulateEditChairCascade,
  getDentistScheduleUnavailableRanges,
  getPlottedAvailableSlotsForDentist,
  isDentistSchedulePlottingEnabled,
  resolveChairCountForDateKey,
  sanitizeDentistPlotDocData,
  subscribeToDentistSchedulesForDate,
  CHAIR_SCHEDULES_DOC_PATH,
  CLINIC_SCHEDULING_COLLECTION,
  DENTIST_PLOT_DOC_PREFIX,
} from "../../utils/dentistSchedulePlots";
import {
  alignBookedSlotTimelineRowToTruth,
  applyTimelineShiftUpdatesToRows,
  appointmentBelongsToDateDoc,
  appointmentRecordKeyForSlotMerge,
  computeCompressibleTimelineShiftUpdates,
  computeDayTimelineCompressionShiftUpdates,
  consolidateContiguousSelectedSlotsByAppointment,
  formatSlotRangeLabelFromMinutes,
  forEachTimelineGapSlot,
  getAppointmentDisplayRangeMinutes,
  isCompressiblePendingStatus,
  parseUnavailableLabelsToMinuteRanges,
  LAST_MINUTE_OF_CALENDAR_DAY,
  maxDurationUntilMidnightFromSlotStart,
  mergeContiguousBookedTimelineRows,
  normalizeAppointmentDateKey,
  timelineShiftUpdatesAlreadyApplied,
  withAppointmentDocDate,
} from "../../utils/appointmentTimeDisplay";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Card, 
  CardContent,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Badge,
  Tooltip,
  Grid,
  Stack,
  Checkbox,
  FormControlLabel,
  Typography,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { Close as CloseIcon, EventBusy as EventBusyIcon } from "@mui/icons-material";
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ManageAppointmentPickersDay from "../../components/appointment/ManageAppointmentPickersDay";
import { buildManageCalendarRenderableSlots } from "../../components/appointment/clinicStaffCalendarHelpers";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

import Header from "../../components/UI/organisms/header/logged-in/HeaderLoggedIn"
import Text from "../../components/UI/atoms/text/Text";
import Icon from "../../components/UI/atoms/icon/Icon";
import SplashScreen from "../../components/UI/atoms/splash/SplashScreen";
import MedicalHistory from "../../components/UI/organisms/history/MedicalHistory";

Modal.setAppElement("#root");

const EDIT_APPOINTMENT_PAST_MIDNIGHT_MESSAGE =
  "Appointment cannot extend past 11:59 PM on the same day. Reduce the duration or choose an earlier start time.";

// 30-min slots 9:00 AM – 6:00 PM for reference schedule picker
const REFERENCE_TIME_OPTIONS = (() => {
  const opts = [];
  for (let m = 540; m <= 1080; m += 30) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    const suffix = h < 12 ? "AM" : "PM";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    opts.push({
      value: `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`,
      label: `${h12}:${String(min).padStart(2, "0")} ${suffix}`,
    });
  }
  return opts;
})();

/** Prefer firstName + lastName; fall back to name/displayName when profiles omit split fields. */
function buildDentistDisplayLabelFromUserDoc(data) {
  if (!data || typeof data !== "object") return "Unknown Dentist";
  const first = (data.firstName || "").trim();
  const last = (data.lastName || "").trim();
  const combined = [first, last].filter(Boolean).join(" ").trim();
  if (combined) {
    return `Dr. ${combined}`;
  }
  const fallback = (data.name || data.displayName || "").trim();
  if (fallback) {
    return /^dr\.?\s/i.test(fallback) ? fallback : `Dr. ${fallback}`;
  }
  return "Unknown Dentist";
}

function isBlockingAppointmentStatusForRosterRemoval(status) {
  const s = (status || "").toString().trim().toLowerCase();
  return s === "pending" || s === "confirmed" || s === "new" || s === "reserved";
}

/** First services array with at least one entry. Empty arrays are truthy in JS and must not block fallbacks (e.g. email payloads). */
function pickFirstNonEmptyServicesArray(...candidates) {
  for (const c of candidates) {
    if (Array.isArray(c) && c.length > 0) return c;
  }
  return [];
}

/** Services from an appointment row for notifications only (`services` array or singular `service`). */
function pickServicesFromAppointmentSnapshot(apptSource) {
  if (!apptSource || typeof apptSource !== "object") return [];
  const singular =
    typeof apptSource.service === "string" && apptSource.service.trim()
      ? [apptSource.service.trim()]
      : Array.isArray(apptSource.service)
        ? apptSource.service
        : [];
  return pickFirstNonEmptyServicesArray(apptSource.services, singular);
}

/** Dentist display from snapshot for notifications; strips useless placeholders (does not affect scheduling). */
function dentistDisplayFromAppointmentSnapshot(apptSource, adjustmentDentistName) {
  const raw =
    (adjustmentDentistName && String(adjustmentDentistName).trim()) ||
    (apptSource?.dentist && String(apptSource.dentist).trim()) ||
    (apptSource?.dentistName && String(apptSource.dentistName).trim()) ||
    "";
  const upper = raw.toUpperCase();
  if (!raw || upper === "N/A" || upper === "—" || upper === "-") return "";
  return raw;
}

/** Scan Firestore appointments snapshot for dates >= today where removed dentists have blocking bookings. */
function computeRosterRemovalAppointmentBlocks(removedUids, appointmentsSnapshot, todayStr, resolveAppointmentDentistUidFn) {
  if (!Array.isArray(removedUids) || removedUids.length === 0) return [];
  const removedSet = new Set(removedUids);
  const byDentist = new Map();
  appointmentsSnapshot.forEach((docSnap) => {
    const dateStr = docSnap.id;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr) || dateStr < todayStr) return;
    const data = docSnap.data();
    if (!data) return;
    Object.values(data).forEach((appt) => {
      if (!appt || typeof appt !== "object") return;
      const uid = resolveAppointmentDentistUidFn(appt);
      if (!uid || !removedSet.has(uid)) return;
      if (!isBlockingAppointmentStatusForRosterRemoval(appt.status)) return;
      if (!byDentist.has(uid)) byDentist.set(uid, new Set());
      byDentist.get(uid).add(dateStr);
    });
  });
  return removedUids
    .filter((uid) => byDentist.has(uid) && byDentist.get(uid).size > 0)
    .map((uid) => ({ uid, dates: [...byDentist.get(uid)].sort() }));
}

function formatRosterRemovalDateKey(dateKey) {
  const parts = dateKey.split("-").map((n) => Number(n));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return dateKey;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function coalesceAppointmentSlotsByStableId(appointmentSlots) {
  if (!Array.isArray(appointmentSlots) || appointmentSlots.length === 0) {
    return appointmentSlots;
  }
  const noKey = [];
  const byKey = new Map();
  for (const slot of appointmentSlots) {
    const key = appointmentRecordKeyForSlotMerge(slot.appointment);
    if (!key) {
      noKey.push({ ...slot });
      continue;
    }
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, { ...slot });
    } else {
      prev.start = Math.min(prev.start, slot.start);
      prev.end = Math.max(prev.end, slot.end);
      prev.timeStr = formatSlotRangeLabelFromMinutes(prev.start, prev.end);
    }
  }
  return [...noKey, ...byKey.values()].sort((a, b) => a.start - b.start);
}

function mergeModalHalfHourSlotsByBookedAppointment(visibleSlots, checkBookedFn) {
  if (!Array.isArray(visibleSlots) || visibleSlots.length === 0) return visibleSlots;
  const withoutNumericRange = visibleSlots.filter(
    (slot) => !Number.isFinite(slot.start) || !Number.isFinite(slot.end)
  );
  const rows = visibleSlots
    .filter((slot) => Number.isFinite(slot.start) && Number.isFinite(slot.end))
    .map((slot) => ({
      slot,
      start: slot.start,
      end: slot.end,
      booked: checkBookedFn(slot.timeStr),
    }))
    .sort((a, b) => a.start - b.start);

  const merged = [];
  for (const row of rows) {
    const key = appointmentRecordKeyForSlotMerge(row.booked);
    const prev = merged[merged.length - 1];
    const prevKey = appointmentRecordKeyForSlotMerge(prev?.booked);
    const contiguous =
      prev &&
      key &&
      prevKey === key &&
      Number.isFinite(prev.end) &&
      Number.isFinite(row.start) &&
      prev.end === row.start;
    if (contiguous) {
      prev.end = row.end;
      prev.segmentTimeStrs.push(row.slot.timeStr);
      continue;
    }
    merged.push({
      start: row.start,
      end: row.end,
      booked: row.booked,
      segmentTimeStrs: [row.slot.timeStr],
      baseSlot: { ...row.slot },
    });
  }

  const mergedMapped = merged.map((m) => {
    let row = {
      ...m.baseSlot,
      start: m.start,
      end: m.end,
      timeStr: formatSlotRangeLabelFromMinutes(m.start, m.end),
      modalSegmentTimeStrs: m.segmentTimeStrs,
    };
    if (m.booked) {
      row = alignBookedSlotTimelineRowToTruth({ ...row, appointment: m.booked });
    }
    return row;
  });
  const passthrough = withoutNumericRange.map((slot) => ({
    ...slot,
    modalSegmentTimeStrs: [slot.timeStr],
  }));
  return [...mergedMapped, ...passthrough].sort((a, b) => {
    const as = Number.isFinite(a.start) ? a.start : 0;
    const bs = Number.isFinite(b.start) ? b.start : 0;
    return as - bs;
  });
}

const ManageAppointment = () => {
  const [appointments, setAppointments] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]); // Added for all appointments across all dates
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDateAppointment, setSelectedDateAppointment] = useState(dayjs());
  const [insuranceDetails, setInsuranceDetails] = useState(null);
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [editingAppointmentId, setEditingAppointmentId] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState({ services: [] });
  const [servicePrices, setServicePrices] = useState({});
  const [dentists, setDentists] = useState([]);
  const [selectedDentist, setSelectedDentist] = useState(null);
  const [pendingCounts, setPendingCounts] = useState({});
  const [userDetails, setUserDetails] = useState(null);
  const [showReferenceScheduleModal, setShowReferenceScheduleModal] = useState(false);
  const [referenceFrom, setReferenceFrom] = useState("09:00");
  const [referenceTo, setReferenceTo] = useState("18:00");
  const [referenceScheduleSaving, setReferenceScheduleSaving] = useState(false);
  const [referenceScheduleSnackbar, setReferenceScheduleSnackbar] = useState({ open: false, message: "", severity: "success" });
  const getDentistDisplayNameFromUID = useCallback(
    (uid) => {
      if (!uid) return "Unknown Dentist";
      
      // First, check if it's the current user (who might be excluded from dentists array)
      const auth = getAuth();
      if (userDetails && auth.currentUser && auth.currentUser.uid === uid) {
        const firstName = (userDetails.firstName || "").trim();
        const lastName = (userDetails.lastName || "").trim();
        const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
        if (fullName) return `Dr. ${fullName}`;
      }
      
      // Otherwise, look in the dentists array
      const dentist = dentists.find((d) => d.uid === uid);
      if (!dentist) return "Unknown Dentist";
      if (dentist.displayName) return dentist.displayName;
      const firstName = (dentist.firstName || "").trim();
      const lastName = (dentist.lastName || "").trim();
      const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
      return fullName ? `Dr. ${fullName}` : "Unknown Dentist";
    },
    [dentists, userDetails]
  );
  const getEditDurationConflictWithAnchoredMessage = useCallback(
    (blockerAppt) => {
      if (!blockerAppt) {
        return "Failed to save changes. The duration conflicts with a confirmed patient appointment.";
      }
      const status = (blockerAppt.status || "").toLowerCase();
      const dentistName =
        (blockerAppt.dentist && String(blockerAppt.dentist).trim()) ||
        getDentistDisplayNameFromUID(blockerAppt.dentistId) ||
        "another dentist";
      if (status === "completed") {
        return `Failed to save changes. The duration conflicts with ${dentistName}'s completed patient appointment.`;
      }
      return `Failed to save changes. The duration conflicts with ${dentistName}'s confirmed patient appointment.`;
    },
    [getDentistDisplayNameFromUID]
  );
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [priceValidationSnackbar, setPriceValidationSnackbar] = useState({ open: false, message: "" });
  const [editFormValidationMessage, setEditFormValidationMessage] = useState("");
  /** Prevents duplicate submit / notifications when Save is clicked repeatedly before the modal closes. */
  const savingAppointmentDetailsRef = useRef(false);
  const timelineReconcilePersistTimerRef = useRef(null);
  const timelineReconcileInFlightRef = useRef(false);
  const timelineCompressSkipUntilRef = useRef(0);
  const lastPersistedCompressKeyRef = useRef("");
  const TIMELINE_RECONCILE_PERSIST_MS = 0;
  const TIMELINE_COMPRESS_SAVE_SKIP_MS = 2500;

  const markTimelineCompressHandledByStaffSave = useCallback(() => {
    timelineCompressSkipUntilRef.current = Date.now() + TIMELINE_COMPRESS_SAVE_SKIP_MS;
  }, [TIMELINE_COMPRESS_SAVE_SKIP_MS]);

  const persistScheduledTimelineShifts = useCallback(
    async (shiftUpdates, formattedDate, sourceAppointments) => {
      if (!shiftUpdates?.length || !formattedDate) return;
      if (Date.now() < timelineCompressSkipUntilRef.current) return;

      const persistKey = shiftUpdates
        .map((u) => `${u.id}:${u.startTime}:${u.endTime}`)
        .join("|");
      if (lastPersistedCompressKeyRef.current === persistKey) return;
      if (timelineReconcileInFlightRef.current) return;

      timelineReconcileInFlightRef.current = true;
      try {
        const appointmentRef = doc(db, "appointments", formattedDate);
        await applyCompressibleTimelineShiftsAndNotify({
          appointmentRef,
          chainUpdates: shiftUpdates,
          sourceAppointments,
          formattedDate,
        });
        lastPersistedCompressKeyRef.current = persistKey;
      } catch (error) {
        console.error("Error persisting timeline compression:", error);
      } finally {
        timelineReconcileInFlightRef.current = false;
      }
    },
    []
  );

  const scheduleTimelineCompressionPersist = useCallback(
    (shiftUpdates, formattedDate, sourceAppointments) => {
      if (!shiftUpdates?.length || !formattedDate) return;
      if (Date.now() < timelineCompressSkipUntilRef.current) return;

      if (timelineReconcilePersistTimerRef.current) {
        clearTimeout(timelineReconcilePersistTimerRef.current);
      }
      timelineReconcilePersistTimerRef.current = setTimeout(() => {
        persistScheduledTimelineShifts(shiftUpdates, formattedDate, sourceAppointments);
      }, TIMELINE_RECONCILE_PERSIST_MS);
    },
    [TIMELINE_RECONCILE_PERSIST_MS, persistScheduledTimelineShifts]
  );

  const [isSavingAppointmentDetails, setIsSavingAppointmentDetails] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [appointmentToRemove, setAppointmentToRemove] = useState(null);
  const [removalReason, setRemovalReason] = useState("");
  const [closedDays, setClosedDays] = useState([]);
  const [showClosedDaysModal, setShowClosedDaysModal] = useState(false);
  const [isManageCalendarLoading, setIsManageCalendarLoading] = useState(false);
  
  // Filter checkboxes state
  const [showAllBookedAppointments, setShowAllBookedAppointments] = useState(false);
  const [showPendingAppointments, setShowPendingAppointments] = useState(false);
  const [showConfirmedAppointments, setShowConfirmedAppointments] = useState(false);
  const [modalCalendarDate, setModalCalendarDate] = useState(new Date());
  const [unavailableTimeSlots, setUnavailableTimeSlots] = useState([]);
  const [selectedModalDate, setSelectedModalDate] = useState(null);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictTimeSlot, setConflictTimeSlot] = useState(null);
  const [conflictAppointment, setConflictAppointment] = useState(null);
  const [modalAppointments, setModalAppointments] = useState([]);
  const [showDateUnavailableDialog, setShowDateUnavailableDialog] = useState(false);
  const [dateToToggle, setDateToToggle] = useState(null);
  const [dateAppointments, setDateAppointments] = useState([]);
  const [isMarkingDateUnavailable, setIsMarkingDateUnavailable] = useState(false);
  const isClosingDateDialog = useRef(false);
  const isClosingRemoveDialog = useRef(false);
  const lastToggledDateRef = useRef(null);
  const lastToggledDateTimeRef = useRef(0);
  const [showTimeSlotDialog, setShowTimeSlotDialog] = useState(false);
  const [timeSlotToToggle, setTimeSlotToToggle] = useState(null);
  const [timeSlotAppointment, setTimeSlotAppointment] = useState(null);
  const [timeSlotDialogValidationMessage, setTimeSlotDialogValidationMessage] =
    useState("");
  const [isMarkingTimeSlotUnavailable, setIsMarkingTimeSlotUnavailable] = useState(false);
  const isClosingTimeSlotDialog = useRef(false);
  const lastToggledTimeSlotRef = useRef(null);
  const lastToggledTimeSlotTimeRef = useRef(0);
  const [selectedModalDentist, setSelectedModalDentist] = useState(null); // Default to no dentist selected
  const [mainAppointmentUnavailableSlots, setMainAppointmentUnavailableSlots] = useState([]);
  const [hasMainUnavailableRecord, setHasMainUnavailableRecord] = useState(false);
  const [hasModalUnavailableRecord, setHasModalUnavailableRecord] = useState(false);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [showMultiConfirmationDialog, setShowMultiConfirmationDialog] = useState(false);
  const [multiConfirmationAction, setMultiConfirmationAction] = useState(null); // 'available' or 'unavailable'
  const [multiSlotValidationMessage, setMultiSlotValidationMessage] = useState("");
  const [multiSlotValidationSlots, setMultiSlotValidationSlots] = useState([]);
  const [multiSlotConflictingAppointments, setMultiSlotConflictingAppointments] = useState([]);
  const [showMultiSlotCancellationDialog, setShowMultiSlotCancellationDialog] = useState(false);
  const [isBulkCancellingAppointments, setIsBulkCancellingAppointments] = useState(false);
  const conflictsGroupedBySlot = useMemo(() => {
    const slotMap = new Map();
    multiSlotConflictingAppointments.forEach((record) => {
      const slotKey = record.slot || "N/A";
      if (!slotMap.has(slotKey)) {
        slotMap.set(slotKey, []);
      }
      slotMap.get(slotKey).push(record);
    });
    return slotMap;
  }, [multiSlotConflictingAppointments]);
  const uniqueConflictingSlotCount = useMemo(
    () => conflictsGroupedBySlot.size,
    [conflictsGroupedBySlot]
  );
  const cancellationDialogTitle = useMemo(() => {
    const dentistName = getDentistDisplayNameFromUID(selectedModalDentist);
    if (!dentistName) {
      return "Cancel Appointments";
    }
    return `Cancel Appointments for ${dentistName}`;
  }, [selectedModalDentist]);
  const cancellationDialogFontSize = useMemo(() => {
    const titleLength = cancellationDialogTitle.length;
    if (titleLength >= 70) return "0.85rem";
    if (titleLength >= 55) return "0.95rem";
    if (titleLength >= 40) return "1.05rem";
    return "1.25rem";
  }, [cancellationDialogTitle]);
  const [showClinicWideDialog, setShowClinicWideDialog] = useState(false);
  const [clinicWideTimeSlot, setClinicWideTimeSlot] = useState(null);
  const clinicWideTitle = useMemo(() => {
    if (!selectedModalDate) return "Mark Date as Unavailable";
    const year = selectedModalDate.getFullYear();
    const month = String(selectedModalDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedModalDate.getDate()).padStart(2, "0");
    const dateLabel = `${month}/${day}/${year}`;
    return `Mark ${dateLabel} as Unavailable`;
  }, [selectedModalDate]);
  const clinicWideTitleFontSize = useMemo(() => {
    const length = clinicWideTitle.length;
    if (length >= 70) return "0.85rem";
    if (length >= 55) return "0.95rem";
    if (length >= 40) return "1.05rem";
    return "1.25rem";
  }, [clinicWideTitle]);
  const [dentistStatuses, setDentistStatuses] = useState([]);
  const [comprehensiveDentistStatuses, setComprehensiveDentistStatuses] = useState([]);
  const [chairSettings, setChairSettings] = useState({ ...DEFAULT_CHAIR_SETTINGS });
  const [unavailableSlotsByDentistForDate, setUnavailableSlotsByDentistForDate] = useState({});
  const [chairSwapsBySlotForDate, setChairSwapsBySlotForDate] = useState({});
  const [chairSwapsBySlotForMainDate, setChairSwapsBySlotForMainDate] = useState({});
  /** Full unavailable map for the main calendar date (bench roster mirror needs other dentists' rows). */
  const [unavailableSlotsByDentistForMainDate, setUnavailableSlotsByDentistForMainDate] =
    useState({});
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [rosterDraftIds, setRosterDraftIds] = useState([]);
  const [rosterDraftCount, setRosterDraftCount] = useState(0);
  const [rosterSavingError, setRosterSavingError] = useState(null);
  const [isSavingRoster, setIsSavingRoster] = useState(false);
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const [replacementContext, setReplacementContext] = useState(null);
  const [replacementSelections, setReplacementSelections] = useState({});
  const [replacementRowErrors, setReplacementRowErrors] = useState({});
  const [showRemovalModal, setShowRemovalModal] = useState(false);
  const [removalContext, setRemovalContext] = useState(null);
  const [removalSelections, setRemovalSelections] = useState({});
  const [removalRowErrors, setRemovalRowErrors] = useState({});
  const [showAssignSecondaryModal, setShowAssignSecondaryModal] = useState(false);
  const [assignSecondaryDentistUid, setAssignSecondaryDentistUid] = useState("");
  const [assignSecondaryBlockLabel, setAssignSecondaryBlockLabel] = useState("");
  const [assignSecondaryValidationMessages, setAssignSecondaryValidationMessages] = useState([]);
  const [showUnassignSecondaryModal, setShowUnassignSecondaryModal] = useState(false);
  const [unassignSecondaryValidationMessage, setUnassignSecondaryValidationMessage] = useState("");
  const [isUnassigningSecondary, setIsUnassigningSecondary] = useState(false);
  const [chairSnackbar, setChairSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [dentistSchedulesForDate, setDentistSchedulesForDate] = useState({});
  const [modalDentistSchedulesForDate, setModalDentistSchedulesForDate] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  useEffect(() => {
    return () => {
      document.body.classList.remove("ReactModal__Body--open");
      document.documentElement.classList.remove("ReactModal__Html--open");
    };
  }, []);
  const [highlightedDays, setHighlightedDays] = React.useState([1, 2, 15]);
  const [showSplash, setShowSplash] = useState(true);
const [medicalHistoryPatientId, setMedicalHistoryPatientId] = useState(null);
const [showMedicalHistoryModal, setShowMedicalHistoryModal] = useState(false);
const [medicalHistoryPatientName, setMedicalHistoryPatientName] = useState("");
const medicalHistoryRef = useRef(null);

  const [accOpen, setAccOpen] = useState({
    dentist: true,
    service: true,
    date: true,
    time: true,
  });
  const toggleAcc = (key) => (_e, isExpanded) =>
    setAccOpen((prev) => ({ ...prev, [key]: isExpanded }));

  const timeSlotManagementTitle = useMemo(() => {
    const dentistName = getDentistDisplayNameFromUID(selectedModalDentist);
    if (!dentistName || dentistName === "Unknown Dentist") {
      return "Time Slot Management";
    }
    return `Time Slot Management for ${dentistName}`;
  }, [selectedModalDentist, getDentistDisplayNameFromUID]);
  const timeSlotManagementFontSize = useMemo(() => {
    const length = timeSlotManagementTitle.length;
    if (length >= 70) return "0.85rem";
    if (length >= 55) return "0.95rem";
    if (length >= 40) return "1.05rem";
    return "1.25rem";
  }, [timeSlotManagementTitle]);
  const dateUnavailableTitle = useMemo(() => {
    if (!dateToToggle) return "Manage Date Availability";
    const year = dateToToggle.getFullYear();
    const month = String(dateToToggle.getMonth() + 1).padStart(2, "0");
    const day = String(dateToToggle.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    const formattedDate = dateToToggle.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const isCurrentlyUnavailable = closedDays.includes(dateStr);
    return isCurrentlyUnavailable
      ? `Mark ${formattedDate} as Available`
      : `Mark ${formattedDate} as Unavailable`;
  }, [dateToToggle, closedDays]);
  const dateUnavailableFontSize = useMemo(() => {
    const length = dateUnavailableTitle.length;
    if (length >= 70) return "0.85rem";
    if (length >= 55) return "0.95rem";
    if (length >= 40) return "1.05rem";
    return "1.25rem";
  }, [dateUnavailableTitle]);

const [showNone, setShowNone] = useState(true);

  // Retrieve userRole from navigation state
  const userRole = location.state?.userRole || "DentistOwner";
  const allowPastDates = ["DentistOwner", "AssociateDentist", "ClinicStaff"].includes(userRole);
  const clinicPersonnelRoles = ["DentistOwner", "AssociateDentist", "ClinicStaff"];

  const getRoleFromUserData = (user) => {
    if (!user) return "";
    return (user.role || user.userType || "").trim();
  };

  const isDentistRole = (role) => role === "DentistOwner" || role === "AssociateDentist";
  
  // Check if current user is a dentist (DentistOwner or AssociateDentist)
  const isCurrentUserDentist = () => {
    if (!userDetails) return false;
    const role = userDetails.role || userDetails.userType;
    return isDentistRole(role);
  };

  const handleSaveReferenceSchedule = async () => {
    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    if (!uid || !selectedDateAppointment) return;
    const dateKey = selectedDateAppointment.format("YYYY-MM-DD");
    setReferenceScheduleSaving(true);
    try {
      const refDoc = doc(db, "dentistReferenceSchedules", dateKey);
      await setDoc(refDoc, { [uid]: { from: referenceFrom, to: referenceTo, updatedAt: new Date().toISOString() } }, { merge: true });
      setShowReferenceScheduleModal(false);
      setReferenceScheduleSnackbar({ open: true, message: "Reference schedule saved. Clinic staff will see this as a reference.", severity: "success" });
    } catch (err) {
      console.error("Error saving reference schedule:", err);
      setReferenceScheduleSnackbar({ open: true, message: "Failed to save reference schedule. Please try again.", severity: "error" });
    } finally {
      setReferenceScheduleSaving(false);
    }
  };

  const isClinicPersonnelUser = (data = {}) => {
    const possibleRoles = [data.userType, data.role];
    return possibleRoles.some((role) => clinicPersonnelRoles.includes(role));
  };

  const buildStaffTitle = (gender = "") => {
    const normalized = gender.toLowerCase();
    if (normalized === "male") return "Mr.";
    if (normalized === "female") return "Ms.";
    return "Mr./Ms.";
  };

  const buildFullName = (user) => {
    if (!user) return "";
    const first = (user.firstName || "").trim();
    const last = (user.lastName || "").trim();
    return [first, last].filter(Boolean).join(" ").trim();
  };

  const getCurrentUserDisplayName = () => {
    const role = getRoleFromUserData(userDetails);
    const fullName = buildFullName(userDetails);
    if (!fullName) {
      return "Clinic Personnel";
    }
    if (isDentistRole(role)) {
      return `Dr. ${fullName}`;
    }
    if (role === "ClinicStaff") {
      return `${buildStaffTitle(userDetails?.gender)} ${fullName}`;
    }
    return fullName;
  };

  // Get name without prefix for system notifications (for ClinicStaff)
  const getCurrentUserDisplayNameForSystemNotification = () => {
    const role = getRoleFromUserData(userDetails);
    const fullName = buildFullName(userDetails);
    if (!fullName) {
      return "Clinic Personnel";
    }
    if (isDentistRole(role)) {
      return `Dr. ${fullName}`;
    }
    // For ClinicStaff, return name without prefix
    return fullName;
  };

  const resolveAppointmentDentistUid = (appointment = {}) => {
    return (
      appointment.dentistId ||
      appointment.dentistUID ||
      appointment.dentistUid ||
      appointment.dentistUserId ||
      appointment.dentistUserID ||
      null
    );
  };

  const buildAppointmentWindow = (dateLong, startTime, endTime) => {
    const safeStart = startTime && startTime !== "N/A" ? startTime : "N/A";
    const safeEnd = endTime && endTime !== "N/A" ? endTime : "N/A";
    return `${dateLong} (${safeStart} - ${safeEnd})`;
  };

  const buildTimeSlotRangesForMessage = (slots = []) => {
    if (!Array.isArray(slots) || slots.length === 0) {
      return [];
    }

    const uniqueSlots = Array.from(new Set(slots.filter(Boolean)));

    const slotDetails = uniqueSlots
      .map((slot) => {
        if (typeof slot !== "string" || !slot.includes("-")) {
          return null;
        }
        const [startRaw, endRaw] = slot.split("-").map((part) => part.trim());
        if (!startRaw || !endRaw) {
          return null;
        }
        const startMinutes = timeToMinutes(startRaw);
        const endMinutes = timeToMinutes(endRaw);
        return {
          startMinutes,
          endMinutes,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.startMinutes - b.startMinutes);

    if (slotDetails.length === 0) {
      return [];
    }

    const merged = [];
    let currentRange = { ...slotDetails[0] };

    for (let i = 1; i < slotDetails.length; i++) {
      const slotInfo = slotDetails[i];
      if (slotInfo.startMinutes === currentRange.endMinutes) {
        currentRange.endMinutes = slotInfo.endMinutes;
      } else {
        merged.push(currentRange);
        currentRange = { ...slotInfo };
      }
    }
    merged.push(currentRange);

    return merged.map(({ startMinutes, endMinutes }) => {
      const startDisplay = formatTimeConsistent(minutesToTime(startMinutes));
      const endDisplay = formatTimeConsistent(minutesToTime(endMinutes));
      return `${startDisplay} - ${endDisplay}`;
    });
  };

  const joinWithAnd = (items = []) => {
    if (!items || items.length === 0) return "";
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    const initial = items.slice(0, -1).join(", ");
    const last = items[items.length - 1];
    return `${initial}, and ${last}`;
  };

  const buildTimeSlotSummary = (slots = []) => {
    const ranges = buildTimeSlotRangesForMessage(slots);
    return joinWithAnd(ranges);
  };

  const buildIndividualTimeSlotList = (slots = []) => {
    if (!Array.isArray(slots) || slots.length === 0) {
      return "";
    }
    const unique = Array.from(new Set(slots.filter(Boolean))).sort((a, b) => {
      const parseStart = (label) => {
        if (typeof label !== "string" || !label.includes("-")) return 0;
        const startRaw = label.split("-")[0]?.trim();
        return timeToMinutes(startRaw);
      };
      return parseStart(a) - parseStart(b);
    });
    return joinWithAnd(unique);
  };

  const getLocalDateKey = (date = new Date()) => {
    const localDate = new Date(date);
    localDate.setHours(0, 0, 0, 0);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  /**
   * Time slot availability change for one dentist: in-app + email to that dentist (slot owner)
   * and all ClinicStaff only — not other DentistOwners or AssociateDentists.
   */
  const notifyClinicPersonnelForTimeSlots = async ({ dentistId, dateStr, slots, action }) => {
    if (!dentistId || !dateStr || !Array.isArray(slots) || slots.length === 0) {
      return;
    }

    const slotList = buildIndividualTimeSlotList(slots);
    if (!slotList) {
      return;
    }

    const formattedDateLong = formatDateLong(dateStr);
    const actorName = getCurrentUserDisplayNameForSystemNotification() || "Clinic Personnel";
    const dentistDisplayName = getDentistDisplayNameFromUID(dentistId);
    const availabilityVerb = action === "available" ? "available" : "unavailable";
    const appMessage = `${dentistDisplayName} has been marked as ${availabilityVerb} by ${actorName} on ${formattedDateLong}, for the following time slots: ${slotList}.`;

    const sendTimeSlotEmailNotification = async (
      recipientEmail,
      recipientName,
      recipientRole
    ) => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const response = await fetch(`${apiUrl}/api/send-appointment-status-change`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "dentist",
            recipientEmail,
            recipientName,
            recipientRole,
            patientName: "N/A",
            appointmentDate: formattedDateLong,
            startTime: "N/A",
            endTime: "N/A",
            dentistName: dentistDisplayName,
            dentistId,
            services: [],
            status: action === "available" ? "Available" : "Unavailable",
            actorName,
            insuranceText: "N/A",
            dentistRemarks: null,
            clinicName: "VSDiamond Dental Clinic",
            isTimeSlotChange: true,
            timeSlots: slotList,
            action,
          }),
        });
        const result = await response.json();
        if (!result.success) {
          console.error("Failed to send time slot email notification:", result.message);
        }
      } catch (error) {
        console.error("Error sending time slot email notification:", error);
      }
    };

    const usersRef = collection(db, "users");
    const usersSnapshot = await getDocs(usersRef);
    usersSnapshot.forEach((docu) => {
      const data = docu.data();
      const userId = docu.id;
      const userRole = data.userType || data.role;
      const isAffectedDentist = userId === dentistId;
      const isClinicStaff =
        data.userType === "ClinicStaff" || data.role === "ClinicStaff";

      if (!isAffectedDentist && !isClinicStaff) {
        return;
      }

      addNotification(userId, appMessage);

      const emailVerified =
        data.emailVerified === "Yes" || data.emailVerified === true;
      const recipientEmail = data.email || data.authEmail;
      if (
        !emailVerified ||
        !recipientEmail ||
        recipientEmail.includes("@mobileuser.dentalapp.com")
      ) {
        return;
      }

      const recipientName =
        data.firstName && data.lastName
          ? `${data.firstName} ${data.lastName}`
          : data.name || (isClinicStaff ? "Staff" : "Doctor");
      void sendTimeSlotEmailNotification(
        recipientEmail,
        recipientName,
        userRole
      );
    });
  };

  const addNotification = async (uid, message) => {
    const notificationsRef = collection(db, "notifications", uid, "userNotifications");
    const newNotification = {
      message,
      timestamp: new Date().toISOString(),
      read: false,
    };
    try {
      await addDoc(notificationsRef, newNotification);
    } catch (error) {
      console.error("Error adding notification:", error);
    }
  };

  const buildNotificationWindow = (dateLabel, startTime, endTime) => {
    const safeStart = startTime && startTime !== "N/A" ? startTime : "N/A";
    const safeEnd = endTime && endTime !== "N/A" ? endTime : "N/A";
    return `${dateLabel} (${safeStart} - ${safeEnd})`;
  };

  // Fetch closed days from Firebase (one-time fetch, kept for backward compatibility)
  const fetchClosedDays = async () => {
    try {
      const closedDaysRef = doc(db, "closedDays", "closedDates");
      const closedDaysSnap = await getDoc(closedDaysRef);
      if (closedDaysSnap.exists()) {
        const data = closedDaysSnap.data();
        setClosedDays(data.dates || []);
      } else {
        setClosedDays([]);
      }
    } catch (error) {
      console.error("Error fetching closed days:", error);
    }
  };

  // Handle Manage Calendar button click
  const handleManageCalendarClick = async () => {
    setIsManageCalendarLoading(true);

    // Add a small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 500));

    // Normalize selectedModalDate to a plain JS Date.
    // selectedDateAppointment may be a dayjs object (has .toDate()) — convert if available,
    // otherwise fall back to selectedDate or new Date().
    const normalizedModalDate = selectedDateAppointment && typeof selectedDateAppointment.toDate === "function"
      ? selectedDateAppointment.toDate()
      : (selectedDate || new Date());
    setSelectedModalDate(normalizedModalDate);

    // Auto-set dentist selection for DentistOwner and AssociateDentist
    if (userDetails && auth.currentUser) {
      const userRole = userDetails.role || userDetails.userType;
      if (userRole === "DentistOwner" || userRole === "AssociateDentist") {
        setSelectedModalDentist(auth.currentUser.uid);
      } else {
        // Reset dentist selection to default state for ClinicStaff
        setSelectedModalDentist(null);
      }
    } else {
      // Reset dentist selection to default state
      setSelectedModalDentist(null);
    }

    // Reset unavailable time slots state
    setUnavailableTimeSlots([]);

    // Open the modal
    setShowClosedDaysModal(true);

    // Reset loading state
    setIsManageCalendarLoading(false);
  };


  // Filter closed days by current calendar month
  const getClosedDaysForCurrentMonth = () => {
    const currentYear = modalCalendarDate.getFullYear();
    const currentMonth = modalCalendarDate.getMonth() + 1; // getMonth() returns 0-11, we need 1-12
    
    return closedDays
      .filter(dateStr => {
        const [year, month] = dateStr.split('-').map(Number);
        return year === currentYear && month === currentMonth;
      })
      .sort((a, b) => new Date(a) - new Date(b));
  };

  // Generate time slots for a specific date (9 AM to 6 PM)
  const generateTimeSlotsForDate = (date) => {
    if (!date) return [];
    
    const timeSlots = [];
    const startHour = 9; // 9 AM
    const endHour = 18; // 6 PM
    const slotDuration = 30; // 30 minutes
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minutes = 0; minutes < 60; minutes += slotDuration) {
        // Calculate start and end times
        const startMinutes = hour * 60 + minutes;
        const endMinutes = startMinutes + slotDuration;
        
        // Skip if the slot would go beyond the end hour
        if (endMinutes > endHour * 60) {
          continue;
        }
        
        // Convert start time to 12-hour format
        const startDisplayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const startAmpm = hour < 12 ? 'AM' : 'PM';
        const startTimeString = `${startDisplayHour}:${minutes.toString().padStart(2, '0')} ${startAmpm}`;
        
        // Convert end time to 12-hour format
        const endHour24 = Math.floor(endMinutes / 60);
        const endMinutes24 = endMinutes % 60;
        const endDisplayHour = endHour24 === 0 ? 12 : endHour24 > 12 ? endHour24 - 12 : endHour24;
        const endAmpm = endHour24 < 12 ? 'AM' : 'PM';
        const endTimeString = `${endDisplayHour}:${endMinutes24.toString().padStart(2, '0')} ${endAmpm}`;
        
        const timeRangeString = `${startTimeString} - ${endTimeString}`;
        timeSlots.push(timeRangeString);
      }
    }
    
    return timeSlots;
  };

  // Get appointments for a specific date
  const getAppointmentsForDate = async (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    try {
      const appointmentsRef = doc(db, "appointments", dateStr);
      const appointmentsSnap = await getDoc(appointmentsRef);
      
      if (appointmentsSnap.exists()) {
        const data = appointmentsSnap.data();
        return Object.entries(data).map(([firestoreDocId, value]) => ({
          ...value,
          id: firestoreDocId,
          patientDisplay: value.patientName || value.email || value.patientEmail || value.userId || "",
        }));
      }
      return [];
    } catch (error) {
      console.error("Error fetching appointments for date:", error);
      return [];
    }
  };

  // Toggle day as closed/open
  const toggleDayClosed = async (date) => {
    // Prevent opening if already processing or closing
    if (isMarkingDateUnavailable || isClosingDateDialog.current || showDateUnavailableDialog) {
      return;
    }
    
    // Use local date formatting instead of UTC to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const dateKey = `${year}-${month}-${day}`;
    
    // Prevent re-triggering the same date within 500ms (debounce)
    const now = Date.now();
    if (lastToggledDateRef.current === dateKey && (now - lastToggledDateTimeRef.current) < 500) {
      return;
    }
    
    lastToggledDateRef.current = dateKey;
    lastToggledDateTimeRef.current = now;
    
    // Check if we're trying to mark the day as unavailable or make it available again
    const isMarkingUnavailable = !closedDays.includes(dateStr);
    
    if (isMarkingUnavailable) {
      // Always show confirmation dialog when marking as unavailable
      // Get appointments for this date
      const appointments = await getAppointmentsForDate(date);
      
      // Show confirmation dialog for all cases (with or without appointments)
      setDateToToggle(date);
      setDateAppointments(appointments);
      setShowDateUnavailableDialog(true);
      return;
    } else {
      // Making the date available again - also show confirmation
      const appointments = await getAppointmentsForDate(date);
      
      // Show confirmation dialog for making date available again
      setDateToToggle(date);
      setDateAppointments(appointments);
      setShowDateUnavailableDialog(true);
      return;
    }
  };

  // Subscribe to chair scheduling settings (Appointment Chair Conflict Prevention)
  useEffect(() => {
    const unsubscribe = subscribeToChairSettings((next) => {
      setChairSettings(next || { ...DEFAULT_CHAIR_SETTINGS });
    });
    return () => unsubscribe && unsubscribe();
  }, []);

  // Real-time map of unavailableTimeSlots for the selected modal date,
  // keyed by dentist UID, used by chair-capacity validations and the
  // replacement / removal modal dropdowns.
  useEffect(() => {
    if (!selectedModalDate) {
      setUnavailableSlotsByDentistForDate({});
      return undefined;
    }
    const dateStr = getLocalDateKey(selectedModalDate);
    const ref = doc(db, "unavailableTimeSlots", dateStr);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const raw = snap.data() || {};
          const next = {};
          Object.entries(raw).forEach(([uid, value]) => {
            if (Array.isArray(value)) next[uid] = value;
          });
          setUnavailableSlotsByDentistForDate(next);
        } else {
          setUnavailableSlotsByDentistForDate({});
        }
      },
      () => setUnavailableSlotsByDentistForDate({})
    );
    return () => unsubscribe();
  }, [selectedModalDate]);

  // Real-time map of chair swaps for the selected modal date.
  useEffect(() => {
    if (!selectedModalDate) {
      setChairSwapsBySlotForDate({});
      return undefined;
    }
    const dateStr = getLocalDateKey(selectedModalDate);
    const ref = doc(db, CHAIR_SWAPS_COLLECTION, dateStr);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setChairSwapsBySlotForDate({});
          return;
        }
        setChairSwapsBySlotForDate(normalizeChairSwaps(snap.data() || {}));
      },
      () => setChairSwapsBySlotForDate({})
    );
    return () => unsubscribe();
  }, [selectedModalDate]);

  // Real-time map of chair swaps for the main appointment-table date.
  useEffect(() => {
    if (!selectedDateAppointment) {
      setChairSwapsBySlotForMainDate({});
      return undefined;
    }
    let dateObj;
    try {
      dateObj =
        selectedDateAppointment &&
        typeof selectedDateAppointment.toDate === "function"
          ? selectedDateAppointment.toDate()
          : new Date(selectedDateAppointment);
    } catch (_) {
      setChairSwapsBySlotForMainDate({});
      return undefined;
    }
    if (!dateObj || Number.isNaN(dateObj.getTime())) {
      setChairSwapsBySlotForMainDate({});
      return undefined;
    }
    const dateStr = getLocalDateKey(dateObj);
    const ref = doc(db, CHAIR_SWAPS_COLLECTION, dateStr);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setChairSwapsBySlotForMainDate({});
          return;
        }
        setChairSwapsBySlotForMainDate(normalizeChairSwaps(snap.data() || {}));
      },
      () => setChairSwapsBySlotForMainDate({})
    );
    return () => unsubscribe();
  }, [selectedDateAppointment]);

  useEffect(() => {
    if (!selectedDateAppointment) {
      setUnavailableSlotsByDentistForMainDate({});
      return undefined;
    }
    let dateObj;
    try {
      dateObj =
        selectedDateAppointment &&
        typeof selectedDateAppointment.toDate === "function"
          ? selectedDateAppointment.toDate()
          : new Date(selectedDateAppointment);
    } catch (_) {
      setUnavailableSlotsByDentistForMainDate({});
      return undefined;
    }
    if (!dateObj || Number.isNaN(dateObj.getTime())) {
      setUnavailableSlotsByDentistForMainDate({});
      return undefined;
    }
    const dateStr = getLocalDateKey(dateObj);
    const ref = doc(db, "unavailableTimeSlots", dateStr);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const raw = snap.data() || {};
          const next = {};
          Object.entries(raw).forEach(([uid, value]) => {
            if (Array.isArray(value)) next[uid] = value;
          });
          setUnavailableSlotsByDentistForMainDate(next);
        } else {
          setUnavailableSlotsByDentistForMainDate({});
        }
      },
      () => setUnavailableSlotsByDentistForMainDate({})
    );
    return () => unsubscribe();
  }, [selectedDateAppointment]);

  // Remove stale legacy full-day non-roster fallback records for selected date.
  // Bench dentists should not be auto-materialized in unavailableTimeSlots unless
  // they are explicitly part of a swap flow.
  useEffect(() => {
    if (!selectedModalDate || !isChairCapacityConfigured(chairSettings)) return;
    const entries = unavailableSlotsByDentistForDate || {};
    const entryUids = Object.keys(entries);
    if (entryUids.length === 0) return;
    const dateStr = getLocalDateKey(selectedModalDate);
    const rosterSet = new Set(
      Array.isArray(chairSettings?.rosterDentistIds)
        ? chairSettings.rosterDentistIds
        : []
    );
    const canonicalSlots = generateTimeSlotsForDate(selectedModalDate);
    const swappedBenchSet = new Set(
      Object.values(chairSwapsBySlotForDate || {})
        .map((value) => value?.toBenchUid)
        .filter(Boolean)
    );
    const staleBenchUids = entryUids.filter((uid) => {
      if (!uid || rosterSet.has(uid)) return false;
      if (swappedBenchSet.has(uid)) return false;
      const slots = entries[uid];
      if (!Array.isArray(slots) || slots.length !== canonicalSlots.length) {
        return false;
      }
      return canonicalSlots.every((slot) => slots.includes(slot));
    });
    if (staleBenchUids.length === 0) return;
    const updates = {};
    staleBenchUids.forEach((uid) => {
      updates[uid] = deleteField();
    });
    updateDoc(doc(db, "unavailableTimeSlots", dateStr), updates).catch((error) => {
      console.error("Error cleaning stale non-roster unavailable slots:", error);
    });
  }, [
    selectedModalDate,
    chairSettings,
    unavailableSlotsByDentistForDate,
    chairSwapsBySlotForDate,
  ]);

  // Fetch dentists from Firebase
  useEffect(() => {
    const usersRef = collection(db, "users");
    const unsubscribe = onSnapshot(usersRef, (snapshot) => {
      const allDentists = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        // Exclude soft-deleted accounts so removed dentists disappear from
        // scheduling dropdowns immediately.
        const isDeleted =
          data.accountDeleted === true ||
          data.accountDeleted === "true" ||
          (typeof data.accountStatus === "string" &&
            data.accountStatus.toLowerCase() === "deleted");
        if (isDeleted) {
          return;
        }
        // Include all dentists regardless of email verification status
        // (they can sign in with mobile number even if email is not verified)
        if (data.userType === "DentistOwner" || data.role === "DentistOwner") {
          allDentists.push({
            uid: doc.id,
            firstName: data.firstName,
            lastName: data.lastName,
            displayName: buildDentistDisplayLabelFromUserDoc(data),
            role: "DentistOwner",
          });
        } else if (data.userType === "AssociateDentist" || data.role === "AssociateDentist") {
          allDentists.push({
            uid: doc.id,
            firstName: data.firstName,
            lastName: data.lastName,
            displayName: buildDentistDisplayLabelFromUserDoc(data),
            role: "AssociateDentist",
          });
        }
      });
      
      // Sort dentists by role first (DentistOwner first), then alphabetically by name
      allDentists.sort((a, b) => {
        // First sort by role: DentistOwner comes before AssociateDentist
        const roleOrder = { 'DentistOwner': 0, 'AssociateDentist': 1 };
        const roleA = roleOrder[a.role] ?? 2; // Default to 2 for unknown roles
        const roleB = roleOrder[b.role] ?? 2;
        
        if (roleA !== roleB) {
          return roleA - roleB;
        }
        
        // If roles are the same, sort alphabetically by display name
        const nameA = (a.displayName || `${a.firstName} ${a.lastName}`).toLowerCase();
        const nameB = (b.displayName || `${b.firstName} ${b.lastName}`).toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setDentists(allDentists);
    });
    return unsubscribe;
  }, []);

  // Real-time listener for unavailable time slots for selected modal date
  useEffect(() => {
    if (!selectedModalDate) {
      setUnavailableTimeSlots([]);
      setHasModalUnavailableRecord(false);
      return;
    }

    const year = selectedModalDate.getFullYear();
    const month = String(selectedModalDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedModalDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const isDateClosed = closedDays.includes(dateStr);

    if (isDateClosed) {
      setUnavailableTimeSlots(generateTimeSlotsForDate(selectedModalDate));
      setHasModalUnavailableRecord(false);
      return;
    }

    const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
    let isActive = true;

    const unsubscribe = onSnapshot(
      timeSlotsRef,
      (snapshot) => {
        if (!isActive) return;

        if (snapshot.exists()) {
          const data = snapshot.data();
          
          // If a specific dentist is selected, get only their slots
          if (selectedModalDentist) {
            const dentistSlots = data[selectedModalDentist];
            // If dentistSlots is undefined, check if it's a new dentist
            if (dentistSlots === undefined) {
              setHasModalUnavailableRecord(false);
              // Check if dentist is new - if so, show all slots as unavailable
              // Otherwise, show all slots as available (empty array)
              getDoc(doc(db, "users", selectedModalDentist)).then(dentistDoc => {
                if (dentistDoc.exists()) {
                  const dentistData = dentistDoc.data();
                  const shouldDefaultUnavailable = shouldDefaultUnavailableByLegacySetup({
                    dentistUid: selectedModalDentist,
                    settings: chairSettings,
                    hasScheduleSetup: dentistData.hasScheduleSetup,
                  });
                  const isBenchInChairMode =
                    isChairCapacityConfigured(chairSettings) &&
                    !isDentistOnRoster(selectedModalDentist, chairSettings);
                  if (shouldDefaultUnavailable) {
                    if (isActive) {
                      // Non-roster dentists should render empty slots when unswapped.
                      setUnavailableTimeSlots(
                        isBenchInChairMode
                          ? []
                          : generateTimeSlotsForDate(selectedModalDate)
                      );
                    }
                  } else {
                    // Existing dentist: all slots available
                    if (isActive) {
                      setUnavailableTimeSlots([]);
                    }
                  }
                } else {
                  // Dentist not found: default to all available
                  if (isActive) {
                    setUnavailableTimeSlots([]);
                  }
                }
              }).catch(() => {
                // On error, default to all available
                if (isActive) {
                  setUnavailableTimeSlots([]);
                }
              });
            } else {
              // Dentist has data in Firestore, use it
              if (isActive) {
                setUnavailableTimeSlots(dentistSlots);
                setHasModalUnavailableRecord(true);
              }
            }
          } else {
            setHasModalUnavailableRecord(false);
            // If no dentist is selected, collect all unavailable slots from all dentists
            const allSlots = new Set();
            Object.values(data).forEach(dentistSlots => {
              if (Array.isArray(dentistSlots)) {
                dentistSlots.forEach(slot => allSlots.add(slot));
              }
            });
            if (isActive) {
              setUnavailableTimeSlots(Array.from(allSlots));
            }
          }
        } else {
          setHasModalUnavailableRecord(false);
          // No document exists - check if it's a new dentist
          if (selectedModalDentist) {
            getDoc(doc(db, "users", selectedModalDentist)).then(dentistDoc => {
              if (dentistDoc.exists()) {
                const dentistData = dentistDoc.data();
                const shouldDefaultUnavailable = shouldDefaultUnavailableByLegacySetup({
                  dentistUid: selectedModalDentist,
                  settings: chairSettings,
                  hasScheduleSetup: dentistData.hasScheduleSetup,
                });
                const isBenchInChairMode =
                  isChairCapacityConfigured(chairSettings) &&
                  !isDentistOnRoster(selectedModalDentist, chairSettings);
                if (shouldDefaultUnavailable) {
                  if (isActive) {
                    // Non-roster dentists should render empty slots when unswapped.
                    setUnavailableTimeSlots(
                      isBenchInChairMode
                        ? []
                        : generateTimeSlotsForDate(selectedModalDate)
                    );
                  }
                } else {
                  // Existing dentist: all slots available
                  if (isActive) {
                    setUnavailableTimeSlots([]);
                  }
                }
              } else {
                // Dentist not found: default to all available
                if (isActive) {
                  setUnavailableTimeSlots([]);
                }
              }
            }).catch(() => {
              // On error, default to all available
              if (isActive) {
                setUnavailableTimeSlots([]);
              }
            });
          } else {
            // No dentist selected and no document: all available
            if (isActive) {
              setUnavailableTimeSlots([]);
            }
          }
        }
      },
      (error) => {
        if (isActive) {
          console.error("Error listening to unavailable time slots:", error);
          setUnavailableTimeSlots([]);
          setHasModalUnavailableRecord(false);
        }
      }
    );

    return () => {
      isActive = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [selectedModalDate, selectedModalDentist, chairSettings, closedDays]);

  // Fetch unavailable time slots from Firebase for a specific date and dentist
  const fetchUnavailableTimeSlots = async (date, dentistId) => {
    if (!date) return [];
    
    try {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      // If the date is in closedDays, return all time slots as unavailable
      if (closedDays.includes(dateStr)) {
        return generateTimeSlotsForDate(date);
      }
      
      const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
      const timeSlotsSnap = await getDoc(timeSlotsRef);
      
      if (timeSlotsSnap.exists()) {
        const data = timeSlotsSnap.data();
        const slots = data[dentistId];
        
        // If dentist has data in Firestore, use it
        if (slots !== undefined) {
          return slots;
        }
      }
      
      // No Firestore data for this dentist/date - check if it's a new dentist
      if (dentistId) {
        const dentistRef = doc(db, "users", dentistId);
        const dentistDoc = await getDoc(dentistRef);
        if (dentistDoc.exists()) {
          const dentistData = dentistDoc.data();
          const shouldDefaultUnavailable = shouldDefaultUnavailableByLegacySetup({
            dentistUid: dentistId,
            settings: chairSettings,
            hasScheduleSetup: dentistData.hasScheduleSetup,
          });

          // Chair-aware fallback defaulting for dentists without per-day slot data.
          if (shouldDefaultUnavailable) {
            const isBenchInChairMode =
              isChairCapacityConfigured(chairSettings) &&
              !isDentistOnRoster(dentistId, chairSettings);
            // Do not auto-persist non-roster fallback arrays.
            return isBenchInChairMode ? [] : generateTimeSlotsForDate(date);
          }
        }
      }
      
      // Default: all slots available (empty array)
      return [];
    } catch (error) {
      console.error("Error fetching unavailable time slots:", error);
      return [];
    }
  };

      // Get appointment counts for a specific date
      const getAppointmentCountsForDate = (date) => {
        if (!date) return { pending: 0, confirmed: 0, completed: 0 };
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        // Filter appointments for this specific date from ALL appointments, not just modal appointments
        let dateAppointments = allAppointments.filter(appointment => {
          const appointmentDate = appointment.date;
          return appointmentDate === dateStr;
        });
        
        // If user is DentistOwner or AssociateDentist, only count their own appointments
        if (isCurrentUserDentist() && selectedDentist) {
          dateAppointments = dateAppointments.filter(appointment => {
            // Check if appointment belongs to current dentist
            if (appointment.dentistId && appointment.dentistId === selectedDentist) {
              return true;
            }
            // Fallback to display name matching
            const currentDentist = dentists.find(d => d.uid === selectedDentist);
            if (currentDentist) {
              const displayName = getDentistDisplayName(currentDentist);
              return appointment.dentist === displayName;
            }
            return false;
          });
        }
        
        const counts = { pending: 0, confirmed: 0, completed: 0 };
        dateAppointments.forEach(appointment => {
          if (appointment.status === "Pending" || appointment.status === "New" || appointment.status === "pending" || appointment.status === "new") {
            counts.pending++;
          } else if (appointment.status === "Confirmed" || appointment.status === "confirmed") {
            counts.confirmed++;
          } else if (appointment.status === "Completed" || appointment.status === "completed") {
            counts.completed++;
          }
        });
        
        return counts;
      };

  // Render calendar tile with appointment badges
  const renderCalendarTile = (date, view) => {
    if (view !== 'month') {
      return null;
    }
    
    const appointmentCounts = getAppointmentCountsForDate(date);
    const hasAppointments = appointmentCounts.pending > 0 || appointmentCounts.confirmed > 0 || appointmentCounts.completed > 0;
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    
    if (!hasAppointments) {
      return null;
    }
    
    
    return (
      <div style={{ 
        position: 'absolute', 
        top: '-35px', 
        left: '50%', 
        transform: 'translateX(-50%)', 
        display: 'flex', 
        gap: '3px',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
        pointerEvents: 'none'
      }}>
        {appointmentCounts.confirmed > 0 && (
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#4CAF50',
            border: '1px solid white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.6)'
          }} />
        )}
        {appointmentCounts.pending > 0 && (
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#FFA500',
            border: '1px solid white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.6)'
          }} />
        )}
      </div>
    );
  };
  // Check if a time slot has a booked appointment
  const checkTimeSlotForBookedAppointment = (timeSlot, date, dentistUid = null) => {
    
    if (!modalAppointments || modalAppointments.length === 0) {
      return null;
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Parse the time slot (e.g., "10:00 AM - 10:30 AM")
    const [startTimeStr, endTimeStr] = timeSlot.split(' - ');
    const slotStartMinutes = timeToMinutes(startTimeStr);
    const slotEndMinutes = timeToMinutes(endTimeStr);
    
    // Find appointments that match this specific time slot
    const matchingAppointments = modalAppointments.filter(appointment => {
      
      // Skip cancelled appointments
      if (appointment.status === "Cancelled" || appointment.status === "cancelled") {
        return false;
      }
      
      // If dentistUid is provided, filter by dentist
      if (dentistUid) {
        const linkedBenchUidsForRosterFilter =
          isChairCapacityConfigured(chairSettings) &&
          isDentistOnRoster(dentistUid, chairSettings)
            ? getRosterPartnerBenchUidsForDay(chairSwapsBySlotForDate, dentistUid)
            : [];
        const benchPartnerRosterUids =
          isChairCapacityConfigured(chairSettings) &&
          !isDentistOnRoster(dentistUid, chairSettings)
            ? getBenchPartnerRosterUidsForDay(chairSwapsBySlotForDate, dentistUid)
            : [];
        // First try to match by dentistId field (more reliable)
        if (appointment.dentistId) {
          const matchesPrimaryDentist = appointment.dentistId === dentistUid;
          const matchesLinkedRoster =
            benchPartnerRosterUids.length > 0 &&
            benchPartnerRosterUids.includes(appointment.dentistId);
          const matchesLinkedBench =
            linkedBenchUidsForRosterFilter.length > 0 &&
            linkedBenchUidsForRosterFilter.includes(appointment.dentistId);
          if (!matchesPrimaryDentist && !matchesLinkedRoster && !matchesLinkedBench) {
            return false;
          }
          // If dentistId matches, this appointment belongs to the selected dentist
        } else {
          // Fallback to display name matching if dentistId is not present
          const dentist = dentists.find(d => d.uid === dentistUid);
          if (dentist) {
            const dentistDisplayName = getDentistDisplayName(dentist);
            if (appointment.dentist !== dentistDisplayName) {
              return false;
            }
          } else {
            return false;
          }
        }
      }
      
      // Skip appointments without time information
      if (!appointment.startTime && !appointment.time && !appointment.endTime) {
        return false;
      }
      
      // Determine appointment start and end times
      let appointmentStart, appointmentEnd;
      
      if (appointment.startTime && appointment.endTime) {
        // Both start and end times are available
        appointmentStart = appointment.startTime;
        appointmentEnd = appointment.endTime;
      } else if (appointment.startTime && !appointment.endTime) {
        // Only start time available, calculate end time
        appointmentStart = appointment.startTime;
        const duration = appointment.duration || 30;
        const startMinutes = timeToMinutes(appointmentStart);
        const endMinutes = startMinutes + duration;
        appointmentEnd = minutesToTime(endMinutes);
      } else if (appointment.time && !appointment.endTime) {
        // Legacy time field, calculate end time
        appointmentStart = appointment.time;
        const duration = appointment.duration || 30;
        const startMinutes = timeToMinutes(appointmentStart);
        const endMinutes = startMinutes + duration;
        appointmentEnd = minutesToTime(endMinutes);
      } else if (!appointment.startTime && appointment.endTime) {
        // Only end time available, calculate start time
        appointmentEnd = appointment.endTime;
        const duration = appointment.duration || 30;
        const endMinutes = timeToMinutes(appointmentEnd);
        const startMinutes = endMinutes - duration;
        appointmentStart = minutesToTime(startMinutes);
      } else {
        return false;
      }
      
      // Convert appointment times to minutes for comparison
      const appointmentStartMinutes = timeToMinutes(appointmentStart);
      const appointmentEndMinutes = timeToMinutes(appointmentEnd);
      
      // Check for exact match or overlap with the specific time slot
      // We want to find appointments that either:
      // 1. Start exactly at the slot start time, OR
      // 2. End exactly at the slot end time, OR  
      // 3. Overlap with the slot (start before slot ends and end after slot starts)
      const hasExactStartMatch = appointmentStartMinutes === slotStartMinutes;
      const hasExactEndMatch = appointmentEndMinutes === slotEndMinutes;
      const hasOverlap = (appointmentStartMinutes < slotEndMinutes && appointmentEndMinutes > slotStartMinutes);
      
      return hasExactStartMatch || hasExactEndMatch || hasOverlap;
    });
    
    // Sort by start time to get the most relevant appointment
    matchingAppointments.sort((a, b) => {
      const aStart = timeToMinutes(a.startTime || a.time);
      const bStart = timeToMinutes(b.startTime || b.time);
      return aStart - bStart;
    });
    
    // Return the appointment that best matches this time slot
    // Prefer appointments that start exactly at the slot start time
    const exactStartMatch = matchingAppointments.find(appointment => {
      const appointmentStart = appointment.startTime || appointment.time;
      const appointmentStartMinutes = timeToMinutes(appointmentStart);
      return appointmentStartMinutes === slotStartMinutes;
    });
    
    if (exactStartMatch) {
      return exactStartMatch;
    }
    
    // If no exact start match, return the first overlapping appointment
    return matchingAppointments.length > 0 ? matchingAppointments[0] : null;
  };

  /** Grid pill label: when an appointment starts at this row but ends after the half-hour grid end, show real end (matches patient booking). */
  const getModalSlotBookedDisplayLabel = (gridTimeSlotStr, bookedAppointment) => {
    if (!gridTimeSlotStr || !bookedAppointment) return gridTimeSlotStr;
    const parts = gridTimeSlotStr.split(" - ").map((p) => p.trim());
    if (parts.length < 2) return gridTimeSlotStr;
    const gridStartMin = timeToMinutes(parts[0]);
    const gridEndMin = timeToMinutes(parts[1]);
    const apptStartMin = timeToMinutes(bookedAppointment.startTime || bookedAppointment.time);
    if (!Number.isFinite(gridStartMin) || !Number.isFinite(gridEndMin)) return gridTimeSlotStr;
    if (apptStartMin !== gridStartMin) return gridTimeSlotStr;
    const apptEndMin = bookedAppointment.endTime
      ? timeToMinutes(bookedAppointment.endTime)
      : apptStartMin + (bookedAppointment.duration || 30);
    if (!Number.isFinite(apptEndMin) || apptEndMin <= gridEndMin) return gridTimeSlotStr;
    const endStr = bookedAppointment.endTime
      ? bookedAppointment.endTime
      : minutesToTime(apptEndMin);
    return `${formatTimeConsistent(
      bookedAppointment.startTime || bookedAppointment.time
    )} - ${formatTimeConsistent(endStr)}`;
  };

  const handleSelectAllTimeSlots = () => {
    if (!selectedModalDate || !selectedModalDentist) return;
    const selectableSlots = getModalSelectableTimeSlots();
    
    const currentlySelectedCount = selectedTimeSlots.length;
    const totalSelectableSlots = selectableSlots.length;

    if (currentlySelectedCount === totalSelectableSlots && totalSelectableSlots > 0) {
      // All selectable slots are selected, so deselect all
      setSelectedTimeSlots([]);
    } else {
      // Select all selectable slots (including unavailable ones)
      setSelectedTimeSlots(selectableSlots);
    }
  };

  /** Matches PatientAppointmentBooking: roster filler inside linked bench inherited timeline. */
  const computeRosterBenchInheritedUnavailableForModal = useCallback(
    (timeSlot) => {
      if (
        !timeSlot ||
        !selectedModalDate ||
        !selectedModalDentist ||
        !isChairCapacityConfigured(chairSettings) ||
        !isDentistOnRoster(selectedModalDentist, chairSettings)
      ) {
        return false;
      }
      const sourceRange = parseChairSlotLabelToRange(timeSlot, timeToMinutes);
      if (!sourceRange) return false;
      const inheritedSlots = Object.values(chairSwapsBySlotForDate || {})
        .filter(
          (entry) =>
            entry?.fromRosterUid === selectedModalDentist && entry?.toBenchUid
        )
        .map((entry) => entry?.slot)
        .filter(Boolean);
      return inheritedSlots.some((slotLabel) => {
        const inheritedRange = parseChairSlotLabelToRange(slotLabel, timeToMinutes);
        return (
          !!inheritedRange &&
          sourceRange.start < inheritedRange.end &&
          sourceRange.end > inheritedRange.start
        );
      });
    },
    [
      selectedModalDentist,
      chairSwapsBySlotForDate,
      timeToMinutes,
    ]
  );

  // Function to get the current status of a time slot (aligned with manage-calendar chip logic:
  // bench dentists inherit swap windows; roster dentists inherit linked-bench booking blocks.)
  const getTimeSlotStatus = (timeSlot) => {
      // Check if the selected date is globally closed
      if (selectedModalDate) {
        const year = selectedModalDate.getFullYear();
        const month = String(selectedModalDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedModalDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        if (closedDays.includes(dateStr)) {
          return { status: "Unavailable", details: "Clinic is closed on this date" };
        }
      }

      if (
        !selectedModalDentist ||
        selectedModalDentist === "clinic" ||
        !selectedModalDate
      ) {
        const dentistSlots = unavailableTimeSlots || [];
        const isUnavailable = dentistSlots.includes(timeSlot);
        return isUnavailable
          ? { status: "Unavailable", details: "Currently unavailable" }
          : { status: "Available", details: "Currently available" };
      }

      const dentistSlots = unavailableTimeSlots || [];
      const inChairMode = isChairCapacityConfigured(chairSettings);
      const isBenchSelected =
        inChairMode &&
        selectedModalDentist &&
        !isDentistOnRoster(selectedModalDentist, chairSettings);
      const benchSwapSlots = isBenchSelected
        ? getBenchSwapSlots({
            swapsBySlot: chairSwapsBySlotForDate,
            benchUid: selectedModalDentist,
          })
        : [];
      const linkedBenchUidsForRoster =
        inChairMode &&
        selectedModalDentist &&
        isDentistOnRoster(selectedModalDentist, chairSettings)
          ? Array.from(
              new Set(
                Object.values(chairSwapsBySlotForDate || {})
                  .filter(
                    (entry) =>
                      entry?.fromRosterUid === selectedModalDentist &&
                      entry?.toBenchUid
                  )
                  .map((entry) => entry.toBenchUid)
              )
            )
          : [];
      const inheritedSlotsForRoster =
        inChairMode &&
        selectedModalDentist &&
        isDentistOnRoster(selectedModalDentist, chairSettings)
          ? getRosterSecondarySlots(selectedModalDentist)
          : [];
      const currentSlotRange = parseChairSlotLabelToRange(timeSlot, timeToMinutes);
      const overlapsInheritedRosterSlot =
        !!currentSlotRange &&
        inheritedSlotsForRoster.some((slotLabel) =>
          doRangesOverlap(
            currentSlotRange,
            parseChairSlotLabelToRange(slotLabel, timeToMinutes)
          )
        );

      const bookedAppointment = checkTimeSlotForBookedAppointment(
        timeSlot,
        selectedModalDate,
        selectedModalDentist
      );
      const isBooked = bookedAppointment !== null;
      const bookedUid = isBooked
        ? resolveAppointmentDentistUid(bookedAppointment)
        : null;

      const overlapsBenchSwapStatus =
        isBenchSelected && doesSlotRangeOverlapSwapSlots(timeSlot, benchSwapSlots);
      const benchOutsideSwapOwnsBookingStatus =
        isBenchSelected &&
        isBooked &&
        bookedUid === selectedModalDentist;

      const baseUnavailable = isBenchSelected
        ? (!overlapsBenchSwapStatus && !benchOutsideSwapOwnsBookingStatus) ||
          isSlotEffectivelyUnavailableForDentist({
            slot: timeSlot,
            dentistUid: selectedModalDentist,
            dentistUnavailableSlots: dentistSlots,
            hasUnavailabilityRecord:
              hasModalUnavailableRecord || benchSwapSlots.length > 0,
            settings: chairSettings,
            timeToMinutes,
          })
        : isSlotEffectivelyUnavailableForDentist({
            slot: timeSlot,
            dentistUid: selectedModalDentist,
            dentistUnavailableSlots: dentistSlots,
            hasUnavailabilityRecord: hasModalUnavailableRecord,
            settings: chairSettings,
            timeToMinutes,
          });

      const isUnavailable = isBooked
        ? isBenchSelected
          ? bookedUid !== selectedModalDentist
          : linkedBenchUidsForRoster.includes(bookedUid)
        : baseUnavailable ||
          overlapsInheritedRosterSlot ||
          computeRosterBenchInheritedUnavailableForModal(timeSlot);

      if (isUnavailable) {
        return { status: "Unavailable", details: "Currently unavailable" };
      }
      return { status: "Available", details: "Currently available" };
    };

  const fetchLinkedBenchConflictMessageForRosterMakeAvailable = async ({
    rosterUid,
    timeSlotsToOpen,
    dateStr,
  }) => {
    if (
      !isChairCapacityConfigured(chairSettings) ||
      !rosterUid ||
      !dateStr ||
      !Array.isArray(timeSlotsToOpen) ||
      timeSlotsToOpen.length === 0 ||
      !isDentistOnRoster(rosterUid, chairSettings)
    ) {
      return "";
    }
    const linkedBenchUids = Array.from(
      new Set(
        Object.values(chairSwapsBySlotForDate || {})
          .filter(
            (entry) =>
              entry?.fromRosterUid === rosterUid && entry?.toBenchUid
          )
          .map((entry) => entry.toBenchUid)
      )
    );
    if (linkedBenchUids.length === 0) {
      return "";
    }

    const appointmentsSnap = await getDoc(doc(db, "appointments", dateStr));
    if (!appointmentsSnap.exists()) {
      return "";
    }

    const conflictingNamesByUid = new Map();

    Object.values(appointmentsSnap.data()).forEach((appointment) => {
      if (!appointment || appointment.date !== dateStr) return;
      if (!isBlockingAppointmentStatus(appointment.status)) return;

      const apptUid = resolveAppointmentDentistUid(appointment);
      if (!apptUid || !linkedBenchUids.includes(apptUid)) return;

      const startDisplay = formatTimeConsistent(appointment.startTime || appointment.time);
      const endDisplay = appointment.endTime
        ? formatTimeConsistent(appointment.endTime)
        : formatTimeConsistent(
            minutesToTime(
              timeToMinutes(startDisplay) + (appointment.duration || 30)
            )
          );
      const appointmentStartMinutes = timeToMinutes(startDisplay);
      const appointmentEndMinutes = timeToMinutes(endDisplay);

      const overlapsAnySelectedSlot = timeSlotsToOpen.some((timeSlot) => {
        const [slotStartRaw, slotEndRaw] = (timeSlot || "")
          .split(" - ")
          .map((part) => part.trim());
        if (!slotStartRaw || !slotEndRaw) return false;
        const slotStartMinutes = timeToMinutes(slotStartRaw);
        const slotEndMinutes = timeToMinutes(slotEndRaw);
        return (
          appointmentStartMinutes < slotEndMinutes &&
          appointmentEndMinutes > slotStartMinutes
        );
      });

      if (overlapsAnySelectedSlot) {
        const display =
          appointment.dentist ||
          getDentistDisplayNameFromUID(apptUid) ||
          "Another dentist";
        conflictingNamesByUid.set(apptUid, display);
      }
    });

    if (conflictingNamesByUid.size === 0) {
      return "";
    }

    const names = Array.from(conflictingNamesByUid.values());
    if (names.length === 1) {
      return `${names[0]} already has patient appointments within the assigned timeslots. Please cancel the patient appointments first.`;
    }
    return `${names.join(
      ", "
    )} already have patient appointments within the assigned timeslots. Please cancel the patient appointments first.`;
  };

  const getAppointmentStatusVisuals = (status = "") => {
    const normalized = (status || "").toString().trim().toLowerCase();
    if (normalized === "confirmed") {
      return {
        cardBackground: "#E8F5E8",
        cardBorder: "2px solid #4CAF50",
        badgeBackground: "#E6F4EA",
        badgeBorder: "2px solid #4CAF50",
        badgeText: "#1B5E20",
      };
    }
    if (normalized === "pending" || normalized === "new") {
      return {
        cardBackground: "#FFF8E1",
        cardBorder: "2px solid #FFA500",
        badgeBackground: "#FFE8C2",
        badgeBorder: "2px solid #FFA500",
        badgeText: "#B26B00",
      };
    }
    if (normalized === "unavailable" || normalized === "cancelled" || normalized === "canceled") {
      return {
        cardBackground: "#FFEBEE",
        cardBorder: "2px solid #F44336",
        badgeBackground: "#FFD6D6",
        badgeBorder: "2px solid #F44336",
        badgeText: "#C62828",
      };
    }
    return {
      cardBackground: "#E0E0E0",
      cardBorder: "1px solid rgba(59, 43, 31, 0.15)",
      badgeBackground: "#E0E0E0",
      badgeBorder: "1px solid rgba(59, 43, 31, 0.2)",
      badgeText: "#2C251E",
    };
  };

  const handleSaveMultipleSlots = async () => {
    if (selectedTimeSlots.length === 0) return;

    // Count using the same swap-aware status source used by the modal badges.
    const unavailableCount = selectedTimeSlots.filter(
      (slot) => getTimeSlotStatus(slot).status === "Unavailable"
    ).length;
    const availableCount = selectedTimeSlots.length - unavailableCount;
    
    // Determine the action based on majority or first slot if equal
    let action;
    if (unavailableCount > availableCount) {
      // More slots are unavailable, so we'll make them available
      action = 'available';
    } else if (availableCount > unavailableCount) {
      // More slots are available, so we'll make them unavailable
      action = 'unavailable';
    } else {
      // Equal count, use the first slot's state to determine action
      const firstSlot = selectedTimeSlots[0];
      action =
        getTimeSlotStatus(firstSlot).status === "Unavailable"
          ? "available"
          : "unavailable";
    }
    
    // Set the action for the multi-confirmation dialog
    setMultiConfirmationAction(action);
    setMultiSlotValidationMessage("");
    setMultiSlotValidationSlots([]);
    setMultiSlotConflictingAppointments([]);
    
    // Show the multi-confirmation dialog
    setShowMultiConfirmationDialog(true);
  };

  const handleCancelMultipleSlots = () => {
    setSelectedTimeSlots([]);
  };

  const confirmMultipleSlotsAction = async (action) => {
    if (selectedTimeSlots.length === 0) return;
    if (!selectedModalDate) return;

    // Use the provided action parameter, or fall back to multiConfirmationAction for backward compatibility
    const targetAction = action || multiConfirmationAction;
    if (!targetAction) return;

    const slotsForNotification = [...selectedTimeSlots];
    // Check if the date is globally unavailable and we're trying to make slots available
    const year = selectedModalDate.getFullYear();
    const month = String(selectedModalDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedModalDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    if (targetAction === 'available' && closedDays.includes(dateStr)) {
      return;
    }

    if (targetAction === "available") {
      const slotsToOpen = selectedTimeSlots.filter(
        (slot) => getTimeSlotStatus(slot).status === "Unavailable"
      );
      const linkedBenchMsg =
        await fetchLinkedBenchConflictMessageForRosterMakeAvailable({
          rosterUid: selectedModalDentist,
          timeSlotsToOpen: slotsToOpen,
          dateStr,
        });
      if (linkedBenchMsg) {
        setMultiSlotValidationMessage(linkedBenchMsg);
        setMultiSlotValidationSlots([]);
        setMultiSlotConflictingAppointments([]);
        return;
      }
    }

    if (targetAction === 'unavailable') {
      const appointmentsRef = doc(db, "appointments", dateStr);
      const appointmentsSnap = await getDoc(appointmentsRef);
      if (appointmentsSnap.exists()) {
        const appointmentsData = appointmentsSnap.data();
        const conflictingSlots = new Set();
        const conflictRecordMap = new Map();

        Object.entries(appointmentsData).forEach(([appointmentId, appointment]) => {
          if (appointment && appointment.date === dateStr) {
            const belongsToSelectedDentist =
              appointment.dentistId === selectedModalDentist ||
              (appointment.dentist &&
                appointment.dentist === getDentistDisplayNameFromUID(selectedModalDentist));

            if (belongsToSelectedDentist) {
              const startDisplay = formatTimeConsistent(appointment.startTime || appointment.time);
              const endDisplay = appointment.endTime
                ? formatTimeConsistent(appointment.endTime)
                : formatTimeConsistent(
                    minutesToTime(timeToMinutes(startDisplay) + (appointment.duration || 30))
                  );
              const appointmentStartMinutes = timeToMinutes(startDisplay);
              const appointmentEndMinutes = timeToMinutes(endDisplay);
              const appointmentSlotKey = `${startDisplay} - ${endDisplay}`;

              const overlapsSelection = selectedTimeSlots.some((timeSlot) => {
                const [slotStartRaw, slotEndRaw] = (timeSlot || "").split(" - ").map((part) => part.trim());
                if (!slotStartRaw || !slotEndRaw) return false;
                const slotStartMinutes = timeToMinutes(slotStartRaw);
                const slotEndMinutes = timeToMinutes(slotEndRaw);
                return appointmentStartMinutes < slotEndMinutes && appointmentEndMinutes > slotStartMinutes;
              });

              const statusNormalized = (appointment.status || "")
                .toString()
                .trim()
                .toLowerCase();
              if (
                overlapsSelection &&
                statusNormalized !== "cancelled" &&
                statusNormalized !== "canceled"
              ) {
                conflictingSlots.add(appointmentSlotKey);
                if (!conflictRecordMap.has(appointmentId)) {
                  conflictRecordMap.set(appointmentId, {
                    key: appointmentId,
                    slot: appointmentSlotKey,
                    appointmentId,
                    appointment: { id: appointmentId, ...appointment },
                    appointmentStart: startDisplay,
                    appointmentEnd: endDisplay,
                  });
                }
              }
            }
          }
        });

        if (conflictingSlots.size > 0) {
          const sortedConflicts = Array.from(conflictingSlots).sort((a, b) => {
            const toMinutes = (slot) => {
              const [start] = slot.split(" - ");
              return timeToMinutes(start);
            };
            return toMinutes(a) - toMinutes(b);
          });

          setMultiSlotConflictingAppointments(Array.from(conflictRecordMap.values()));
          setMultiSlotValidationSlots(sortedConflicts);
          setMultiSlotValidationMessage(
            "Selected timeslot(s) contain patient appointments. Please cancel the patient appointments first."
          );
          return;
        }
        setMultiSlotConflictingAppointments([]);
        setMultiSlotValidationSlots([]);
      }
    }

    // Chair Conflict Prevention intercept should run only after clinic personnel
    // confirms the action in this modal (not when they click Save(N) below the grid),
    // and only after we pass patient-appointment conflict checks.
    const isSlotCurrentlyUnavailable = (slot) =>
      getTimeSlotStatus(slot).status === "Unavailable";
    const slotsForChairFlow =
      targetAction === "available"
        ? selectedTimeSlots.filter((slot) => isSlotCurrentlyUnavailable(slot))
        : selectedTimeSlots.filter((slot) => !isSlotCurrentlyUnavailable(slot));
    if (
      slotsForChairFlow.length > 0 &&
      maybeOpenChairAwareModal({
        dentistUid: selectedModalDentist,
        slots: slotsForChairFlow,
        isMakingUnavailable: targetAction === "unavailable",
      })
    ) {
      setShowMultiConfirmationDialog(false);
      setSelectedTimeSlots([]);
      setMultiConfirmationAction(null);
      setMultiSlotValidationMessage("");
      setMultiSlotValidationSlots([]);
      setMultiSlotConflictingAppointments([]);
      return;
    }

    setMultiSlotValidationMessage("");
    setMultiSlotConflictingAppointments([]);
    setIsMarkingTimeSlotUnavailable(true);

    try {
      // Update the unavailable time slots
      const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
      const timeSlotsSnap = await getDoc(timeSlotsRef);
      const currentData = timeSlotsSnap.exists() ? timeSlotsSnap.data() : {};

      // Update only the selected dentist's slots
      const updateData = {};
      
        // For individual dentist changes, update only the selected dentist
        const currentSlots = currentData[selectedModalDentist] || [];
        let updatedSlots = [...currentSlots];
        
        selectedTimeSlots.forEach(timeSlot => {
          if (targetAction === 'available') {
            // Make slot available
            updatedSlots = updatedSlots.filter(slot => slot !== timeSlot);
          } else {
            // Make slot unavailable
            if (!updatedSlots.includes(timeSlot)) {
              updatedSlots.push(timeSlot);
            }
          }
        });
        
        updateData[selectedModalDentist] = updatedSlots;

      await setDoc(timeSlotsRef, updateData, { merge: true });

      // Update local state for individual dentist
        setUnavailableTimeSlots(updateData[selectedModalDentist] || []);

      await notifyClinicPersonnelForTimeSlots({
        dentistId: selectedModalDentist,
        dateStr,
        slots: slotsForNotification,
        action: targetAction,
      });

    } catch (error) {
      console.error("Error updating multiple time slots:", error);
    } finally {
      setIsMarkingTimeSlotUnavailable(false);
      setShowMultiConfirmationDialog(false);
      setSelectedTimeSlots([]);
      setMultiConfirmationAction(null);
      setMultiSlotValidationMessage("");
      setMultiSlotValidationSlots([]);
    }
  };
    
  const toggleTimeSlotUnavailable = async (timeSlot) => {
    if (!selectedModalDate) {
      console.error("No date selected");
      return;
    }
    
    // Prevent opening if already processing or closing
    if (isMarkingTimeSlotUnavailable || isClosingTimeSlotDialog.current || showTimeSlotDialog) {
      return;
    }
    
    // Prevent re-triggering the same time slot within 500ms (debounce)
    const now = Date.now();
    if (lastToggledTimeSlotRef.current === timeSlot && (now - lastToggledTimeSlotTimeRef.current) < 500) {
      return;
    }
    
    lastToggledTimeSlotRef.current = timeSlot;
    lastToggledTimeSlotTimeRef.current = now;
    
    const year = selectedModalDate.getFullYear();
    const month = String(selectedModalDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedModalDate.getDate()).padStart(2, '0');
    
    // Get current unavailable slots for this date and dentist
    const currentSlots = await fetchUnavailableTimeSlots(selectedModalDate, selectedModalDentist);
    
    // Check if we're trying to mark a slot as unavailable or available
    const isMakingUnavailable = !currentSlots.includes(timeSlot);

    // Chair Conflict Prevention intercept (single-slot click flow)
    if (
      maybeOpenChairAwareModal({
        dentistUid: selectedModalDentist,
        slots: [timeSlot],
        isMakingUnavailable,
      })
    ) {
      return;
    }
    
    // For individual dentist operations, proceed directly to confirmation dialog
    
    if (isMakingUnavailable) {
      // Check if this time slot has a booked appointment
      const conflictingAppointment = checkTimeSlotForBookedAppointment(timeSlot, selectedModalDate, selectedModalDentist);
      
      if (conflictingAppointment) {
        // Show conflict dialog for appointments
        setConflictTimeSlot(timeSlot);
        setConflictAppointment(conflictingAppointment);
        setShowConflictDialog(true);
        return;
      } else {
        // No appointment conflict, show confirmation dialog for marking unavailable
        setTimeSlotToToggle(timeSlot);
        setTimeSlotAppointment(null);
        setTimeSlotDialogValidationMessage("");
        setShowTimeSlotDialog(true);
        return;
      }
    } else {
      // Making slot available again, show confirmation dialog
      setTimeSlotToToggle(timeSlot);
      setTimeSlotAppointment(null);
      setTimeSlotDialogValidationMessage("");
      setShowTimeSlotDialog(true);
      return;
    }
  };

  const handleBulkCancelAppointments = async () => {
    if (!selectedModalDate || multiSlotConflictingAppointments.length === 0) {
      setShowMultiSlotCancellationDialog(false);
      return;
    }

    const appointmentMap = new Map();
    multiSlotConflictingAppointments.forEach(({ appointmentId, appointment, slot }) => {
      if (!appointmentId) return;
      if (!appointmentMap.has(appointmentId)) {
        appointmentMap.set(appointmentId, {
          appointment: { id: appointmentId, ...appointment },
          slots: new Set(),
        });
      }
      appointmentMap.get(appointmentId).slots.add(slot);
    });

    const appointmentsToCancel = Array.from(appointmentMap.values()).map((entry) => ({
      appointment: entry.appointment,
      slots: Array.from(entry.slots),
    }));

    if (appointmentsToCancel.length === 0) {
      setShowMultiSlotCancellationDialog(false);
      return;
    }

    const formattedDate = new Date(
      selectedModalDate.getTime() - selectedModalDate.getTimezoneOffset() * 60000
    )
      .toISOString()
      .split("T")[0];

    setIsBulkCancellingAppointments(true);

    try {
      const appointmentsRef = doc(db, "appointments", formattedDate);

      const updates = {};
      appointmentsToCancel.forEach(({ appointment }) => {
        if (appointment?.id) {
          updates[appointment.id] = deleteField();
        }
      });

      if (Object.keys(updates).length > 0) {
        await updateDoc(appointmentsRef, updates);
      }

      for (const { appointment } of appointmentsToCancel) {
        await sendCancellationNotifications(appointment, formattedDate, "Unavailable");
      }

      const cancelledIds = appointmentsToCancel
        .map(({ appointment }) => appointment?.id)
        .filter(Boolean);

      if (cancelledIds.length > 0) {
        setModalAppointments((prev) =>
          prev.filter((appointment) => !cancelledIds.includes(appointment.id))
        );
        setAppointments((prev) =>
          prev.filter((appointment) => !cancelledIds.includes(appointment.id))
        );
        setAllAppointments((prev) =>
          prev.filter((appointment) => !cancelledIds.includes(appointment.id))
        );
        setDateAppointments((prev) =>
          prev.filter((appointment) => !cancelledIds.includes(appointment.id))
        );
      }

      setMultiSlotConflictingAppointments([]);
      setMultiSlotValidationMessage("");
      setShowMultiSlotCancellationDialog(false);
      setShowMultiConfirmationDialog(false);
      setSelectedTimeSlots([]);
      setMultiConfirmationAction(null);
    } catch (error) {
      console.error("Error cancelling appointments:", error);
    } finally {
      setIsBulkCancellingAppointments(false);
    }
  };

  // Cancel appointment and mark time slot as unavailable
  const cancelAppointmentAndMarkUnavailable = async () => {
    if (!conflictAppointment || !conflictTimeSlot || !selectedModalDate) {
      console.error("Missing required data for cancellation");
      return;
    }

    const year = selectedModalDate.getFullYear();
    const month = String(selectedModalDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedModalDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const formattedDate = new Date(selectedModalDate.getTime() - selectedModalDate.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];

    try {
      // 1. Delete the appointment
      const appointmentRef = doc(db, "appointments", formattedDate);
      await updateDoc(appointmentRef, {
        [conflictAppointment.id]: deleteField()
      });

      // 2. Mark the time slot as unavailable
      const currentSlots = await fetchUnavailableTimeSlots(selectedModalDate);
      const updatedSlots = [...currentSlots, conflictTimeSlot];
      
      const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
      await setDoc(timeSlotsRef, {
        slots: updatedSlots
      }, { merge: true });

      // 3. Update local state
      setUnavailableTimeSlots(updatedSlots);
      setAppointments(prev => prev.filter(appointment => appointment.id !== conflictAppointment.id));
      setModalAppointments(prev => prev.filter(appointment => appointment.id !== conflictAppointment.id));
      
      // Update allAppointments state to remove the appointment from calendar badges
      setAllAppointments(prev => prev.filter(appointment => appointment.id !== conflictAppointment.id));

      // 4. Send notifications
      await sendCancellationNotifications(conflictAppointment, formattedDate, "Unavailable");

      // 5. Close dialog
      setShowConflictDialog(false);
      setConflictTimeSlot(null);
      setConflictAppointment(null);

    } catch (error) {
      console.error("Error cancelling appointment:", error);
    }
  };

  // Send notifications for cancelled appointment
  const sendCancellationNotifications = async (appointment, formattedDate, reason = "Unavailable") => {
    try {
      const formattedDateLong = formatDateLong(formattedDate);
      const startTime = appointment.startTime ? formatTimeConsistent(appointment.startTime) : 
                       (appointment.time ? formatTimeConsistent(appointment.time) : "N/A");
      const endTime = appointment.endTime ? formatTimeConsistent(appointment.endTime) : "N/A";
      const appointmentWindow = buildAppointmentWindow(formattedDateLong, startTime, endTime);
      const reasonValue = reason && reason.trim() ? reason.trim() : "Unavailable";
      
      // Check if reason is about clinic closure
      const isClinicClosure = reasonValue.toLowerCase().includes("clinic will be closed") || 
                             reasonValue.toLowerCase().includes("clinic is closed");
      
      // Format reason sentence based on type
      const reasonSentence = isClinicClosure 
        ? `because ${reasonValue.toLowerCase()}` 
        : `Reason: ${reasonValue}.`;
      
      const actorName = getCurrentUserDisplayNameForSystemNotification() || "Clinic Personnel";
      const patientName =
        appointment.patientDisplay ||
        appointment.patientName ||
        `${appointment.patientFirstName || ""} ${appointment.patientLastName || ""}`.trim() ||
        appointment.name ||
        "the patient";

      // Helper function to send email cancellation notification
      const sendEmailCancellationNotification = async (type, recipientEmail, recipientName = null, recipientRole = null) => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          // Format services - extract names if they're objects
          const servicesRaw = appointment.services || [];
          const services = Array.isArray(servicesRaw) 
            ? servicesRaw.map(service => {
                if (typeof service === 'string') {
                  return service;
                } else if (service && typeof service === 'object') {
                  return service.name || service.serviceName || JSON.stringify(service);
                }
                return String(service);
              })
            : [];
          const response = await fetch(`${apiUrl}/api/send-appointment-cancellation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type,
              recipientEmail,
              recipientName: recipientName || (type === 'patient' ? patientName : 'Doctor'),
              recipientRole: recipientRole || (type === 'patient' ? null : 'ClinicStaff'),
              patientName,
              appointmentDate: formattedDateLong,
              startTime,
              endTime: endTime !== "N/A" ? endTime : startTime,
              dentistName: appointment.dentist || 'N/A',
              services,
              reason: reasonValue,
              actorName: actorName,
              clinicName: 'VSDiamond Dental Clinic'
            })
          });
          const result = await response.json();
          if (!result.success) {
            console.error(`Failed to send cancellation email notification to ${type}:`, result.message);
          }
        } catch (error) {
          console.error(`Error sending cancellation email notification to ${type}:`, error);
        }
      };

      // Notify the patient
      const patientUid = appointment.uid || appointment.userId || appointment.patientId;
      if (patientUid) {
        const patientMessage = isClinicClosure
          ? `Your appointment on ${appointmentWindow} has been cancelled ${reasonSentence}.`
          : `Your appointment on ${appointmentWindow} has been cancelled. ${reasonSentence}`;
        await addNotification(patientUid, patientMessage);
        
        // Send email notification to patient if their email is verified
        const patientDoc = await getDoc(doc(db, "users", patientUid));
        if (patientDoc.exists()) {
          const patientData = patientDoc.data();
          const patientEmailVerified = patientData.emailVerified === "Yes" || patientData.emailVerified === true;
          const patientEmail = patientData.email || patientData.authEmail;
          if (patientEmailVerified && patientEmail && !patientEmail.includes("@mobileuser.dentalapp.com")) {
            sendEmailCancellationNotification('patient', patientEmail, patientName);
          }
        }
      }

      // Notify the dentist assigned to this appointment
      const dentistUid = resolveAppointmentDentistUid(appointment);
      if (dentistUid) {
        const dentistMessage = isClinicClosure
          ? `Your upcoming patient appointment on ${appointmentWindow} has been cancelled ${reasonSentence}.`
          : `Your upcoming patient appointment for ${patientName} on ${appointmentWindow} has been cancelled by ${actorName}. ${reasonSentence}`;
        await addNotification(dentistUid, dentistMessage);
        
        // Send email notification to dentist if their email is verified
        const dentistDoc = await getDoc(doc(db, "users", dentistUid));
        if (dentistDoc.exists()) {
          const dentistData = dentistDoc.data();
          const dentistEmailVerified = dentistData.emailVerified === "Yes" || dentistData.emailVerified === true;
          const dentistEmail = dentistData.email || dentistData.authEmail;
          if (dentistEmailVerified && dentistEmail && !dentistEmail.includes("@mobileuser.dentalapp.com")) {
            const dentistName = dentistData.firstName && dentistData.lastName 
              ? `${dentistData.firstName} ${dentistData.lastName}` 
              : (dentistData.name || "Doctor");
            const dentistRole = dentistData.userType || dentistData.role;
            sendEmailCancellationNotification('dentist', dentistEmail, dentistName, dentistRole);
          }
        }
      }

      // Notify all ClinicStaff (assigned dentist is already notified above; other dentists are not notified)
      const usersRef = collection(db, "users");
      const usersSnapshot = await getDocs(usersRef);
      const clinicNotificationMessage = isClinicClosure
        ? `The appointment for ${patientName} on ${appointmentWindow} has been cancelled ${reasonSentence}.`
        : `The appointment for ${patientName} on ${appointmentWindow} has been cancelled by ${actorName}. ${reasonSentence}`;
      
      usersSnapshot.forEach((docu) => {
        const data = docu.data();
        const userId = docu.id;
        const userRole = data.userType || data.role;
        
        // Skip the assigned dentist (already notified above)
        if (dentistUid && userId === dentistUid) {
          return;
        }
        
        if (userRole === "ClinicStaff") {
          addNotification(userId, clinicNotificationMessage);
          
          const staffEmailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
          const staffEmail = data.email || data.authEmail;
          if (staffEmailVerified && staffEmail && !staffEmail.includes("@mobileuser.dentalapp.com")) {
            const staffName = data.firstName && data.lastName 
              ? `${data.firstName} ${data.lastName}` 
              : (data.name || "Staff");
            sendEmailCancellationNotification('dentist', staffEmail, staffName, userRole);
          }
        }
      });
    } catch (error) {
      console.error("Error sending cancellation notifications:", error);
    }
  };

  // Cancel the conflict dialog without action
  const cancelConflictDialog = () => {
    setShowConflictDialog(false);
    setConflictTimeSlot(null);
    setConflictAppointment(null);
  };
  // Confirm marking date as unavailable or making it available
  const confirmMarkDateUnavailable = async () => {
    if (!dateToToggle) return;

    setIsMarkingDateUnavailable(true);

    const year = dateToToggle.getFullYear();
    const month = String(dateToToggle.getMonth() + 1).padStart(2, '0');
    const day = String(dateToToggle.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const formattedDate = new Date(dateToToggle.getTime() - dateToToggle.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];
    

    // Check if we're making the date unavailable or available
    const isCurrentlyUnavailable = closedDays.includes(dateStr);
    const isMakingUnavailable = !isCurrentlyUnavailable;

    try {
      let updatedClosedDays;
      let appointmentsToCancel = []; // Declare outside the if block
      
      if (isMakingUnavailable) {
        // Mark the date as unavailable
        updatedClosedDays = [...closedDays, dateStr];
      } else {
        // Make the date available again
        updatedClosedDays = closedDays.filter(d => d !== dateStr);
      }
      
      const closedDaysRef = doc(db, "closedDays", "closedDates");
      await setDoc(closedDaysRef, {
        dates: updatedClosedDays
      }, { merge: true });
      setClosedDays(updatedClosedDays);

      // Reset date-level chair swaps when date availability is toggled.
      // This prevents stale bench swap links from surviving a closed/open cycle.
      const chairSwapsRef = doc(db, CHAIR_SWAPS_COLLECTION, dateStr);
      await deleteDoc(chairSwapsRef);

      // Auto-mark all dentist time slots as unavailable/available when date status changes
      if (isMakingUnavailable) {
        // Generate all time slots for the day (9 AM - 6 PM, 30-minute intervals)
        const allTimeSlots = [];
        for (let hour = 9; hour < 18; hour++) {
          for (let minutes = 0; minutes < 60; minutes += 30) {
            const startMinutes = hour * 60 + minutes;
            const endMinutes = startMinutes + 30;
            
            // Skip if the slot would go beyond 6 PM
            if (endMinutes > 18 * 60) {
              continue;
            }
            
            const startHours = Math.floor(startMinutes / 60);
            const startMins = startMinutes % 60;
            const startAmpm = startHours >= 12 ? 'PM' : 'AM';
            const startDisplayHours = startHours % 12 || 12;
            const startTimeStr = `${startDisplayHours}:${startMins.toString().padStart(2, '0')} ${startAmpm}`;
            
            const endHours = Math.floor(endMinutes / 60);
            const endMins = endMinutes % 60;
            const endAmpm = endHours >= 12 ? 'PM' : 'AM';
            const endDisplayHours = endHours % 12 || 12;
            const endTimeStr = `${endDisplayHours}:${endMins.toString().padStart(2, '0')} ${endAmpm}`;
            
            allTimeSlots.push(`${startTimeStr} - ${endTimeStr}`);
          }
        }
        
        // Mark all time slots as unavailable for all dentists
        const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
        const timeSlotsData = {};
        
        // Get all dentists and mark all their time slots as unavailable
        const usersRef = collection(db, "users");
        const usersSnapshot = await getDocs(usersRef);
        
        const rosterSet = new Set(
          Array.isArray(chairSettings?.rosterDentistIds)
            ? chairSettings.rosterDentistIds
            : []
        );
        const restrictToRoster =
          isChairCapacityConfigured(chairSettings) && rosterSet.size > 0;
        usersSnapshot.forEach((docu) => {
          const data = docu.data();
          if ((data.userType === "DentistOwner" || data.role === "DentistOwner" ||
              data.userType === "AssociateDentist" || data.role === "AssociateDentist") &&
              (data.emailVerified === "Yes" || data.emailVerified === true)) {
            if (restrictToRoster && !rosterSet.has(docu.id)) return;
            timeSlotsData[docu.id] = allTimeSlots;
          }
        });
        
        if (Object.keys(timeSlotsData).length > 0) {
          await setDoc(timeSlotsRef, timeSlotsData, { merge: true });
        }
      } else {
        // When making date available again, remove all unavailable time slots for that date
        const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
        await deleteDoc(timeSlotsRef);
      }

      // Only cancel appointments and send notifications when making date unavailable
      if (isMakingUnavailable) {
        
        // Cancel only confirmed/pending appointments (preserve completed appointments for analytics)
        const appointmentsRef = doc(db, "appointments", formattedDate);
        
        // Find appointments to cancel
        dateAppointments.forEach(appointment => {
          if (appointment.status === "Confirmed" || appointment.status === "confirmed" ||
              appointment.status === "Pending" || appointment.status === "New" ||
              appointment.status === "pending" || appointment.status === "new") {
            appointmentsToCancel.push(appointment);
          }
          // Note: Completed appointments are NOT cancelled to preserve analytics data
        });


        if (appointmentsToCancel.length > 0) {
          // Use sendCancellationNotifications for each appointment to notify all personnel
          for (const appointment of appointmentsToCancel) {
            await sendCancellationNotifications(appointment, formattedDate, "The clinic will be closed on that date");
          }
          
          // Delete appointments from database using deleteField
          const updates = {};
          appointmentsToCancel.forEach(appointment => {
            updates[appointment.appointmentId || appointment.id] = deleteField();
          });
          
          await updateDoc(appointmentsRef, updates);
          
          // Wait a moment for the database update to propagate
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Update local state to remove deleted appointments
          setDateAppointments(prev => 
            prev.filter(appointment => 
              !appointmentsToCancel.some(deleted => 
                (deleted.appointmentId || deleted.id) === (appointment.appointmentId || appointment.id)
              )
            )
          );
          
          // Update main appointments state as well
          setAppointments(prev => 
            prev.filter(appointment => 
              !appointmentsToCancel.some(deleted => 
                (deleted.appointmentId || deleted.id) === (appointment.appointmentId || appointment.id)
              )
            )
          );
          
        } else {
        }
      }

      // Broadcast to every DentistOwner, AssociateDentist, and ClinicStaff (calendar-wide; per-appointment cancels use sendCancellationNotifications above)
      // Send notifications to personnel
      const usersRef = collection(db, "users");
      const usersSnapshot = await getDocs(usersRef);
      
      // Format the date to full format (e.g., "November 7, 2025")
      const formattedDateLong = formatDateLong(formattedDate);
      
      // Get the name without prefix for both system and email notifications (for ClinicStaff)
      const changerNameForSystem = getCurrentUserDisplayNameForSystemNotification();
      const nameForMessage = changerNameForSystem || "Clinic Personnel";
      const nameForEmail = changerNameForSystem || "Clinic Personnel";

      const personnelMessage = isMakingUnavailable
        ? `${formattedDateLong} has been marked as unavailable by ${nameForMessage}.`
        : `${formattedDateLong} has been marked back as available by ${nameForMessage}.`;
      
      // Helper function to send email notification for date availability change
      // Using existing send-appointment-status-change API to avoid creating new serverless function
      const sendDateAvailabilityEmailNotification = async (recipientEmail, recipientName, recipientRole) => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          const response = await fetch(`${apiUrl}/api/send-appointment-status-change`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'dentist',
              recipientEmail,
              recipientName,
              recipientRole,
              patientName: 'N/A',
              appointmentDate: formattedDateLong,
              startTime: 'N/A',
              endTime: 'N/A',
              dentistName: 'N/A',
              services: [],
              status: isMakingUnavailable ? 'Unavailable' : 'Available',
              actorName: nameForEmail,
              insuranceText: 'N/A',
              dentistRemarks: null,
              clinicName: 'VSDiamond Dental Clinic',
              isDateAvailabilityChange: true,
              isMakingUnavailable
            })
          });
          const result = await response.json();
          if (!result.success) {
            console.error(`Failed to send date availability email notification:`, result.message);
          }
        } catch (error) {
          console.error(`Error sending date availability email notification:`, error);
        }
      };
      
      usersSnapshot.forEach((docu) => {
        const data = docu.data();
        if (isClinicPersonnelUser(data)) {
          addNotification(docu.id, personnelMessage);
          
          // Send email notification if personnel's email is verified
          const personnelEmailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
          const personnelEmail = data.email || data.authEmail;
          if (personnelEmailVerified && personnelEmail && !personnelEmail.includes("@mobileuser.dentalapp.com")) {
            const personnelName = data.firstName && data.lastName 
              ? `${data.firstName} ${data.lastName}` 
              : (data.name || "Staff");
            const personnelRole = data.userType || data.role;
            sendDateAvailabilityEmailNotification(personnelEmail, personnelName, personnelRole);
          }
        }
      });

      // Close dialog and reset state - reset data first to prevent modal flash
      if (!isClosingDateDialog.current) {
        isClosingDateDialog.current = true;
        // Reset data first
        setDateToToggle(null);
        setDateAppointments([]);
        // Close dialog immediately
        setShowDateUnavailableDialog(false);
        // Ensure other modals are also closed and reset their state
        setShowMultiConfirmationDialog(false);
        setShowClinicWideDialog(false);
        setSelectedTimeSlots([]);
        setMultiConfirmationAction(null);
        setClinicWideTimeSlot(null);
        // Clear the last toggled date to allow it to be clicked again after a delay
        setTimeout(() => {
          lastToggledDateRef.current = null;
          lastToggledDateTimeRef.current = 0;
        }, 500);
        // Reset the closing flag after state has settled (longer delay to prevent re-opening)
        setTimeout(() => {
          isClosingDateDialog.current = false;
        }, 500);
      }

    } catch (error) {
      console.error("Error updating date availability:", error);
    } finally {
      setIsMarkingDateUnavailable(false);
    }
  };

  // Cancel marking date as unavailable
  const cancelMarkDateUnavailable = () => {
    // Prevent multiple calls
    if (isClosingDateDialog.current) {
      return;
    }
    isClosingDateDialog.current = true;
    // Reset data first, then close dialog
    setDateToToggle(null);
    setDateAppointments([]);
    // Close dialog immediately
    setShowDateUnavailableDialog(false);
    // Ensure other modals are also closed and reset their state
    setShowMultiConfirmationDialog(false);
    setShowClinicWideDialog(false);
    setSelectedTimeSlots([]);
    setMultiConfirmationAction(null);
    setClinicWideTimeSlot(null);
    // Clear the last toggled date to allow it to be clicked again after a delay
    setTimeout(() => {
      lastToggledDateRef.current = null;
      lastToggledDateTimeRef.current = 0;
    }, 500);
    // Reset the closing flag after state has settled (longer delay to prevent re-opening)
    setTimeout(() => {
      isClosingDateDialog.current = false;
    }, 500);
  };

  // Confirm time slot toggle
  const confirmTimeSlotToggle = async () => {
    if (!timeSlotToToggle || !selectedModalDate) {
      return;
    }

    const slotsForNotification = [timeSlotToToggle];
    const year = selectedModalDate.getFullYear();
    const month = String(selectedModalDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedModalDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

      // Get current unavailable slots for the selected dentist
      const currentSlots = await fetchUnavailableTimeSlots(selectedModalDate, selectedModalDentist);
      const isCurrentlyUnavailable =
        getTimeSlotStatus(timeSlotToToggle).status === "Unavailable";
      const actionForNotification = isCurrentlyUnavailable ? "available" : "unavailable";

    // Check if the date is globally unavailable and we're trying to make slot available
    if (!isCurrentlyUnavailable && closedDays.includes(dateStr)) {
      return;
    }

    if (isCurrentlyUnavailable && selectedModalDentist) {
      const linkedBenchMsg =
        await fetchLinkedBenchConflictMessageForRosterMakeAvailable({
          rosterUid: selectedModalDentist,
          timeSlotsToOpen: [timeSlotToToggle],
          dateStr,
        });
      if (linkedBenchMsg) {
        setTimeSlotDialogValidationMessage(linkedBenchMsg);
        return;
      }
    }
    setTimeSlotDialogValidationMessage("");

    setIsMarkingTimeSlotUnavailable(true);

    try {
      // If making slot unavailable, check for appointments and cancel them
      if (!isCurrentlyUnavailable) {
        const appointmentsRef = doc(db, "appointments", dateStr);
        const appointmentsSnap = await getDoc(appointmentsRef);
        
        if (appointmentsSnap.exists()) {
          const appointmentsData = appointmentsSnap.data();
          const appointmentsToCancel = [];
          
          // Find appointments that match the time slot and dentist
          Object.entries(appointmentsData).forEach(([appointmentId, appointment]) => {
            if (appointment && appointment.date === dateStr) {
              // Check if this appointment belongs to the selected dentist
              const belongsToSelectedDentist = appointment.dentistId === selectedModalDentist || 
                (appointment.dentist && appointment.dentist === getDentistDisplayNameFromUID(selectedModalDentist));
              
              if (belongsToSelectedDentist) {
                // Check if this appointment's time slot matches the selected time slot
                const appointmentTimeSlot = appointment.startTime && appointment.endTime 
                  ? `${formatTimeConsistent(appointment.startTime)} - ${formatTimeConsistent(appointment.endTime)}`
                  : appointment.time ? formatTimeConsistent(appointment.time) : null;
                
                if (appointmentTimeSlot === timeSlotToToggle) {
                  // Only cancel if it's not already completed
                  if (appointment.status !== "Completed" && appointment.status !== "completed" && 
                      appointment.status !== "Cancelled" && appointment.status !== "cancelled") {
                    appointmentsToCancel.push({ id: appointmentId, ...appointment });
                  }
                }
              }
            }
          });
          
          // Cancel the appointments by deleting them from database
          if (appointmentsToCancel.length > 0) {
            
            // Send cancellation notifications first
            for (const appointment of appointmentsToCancel) {
              await sendCancellationNotifications(appointment, dateStr, "Unavailable");
            }
            
            // Delete appointments from database using deleteField
            const updates = {};
            appointmentsToCancel.forEach(appointment => {
              updates[appointment.id] = deleteField();
            });
            
            await updateDoc(appointmentsRef, updates);
            
            // Update local state to remove deleted appointments
            setModalAppointments(prev => 
              prev.filter(appointment => 
                !appointmentsToCancel.some(deleted => deleted.id === appointment.id)
              )
            );
            
            // Update main appointments state as well
            setAppointments(prev => 
              prev.filter(appointment => 
                !appointmentsToCancel.some(deleted => deleted.id === appointment.id)
              )
            );
            
            // Force refresh modalAppointments from database to ensure UI updates
            const refreshedAppointmentsSnap = await getDoc(appointmentsRef);
            if (refreshedAppointmentsSnap.exists()) {
              const refreshedData = refreshedAppointmentsSnap.data();
              const refreshedAppointments = Object.entries(refreshedData).map(([firestoreDocId, value]) => ({
                ...value,
                id: firestoreDocId,
                patientDisplay: value.patientName || value.email || value.patientEmail || value.userId || "",
              }));
              setModalAppointments(refreshedAppointments);
            } else {
              setModalAppointments([]);
            }
          }
        }
      }
      
      let updatedSlots;
      if (isCurrentlyUnavailable) {
        // Make slot available
        updatedSlots = currentSlots.filter(slot => slot !== timeSlotToToggle);
      } else {
        // Make slot unavailable
        updatedSlots = [...currentSlots, timeSlotToToggle];
      }

      // Update Firestore with dentist-specific data structure
      const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
      const updateData = {
        [selectedModalDentist]: updatedSlots
      };
      
      await setDoc(timeSlotsRef, updateData, { merge: true });
      
      // Update local state for modal
      setUnavailableTimeSlots(updatedSlots);

      await notifyClinicPersonnelForTimeSlots({
        dentistId: selectedModalDentist,
        dateStr,
        slots: slotsForNotification,
        action: actionForNotification,
      });

      // Close dialog and reset state - reset data first to prevent modal flash
      if (!isClosingTimeSlotDialog.current) {
        isClosingTimeSlotDialog.current = true;
        // Reset data first
        setTimeSlotToToggle(null);
        setTimeSlotAppointment(null);
        setTimeSlotDialogValidationMessage("");
        // Close dialog immediately
        setShowTimeSlotDialog(false);
        // Ensure other modals are also closed and reset their state
        setShowMultiConfirmationDialog(false);
        setShowClinicWideDialog(false);
        setSelectedTimeSlots([]);
        setMultiConfirmationAction(null);
        setClinicWideTimeSlot(null);
        // Clear the last toggled time slot to allow it to be clicked again after a delay
        setTimeout(() => {
          lastToggledTimeSlotRef.current = null;
          lastToggledTimeSlotTimeRef.current = 0;
        }, 500);
        // Reset the closing flag after state has settled (longer delay to prevent re-opening)
        setTimeout(() => {
          isClosingTimeSlotDialog.current = false;
        }, 500);
      }

    } catch (error) {
      console.error("Error updating time slot availability:", error);
    } finally {
      setIsMarkingTimeSlotUnavailable(false);
    }
  };

  // Cancel time slot toggle
  const cancelTimeSlotToggle = () => {
    // Prevent multiple calls
    if (isClosingTimeSlotDialog.current) {
      return;
    }
    isClosingTimeSlotDialog.current = true;
    // Reset data first, then close dialog
    setTimeSlotToToggle(null);
    setTimeSlotAppointment(null);
    setTimeSlotDialogValidationMessage("");
    // Close dialog immediately
    setShowTimeSlotDialog(false);
    // Ensure other modals are also closed and reset their state
    setShowMultiConfirmationDialog(false);
    setShowClinicWideDialog(false);
    setSelectedTimeSlots([]);
    setMultiConfirmationAction(null);
    setClinicWideTimeSlot(null);
    // Clear the last toggled time slot to allow it to be clicked again after a delay
    setTimeout(() => {
      lastToggledTimeSlotRef.current = null;
      lastToggledTimeSlotTimeRef.current = 0;
    }, 500);
    // Reset the closing flag after state has settled (longer delay to prevent re-opening)
    setTimeout(() => {
      isClosingTimeSlotDialog.current = false;
    }, 500);
  };

  // Real-time listener for closed days - updates automatically when dates are marked as unavailable/available
  useEffect(() => {
    const closedDaysRef = doc(db, "closedDays", "closedDates");
    
    const unsubscribe = onSnapshot(
      closedDaysRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setClosedDays(data.dates || []);
        } else {
          setClosedDays([]);
        }
      },
      (error) => {
        if (error.code === 'permission-denied') {
          console.log('Closed days listener stopped due to sign-out');
          return;
        }
        console.error("Error listening to closed days:", error);
        // Fallback to empty array on error
        setClosedDays([]);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!selectedDateAppointment) {
      setDentistSchedulesForDate({});
      return undefined;
    }
    let dateObj;
    try {
      dateObj =
        selectedDateAppointment &&
        typeof selectedDateAppointment.toDate === "function"
          ? selectedDateAppointment.toDate()
          : selectedDateAppointment;
    } catch {
      setDentistSchedulesForDate({});
      return undefined;
    }
    const dateStr = getLocalDateKey(dateObj);
    return subscribeToDentistSchedulesForDate(dateStr, setDentistSchedulesForDate);
  }, [selectedDateAppointment]);

  useEffect(() => {
    if (!selectedModalDate) {
      setModalDentistSchedulesForDate({});
      return undefined;
    }
    const dateStr = getLocalDateKey(selectedModalDate);
    return subscribeToDentistSchedulesForDate(dateStr, setModalDentistSchedulesForDate);
  }, [selectedModalDate]);

  // Real-time listener for unavailable time slots in main appointment table
  useEffect(() => {
    if (!selectedDate) return;
    if (!selectedDateAppointment) return;
    
    const year = selectedDateAppointment.toDate().getFullYear();
    const month = String(selectedDateAppointment.toDate().getMonth() + 1).padStart(2, '0');
    const day = String(selectedDateAppointment.toDate().getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // If the date is in closedDays, mark all time slots as unavailable
    if (closedDays.includes(dateStr)) {
      // Generate all time slots for the day (9 AM - 6 PM, 30-minute intervals)
      const allTimeSlots = [];
      for (let hour = 9; hour < 18; hour++) {
        for (let minutes = 0; minutes < 60; minutes += 30) {
          const startMinutes = hour * 60 + minutes;
          const endMinutes = startMinutes + 30;
          
          // Skip if the slot would go beyond 6 PM
          if (endMinutes > 18 * 60) {
            continue;
          }
          
          const startHours = Math.floor(startMinutes / 60);
          const startMins = startMinutes % 60;
          const startAmpm = startHours >= 12 ? 'PM' : 'AM';
          const startDisplayHours = startHours % 12 || 12;
          const startTimeStr = `${startDisplayHours}:${startMins.toString().padStart(2, '0')} ${startAmpm}`;
          
          const endHours = Math.floor(endMinutes / 60);
          const endMins = endMinutes % 60;
          const endAmpm = endHours >= 12 ? 'PM' : 'AM';
          const endDisplayHours = endHours % 12 || 12;
          const endTimeStr = `${endDisplayHours}:${endMins.toString().padStart(2, '0')} ${endAmpm}`;
          
          allTimeSlots.push(`${startTimeStr} - ${endTimeStr}`);
        }
      }
      setMainAppointmentUnavailableSlots(allTimeSlots);
      setHasMainUnavailableRecord(true);
      return; // Don't set up listener for closed dates
    }
    
    const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
    
    const unsubscribe = onSnapshot(timeSlotsRef, async (snapshot) => {
      const dentistId = selectedDentist;
      if (!dentistId) {
        setMainAppointmentUnavailableSlots([]);
        setHasMainUnavailableRecord(false);
        return;
      }

      const allTimeSlots = generateTimeSlotsForDate(selectedDateAppointment.toDate());

      if (snapshot.exists()) {
        const data = snapshot.data() || {};
        const hasDentistEntry = data[dentistId] !== undefined;

        // For main appointment table, show the selected dentist's slots when
        // explicit data exists.
        if (hasDentistEntry) {
          const explicitSlots = data[dentistId] || [];
          const isRoster = isDentistOnRoster(dentistId, chairSettings);
          const isFullDayEntry =
            Array.isArray(explicitSlots) &&
            explicitSlots.length === allTimeSlots.length &&
            allTimeSlots.every((slot) => explicitSlots.includes(slot));

          // If this dentist was previously default-blocked by legacy setup
          // (hasScheduleSetup=false), and is now on roster, clear only the
          // stale full-day fallback entry to restore expected availability.
          if (isRoster && isFullDayEntry) {
            try {
              const dentistDoc = await getDoc(doc(db, "users", dentistId));
              const dentistData = dentistDoc.exists() ? dentistDoc.data() : {};
              const appts = await getAppointmentsForDate(selectedDateAppointment.toDate());
              const hasDentistAppointments = appts.some(
                (appointment) => resolveAppointmentDentistUid(appointment) === dentistId
              );
              const shouldClearLegacyFallback = !hasDentistAppointments;

              if (shouldClearLegacyFallback) {
                await updateDoc(timeSlotsRef, { [dentistId]: deleteField() });
                setMainAppointmentUnavailableSlots([]);
                setHasMainUnavailableRecord(false);
                return;
              }
            } catch (cleanupError) {
              console.error("Error evaluating/clearing stale full-day fallback entry:", cleanupError);
            }
          }

          setMainAppointmentUnavailableSlots(explicitSlots);
          setHasMainUnavailableRecord(true);
          return;
        }

        // Fallback parity with patient view: when no explicit per-dentist entry
        // exists, resolve default unavailable using chair-aware legacy rule.
        try {
          const dentistDoc = await getDoc(doc(db, "users", dentistId));
          const dentistData = dentistDoc.exists() ? dentistDoc.data() : {};
          const shouldDefaultUnavailable = shouldDefaultUnavailableByLegacySetup({
            dentistUid: dentistId,
            settings: chairSettings,
            hasScheduleSetup: dentistData?.hasScheduleSetup,
          });

          if (shouldDefaultUnavailable) {
            const isBenchInChairMode =
              isChairCapacityConfigured(chairSettings) &&
              !isDentistOnRoster(dentistId, chairSettings);
            setMainAppointmentUnavailableSlots(isBenchInChairMode ? [] : allTimeSlots);
            setHasMainUnavailableRecord(false);
            return;
          }
        } catch (error) {
          console.error("Error resolving main table fallback unavailable slots:", error);
        }

        setMainAppointmentUnavailableSlots([]);
        setHasMainUnavailableRecord(false);
      } else {
        // Missing date doc: apply the same chair-aware fallback as above.
        try {
          const dentistDoc = await getDoc(doc(db, "users", dentistId));
          const dentistData = dentistDoc.exists() ? dentistDoc.data() : {};
          const shouldDefaultUnavailable = shouldDefaultUnavailableByLegacySetup({
            dentistUid: dentistId,
            settings: chairSettings,
            hasScheduleSetup: dentistData?.hasScheduleSetup,
          });

          if (shouldDefaultUnavailable) {
            const isBenchInChairMode =
              isChairCapacityConfigured(chairSettings) &&
              !isDentistOnRoster(dentistId, chairSettings);
            setMainAppointmentUnavailableSlots(isBenchInChairMode ? [] : allTimeSlots);
            setHasMainUnavailableRecord(false);
            return;
          }
        } catch (error) {
          console.error("Error resolving main table fallback for missing date doc:", error);
        }

        setMainAppointmentUnavailableSlots([]);
        setHasMainUnavailableRecord(false);
      }
    });
    
    return () => unsubscribe();
  }, [selectedDateAppointment, selectedDentist, closedDays, chairSettings]);

  // Real-time listener for appointments in the Mark Date modal
  useEffect(() => {
    if (!dateToToggle) return;
    
    const year = dateToToggle.getFullYear();
    const month = String(dateToToggle.getMonth() + 1).padStart(2, '0');
    const day = String(dateToToggle.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const appointmentsRef = doc(db, "appointments", dateStr);
    
    const unsubscribe = onSnapshot(appointmentsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const appointments = Object.values(data).filter(appointment => 
          appointment && appointment.date === dateStr
        );
        setDateAppointments(appointments);
      } else {
        setDateAppointments([]);
      }
    });
    
    return () => unsubscribe();
  }, [dateToToggle]);

  // Fetch appointments for the modal date when it changes
  useEffect(() => {
    if (!selectedModalDate) return;
    const formattedDate = new Date(selectedModalDate.getTime() - selectedModalDate.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];
    
    
    const appointmentsRef = doc(db, "appointments", formattedDate);

    const unsubscribe = onSnapshot(appointmentsRef, async (snapshot) => {
      
      const data = snapshot.data();
      const modalAppointments = data
        ? Object.entries(data).map(([firestoreDocId, value]) => ({
            ...value,
            id: firestoreDocId,
            patientDisplay: value.patientName || value.email || value.patientEmail || value.userId || "",
          }))
        : [];
      
      
      
      // Update the modal appointments state for conflict checking
      setModalAppointments(modalAppointments);
    });
    return unsubscribe;
  }, [selectedModalDate]);

  // Fetch ALL appointments from ALL dates for badge display with real-time updates
  useEffect(() => {
    if (!selectedDate) {
      setAppointments([]);
      return;
    }
    if (!selectedDateAppointment) {
      setAppointments([]);
      return;
    }
    
    const formattedDate = new Date(selectedDateAppointment.toDate().getTime() - selectedDateAppointment.toDate().getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];
    
    const appointmentsRef = doc(db, "appointments", formattedDate);
    
    const unsubscribe = onSnapshot(appointmentsRef, (snapshot) => {
      const data = snapshot.data();
      const appointments = data
        ? Object.entries(data).map(([firestoreDocId, value]) => ({
            ...value,
            id: firestoreDocId,
            patientDisplay: value.patientName || value.email || value.patientEmail || value.userId || "",
          }))
        : [];
      
      setAppointments(appointments);
    });
    
    return () => unsubscribe();
  }, [selectedDateAppointment]);

  // Fetch ALL appointments from ALL dates for badge display with real-time updates
  useEffect(() => {
    const appointmentsCollection = collection(db, "appointments");
    
    const unsubscribe = onSnapshot(appointmentsCollection, async (snapshot) => {
      const allAppointmentsData = [];
      const todayStr = getLocalDateKey(new Date());
      
      snapshot.forEach((docSnapshot) => {
        const dateStr = docSnapshot.id; // The document ID is the date
        const data = docSnapshot.data();
        
        if (data) {
          Object.entries(data).forEach(([id, value]) => {
            allAppointmentsData.push({
              id,
              date: dateStr, // Add the date to each appointment
              ...value,
              patientDisplay: value.patientName || value.email || value.patientEmail || value.userId || "",
            });
          });
        }
      });
      
      setAllAppointments(allAppointmentsData);
      
      // Check for past appointments and clean them up in real-time
      const appointmentsToDelete = {};
      snapshot.forEach((docSnapshot) => {
        const dateStr = docSnapshot.id;
        
        // Skip if the date is today or in the future
        if (dateStr >= todayStr) {
          return;
        }
        
        const data = docSnapshot.data();
        if (!data || Object.keys(data).length === 0) {
          return;
        }
        
        // Find past appointments with Pending or Confirmed status
        Object.entries(data).forEach(([appointmentId, appointment]) => {
          if (appointment && (appointment.status === "Pending" || appointment.status === "Confirmed" || 
              appointment.status === "pending" || appointment.status === "confirmed")) {
            if (!appointmentsToDelete[dateStr]) {
              appointmentsToDelete[dateStr] = [];
            }
            appointmentsToDelete[dateStr].push(appointmentId);
          }
        });
      });
      
      // Delete past appointments if any were found
      if (Object.keys(appointmentsToDelete).length > 0) {
        for (const [dateStr, appointmentIds] of Object.entries(appointmentsToDelete)) {
          const dateDocRef = doc(db, "appointments", dateStr);
          const updates = {};
          appointmentIds.forEach(appointmentId => {
            updates[appointmentId] = deleteField();
          });
          
          try {
            await updateDoc(dateDocRef, updates);
          } catch (error) {
            console.error(`Error deleting past appointments from ${dateStr}:`, error);
          }
        }
      }
    });

    return unsubscribe;
  }, []);

  // Generate unavailable time slots for display in appointment list
  const generateUnavailableTimeSlotsForDisplay = () => {
    if (!selectedDate) return [];
    if (!selectedDateAppointment) return [];
    
    const year = selectedDateAppointment.toDate().getFullYear();
    const month = String(selectedDateAppointment.toDate().getMonth() + 1).padStart(2, '0');
    const day = String(selectedDateAppointment.toDate().getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Get unavailable slots for this date
    return unavailableTimeSlots.map(timeSlot => {
      // Extract start time from the time slot range (e.g., "9:00 AM - 9:30 AM" -> "9:00 AM")
      const startTime = timeSlot.split(' - ')[0];
      
      return {
        id: `unavailable-${dateStr}-${timeSlot}`,
        time: startTime, // Use only the start time for sorting
        startTime: startTime,
        endTime: timeSlot.split(' - ')[1], // Extract end time
        patientDisplay: "Unavailable",
        service: "Time Slot Unavailable",
        status: "Unavailable",
        dentist: "N/A",
        isUnavailableSlot: true
      };
    });
  };


  // fetch appointments for the selected date and count pending per dentist
  useEffect(() => {
    if (!selectedDateAppointment) return;
    const formattedDate = new Date(selectedDateAppointment.toDate().getTime() - selectedDateAppointment.toDate().getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];
    const appointmentsRef = doc(db, "appointments", formattedDate);

    const unsubscribe = onSnapshot(appointmentsRef, async (snapshot) => {
      const data = snapshot.data();
      const allAppointments = data
        ? Object.entries(data).map(([firestoreDocId, value]) => ({
            ...value,
            id: firestoreDocId,
            date: normalizeAppointmentDateKey(value.date) || formattedDate,
            patientDisplay: value.patientName || value.email || value.patientEmail || value.userId || "",
          }))
        : [];
      setAppointments(allAppointments);

      // Count pending per dentist (by display name)
      const counts = {};
      allAppointments.forEach((appt) => {
        if (appt.status === "Pending" || appt.status === "New") {
          counts[appt.dentist] = (counts[appt.dentist] || 0) + 1;
        }
      });
      setPendingCounts(counts);

      // Don't call auto-adjustment here - let the useEffect handle it to avoid race conditions
      // The real-time listener should only update state, and a separate useEffect will handle adjustments
    });
    return unsubscribe;
  }, [selectedDateAppointment]);

  const selectedDateKey = useMemo(() => {
    if (!selectedDateAppointment) return null;
    return new Date(
      selectedDateAppointment.toDate().getTime() -
        selectedDateAppointment.toDate().getTimezoneOffset() * 60000
    )
      .toISOString()
      .split("T")[0];
  }, [selectedDateAppointment]);

  /** Compressed view for UI — updates synchronously on every render (no Firestore wait). */
  const timelineDisplayAppointments = useMemo(() => {
    if (!selectedDateKey || !Array.isArray(appointments)) {
      return appointments;
    }
    const rows = appointments.map((a) => withAppointmentDocDate(a, selectedDateKey));
    const { updates } = computeDayTimelineCompressionShiftUpdates(rows, selectedDateKey);
    if (!updates.length) return rows;
    return applyTimelineShiftUpdatesToRows(rows, updates);
  }, [appointments, selectedDateKey]);

  useEffect(() => {
    return () => {
      if (timelineReconcilePersistTimerRef.current) {
        clearTimeout(timelineReconcilePersistTimerRef.current);
      }
      lastPersistedCompressKeyRef.current = "";
    };
  }, [selectedDateAppointment]);

  // Persist compression when raw Firestore rows still overlap (background; UI uses timelineDisplayAppointments).
  useEffect(() => {
    if (!selectedDateKey || !Array.isArray(appointments) || appointments.length === 0) {
      return undefined;
    }
    if (showEditForm || savingAppointmentDetailsRef.current || isSavingAppointmentDetails) {
      return undefined;
    }

    const sourceRows = appointments
      .filter((ap) => ap?.dentistId && appointmentBelongsToDateDoc(ap, selectedDateKey))
      .map((ap) => withAppointmentDocDate(ap, selectedDateKey));

    const { updates: shiftUpdates } = computeDayTimelineCompressionShiftUpdates(
      sourceRows,
      selectedDateKey
    );

    if (shiftUpdates.length === 0) return undefined;
    if (timelineShiftUpdatesAlreadyApplied(sourceRows, shiftUpdates)) return undefined;

    scheduleTimelineCompressionPersist(shiftUpdates, selectedDateKey, sourceRows);

    return undefined;
  }, [
    appointments,
    selectedDateKey,
    showEditForm,
    isSavingAppointmentDetails,
    scheduleTimelineCompressionPersist,
  ]);

  // Get unavailable time slots for a specific dentist on a specific date
  const getDentistUnavailableSlots = async (date, dentistId) => {
    if (!date) return [];
    return await fetchUnavailableTimeSlots(date, dentistId);
  };
  // Generate time slots for main appointment table with dentist-specific unavailable slots.
  // Optional chair swap context aligns gap filling with swap edges (same as main table / modal split).
  const generateMainAppointmentTimeSlots = (appointments, dentistId, options = {}) => {
    const timeSlots = [];
    const officeStartTime = 9 * 60; // 9:00 AM
    const officeEndTime = 18 * 60; // 6:00 PM
    const slotInterval = 30; // 30-minute intervals
    const {
      chairSettings: optChairSettings = null,
      chairSwapsBySlotForDate: optSwapsForDate = null,
      unavailableSlotLabels = [],
    } = options || {};
    const unavailableRangesForGen = parseUnavailableLabelsToMinuteRanges(
      unavailableSlotLabels,
      timeToMinutes
    );

    // Sort appointments by start time
    const sortedAppointmentsByTime = [...appointments].sort((a, b) => {
      const aStart = timeToMinutes(a.startTime || a.time);
      const bStart = timeToMinutes(b.startTime || b.time);
      return aStart - bStart;
    });

    // First, create slots for all appointments at their original times
    const appointmentSlots = [];
    sortedAppointmentsByTime.forEach(appointment => {
      const apptStart = timeToMinutes(appointment.startTime || appointment.time);
      // Use appointment.endTime if available, otherwise calculate from duration
      const apptEnd = appointment.endTime 
        ? timeToMinutes(appointment.endTime)
        : apptStart + (appointment.duration || 30);
      
      // Convert start time to 12-hour format with AM/PM
      const startHours = Math.floor(apptStart / 60);
      const startMinutes = apptStart % 60;
      const startAmpm = startHours >= 12 ? 'PM' : 'AM';
      const startDisplayHours = startHours % 12 || 12;
      const startTimeStr = `${startDisplayHours}:${startMinutes.toString().padStart(2, '0')} ${startAmpm}`;
      
      // Convert end time to 12-hour format with AM/PM
      const endHours = Math.floor(apptEnd / 60);
      const endMinutes = apptEnd % 60;
      const endAmpm = endHours >= 12 ? 'PM' : 'AM';
      const endDisplayHours = endHours % 12 || 12;
      const endTimeStr = `${endDisplayHours}:${endMinutes.toString().padStart(2, '0')} ${endAmpm}`;
      
      // Create full slot range string
      const timeStr = `${startTimeStr} - ${endTimeStr}`;
      
      appointmentSlots.push({ 
        start: apptStart, 
        end: apptEnd, 
        timeStr, 
        appointment: appointment,
      });
    });

    // Sort appointment slots by start time
    appointmentSlots.sort((a, b) => a.start - b.start);
    const coalescedAppointmentSlots = coalesceAppointmentSlotsByStableId(appointmentSlots);

    // Merge overlapping appointments into continuous blocks
    const mergedBlocks = [];
    let currentBlock = null;

    coalescedAppointmentSlots.forEach((slot) => {
      if (!currentBlock) {
        // Start a new block
        currentBlock = {
          start: slot.start,
          end: slot.end,
          appointments: [slot],
        };
      } else if (slot.start < currentBlock.end) {
        // Overlaps the current block (strict < so back-to-back slots stay separate)
        currentBlock.end = Math.max(currentBlock.end, slot.end);
        currentBlock.appointments.push(slot);
      } else {
        // No overlap, finalize current block and start new one
        mergedBlocks.push(currentBlock);
        currentBlock = {
          start: slot.start,
          end: slot.end,
          appointments: [slot],
        };
      }
    });
    
    // Don't forget the last block
    if (currentBlock) {
      mergedBlocks.push(currentBlock);
    }

    // Now create time slots from merged blocks
    mergedBlocks.forEach(block => {
      // Add all appointments in this block
      block.appointments.forEach(appointmentSlot => {
        timeSlots.push(appointmentSlot);
      });
    });

    // Sort all slots by start time
    timeSlots.sort((a, b) => a.start - b.start);

    const inheritedBoundaryMinutesForGen = (() => {
      if (
        !dentistId ||
        !optChairSettings ||
        !optSwapsForDate ||
        !isChairCapacityConfigured(optChairSettings)
      ) {
        return [];
      }
      const isRosterDentist = isDentistOnRoster(dentistId, optChairSettings);
      const slotLabels = isRosterDentist
        ? Object.values(normalizeChairSwaps(optSwapsForDate) || {})
            .filter(
              (entry) =>
                entry?.fromRosterUid === dentistId && entry?.toBenchUid
            )
            .map((entry) => entry?.slot)
            .filter(Boolean)
        : getBenchSwapSlots({
            swapsBySlot: optSwapsForDate,
            benchUid: dentistId,
          });
      return Array.from(
        new Set(
          slotLabels
            .map((label) => parseChairSlotLabelToRange(label, timeToMinutes))
            .filter(Boolean)
            .flatMap((range) => [range.start, range.end])
        )
      ).sort((a, b) => a - b);
    })();
    const clampEndToInheritedBoundaryForGen = (startMinutes, proposedEndMinutes) => {
      if (!inheritedBoundaryMinutesForGen.length) return proposedEndMinutes;
      const boundary = inheritedBoundaryMinutesForGen.find(
        (minute) => minute > startMinutes && minute < proposedEndMinutes
      );
      return boundary || proposedEndMinutes;
    };

    const pushGapSlotsForGen = (gapStart, gapEnd) => {
      forEachTimelineGapSlot({
        gapStart,
        gapEnd,
        unavailableRanges: unavailableRangesForGen,
        slotInterval,
        officeEndTime,
        minSlotMinutes: 20,
        clampEnd: clampEndToInheritedBoundaryForGen,
        formatRangeLabel: formatSlotRangeLabelFromMinutes,
        onSlot: ({ start, end, timeStr }) => {
          timeSlots.push({ start, end, timeStr, appointment: null });
        },
      });
    };

    // Fill gaps between blocks, respecting persisted unavailable windows.
    let currentTime = officeStartTime;
    mergedBlocks.forEach((block) => {
      if (currentTime < block.start && currentTime < officeEndTime) {
        pushGapSlotsForGen(
          currentTime,
          Math.min(block.start, officeEndTime)
        );
      }
      currentTime = Math.max(currentTime, block.end);
    });
    if (currentTime < officeEndTime) {
      pushGapSlotsForGen(currentTime, officeEndTime);
    }

    // Sort all slots by start time again (after adding available slots)
    timeSlots.sort((a, b) => a.start - b.start);
    const viewDentistUidForMerge = dentistId || null;
    const skipMergeOtherOwners =
      viewDentistUidForMerge &&
      ((slot) => {
        const appt = slot?.appointment;
        if (!appt) return false;
        const owner = appt.dentistId;
        return !!(owner && owner !== viewDentistUidForMerge);
      });
    const displayConsolidated = mergeContiguousBookedTimelineRows(timeSlots, {
      skipMergeForSlot: skipMergeOtherOwners || undefined,
    });

    // Remove duplicates based on timeStr
    const uniqueTimeSlots = [];
    const seenTimeStrs = new Set();

    for (const slot of displayConsolidated) {
      if (!seenTimeStrs.has(slot.timeStr)) {
        // Include all slots (appointments and valid available slots)
        // Note: available slot fragments shorter than 20 minutes are filtered out.
        seenTimeStrs.add(slot.timeStr);
        uniqueTimeSlots.push(slot);
      }
    }

    return uniqueTimeSlots;
  };

  // Helper to get dentist display name
  const getDentistDisplayName = (dentist) => {
    if (!dentist) return "";
    if (dentist.displayName) return dentist.displayName;
    const first = (dentist.firstName || "").trim();
    const last = (dentist.lastName || "").trim();
    const combined = [first, last].filter(Boolean).join(" ").trim();
    return combined ? `Dr. ${combined}` : "";
  };

  const isClinicStaffUser =
    userDetails && (userDetails.role || userDetails.userType) === "ClinicStaff";

  // Returns the dentist's OWN appointment-derived timeline for the selected
  // modal date. Bench dentists do not mirror the roster baseline here; that
  // mirroring is decided separately via `findAlignedRosterUidForBench(...)`.
  const buildSelectedDateSlotUniverseForDentist = useCallback(
    (dentistUid) => {
      if (!selectedModalDate || !dentistUid) return [];
      const dateKey = getLocalDateKey(selectedModalDate);
      const ownAppts = (Array.isArray(allAppointments) ? allAppointments : []).filter(
        (appointment) =>
          appointment &&
          appointment.date === dateKey &&
          resolveAppointmentDentistUid(appointment) === dentistUid
      );
      const dynamicSlots = generateMainAppointmentTimeSlots(
        ownAppts,
        dentistUid,
        {
          chairSettings,
          chairSwapsBySlotForDate,
          unavailableSlotLabels:
            unavailableSlotsByDentistForDate?.[dentistUid] || [],
        }
      ).map((slot) => slot.timeStr);
      return Array.from(new Set(dynamicSlots));
    },
    [
      selectedModalDate,
      allAppointments,
      resolveAppointmentDentistUid,
      generateMainAppointmentTimeSlots,
      chairSettings,
      chairSwapsBySlotForDate,
      unavailableSlotsByDentistForDate,
    ]
  );

  // Determines which roster dentist (if any) a bench dentist is currently
  // swapped in for on the selected modal date from chairSwaps source-of-truth.
  const findAlignedRosterUidForBench = useCallback(
    (benchUid) => {
      if (!isChairCapacityConfigured(chairSettings)) return null;
      if (!benchUid || isDentistOnRoster(benchUid, chairSettings)) return null;
      const links = Object.values(chairSwapsBySlotForDate || {}).filter(
        (entry) => entry?.toBenchUid === benchUid && entry?.fromRosterUid
      );
      return links.length > 0 ? links[0].fromRosterUid : null;
    },
    [chairSettings, chairSwapsBySlotForDate]
  );

  const getLinkedBenchUidsForRosterFromSwaps = useCallback(({ rosterUid, swapsBySlot }) => {
    return getRosterPartnerBenchUidsForDay(swapsBySlot, rosterUid);
  }, []);

  const doesSlotOverlapSwapSlots = useCallback(
    (slotLabel, swapSlots = []) => {
      if (!slotLabel || !Array.isArray(swapSlots) || swapSlots.length === 0) {
        return false;
      }
      const parseRange = (label) => {
        if (!label || typeof label !== "string") return null;
        const [startRaw, endRaw] = label.split(" - ").map((part) => part.trim());
        if (!startRaw || !endRaw) return null;
        const start = timeToMinutes(startRaw);
        const end = timeToMinutes(endRaw);
        if (
          typeof start !== "number" ||
          typeof end !== "number" ||
          Number.isNaN(start) ||
          Number.isNaN(end)
        ) {
          return null;
        }
        return { start, end };
      };
      const source = parseRange(slotLabel);
      if (!source) return false;
      const mergedWindows = swapSlots
        .map(parseRange)
        .filter(Boolean)
        .sort((a, b) => a.start - b.start)
        .reduce((acc, next) => {
          if (acc.length === 0) return [next];
          const last = acc[acc.length - 1];
          if (next.start <= last.end) {
            last.end = Math.max(last.end, next.end);
            return acc;
          }
          acc.push({ ...next });
          return acc;
        }, []);
      return mergedWindows.some((target) => {
        // Bench slot must be fully contained in the explicit/merged swap window.
        return source.start >= target.start && source.end <= target.end;
      });
    },
    [timeToMinutes]
  );

  /** True if the label range overlaps any merged swap window (for appointments that extend past the nominal window end). */
  const doesSlotRangeOverlapSwapSlots = useCallback(
    (slotLabel, swapSlots = []) => {
      if (!slotLabel || !Array.isArray(swapSlots) || swapSlots.length === 0) {
        return false;
      }
      const parseRange = (label) => {
        if (!label || typeof label !== "string") return null;
        const [startRaw, endRaw] = label.split(" - ").map((part) => part.trim());
        if (!startRaw || !endRaw) return null;
        const start = timeToMinutes(startRaw);
        const end = timeToMinutes(endRaw);
        if (
          typeof start !== "number" ||
          typeof end !== "number" ||
          Number.isNaN(start) ||
          Number.isNaN(end)
        ) {
          return null;
        }
        return { start, end };
      };
      const source = parseRange(slotLabel);
      if (!source) return false;
      const mergedWindows = swapSlots
        .map(parseRange)
        .filter(Boolean)
        .sort((a, b) => a.start - b.start)
        .reduce((acc, next) => {
          if (acc.length === 0) return [next];
          const last = acc[acc.length - 1];
          if (next.start <= last.end) {
            last.end = Math.max(last.end, next.end);
            return acc;
          }
          acc.push({ ...next });
          return acc;
        }, []);
      return mergedWindows.some(
        (target) => source.start < target.end && source.end > target.start
      );
    },
    [timeToMinutes]
  );

  const buildBenchTimelineSlotsFromSwaps = useCallback(
    ({ appointments = [], swapSlots = [] }) => {
      if (!Array.isArray(swapSlots) || swapSlots.length === 0) return [];
      const SLOT_INTERVAL_MINUTES = 30;
      const MIN_VISIBLE_SLOT_MINUTES = 20;
      const parseRange = (label) => {
        if (!label || typeof label !== "string") return null;
        const [startRaw, endRaw] = label.split(" - ").map((part) => part.trim());
        if (!startRaw || !endRaw) return null;
        const start = timeToMinutes(startRaw);
        const end = timeToMinutes(endRaw);
        if (
          typeof start !== "number" ||
          typeof end !== "number" ||
          Number.isNaN(start) ||
          Number.isNaN(end) ||
          end <= start
        ) {
          return null;
        }
        return { start, end };
      };
      const toLabel = (start, end) => {
        const toTime = (minutes) => {
          const hrs24 = Math.floor(minutes / 60);
          const mins = minutes % 60;
          const ampm = hrs24 >= 12 ? "PM" : "AM";
          const hrs12 = hrs24 % 12 || 12;
          return `${hrs12}:${mins.toString().padStart(2, "0")} ${ampm}`;
        };
        return `${toTime(start)} - ${toTime(end)}`;
      };
      const mergedWindows = swapSlots
        .map(parseRange)
        .filter(Boolean)
        .sort((a, b) => a.start - b.start)
        .reduce((acc, next) => {
          if (acc.length === 0) return [next];
          const last = acc[acc.length - 1];
          if (next.start <= last.end) {
            last.end = Math.max(last.end, next.end);
            return acc;
          }
          acc.push({ ...next });
          return acc;
        }, []);
      const apptRanges = (Array.isArray(appointments) ? appointments : [])
        .map((appointment) => {
          const start = timeToMinutes(appointment.startTime || appointment.time);
          const end = appointment.endTime
            ? timeToMinutes(appointment.endTime)
            : start + (appointment.duration || 30);
          if (
            typeof start !== "number" ||
            typeof end !== "number" ||
            Number.isNaN(start) ||
            Number.isNaN(end) ||
            end <= start
          ) {
            return null;
          }
          return { start, end, appointment };
        })
        .filter(Boolean)
        .sort((a, b) => a.start - b.start);

      const pushAvailableChunks = (slots, start, end) => {
        let cursor = start;
        while (cursor < end) {
          const chunkEnd = Math.min(cursor + SLOT_INTERVAL_MINUTES, end);
          if (chunkEnd - cursor >= MIN_VISIBLE_SLOT_MINUTES) {
            slots.push({
              start: cursor,
              end: chunkEnd,
              timeStr: toLabel(cursor, chunkEnd),
              appointment: null,
            });
          }
          cursor = chunkEnd;
        }
      };

      const slots = [];
      mergedWindows.forEach((window) => {
        const overlaps = apptRanges.filter(
          (appt) => appt.start < window.end && appt.end > window.start
        );
        let cursor = window.start;
        overlaps.forEach((appt) => {
          const overlapStart = Math.max(appt.start, window.start);
          const overlapEnd = Math.min(appt.end, window.end);
          if (overlapStart > cursor) {
            pushAvailableChunks(slots, cursor, overlapStart);
          }
          if (overlapEnd > overlapStart) {
            slots.push({
              start: overlapStart,
              end: overlapEnd,
              timeStr: toLabel(overlapStart, overlapEnd),
              appointment: appt.appointment,
            });
          }
          cursor = Math.max(cursor, overlapEnd);
        });
        if (window.end > cursor) {
          pushAvailableChunks(slots, cursor, window.end);
        }
      });
      const unique = [];
      const seen = new Set();
      slots
        .sort((a, b) => a.start - b.start)
        .forEach((slot) => {
          if (seen.has(slot.timeStr)) return;
          seen.add(slot.timeStr);
          unique.push(slot);
        });
      return unique;
    },
    [timeToMinutes]
  );

  const getAppointmentsForSlotTimelineUid = useCallback(
    (uid) => {
      if (!selectedModalDate || !uid) return [];
      const dateKey = getLocalDateKey(selectedModalDate);
      const allOnDate = (Array.isArray(allAppointments) ? allAppointments : []).filter(
        (appointment) => appointment && appointment.date === dateKey
      );
      const onChairMode = isChairCapacityConfigured(chairSettings);
      const onRoster = isDentistOnRoster(uid, chairSettings);
      if (!onChairMode || onRoster) {
        return allOnDate.filter(
          (appointment) => resolveAppointmentDentistUid(appointment) === uid
        );
      }
      // Bench dentist in chair mode: include both the aligned roster timeline
      // and bench-owned bookings within swapped windows.
      const alignedRosterUid = findAlignedRosterUidForBench(uid);
      if (!alignedRosterUid) return [];
      return allOnDate.filter(
        (appointment) => {
          const apptUid = resolveAppointmentDentistUid(appointment);
          return apptUid === alignedRosterUid || apptUid === uid;
        }
      );
    },
    [selectedModalDate, allAppointments, chairSettings, resolveAppointmentDentistUid, findAlignedRosterUidForBench]
  );

  const getModalVisibleTimeSlots = useCallback(() => {
    if (!selectedModalDate || !selectedModalDentist) return [];
    let benchAppointmentSlots = [];
    const inChairMode = isChairCapacityConfigured(chairSettings);
    const isBenchSelected =
      inChairMode && !isDentistOnRoster(selectedModalDentist, chairSettings);
    const alignedRosterUid = isBenchSelected
      ? findAlignedRosterUidForBench(selectedModalDentist)
      : null;
    const linkedBenchUidsForRoster = !isBenchSelected
      ? getLinkedBenchUidsForRosterFromSwaps({
          rosterUid: selectedModalDentist,
          swapsBySlot: chairSwapsBySlotForDate,
        })
      : [];
    if (isBenchSelected && !alignedRosterUid) {
      return [];
    }
    const appointmentsForTimeline = isBenchSelected
      ? getAppointmentsForSlotTimelineUid(selectedModalDentist)
      : modalAppointments.filter((appointment) => {
          const appointmentDentistUid = resolveAppointmentDentistUid(appointment);
          if (appointmentDentistUid) {
            return appointmentDentistUid === selectedModalDentist;
          }
          if (appointment.dentistId) {
            return appointment.dentistId === selectedModalDentist;
          }
          const dentist = dentists.find((d) => d.uid === selectedModalDentist);
          if (dentist) {
            const dentistDisplayName = getDentistDisplayName(dentist);
            return appointment.dentist === dentistDisplayName;
          }
          return false;
        });
    const generated = generateMainAppointmentTimeSlots(
      appointmentsForTimeline,
      selectedModalDentist,
      {
        chairSettings,
        chairSwapsBySlotForDate,
        unavailableSlotLabels: Array.isArray(unavailableTimeSlots)
          ? unavailableTimeSlots
          : [],
      }
    );
    const parseRange = (label) => {
      if (!label || typeof label !== "string") return null;
      const [startRaw, endRaw] = label.split(" - ").map((part) => part.trim());
      if (!startRaw || !endRaw) return null;
      const start = timeToMinutes(startRaw);
      const end = timeToMinutes(endRaw);
      if (
        typeof start !== "number" ||
        typeof end !== "number" ||
        Number.isNaN(start) ||
        Number.isNaN(end) ||
        end <= start
      ) {
        return null;
      }
      return { start, end };
    };
    const formatRangeLabel = (start, end) => {
      const formatMinute = (minutes) => {
        const hours24 = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const ampm = hours24 >= 12 ? "PM" : "AM";
        const hours12 = hours24 % 12 || 12;
        return `${hours12}:${mins.toString().padStart(2, "0")} ${ampm}`;
      };
      return `${formatMinute(start)} - ${formatMinute(end)}`;
    };
    const standardGridSlots = buildStandardHalfHourSlotLabels({})
      .map((label) => {
        const range = parseRange(label);
        if (!range) return null;
        return { start: range.start, end: range.end, timeStr: label, appointment: null };
      })
      .filter(Boolean);
    const relevantSwapSlots = isBenchSelected
      ? getBenchSwapSlots({
          swapsBySlot: chairSwapsBySlotForDate,
          benchUid: selectedModalDentist,
        })
      : Object.values(chairSwapsBySlotForDate || {})
          .filter(
            (entry) =>
              entry?.fromRosterUid === selectedModalDentist && entry?.toBenchUid
          )
          .map((entry) => entry?.slot)
          .filter(Boolean);
    const mergedBenchModalCoverage = isBenchSelected
      ? getBenchSwapCoverageRanges({
          swapsBySlot: chairSwapsBySlotForDate,
          benchUid: selectedModalDentist,
          timeToMinutes,
        })
      : [];
    // Bench-owned bookings (e.g. compressed pendings after inherited close) may run past the swap
    // window; they must still appear in Time Slot Management and the manage-calendar grid.
    benchAppointmentSlots = isBenchSelected
      ? generated.filter((slotEntry) => {
          if (!slotEntry?.appointment) return false;
          const ownerUid = resolveAppointmentDentistUid(slotEntry.appointment);
          return ownerUid === selectedModalDentist;
        })
      : [];
    const boundaryMinutes = Array.from(
      new Set(
        relevantSwapSlots
          .map((slotLabel) => parseRange(slotLabel))
          .filter(Boolean)
          .flatMap((range) => [range.start, range.end])
      )
    ).sort((a, b) => a - b);
    const rangesOverlap = (a, b) =>
      !!a && !!b && a.start < b.end && a.end > b.start;
    /** Half-hour cells ∩ swap, minus bench appointment ranges — matches main table / PatientBooking grid (no duplicate fragments from generateMain + standard grid). */
    const benchApptRangesSorted = [...benchAppointmentSlots].sort(
      (a, b) => (a.start || 0) - (b.start || 0)
    );
    const benchAvailableFromHalfHourGrid = standardGridSlots.flatMap((slotEntry) => {
      if (!doesSlotOverlapSwapSlots(slotEntry.timeStr, relevantSwapSlots)) return [];
      let cursor = slotEntry.start;
      const cellEnd = slotEntry.end;
      const out = [];
      for (const ap of benchApptRangesSorted) {
        if (ap.end <= cursor || ap.start >= cellEnd) continue;
        if (cursor < ap.start) {
          const segEnd = Math.min(ap.start, cellEnd);
          let s = cursor;
          let e = segEnd;
          if (mergedBenchModalCoverage.length) {
            for (const r of mergedBenchModalCoverage) {
              const rs = Math.max(s, r.start);
              const re = Math.min(e, r.end);
              if (re - rs >= 20) {
                out.push({
                  start: rs,
                  end: re,
                  timeStr: formatRangeLabel(rs, re),
                  appointment: null,
                });
              }
            }
          } else if (e - s >= 20) {
            out.push({
              start: s,
              end: e,
              timeStr: formatRangeLabel(s, e),
              appointment: null,
            });
          }
        }
        cursor = Math.max(cursor, ap.end);
        if (cursor >= cellEnd) return out;
      }
      if (cursor < cellEnd) {
        let s = cursor;
        let e = cellEnd;
        if (mergedBenchModalCoverage.length) {
          for (const r of mergedBenchModalCoverage) {
            const rs = Math.max(s, r.start);
            const re = Math.min(e, r.end);
            if (re - rs >= 20) {
              out.push({
                start: rs,
                end: re,
                timeStr: formatRangeLabel(rs, re),
                appointment: null,
              });
            }
          }
        } else if (e - s >= 20) {
          out.push({
            start: s,
            end: e,
            timeStr: formatRangeLabel(s, e),
            appointment: null,
          });
        }
      }
      return out;
    });
    const benchGridInside = benchAvailableFromHalfHourGrid;
    const benchOutsideStandard = standardGridSlots.filter(
      (slotEntry) => !doesSlotOverlapSwapSlots(slotEntry.timeStr, relevantSwapSlots)
    );
    const finalizeBenchSlots = () => {
      const dedup = new Map();
      [...benchAppointmentSlots, ...benchGridInside, ...benchOutsideStandard]
        .sort((a, b) => (a.start || 0) - (b.start || 0))
        .forEach((slotEntry) => {
          if (!slotEntry?.timeStr) return;
          if (!dedup.has(slotEntry.timeStr)) dedup.set(slotEntry.timeStr, slotEntry);
        });
      return Array.from(dedup.values());
    };
    if (boundaryMinutes.length === 0) {
      if (!isBenchSelected) {
        return generated;
      }
      return finalizeBenchSlots();
    }

    const splitAtBoundaries = [];
    generated.forEach((slotEntry) => {
      if (!slotEntry || slotEntry.appointment) {
        splitAtBoundaries.push(slotEntry);
        return;
      }
      const sourceStart = Number.isFinite(slotEntry.start)
        ? slotEntry.start
        : parseRange(slotEntry.timeStr)?.start;
      const sourceEnd = Number.isFinite(slotEntry.end)
        ? slotEntry.end
        : parseRange(slotEntry.timeStr)?.end;
      if (
        !Number.isFinite(sourceStart) ||
        !Number.isFinite(sourceEnd) ||
        sourceEnd <= sourceStart
      ) {
        splitAtBoundaries.push(slotEntry);
        return;
      }
      const cuts = [
        sourceStart,
        ...boundaryMinutes.filter((minute) => minute > sourceStart && minute < sourceEnd),
        sourceEnd,
      ];
      for (let i = 0; i < cuts.length - 1; i += 1) {
        const start = cuts[i];
        const end = cuts[i + 1];
        if (end - start < 20) continue;
        splitAtBoundaries.push({
          ...slotEntry,
          start,
          end,
          timeStr: formatRangeLabel(start, end),
        });
      }
    });
    if (!isBenchSelected) {
      return splitAtBoundaries.sort((a, b) => (a.start || 0) - (b.start || 0));
    }
    return finalizeBenchSlots();
  }, [
    selectedModalDate,
    selectedModalDentist,
    chairSettings,
    findAlignedRosterUidForBench,
    getBenchSwapSlots,
    getLinkedBenchUidsForRosterFromSwaps,
    getAppointmentsForSlotTimelineUid,
    modalAppointments,
    dentists,
    chairSwapsBySlotForDate,
    resolveAppointmentDentistUid,
    generateMainAppointmentTimeSlots,
    buildStandardHalfHourSlotLabels,
    timeToMinutes,
    doesSlotRangeOverlapSwapSlots,
    doesSlotOverlapSwapSlots,
    getBenchSwapCoverageRanges,
    unavailableTimeSlots,
  ]);

  const getModalSelectableTimeSlots = useCallback(() => {
    if (!selectedModalDate || !selectedModalDentist) return [];
    const isDateClosed = closedDays.includes(getLocalDateKey(selectedModalDate));
    if (isDateClosed) return [];
    const selectedIsRoster = isDentistOnRoster(selectedModalDentist, chairSettings);
    const normalizedSwaps = normalizeChairSwaps(chairSwapsBySlotForDate || {});
    const parseRange = (slotLabel) => {
      if (!slotLabel || typeof slotLabel !== "string") return null;
      const [startRaw, endRaw] = slotLabel.split(" - ").map((part) => part.trim());
      if (!startRaw || !endRaw) return null;
      const start = timeToMinutes(startRaw);
      const end = timeToMinutes(endRaw);
      if (
        typeof start !== "number" ||
        typeof end !== "number" ||
        Number.isNaN(start) ||
        Number.isNaN(end) ||
        end <= start
      ) {
        return null;
      }
      return { start, end };
    };
    const rangesOverlap = (a, b) => Boolean(a && b && a.start < b.end && a.end > b.start);
    const doesSlotBelongToSelectedDentist = (slotLabel) => {
      if (!isChairCapacityConfigured(chairSettings)) return true;
      const sourceRange = parseRange(slotLabel);
      if (!sourceRange) return false;
      if (!selectedIsRoster) {
        const overlapsInheritedSwap = Object.entries(normalizedSwaps).some(([, swapEntry]) => {
          if (swapEntry?.toBenchUid !== selectedModalDentist) return false;
          const swapRange = parseRange(swapEntry?.slot || "");
          return rangesOverlap(sourceRange, swapRange);
        });
        if (overlapsInheritedSwap) return true;
        const dateKey = getLocalDateKey(selectedModalDate);
        return (Array.isArray(modalAppointments) ? modalAppointments : []).some((appt) => {
          if (!appt || appt.date !== dateKey) return false;
          if (resolveAppointmentDentistUid(appt) !== selectedModalDentist) return false;
          const aStart = timeToMinutes(appt.startTime || appt.time);
          const aEnd = appt.endTime
            ? timeToMinutes(appt.endTime)
            : aStart + (appt.duration || 30);
          return sourceRange.start < aEnd && sourceRange.end > aStart;
        });
      }
      const swappedOut = Object.entries(normalizedSwaps).some(([, swapEntry]) => {
        if (swapEntry?.fromRosterUid !== selectedModalDentist) return false;
        const swapRange = parseRange(swapEntry?.slot || "");
        return rangesOverlap(sourceRange, swapRange);
      });
      return !swappedOut;
    };
    const plottedAvailable = getPlottedAvailableSlotsForDentist(
      modalDentistSchedulesForDate,
      selectedModalDentist,
      chairSettings
    );
    if (plottedAvailable !== null) {
      return plottedAvailable.filter((slotLabel) => doesSlotBelongToSelectedDentist(slotLabel));
    }
    const slotLabels = getModalVisibleTimeSlots()
      .map((slotData) => slotData.timeStr)
      .filter((slotLabel) => doesSlotBelongToSelectedDentist(slotLabel));
    return filterSlotLabelsToPlottedSchedule(
      slotLabels,
      modalDentistSchedulesForDate,
      selectedModalDentist,
      chairSettings
    );
  }, [
    selectedModalDate,
    selectedModalDentist,
    closedDays,
    getModalVisibleTimeSlots,
    chairSettings,
    chairSwapsBySlotForDate,
    timeToMinutes,
    modalAppointments,
    resolveAppointmentDentistUid,
    modalDentistSchedulesForDate,
  ]);

  useEffect(() => {
    const selectable = new Set(getModalSelectableTimeSlots());
    setSelectedTimeSlots((prev) => prev.filter((slot) => selectable.has(slot)));
  }, [getModalSelectableTimeSlots]);

  const modalSelectableSlotSet = useMemo(
    () => new Set(getModalSelectableTimeSlots()),
    [getModalSelectableTimeSlots]
  );
  const modalRenderableTimeSlots = useMemo(() => {
    const dentistUidForCheck =
      selectedModalDentist && selectedModalDentist !== "clinic"
        ? selectedModalDentist
        : null;

    const plottedAvailable = getPlottedAvailableSlotsForDentist(
      modalDentistSchedulesForDate,
      dentistUidForCheck,
      chairSettings
    );

    if (plottedAvailable !== null) {
      if (!selectedModalDate || !dentistUidForCheck) {
        return [];
      }
      const dateKey = getLocalDateKey(selectedModalDate);
      return buildManageCalendarRenderableSlots({
        plottedSlotLabels: plottedAvailable,
        appointments: modalAppointments,
        dateKey,
        dentistUid: dentistUidForCheck,
      });
    }

    const visibleSlots = getModalVisibleTimeSlots();
    let base =
      !isChairCapacityConfigured(chairSettings) || !selectedModalDentist
        ? visibleSlots
        : visibleSlots.filter((slotData) =>
            modalSelectableSlotSet.has(slotData.timeStr)
          );
    if (isDentistSchedulePlottingEnabled(chairSettings) && selectedModalDentist) {
      const allowedLabels = filterSlotLabelsToPlottedSchedule(
        base.map((slotData) => slotData.timeStr),
        modalDentistSchedulesForDate,
        selectedModalDentist,
        chairSettings
      );
      const allowedSet = new Set(allowedLabels);
      base = base.filter((slotData) => allowedSet.has(slotData.timeStr));
    }
    if (!selectedModalDate || !dentistUidForCheck) {
      return base.map((s) => ({
        ...s,
        modalSegmentTimeStrs: [s.timeStr],
      }));
    }
    return mergeModalHalfHourSlotsByBookedAppointment(base, (slotLabel) =>
      checkTimeSlotForBookedAppointment(
        slotLabel,
        selectedModalDate,
        dentistUidForCheck
      )
    );
  }, [
    getModalVisibleTimeSlots,
    chairSettings,
    selectedModalDentist,
    modalSelectableSlotSet,
    selectedModalDate,
    modalAppointments,
    modalDentistSchedulesForDate,
    checkTimeSlotForBookedAppointment,
    getLocalDateKey,
  ]);

  const handleTimeSlotSelection = (timeSlot, segmentTimeStrs) => {
    const segments =
      Array.isArray(segmentTimeStrs) && segmentTimeStrs.length > 0
        ? segmentTimeStrs.filter((s) => modalSelectableSlotSet.has(s))
        : modalSelectableSlotSet.has(timeSlot)
          ? [timeSlot]
          : [];
    if (segments.length === 0) return;
    setSelectedTimeSlots((prev) => {
      const allSelected = segments.every((s) => prev.includes(s));
      if (allSelected) {
        return prev.filter((s) => !segments.includes(s));
      }
      const next = [...prev];
      segments.forEach((s) => {
        if (!next.includes(s)) next.push(s);
      });
      return next;
    });
  };

  const timeSlotDropdownDentists = useMemo(() => {
    if (!isChairCapacityConfigured(chairSettings)) {
      return dentists;
    }
    const normalizedSwaps = normalizeChairSwaps(chairSwapsBySlotForDate || {});
    const rosterSet = new Set(
      Array.isArray(chairSettings?.rosterDentistIds) ? chairSettings.rosterDentistIds : []
    );
    const swappedBenchSet = new Set(
      Object.values(normalizedSwaps)
        .map((entry) => entry?.toBenchUid)
        .filter(Boolean)
    );
    return (Array.isArray(dentists) ? dentists : []).filter(
      (dentist) => rosterSet.has(dentist.uid) || swappedBenchSet.has(dentist.uid)
    );
  }, [dentists, chairSettings, chairSwapsBySlotForDate]);

  useEffect(() => {
    if (!isClinicStaffUser) return;
    if (!timeSlotDropdownDentists.length) {
      setSelectedModalDentist(null);
      return;
    }
    const isCurrentSelectionValid = timeSlotDropdownDentists.some(
      (dentist) => dentist.uid === selectedModalDentist
    );
    if (!isCurrentSelectionValid) {
      setSelectedModalDentist(timeSlotDropdownDentists[0].uid);
    }
  }, [isClinicStaffUser, timeSlotDropdownDentists, selectedModalDentist]);

  const consolidatedSelectedSlotsForConfirm = useMemo(() => {
    const dentistUid =
      selectedModalDentist && selectedModalDentist !== "clinic"
        ? selectedModalDentist
        : null;
    return consolidateContiguousSelectedSlotsByAppointment(
      selectedTimeSlots,
      (slotLabel) =>
        checkTimeSlotForBookedAppointment(
          slotLabel,
          selectedModalDate,
          dentistUid
        ),
      { timelineGridEndMinutes: 18 * 60 }
    );
  }, [selectedTimeSlots, selectedModalDate, selectedModalDentist, modalAppointments]);

  // Filter appointments by status and group by dentist
  const getFilteredAppointmentsByDentist = () => {
    
    if (!showAllBookedAppointments && !showPendingAppointments && !showConfirmedAppointments) {
      return null; // No filters active
    }

    const year = selectedDateAppointment.toDate().getFullYear();
    const month = String(selectedDateAppointment.toDate().getMonth() + 1).padStart(2, '0');
    const day = String(selectedDateAppointment.toDate().getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;


    // Get all appointments for the selected date
    const dateAppointments = timelineDisplayAppointments.filter((appointment) =>
      appointmentBelongsToDateDoc(appointment, dateStr)
    );

    // Filter by status based on active checkboxes
    let filteredAppointments = [];
    
    if (showAllBookedAppointments) {
      // Show all booked appointments (pending and confirmed)
      filteredAppointments = dateAppointments.filter(appointment => 
        appointment.status === "Pending" || appointment.status === "pending" ||
        appointment.status === "Confirmed" || appointment.status === "confirmed" ||
        appointment.status === "New" || appointment.status === "new"
      );
    } else {
      // Show specific status based on individual checkboxes
      if (showPendingAppointments) {
        const pendingAppointments = dateAppointments.filter(appointment => 
          appointment.status === "Pending" || appointment.status === "pending" ||
          appointment.status === "New" || appointment.status === "new"
        );
        filteredAppointments = [...filteredAppointments, ...pendingAppointments];
      }
      
      if (showConfirmedAppointments) {
        const confirmedAppointments = dateAppointments.filter(appointment => 
          appointment.status === "Confirmed" || appointment.status === "confirmed"
        );
        filteredAppointments = [...filteredAppointments, ...confirmedAppointments];
      }
    }

    // If user is DentistOwner or AssociateDentist, only show their own appointments
    if (isCurrentUserDentist() && selectedDentist) {
      filteredAppointments = filteredAppointments.filter(appointment => {
        // Check if appointment belongs to current dentist by dentistId
        if (appointment.dentistId && appointment.dentistId === selectedDentist) {
          return true;
        }
        // Fallback: check by dentist display name
        const currentDentist = dentists.find(d => d.uid === selectedDentist);
        if (currentDentist) {
          const displayName = getDentistDisplayName(currentDentist);
          return appointment.dentist === displayName;
        }
        return false;
      });
    }

    // Group appointments by dentist
    const groupedByDentist = {};
    
    filteredAppointments.forEach(appointment => {
      const dentistName = appointment.dentist || "Unknown Dentist";
      if (!groupedByDentist[dentistName]) {
        groupedByDentist[dentistName] = [];
      }
      groupedByDentist[dentistName].push(appointment);
    });

    // Sort appointments within each dentist group by time
    Object.keys(groupedByDentist).forEach(dentistName => {
      groupedByDentist[dentistName].sort((a, b) => {
        const timeA = timeToMinutes(a.startTime || a.time);
        const timeB = timeToMinutes(b.startTime || b.time);
        return timeA - timeB;
      });
    });

    return groupedByDentist;
  };

  // Get comprehensive status of all dentists for multiple time slots
  const getComprehensiveDentistStatuses = async (date, timeSlots) => {
    if (!date || !timeSlots || timeSlots.length === 0) return [];
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Check if the date is closed
    const isDateClosed = closedDays.includes(dateStr);
    
    try {
      const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
      const timeSlotsSnap = await getDoc(timeSlotsRef);
      
      const dentistStatuses = [];
      
      // Check the selected dentist's status for all selected time slots
      const dentistsToCheck = dentists.filter(dentist => dentist.uid === selectedModalDentist);
      
      for (const dentist of dentistsToCheck) {
        const dentistSlots = timeSlotsSnap.exists() ? 
          (timeSlotsSnap.data()[dentist.uid] || []) : [];
        
        const dentistInfo = {
          dentist,
          displayName: getDentistDisplayName(dentist),
          timeSlotDetails: []
        };
        
        // Check each selected time slot for this dentist
        for (const timeSlot of timeSlots) {
          // Check if dentist has appointments for this time slot
          const hasAppointment = await checkTimeSlotForBookedAppointment(timeSlot, date, dentist.uid);
          
          let status = "Available";
          let reason = "";
          
          // If date is closed, all slots are unavailable
          if (isDateClosed) {
            status = "Unavailable";
            reason = "Clinic is closed on this date";
          } else if (dentistSlots.includes(timeSlot)) {
            status = "Unavailable";
            reason = "Marked as unavailable by dentist";
          } else {
            status = "Available";
            reason = hasAppointment ? `Has ${hasAppointment.status} appointment` : "No conflicts";
          }
          
          dentistInfo.timeSlotDetails.push({
            timeSlot,
            status,
            reason,
            appointment: hasAppointment || null
          });
        }
        
        dentistStatuses.push(dentistInfo);
      }
      
      return dentistStatuses;
    } catch (error) {
      console.error("Error getting comprehensive dentist statuses:", error);
      return [];
    }
  };

  // Get status of all dentists for a specific time slot
  const getDentistStatusesForTimeSlot = async (date, timeSlot) => {
    if (!date || !timeSlot) return [];
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Check if the date is closed
    const isDateClosed = closedDays.includes(dateStr);
    
    try {
      const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
      const timeSlotsSnap = await getDoc(timeSlotsRef);
      
      const dentistStatuses = [];
      
      // Check each dentist's status
      for (const dentist of dentists) {
        const dentistSlots = timeSlotsSnap.exists() ? 
          (timeSlotsSnap.data()[dentist.uid] || []) : [];
        
        // Check if dentist has appointments for this time slot
        const hasAppointment = await checkTimeSlotForBookedAppointment(timeSlot, date, dentist.uid);
        
        let status = "Available";
        let reason = "";
        
        // If date is closed, all slots are unavailable
        if (isDateClosed) {
          status = "Unavailable";
          reason = "Clinic is closed on this date";
        } else if (dentistSlots.includes(timeSlot)) {
          status = "Unavailable";
          reason = "Marked as unavailable";
        } else if (hasAppointment) {
          status = hasAppointment.status;
          reason = `Has ${hasAppointment.status} appointment`;
        } else {
          status = "Available";
          reason = "No conflicts";
        }
        
        dentistStatuses.push({
          dentist,
          displayName: getDentistDisplayName(dentist),
          status,
          reason,
          appointment: hasAppointment || null
        });
      }
      
      return dentistStatuses;
    } catch (error) {
      console.error("Error getting dentist statuses:", error);
      return [];
    }
  };

  // Filter appointments by selected dentist and exclude cancelled appointments
  const filteredAppointments = selectedDentist && selectedDentist !== ""
    ? timelineDisplayAppointments.filter((appt) => {
        // Skip cancelled appointments
        if (appt.status === "Cancelled" || appt.status === "cancelled") {
          return false;
        }
        
        // If selectedDentist is a UID, compare with dentist UID
        // If it's a display name (for backward compatibility), compare with display name
        const dentist = dentists.find(d => d.uid === selectedDentist);
        if (dentist) {
          // First try to match by dentistId field (more reliable)
          if (appt.dentistId && appt.dentistId === dentist.uid) {
            return true;
          }
          // Fallback to display name matching
          const displayName = getDentistDisplayName(dentist);
          return appt.dentist === displayName;
        }
        return appt.dentist === selectedDentist;
      })
    : [];

  // handle date change
  const handleDateChange = (date) => {
    if (!date) return;
    const normalized = dayjs(date);
    if (!normalized.isValid()) return;
    setSelectedDateAppointment(normalized);
    setSelectedDate(normalized.toDate());
  };

  // Handle appointment removal confirmation
  const handleRemoveAppointment = (id) => {
    const appointment = appointments.find((appt) => appt.id === id);
    setAppointmentToRemove(appointment);
    setShowRemoveConfirmation(true);
  };

  // Format date as MM-DD-YYYY
  const formatDateMMDDYYYY = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${month}-${day}-${year}`;
  };

  // Format date as "October 31, 2025"
  const formatDateLong = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    const date = new Date(year, month - 1, day);
    const monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"];
    return `${monthNames[date.getMonth()]} ${day}, ${year}`;
  };

  // Handle appointment removal
  const confirmRemoveAppointment = async () => {
    if (!appointmentToRemove) return;

    const formattedDate = new Date(selectedDateAppointment.toDate().getTime() - selectedDateAppointment.toDate().getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];
    const appointmentRef = doc(db, "appointments", formattedDate);

    try {
      // Delete the appointment
      await updateDoc(appointmentRef, {
        [appointmentToRemove.id]: deleteField()
      });
      
      setAppointments((prev) => prev.filter((appointment) => appointment.id !== appointmentToRemove.id));
      
      // Update allAppointments state to remove the appointment from calendar badges
      setAllAppointments((prev) => prev.filter((appointment) => appointment.id !== appointmentToRemove.id));

      // Get the remover's name with appropriate title based on role and gender
      let removerName = "Clinic Personnel";
      if (userDetails) {
        const userRole = userDetails.role || userDetails.userType || "";
        const gender = userDetails.gender || "";
        // Remove any existing title prefixes from firstName/lastName
        let firstName = userDetails.firstName || "";
        let lastName = userDetails.lastName || "";
        
        // Strip common prefixes if they exist
        firstName = firstName.replace(/^(Dr\.|Mr\.|Mrs\.|Ms\.)\s*/i, "").trim();
        
        const fullName = `${firstName} ${lastName}`.trim();
        
        if (userRole === "DentistOwner" || userRole === "AssociateDentist") {
          removerName = `Dr. ${fullName}`;
        } else {
          // For ClinicStaff and other roles, use name without prefix
          removerName = fullName;
        }
      }

      const formattedDateLong = formatDateLong(formattedDate);
      const startTime = appointmentToRemove?.startTime ? formatTimeConsistent(appointmentToRemove.startTime) : "N/A";
      const endTime = appointmentToRemove?.endTime ? formatTimeConsistent(appointmentToRemove.endTime) : "N/A";
      const appointmentWindow = buildNotificationWindow(formattedDateLong, startTime, endTime);

      const reasonValue = removalReason.trim() ? removalReason.trim() : "N/A";
      const reasonSentence = `Reason: ${reasonValue}.`;
      
      // Get patient name early for use in notifications
      const patientName = appointmentToRemove?.patientDisplay || appointmentToRemove?.patientName || "A patient";

      // Helper function to send email removal notification
      const sendEmailRemovalNotification = async (type, recipientEmail, recipientName = null, recipientRole = null) => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          // Format services - extract names if they're objects
          const servicesRaw = appointmentToRemove?.services || [];
          const services = Array.isArray(servicesRaw) 
            ? servicesRaw.map(service => {
                if (typeof service === 'string') {
                  return service;
                } else if (service && typeof service === 'object') {
                  return service.name || service.serviceName || JSON.stringify(service);
                }
                return String(service);
              })
            : [];
          const response = await fetch(`${apiUrl}/api/send-appointment-removal`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type,
              recipientEmail,
              recipientName: recipientName || (type === 'patient' ? patientName : 'Doctor'),
              recipientRole: recipientRole || (type === 'patient' ? null : 'ClinicStaff'),
              patientName,
              appointmentDate: formattedDateLong,
              startTime,
              endTime: endTime !== "N/A" ? endTime : startTime,
              dentistName: appointmentToRemove?.dentist || 'N/A',
              services,
              removerName,
              reason: reasonValue,
              clinicName: 'VSDiamond Dental Clinic'
            })
          });
          const result = await response.json();
          if (!result.success) {
            console.error(`Failed to send removal email notification to ${type}:`, result.message);
          }
        } catch (error) {
          console.error(`Error sending removal email notification to ${type}:`, error);
        }
      };

      // Add notification for the patient
      // Check for uid, userId, or patientId (appointments may use different field names)
      const patientUid = appointmentToRemove?.uid || appointmentToRemove?.userId || appointmentToRemove?.patientId;
      if (patientUid) {
        const patientMessage = `Your appointment on ${appointmentWindow} has been removed. ${reasonSentence}`;
        addNotification(patientUid, patientMessage);
        
        // Send email notification to patient if their email is verified
        const patientDoc = await getDoc(doc(db, "users", patientUid));
        if (patientDoc.exists()) {
          const patientData = patientDoc.data();
          const patientEmailVerified = patientData.emailVerified === "Yes" || patientData.emailVerified === true;
          const patientEmail = patientData.email || patientData.authEmail;
          if (patientEmailVerified && patientEmail && !patientEmail.includes("@mobileuser.dentalapp.com")) {
            sendEmailRemovalNotification('patient', patientEmail, patientName);
          }
        }
      }

      // Notify assigned dentist, then all ClinicStaff only (not every DentistOwner / AssociateDentist)
      const assignedDentistUidRemoval = resolveAppointmentDentistUid(appointmentToRemove);
      if (assignedDentistUidRemoval) {
        const personnelMessageForAssigned = `The appointment for ${patientName} on ${appointmentWindow} has been removed by ${removerName}. ${reasonSentence}`;
        addNotification(assignedDentistUidRemoval, personnelMessageForAssigned);
        try {
          const dentistDoc = await getDoc(doc(db, "users", assignedDentistUidRemoval));
          if (dentistDoc.exists()) {
            const dentistData = dentistDoc.data();
            const dentistEmailVerified = dentistData.emailVerified === "Yes" || dentistData.emailVerified === true;
            const dentistEmail = dentistData.email || dentistData.authEmail;
            if (dentistEmailVerified && dentistEmail && !dentistEmail.includes("@mobileuser.dentalapp.com")) {
              const dentistNameForEmail = dentistData.firstName && dentistData.lastName
                ? `${dentistData.firstName} ${dentistData.lastName}`
                : (dentistData.name || "Doctor");
              const dentistRole = dentistData.userType || dentistData.role;
              sendEmailRemovalNotification("dentist", dentistEmail, dentistNameForEmail, dentistRole);
            }
          }
        } catch (_) {
          /* non-fatal */
        }
      }

      const usersRef = collection(db, "users");
      const usersSnapshot = await getDocs(usersRef);

      usersSnapshot.forEach((docu) => {
        const data = docu.data();
        const userId = docu.id;
        if (assignedDentistUidRemoval && userId === assignedDentistUidRemoval) {
          return;
        }
        if (data.userType === "ClinicStaff" || data.role === "ClinicStaff") {
          const personnelMessage = `The appointment for ${patientName} on ${appointmentWindow} has been removed by ${removerName}. ${reasonSentence}`;
          addNotification(docu.id, personnelMessage);
          
          const personnelEmailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
          const personnelEmail = data.email || data.authEmail;
          if (personnelEmailVerified && personnelEmail && !personnelEmail.includes("@mobileuser.dentalapp.com")) {
            const personnelName = data.firstName && data.lastName 
              ? `${data.firstName} ${data.lastName}` 
              : (data.name || "Staff");
            const personnelRole = data.userType || data.role;
            sendEmailRemovalNotification('dentist', personnelEmail, personnelName, personnelRole);
          }
        }
      });

      // Close the confirmation modal and reset reason
      isClosingRemoveDialog.current = true;
      setAppointmentToRemove(null);
      setRemovalReason("");
      setShowRemoveConfirmation(false);
      
      setTimeout(() => {
        isClosingRemoveDialog.current = false;
      }, 500);

    } catch (error) {
      console.error("Error removing appointment:", error);
    }
  };

  // Cancel removal
  const cancelRemoveAppointment = () => {
    isClosingRemoveDialog.current = true;
    setAppointmentToRemove(null);
    setRemovalReason("");
    setShowRemoveConfirmation(false);
    
    setTimeout(() => {
      isClosingRemoveDialog.current = false;
    }, 500);
  };

  // Handle viewing insurance details
  const handleViewInsuranceDetails = (appointment) => {
    setInsuranceDetails(appointment.insuranceDetails);
    setShowInsuranceForm(true);
  };

  const handleInsuranceClose = () => {
    setShowInsuranceForm(false);
    setInsuranceDetails(null);
  };

  /** Minute-of-day on one 24h cycle (supports totals > 1439 from start + duration). */
  function formatMinutesOfDayTo12h(totalMinutes) {
    const m = ((totalMinutes % 1440) + 1440) % 1440;
    const hours24 = Math.floor(m / 60);
    const mins = m % 60;
    const date = new Date(0, 0, 0, hours24, mins, 0);
    let hours = date.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    if (hours === 0) hours = 12;
    const formattedMinutes = mins < 10 ? `0${mins}` : mins;
    return `${hours}:${formattedMinutes} ${ampm}`;
  }

  function formatTimeConsistent(timeStr) {
    if (!timeStr) return "N/A";
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }
    if (timeStr.includes(':')) {
      const parts = timeStr.split(':');
      const hoursStr = parts[0];
      const rawMinutes = parts[1] ?? '0';
      const hour = parseInt(hoursStr, 10);
      const min = parseInt(String(rawMinutes).trim(), 10);
      if (Number.isNaN(hour) || Number.isNaN(min)) return timeStr;
      return formatMinutesOfDayTo12h(hour * 60 + min);
    }
    return timeStr;
  }

  // Convert 12-hour format to 24-hour format for HTML time input
  const convertTo24HourFormat = (timeStr) => {
    if (!timeStr) return "";
    
    // If already in 24-hour format (HH:MM), return as is
    if (timeStr.match(/^\d{1,2}:\d{2}$/)) {
      return timeStr;
    }
    
    // If in 12-hour format (H:MM AM/PM), convert to 24-hour
    if (timeStr.match(/^\d{1,2}:\d{2}\s?(AM|PM)$/i)) {
      const [time, period] = timeStr.split(/\s?(AM|PM)$/i);
      const [hours, minutes] = time.split(':');
      let hour24 = parseInt(hours);
      
      if (period.toUpperCase() === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      
      return `${hour24.toString().padStart(2, '0')}:${minutes}`;
    }
    
    return timeStr;
  };

  /** Normalize stored / legacy service price values to a non-negative integer string for the price input. */
  const normalizeLoadedServicePriceForInput = (v) => {
    if (v === "" || v == null) return "";
    const s = String(v).trim();
    if (/^\d+$/.test(s)) return s;
    const n = Number(s);
    if (!Number.isFinite(n) || n < 0) return "";
    return String(Math.round(n));
  };

  // Handle opening the edit form
  const handleEditAppointment = (appointment) => {
    let servicesArray = [];
    if (appointment.services && Array.isArray(appointment.services)) {
      if (appointment.services.length > 0 && typeof appointment.services[0] === 'object') {
        servicesArray = appointment.services;
      } else {
        servicesArray = appointment.services.map(service => {
          let price = "";
          if (appointment.servicePrices && appointment.servicePrices[service]) {
            price = appointment.servicePrices[service];
          }
          return { name: service, price: price };
        });
      }
    }

    // Use existing duration or default to 30 minutes
    const existingDuration = appointment.duration || 30;
    const startTime = appointment.startTime || appointment.time;
    const startTime24Hour = convertTo24HourFormat(startTime);
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = startMinutes + existingDuration;
    const calculatedEndTime = minutesToTime(endMinutes);

    setEditFormData({ 
      ...appointment, 
      services: servicesArray,
      startTime: startTime24Hour, // Convert to 24-hour format for the input field
      endTime: appointment.endTime || calculatedEndTime,
      duration: existingDuration || "" // Allow empty duration for manual entry
    });
    
    const initialPrices = {};
    servicesArray.forEach((serviceObj) => {
      initialPrices[serviceObj.name] = normalizeLoadedServicePriceForInput(serviceObj.price);
    });
    setServicePrices(initialPrices);
    setEditingAppointmentId(appointment.id);
    setEditFormValidationMessage("");
    setShowEditForm(true);
  };

  const resolvePatientId = (appointment = {}) =>
    appointment.patientId || appointment.uid || appointment.userId || null;

  const handleViewMedicalHistory = (appointment) => {
    const patientId = resolvePatientId(appointment);
    if (!patientId) {
      console.warn("Unable to resolve patient ID for medical history view.", appointment);
      return;
    }
    const patientName = appointment.patientDisplay || appointment.patientName || appointment.name || "Patient";
    setMedicalHistoryPatientId(patientId);
    setMedicalHistoryPatientName(patientName);
    setShowMedicalHistoryModal(true);
  };

  // Handle edit form submission
  const handleEditFormSubmit = async (formData) => {
    if (!editingAppointmentId) return;
    if (savingAppointmentDetailsRef.current) return;

    savingAppointmentDetailsRef.current = true;

    try {
      const formattedDate = new Date(selectedDateAppointment.toDate().getTime() - selectedDateAppointment.toDate().getTimezoneOffset() * 60000)
        .toISOString()
        .split("T")[0];
      const appointmentRef = doc(db, "appointments", formattedDate);

      // Find the original appointment
      const originalAppointment = appointments.find((appt) => appt.id === editingAppointmentId);

      const editStatusNorm = (formData.status || "").trim();
      if (editStatusNorm !== "Confirmed" && editStatusNorm !== "Completed") {
        if (!isEditFormStartTimeFieldComplete(editFormData)) {
          setEditFormValidationMessage(
            "Please enter a complete start time (hours and minutes)."
          );
          return;
        }
        if (!isEditFormDurationFieldComplete(editFormData)) {
          setEditFormValidationMessage("Please enter a duration of at least 1 minute.");
          return;
        }
      }

      // Validate service selection for Completed, Confirmed, or Pending status
      if (formData.status === "Completed" || formData.status === "Confirmed" || formData.status === "Pending") {
        // First check if any services are selected
        if (!editFormData.services || editFormData.services.length === 0) {
          setPriceValidationSnackbar({
            open: true,
            message: "Please select the services availed"
          });
          return;
        }
        }
        
      {
        const rawDur = editFormData.duration;
        if (rawDur !== "" && rawDur != null) {
          const d = typeof rawDur === "number" ? rawDur : parseInt(String(rawDur), 10);
          if (!Number.isFinite(d) || d < 1) {
            setEditFormValidationMessage("Duration must be at least 1 minute.");
            return;
          }
        }
      }
        
      // CRITICAL: Validate that the new time range doesn't overlap with Confirmed or Completed appointments
      // This check runs FIRST before all other validations to prevent ANY edit that would conflict
      {
        const newStartTime = resolveEditFormStartTimeForSave(
          editFormData,
          originalAppointment
        );
        const newDuration = editFormData.duration || originalAppointment?.duration || 30;
        
        const newStart = timeToMinutes(newStartTime);
        const newEnd = newStart + newDuration;
        
        const dentistId = editFormData.dentistId || originalAppointment?.dentistId;

        const normalizeEditedDateKey = (value) => {
          if (!value || typeof value !== "string") return null;
          const trimmed = value.trim();
          if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
          if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
            const [mm, dd, yyyy] = trimmed.split("-");
            return `${yyyy}-${mm}-${dd}`;
          }
          return null;
        };
        const editedDateKey =
          normalizeEditedDateKey(editFormData.date) ||
          normalizeEditedDateKey(originalAppointment?.date) ||
          formattedDate;

        // Pending/new edits must not overlap confirmed/completed visits on the same chair column.
        const editingStatus = (editFormData.status || originalAppointment?.status || "").toLowerCase();
        const isEditingPending = editingStatus === "pending" || editingStatus === "new";

        if (isEditingPending) {
          let allDayAppointments = (Array.isArray(allAppointments) ? allAppointments : [])
            .filter((a) => a && a.date === editedDateKey);

          if (allDayAppointments.length === 0 && editedDateKey === formattedDate) {
            allDayAppointments = (Array.isArray(appointments) ? appointments : [])
              .map((a) => ({ ...a, date: a.date || editedDateKey }))
              .filter(Boolean);
          }

          let scheduleDocForChairCheck =
            editedDateKey === formattedDate ? dentistSchedulesForDate : null;
          if (!scheduleDocForChairCheck) {
            const plotSnap = await getDoc(
              doc(
                db,
                CLINIC_SCHEDULING_COLLECTION,
                `${DENTIST_PLOT_DOC_PREFIX}${editedDateKey}`
              )
            );
            scheduleDocForChairCheck = plotSnap.exists()
              ? sanitizeDentistPlotDocData(plotSnap.data() || {})
              : {};
          } else if (scheduleDocForChairCheck) {
            scheduleDocForChairCheck = sanitizeDentistPlotDocData(scheduleDocForChairCheck);
          }

          const chairSchedSnap = await getDoc(doc(db, ...CHAIR_SCHEDULES_DOC_PATH));
          const configuredChairCount = resolveChairCountForDateKey(
            editedDateKey,
            chairSchedSnap.exists() ? chairSchedSnap.data() : null,
            chairSettings
          );

          const anchoredChairConflict = findPendingEditImmovableConflict({
            editingAppointmentId,
            editingDentistId: dentistId,
            newStartMin: newStart,
            newEndMin: newEnd,
            allDayAppointments,
            scheduleDocData: scheduleDocForChairCheck,
            chairCount: configuredChairCount,
          });

          if (anchoredChairConflict) {
            setEditFormValidationMessage(
              getEditDurationConflictWithAnchoredMessage(anchoredChairConflict)
            );
            return;
          }

          if (
            dentistId &&
            doesRangeViolateDentistPlottedSchedule(
              scheduleDocForChairCheck || {},
              dentistId,
              newStart,
              newEnd
            )
          ) {
            const dentistName = getDentistDisplayNameFromUID(dentistId);
            setEditFormValidationMessage(
              `Failed to save changes. The duration goes beyond ${dentistName}'s assigned timeslots.`
            );
            return;
          }

          if (configuredChairCount > 0) {
            const origStartForCascade = originalAppointment
              ? timeToMinutes(
                  originalAppointment.startTime || originalAppointment.time
                )
              : newStart;
            const cascadeViolation = findEditChairCascadeScheduleViolation({
              editingAppointmentId,
              editingDentistId: dentistId,
              newStartMin: newStart,
              newEndMin: newEnd,
              originalStartMin: origStartForCascade,
              allDayAppointments,
              scheduleDocData: scheduleDocForChairCheck || {},
              chairCount: configuredChairCount,
            });
            if (cascadeViolation) {
              if (cascadeViolation.reason === "day_end") {
                setEditFormValidationMessage(EDIT_APPOINTMENT_PAST_MIDNIGHT_MESSAGE);
              } else if (cascadeViolation.reason === "same_dentist_conflict") {
                const pushedDentistName = cascadeViolation.dentistUid
                  ? getDentistDisplayNameFromUID(cascadeViolation.dentistUid)
                  : "another dentist";
                setEditFormValidationMessage(
                  `Failed to save changes. The duration would shift an appointment into a time already occupied by ${pushedDentistName}.`
                );
              } else if (cascadeViolation.blockerAppt) {
                setEditFormValidationMessage(
                  getEditDurationConflictWithAnchoredMessage(cascadeViolation.blockerAppt)
                );
              } else {
                const pushedDentistName = cascadeViolation.dentistUid
                  ? getDentistDisplayNameFromUID(cascadeViolation.dentistUid)
                  : "another dentist";
                setEditFormValidationMessage(
                  `Failed to save changes. The duration would push ${pushedDentistName}'s patient appointment beyond the assigned timeslots.`
                );
              }
              return;
            }
          }
        }
        
        // CRITICAL: Chair capacity validation - ensure there's an available chair
        // For confirmed/completed appointments, check that chairs aren't overbooked
        if (!isEditingPending && isChairCapacityConfigured(chairSettings)) {
          const configuredChairCount = 
            typeof chairSettings?.chairCount === "number" && chairSettings.chairCount > 0
              ? chairSettings.chairCount
              : 0;
          
          if (configuredChairCount > 0) {
            // Get all appointments for the date across ALL dentists
            let allDayAppointments = (Array.isArray(allAppointments) ? allAppointments : [])
              .filter(a => a && a.date === editedDateKey && a.id !== editingAppointmentId);
            
            if (allDayAppointments.length === 0 && editedDateKey === formattedDate) {
              allDayAppointments = (Array.isArray(appointments) ? appointments : [])
                .map(a => ({ ...a, date: a.date || editedDateKey }))
                .filter(a => a && a.id !== editingAppointmentId);
            }
            
            // Check each minute in the new time range for chair availability
            for (let minute = newStart; minute < newEnd; minute++) {
              let confirmedCompletedCount = 0;
              
              for (const appt of allDayAppointments) {
                const status = (appt.status || "").toLowerCase();
                // Only count immovable appointments (confirmed/completed)
                if (status !== "confirmed" && status !== "completed") continue;
                
                const apptStart = timeToMinutes(appt.startTime || appt.time);
                const apptEnd = appt.endTime
                  ? timeToMinutes(appt.endTime)
                  : apptStart + (appt.duration || 30);
                
                // Check if this appointment occupies a chair at this minute
                if (minute >= apptStart && minute < apptEnd) {
                  confirmedCompletedCount++;
                }
              }
              
              // If all chairs are full with confirmed/completed appointments, reject the edit
              if (confirmedCompletedCount >= configuredChairCount) {
                const conflictTime = formatTime(minute);
                setEditFormValidationMessage(
                  `Failed to save changes. All ${configuredChairCount} chair${configuredChairCount === 1 ? "" : "s"} are occupied by confirmed/completed appointments at ${conflictTime}.`
                );
                return;
              }
            }
          }
        }
      }

      // Validate that the new time range doesn't overlap with Confirmed or Completed appointments
      {
        // Calculate the new time range (empty / placeholder / incomplete start → keep original)
        const newStartTime = resolveEditFormStartTimeForSave(
          editFormData,
          originalAppointment
        );
        const newDuration = editFormData.duration || originalAppointment?.duration || 30;
        
        const newStart = timeToMinutes(newStartTime);
        const newEnd = newStart + newDuration;
        const maxEndAllowedSameCalendarDay =
          newStart + maxDurationUntilMidnightFromSlotStart(newStart);
        
        // Get the dentist ID for filtering
        const dentistId = editFormData.dentistId || originalAppointment?.dentistId;

        const normalizeEditedDateKey = (value) => {
          if (!value || typeof value !== "string") return null;
          const trimmed = value.trim();
          if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
          if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
            const [mm, dd, yyyy] = trimmed.split("-");
            return `${yyyy}-${mm}-${dd}`;
          }
          return null;
        };
        const editedDateKey =
          normalizeEditedDateKey(editFormData.date) ||
          normalizeEditedDateKey(originalAppointment?.date) ||
          formattedDate;

        let unavailableForEditedDate = unavailableSlotsByDentistForMainDate;
        if (editedDateKey !== formattedDate) {
          unavailableForEditedDate = {};
          const uSnapPrefetch = await getDoc(doc(db, "unavailableTimeSlots", editedDateKey));
          if (uSnapPrefetch.exists()) {
            const raw = uSnapPrefetch.data() || {};
            Object.entries(raw).forEach(([uid, value]) => {
              if (Array.isArray(value)) unavailableForEditedDate[uid] = value;
            });
          }
        }

        let dentistScheduleDocForEdit =
          editedDateKey === formattedDate ? dentistSchedulesForDate : null;
        if (editedDateKey !== formattedDate) {
          const plotSnap = await getDoc(
            doc(db, CLINIC_SCHEDULING_COLLECTION, `${DENTIST_PLOT_DOC_PREFIX}${editedDateKey}`)
          );
          dentistScheduleDocForEdit = plotSnap.exists() ? plotSnap.data() || {} : {};
        }

        const assignedTimeslotsErr =
          "Failed to save changes. The duration must not exceed the assigned timeslots.";

        const durationBeyondAssignedTimeslotsMessage = () => {
          const dentistName = dentistId
            ? getDentistDisplayNameFromUID(dentistId)
            : "This dentist";
          return `Failed to save changes. The duration goes beyond ${dentistName}'s assigned timeslots.`;
        };

        const setPastDayEndValidationMessage = () => {
          if (
            dentistId &&
            dentistHasPlottedSchedule(dentistScheduleDocForEdit || {}, dentistId)
          ) {
            setEditFormValidationMessage(durationBeyondAssignedTimeslotsMessage());
          } else {
            setEditFormValidationMessage(EDIT_APPOINTMENT_PAST_MIDNIGHT_MESSAGE);
          }
        };

        const rangeOverlapsMinutesForEdit = (a, b) =>
          a && b && a.start < b.end && a.end > b.start;

        const parseUnavailableRangesForEdit = (uid) => {
          const labels = unavailableForEditedDate[uid];
          const ranges = [];
          if (!Array.isArray(labels) || labels.length === 0) return ranges;
          for (const label of labels) {
            const parts = (label || "").split(" - ").map((p) => p.trim());
            if (parts.length !== 2) continue;
            const uStart = timeToMinutes(parts[0]);
            const uEnd = timeToMinutes(parts[1]);
            if (
              typeof uStart !== "number" ||
              typeof uEnd !== "number" ||
              Number.isNaN(uStart) ||
              Number.isNaN(uEnd) ||
              uEnd <= uStart
            ) {
              continue;
            }
            ranges.push({ start: uStart, end: uEnd });
          }
          return ranges;
        };

        const unavailableRangesForEdit = dentistId
          ? parseUnavailableRangesForEdit(dentistId)
          : [];

        const apptsPoolForOverlap = (() => {
          if (!dentistId) return [];
          let pool = (Array.isArray(allAppointments) ? allAppointments : []).filter(
            (a) => a && a.dentistId === dentistId && a.date === editedDateKey
          );
          if (pool.length === 0 && editedDateKey === formattedDate) {
            pool = (Array.isArray(appointments) ? appointments : [])
              .map((a) => ({ ...a, date: a.date || editedDateKey }))
              .filter((a) => a && a.dentistId === dentistId);
          }
          return pool;
        })();

        let rosterSwapAwayRangesForEdit = [];
        let rosterAssignableRangesForEdit = [];
        if (
          isChairCapacityConfigured(chairSettings) &&
          dentistId &&
          isDentistOnRoster(dentistId, chairSettings)
        ) {
          let swapsBySlotForRosterEdit = {};
          if (editedDateKey === formattedDate) {
            swapsBySlotForRosterEdit = chairSwapsBySlotForMainDate || {};
          } else {
            const swapsSnapRosterEdit = await getDoc(
              doc(db, CHAIR_SWAPS_COLLECTION, editedDateKey)
            );
            swapsBySlotForRosterEdit = swapsSnapRosterEdit.exists()
              ? normalizeChairSwaps(swapsSnapRosterEdit.data() || {})
              : {};
          }
          rosterSwapAwayRangesForEdit = getRosterSwapAwayCoverageRanges({
            swapsBySlot: swapsBySlotForRosterEdit,
            rosterUid: dentistId,
            timeToMinutes,
          });
          rosterAssignableRangesForEdit = getRosterAssignablePatientCoverageRanges({
            swapsBySlot: swapsBySlotForRosterEdit,
            rosterUid: dentistId,
            timeToMinutes,
          });
        }

        const assignedScheduleViolationMessage = (affectsOtherPatient) => {
          const dentistName = dentistId
            ? getDentistDisplayNameFromUID(dentistId)
            : "This dentist";
          if (affectsOtherPatient) {
            return `Failed to save changes. The duration would cause another patient appointment to go beyond ${dentistName}'s assigned timeslots.`;
          }
          return durationBeyondAssignedTimeslotsMessage();
        };

        const rosterChairEditRangeMustStayAssignable = (
          rangeMin,
          affectsOtherPatient = false
        ) => {
          if (!rangeMin || typeof rangeMin.start !== "number" || typeof rangeMin.end !== "number") {
            setEditFormValidationMessage(assignedTimeslotsErr);
            return false;
          }

          if (
            dentistId &&
            isDentistSchedulePlottingEnabled(chairSettings) &&
            dentistHasPlottedSchedule(dentistScheduleDocForEdit || {}, dentistId)
          ) {
            const scheduleUnavailableRanges =
              getDentistScheduleUnavailableRanges(
                dentistScheduleDocForEdit || {},
                dentistId
              ) || [];
            for (const ur of scheduleUnavailableRanges) {
              if (rangeOverlapsMinutesForEdit(rangeMin, ur)) {
                setEditFormValidationMessage(
                  assignedScheduleViolationMessage(affectsOtherPatient)
                );
                return false;
              }
            }
            return true;
          }

          if (
            !isChairCapacityConfigured(chairSettings) ||
            !dentistId ||
            !isDentistOnRoster(dentistId, chairSettings)
          ) {
            return true;
          }
          if (rosterAssignableRangesForEdit.length === 0) {
            setEditFormValidationMessage(
              assignedScheduleViolationMessage(affectsOtherPatient)
            );
            return false;
          }
          if (!minuteRangeFullyContainedInUnion(rangeMin, rosterAssignableRangesForEdit)) {
            setEditFormValidationMessage(
              assignedScheduleViolationMessage(affectsOtherPatient)
            );
            return false;
          }
          return true;
        };

        const unavailableOverlapEditMessage = (rangeMin) => {
          if (
            rosterSwapAwayRangesForEdit.length > 0 &&
            rangeMin &&
            typeof rangeMin.start === "number" &&
            typeof rangeMin.end === "number" &&
            rosterSwapAwayRangesForEdit.some((sr) =>
              rangeOverlapsMinutesForEdit(rangeMin, sr)
            )
          ) {
            return assignedTimeslotsErr;
          }
          if (
            isDentistSchedulePlottingEnabled(chairSettings) &&
            dentistId &&
            dentistHasPlottedSchedule(dentistScheduleDocForEdit || {}, dentistId)
          ) {
            return durationBeyondAssignedTimeslotsMessage();
          }
          return "Failed to save changes. The duration overlaps with an unavailable time slot.";
        };

        if (!rosterChairEditRangeMustStayAssignable({ start: newStart, end: newEnd })) {
          return;
        }

        // When a bench booking may extend past inherited roster hours, half-hours recorded as
        // "unavailable" after that cutoff should not block the save (see edit validation below).
        let benchInheritedCoverageEndForUnavailableSkip = null;
        let skipUnavailableAfterBenchInheritedEnd = false;
        let canExtendPastBenchAssignedCoverageForEdit = false;

        // In chair mode, bench dentists can only be edited within their allotted
        // swap windows for the selected date.
        if (
          isChairCapacityConfigured(chairSettings) &&
          dentistId &&
          !isDentistOnRoster(dentistId, chairSettings)
        ) {
          let swapsBySlotForEditedDate = {};
          if (editedDateKey === formattedDate) {
            swapsBySlotForEditedDate = chairSwapsBySlotForMainDate || {};
          } else {
            const swapsSnap = await getDoc(doc(db, CHAIR_SWAPS_COLLECTION, editedDateKey));
            swapsBySlotForEditedDate = swapsSnap.exists()
              ? normalizeChairSwaps(swapsSnap.data() || {})
              : {};
          }
          const benchSwapSlots = getBenchSwapSlots({
            swapsBySlot: swapsBySlotForEditedDate,
            benchUid: dentistId,
          });
          if (benchSwapSlots.length > 0) {
            const parseRawBenchSwapRange = (slotLabel) => {
              const [slotStartRaw, slotEndRaw] = (slotLabel || "")
                .split(" - ")
                .map((part) => part.trim());
              if (!slotStartRaw || !slotEndRaw) return null;
              const slotStart = timeToMinutes(slotStartRaw);
              const slotEnd = timeToMinutes(slotEndRaw);
              if (
                typeof slotStart !== "number" ||
                typeof slotEnd !== "number" ||
                Number.isNaN(slotStart) ||
                Number.isNaN(slotEnd) ||
                slotEnd <= slotStart
              ) {
                return null;
              }
              return { start: slotStart, end: slotEnd };
            };
            const rawBenchSwapRanges = benchSwapSlots
              .map(parseRawBenchSwapRange)
              .filter(Boolean)
              .sort((a, b) => a.start - b.start);
            const maxRawBenchSwapEnd =
              rawBenchSwapRanges.length > 0
                ? Math.max(...rawBenchSwapRanges.map((r) => r.end))
                : null;
            const OFFICE_GRID_END_MINUTES = 18 * 60;
            const rawContainingBenchSwap =
              rawBenchSwapRanges.find(
                (r) =>
                  typeof newStart === "number" &&
                  !Number.isNaN(newStart) &&
                  newStart >= r.start &&
                  newStart < r.end
              ) || null;

            const mergedBenchCoverage = getBenchSwapCoverageRanges({
              swapsBySlot: swapsBySlotForEditedDate,
              benchUid: dentistId,
              timeToMinutes,
            });
            let maxBenchInheritedEnd =
              mergedBenchCoverage.length > 0
                ? Math.max(...mergedBenchCoverage.map((r) => r.end))
                : null;
            if (maxBenchInheritedEnd == null || Number.isNaN(maxBenchInheritedEnd)) {
              for (const slotLabel of benchSwapSlots) {
                const parts = (slotLabel || "").split(" - ").map((p) => p.trim());
                if (parts.length !== 2) continue;
                const endM = timeToMinutes(parts[1]);
                if (typeof endM !== "number" || Number.isNaN(endM)) continue;
                maxBenchInheritedEnd =
                  maxBenchInheritedEnd == null || Number.isNaN(maxBenchInheritedEnd)
                    ? endM
                    : Math.max(maxBenchInheritedEnd, endM);
              }
            }
            const containingMergedBenchRangeForMidnight =
              mergedBenchCoverage.length > 0
                ? mergedBenchCoverage.find(
                    (r) =>
                      typeof newStart === "number" &&
                      !Number.isNaN(newStart) &&
                      newStart >= r.start &&
                      newStart < r.end
                  ) || null
                : null;
            const SLOT_GRID_MINUTES = 30;

            let benchDayApptsForExtendRule = (
              Array.isArray(allAppointments) ? allAppointments : []
            ).filter(
              (appt) =>
                appt &&
                appt.dentistId === dentistId &&
                appt.date === editedDateKey &&
                !["cancelled", "canceled", "completed"].includes(
                  (appt.status || "").toLowerCase()
                )
            );
            if (benchDayApptsForExtendRule.length === 0 && editedDateKey === formattedDate) {
              benchDayApptsForExtendRule = (Array.isArray(appointments) ? appointments : [])
                .map((a) => ({ ...a, date: a.date || editedDateKey }))
                .filter(
                  (appt) =>
                    appt &&
                    appt.dentistId === dentistId &&
                    !["cancelled", "canceled", "completed"].includes(
                      (appt.status || "").toLowerCase()
                    )
                );
            }
            const sortedBenchForExtendRule = [...benchDayApptsForExtendRule].sort(
              (a, b) =>
                timeToMinutes(a.startTime || a.time) -
                timeToMinutes(b.startTime || b.time)
            );
            const benchEditIdxForExtendRule = sortedBenchForExtendRule.findIndex(
              (appt) => appt.id === editingAppointmentId
            );
            // Sole/last active bench appointment that day (used with terminal swap check below).
            const allowExtendPastBenchInheritedWindow =
              sortedBenchForExtendRule.length > 0 &&
              benchEditIdxForExtendRule !== -1 &&
              (sortedBenchForExtendRule.length === 1 ||
                benchEditIdxForExtendRule === sortedBenchForExtendRule.length - 1);

            const bookingAnchoredInTerminalBenchSwap =
              typeof maxRawBenchSwapEnd === "number" &&
              !Number.isNaN(maxRawBenchSwapEnd) &&
              maxRawBenchSwapEnd >= OFFICE_GRID_END_MINUTES &&
              rawContainingBenchSwap != null &&
              rawContainingBenchSwap.end === maxRawBenchSwapEnd;

            const allowPastAssignedWindowForLastSlot =
              typeof maxBenchInheritedEnd === "number" &&
              !Number.isNaN(maxBenchInheritedEnd) &&
              maxBenchInheritedEnd >= OFFICE_GRID_END_MINUTES &&
              newStart >= maxBenchInheritedEnd - SLOT_GRID_MINUTES &&
              newStart < maxBenchInheritedEnd;
            // Past close: only for the last swap segment that reaches grid end (e.g. 1:30–6:00),
            // not a morning-only swap (9:00–1:30). Uses unmerged swap rows so adjacent morning +
            // afternoon labels are not collapsed into one "all day" range.
            const canExtendPastBenchAssignedCoverage =
              allowPastAssignedWindowForLastSlot ||
              (bookingAnchoredInTerminalBenchSwap && allowExtendPastBenchInheritedWindow);
            const benchMayExtendPastInheritedClose =
              typeof containingMergedBenchRangeForMidnight?.end === "number" &&
              !Number.isNaN(containingMergedBenchRangeForMidnight.end) &&
              containingMergedBenchRangeForMidnight.end >= OFFICE_GRID_END_MINUTES;
            // Through-close inherited day (e.g. 9–6 or 1:30–6): visits may continue after 6:00 PM on the
            // bench timeline (compressed queue). Starts are outside merged swap coverage but still valid.
            const benchThroughCloseInheritedDay =
              typeof maxBenchInheritedEnd === "number" &&
              !Number.isNaN(maxBenchInheritedEnd) &&
              maxBenchInheritedEnd >= OFFICE_GRID_END_MINUTES;
            const benchExtendedTimelineEdit =
              benchThroughCloseInheritedDay && newStart >= maxBenchInheritedEnd;
            if (typeof maxBenchInheritedEnd === "number" && !Number.isNaN(maxBenchInheritedEnd)) {
              benchInheritedCoverageEndForUnavailableSkip = maxBenchInheritedEnd;
            }
            skipUnavailableAfterBenchInheritedEnd =
              (canExtendPastBenchAssignedCoverage ||
                benchMayExtendPastInheritedClose ||
                benchExtendedTimelineEdit) &&
              typeof benchInheritedCoverageEndForUnavailableSkip === "number" &&
              !Number.isNaN(benchInheritedCoverageEndForUnavailableSkip);
            canExtendPastBenchAssignedCoverageForEdit =
              canExtendPastBenchAssignedCoverage ||
              benchMayExtendPastInheritedClose ||
              benchExtendedTimelineEdit;

            // Prefer merged swap coverage (contiguous inherited windows) so an edit
            // can span adjacent half-hour swap rows (e.g. 5:10–5:31 across 5:00–5:30
            // and 5:30–6:00). Per-slot cursor was stopping at the first row boundary.
            if (mergedBenchCoverage.length > 0) {
              const containingRange = mergedBenchCoverage.find(
                (r) => newStart >= r.start && newStart < r.end
              );
              if (
                !containingRange &&
                !canExtendPastBenchAssignedCoverage &&
                !benchExtendedTimelineEdit
              ) {
                setEditFormValidationMessage(assignedTimeslotsErr);
                return;
              }
              if (
                containingRange &&
                newEnd > containingRange.end
              ) {
                if (!canExtendPastBenchAssignedCoverageForEdit) {
                  setEditFormValidationMessage(assignedTimeslotsErr);
                  return;
                }
                if (newEnd > maxEndAllowedSameCalendarDay) {
                  setEditFormValidationMessage(assignedTimeslotsErr);
                  return;
                }
              }
            } else {
              const swapRanges = rawBenchSwapRanges;
              let cursor = newStart;
              for (const range of swapRanges) {
                if (range.end <= cursor) continue;
                if (range.start > cursor) break;
                cursor = Math.min(newEnd, Math.max(cursor, range.end));
                if (cursor >= newEnd) break;
              }
              if (cursor < newEnd) {
                if (!canExtendPastBenchAssignedCoverageForEdit) {
                  setEditFormValidationMessage(assignedTimeslotsErr);
                  return;
                }
                if (newEnd > maxEndAllowedSameCalendarDay) {
                  setEditFormValidationMessage(assignedTimeslotsErr);
                  return;
                }
              }
            }

            if (mergedBenchCoverage.length > 0) {
              if (newEnd > maxBenchInheritedEnd) {
                if (!canExtendPastBenchAssignedCoverageForEdit) {
                  setEditFormValidationMessage(assignedTimeslotsErr);
                  return;
                }
                if (newEnd > maxEndAllowedSameCalendarDay) {
                  setEditFormValidationMessage(assignedTimeslotsErr);
                  return;
                }
              }

              const blockedUnavailableRanges = unavailableRangesForEdit;

              let dayAppts = (Array.isArray(allAppointments) ? allAppointments : []).filter(
                (appt) =>
                  appt &&
                  appt.dentistId === dentistId &&
                  appt.date === editedDateKey &&
                  !["cancelled", "canceled", "completed"].includes(
                    (appt.status || "").toLowerCase()
                  )
              );
              if (dayAppts.length === 0 && editedDateKey === formattedDate) {
                dayAppts = (Array.isArray(appointments) ? appointments : [])
                  .map((a) => ({ ...a, date: a.date || editedDateKey }))
                  .filter(
                    (appt) =>
                      appt &&
                      appt.dentistId === dentistId &&
                      !["cancelled", "canceled", "completed"].includes(
                        (appt.status || "").toLowerCase()
                      )
                  );
              }
              const sortedDay = [...dayAppts].sort(
                (a, b) =>
                  timeToMinutes(a.startTime || a.time) -
                  timeToMinutes(b.startTime || b.time)
              );
              const updatedIndex = sortedDay.findIndex(
                (appt) => appt.id === editingAppointmentId
              );
              if (updatedIndex !== -1) {
                let currentEndTime = newEnd;
                let ranPendingCascade = false;
                let exitedDueToNonPending = false;
                for (let i = updatedIndex + 1; i < sortedDay.length; i += 1) {
                  const nextAppt = sortedDay[i];
                  if (!isCompressiblePendingStatus(nextAppt.status)) {
                    exitedDueToNonPending = true;
                    break;
                  }
                  ranPendingCascade = true;
                  const nextStart = timeToMinutes(nextAppt.startTime || nextAppt.time);
                  const nextDuration = nextAppt.duration || 30;
                  const nextEnd = nextAppt.endTime
                    ? timeToMinutes(nextAppt.endTime)
                    : nextStart + nextDuration;

                  let adjustedStart;
                  let adjustedEnd;
                  if (nextStart < currentEndTime) {
                    adjustedStart = currentEndTime;
                    adjustedEnd = adjustedStart + nextDuration;
                  } else if (nextStart > currentEndTime) {
                    adjustedStart = currentEndTime;
                    adjustedEnd = adjustedStart + nextDuration;
                  } else {
                    currentEndTime = nextEnd;
                    continue;
                  }

                  if (adjustedEnd > LAST_MINUTE_OF_CALENDAR_DAY) {
                    setPastDayEndValidationMessage();
                    return;
                  }

                  const shifted = { start: adjustedStart, end: adjustedEnd };
                  for (let r = 0; r < blockedUnavailableRanges.length; r += 1) {
                    if (rangeOverlapsMinutesForEdit(shifted, blockedUnavailableRanges[r])) {
                      setEditFormValidationMessage(unavailableOverlapEditMessage(shifted));
                      return;
                    }
                  }
                  currentEndTime = adjustedEnd;
                }
                if (currentEndTime > maxBenchInheritedEnd) {
                  const pendingOnlyCascadeFollowups =
                    ranPendingCascade && !exitedDueToNonPending;
                  if (!pendingOnlyCascadeFollowups) {
                    if (!canExtendPastBenchAssignedCoverageForEdit) {
                      setEditFormValidationMessage(assignedTimeslotsErr);
                      return;
                    }
                    if (currentEndTime > maxEndAllowedSameCalendarDay) {
                      setEditFormValidationMessage(assignedTimeslotsErr);
                      return;
                    }
                    if (currentEndTime > newEnd) {
                      setEditFormValidationMessage(assignedTimeslotsErr);
                      return;
                    }
                  }
                }
              }
            }
          }
        }

        if (
          dentistId &&
          dentistHasPlottedSchedule(dentistScheduleDocForEdit || {}, dentistId)
        ) {
          const scheduleUnavailableRanges =
            getDentistScheduleUnavailableRanges(dentistScheduleDocForEdit || {}, dentistId) ||
            [];
          for (const r of scheduleUnavailableRanges) {
            if (rangeOverlapsMinutesForEdit({ start: newStart, end: newEnd }, r)) {
              setEditFormValidationMessage(durationBeyondAssignedTimeslotsMessage());
              return;
            }
          }
        } else if (dentistId && unavailableRangesForEdit.length > 0) {
          for (const r of unavailableRangesForEdit) {
            if (
              skipUnavailableAfterBenchInheritedEnd &&
              typeof benchInheritedCoverageEndForUnavailableSkip === "number" &&
              !Number.isNaN(benchInheritedCoverageEndForUnavailableSkip) &&
              r.start >= benchInheritedCoverageEndForUnavailableSkip
            ) {
              continue;
            }
            if (rangeOverlapsMinutesForEdit({ start: newStart, end: newEnd }, r)) {
              setEditFormValidationMessage(
                unavailableOverlapEditMessage({ start: newStart, end: newEnd })
              );
              return;
            }
          }
        }

        // Simulates adjustAppointmentTimes forward cascade: succeeding pendings may shift into
        // confirmed/completed even when the edited row alone does not overlap them.
        const editingStatusNorm = (
          (editFormData.status || originalAppointment?.status || "") + ""
        )
          .trim()
          .toLowerCase();
        if (editingStatusNorm === "pending" && dentistId) {
          let cascadeDayAppts = (Array.isArray(allAppointments) ? allAppointments : []).filter(
            (appt) =>
              appt &&
              appt.dentistId === dentistId &&
              appt.date === editedDateKey &&
              !["cancelled", "canceled", "completed"].includes(
                (appt.status || "").toLowerCase()
              )
          );
          if (cascadeDayAppts.length === 0 && editedDateKey === formattedDate) {
            cascadeDayAppts = (Array.isArray(appointments) ? appointments : [])
              .map((a) => ({ ...a, date: a.date || editedDateKey }))
              .filter(
                (appt) =>
                  appt &&
                  appt.dentistId === dentistId &&
                  !["cancelled", "canceled", "completed"].includes(
                    (appt.status || "").toLowerCase()
                  )
              );
          }

          const getFirstBlockingConfirmedOrCompletedForRange = (rangeStart, rangeEnd) => {
            for (let bi = 0; bi < apptsPoolForOverlap.length; bi += 1) {
              const appt = apptsPoolForOverlap[bi];
              if (!appt || appt.id === editingAppointmentId) continue;
              const st = (appt.status || "").toLowerCase();
              if (st !== "confirmed" && st !== "completed") continue;
              const oS = timeToMinutes(appt.startTime || appt.time);
              const oE = appt.endTime
                ? timeToMinutes(appt.endTime)
                : oS + (appt.duration || 30);
              if (rangeStart < oE && oS < rangeEnd) return appt;
            }
            return null;
          };

          const cascadeSorted = [...cascadeDayAppts].sort(
            (a, b) =>
              timeToMinutes(a.startTime || a.time) - timeToMinutes(b.startTime || b.time)
          );
          const cascadeIdx = cascadeSorted.findIndex((a) => a.id === editingAppointmentId);
          if (cascadeIdx !== -1) {
            const oldEditedStartMin = timeToMinutes(
              originalAppointment.startTime || originalAppointment.time
            );
            let slackMinutesBeforeEdited = 0;
            if (cascadeIdx > 0) {
              const prevAp = cascadeSorted[cascadeIdx - 1];
              const prevStart = timeToMinutes(prevAp.startTime || prevAp.time);
              const prevEndMin = prevAp.endTime
                ? timeToMinutes(prevAp.endTime)
                : prevStart + (prevAp.duration || 30);
              slackMinutesBeforeEdited = Math.max(0, oldEditedStartMin - prevEndMin);
            }
            const requestedEarlierMinutes = Math.max(0, oldEditedStartMin - newStart);
            const deltaEarlier =
              cascadeIdx > 0
                ? Math.max(0, requestedEarlierMinutes - slackMinutesBeforeEdited)
                : requestedEarlierMinutes;
            if (deltaEarlier > 0) {
              const syntheticSorted = cascadeSorted
                .map((ap, j) => {
                  const clone = { ...ap };
                  if (j === cascadeIdx) {
                    clone.startTime = newStartTime;
                    clone.time = newStartTime;
                    clone.duration = newDuration;
                    clone.endTime = formatTime(newEnd);
                    return clone;
                  }
                  const stLow = (clone.status || "").toLowerCase();
                  if (j < cascadeIdx && (stLow === "pending" || stLow === "new")) {
                    const sm = timeToMinutes(clone.startTime || clone.time);
                    const dur = clone.duration || 30;
                    const em = clone.endTime
                      ? timeToMinutes(clone.endTime)
                      : sm + dur;
                    const ns = sm - deltaEarlier;
                    const ne = em - deltaEarlier;
                    clone.startTime = formatTime(ns);
                    clone.time = clone.startTime;
                    clone.endTime = formatTime(ne);
                  }
                  return clone;
                })
                .sort(
                  (a, b) =>
                    timeToMinutes(a.startTime || a.time) -
                    timeToMinutes(b.startTime || b.time)
                );

              // Before compression heals overlaps by snapping pendings after anchors, reject saves
              // where δ-shift would drag a predecessor pending/new into unavailable or confirmed/completed.
              for (let pj = 0; pj < cascadeIdx; pj += 1) {
                const predOrig = cascadeSorted[pj];
                const predSt = (predOrig.status || "").toLowerCase();
                if (predSt !== "pending" && predSt !== "new") continue;
                const preRow = syntheticSorted.find((a) => a.id === predOrig.id);
                if (!preRow) continue;
                const preRange = getAppointmentDisplayRangeMinutes(preRow);
                if (!preRange) continue;
                if (!rosterChairEditRangeMustStayAssignable(preRange, true)) {
                  return;
                }
                if (unavailableRangesForEdit.length > 0) {
                  for (const ur of unavailableRangesForEdit) {
                    if (
                      skipUnavailableAfterBenchInheritedEnd &&
                      typeof benchInheritedCoverageEndForUnavailableSkip === "number" &&
                      !Number.isNaN(benchInheritedCoverageEndForUnavailableSkip) &&
                      ur.start >= benchInheritedCoverageEndForUnavailableSkip
                    ) {
                      continue;
                    }
                    if (rangeOverlapsMinutesForEdit(preRange, ur)) {
                      setEditFormValidationMessage(unavailableOverlapEditMessage(preRange));
                      return;
                    }
                  }
                }
                const preBlocker = getFirstBlockingConfirmedOrCompletedForRange(
                  preRange.start,
                  preRange.end
                );
                if (preBlocker) {
                  setEditFormValidationMessage(
                    getEditDurationConflictWithAnchoredMessage(preBlocker)
                  );
                  return;
                }
              }

              const {
                updates: compressBackwardUpdates,
                exceedsDayEnd: compressBackwardExceedsDay,
              } = computeCompressibleTimelineShiftUpdates(syntheticSorted, {
                isCompressible: (apt, st) =>
                  st === "pending" || st === "new",
              });

              if (compressBackwardExceedsDay) {
                setPastDayEndValidationMessage();
                return;
              }

              const finalRangesById = new Map(
                syntheticSorted.map((a) => [a.id, { ...a }])
              );
              for (const u of compressBackwardUpdates) {
                const row = finalRangesById.get(u.id);
                if (!row) continue;
                row.startTime = u.startTime;
                row.time = u.startTime;
                row.endTime = u.endTime;
              }

              for (const row of finalRangesById.values()) {
                const stRow = (row.status || "").toLowerCase();
                if (stRow !== "pending" && stRow !== "new") continue;
                const range = getAppointmentDisplayRangeMinutes(row);
                if (!range) continue;
                if (
                  !rosterChairEditRangeMustStayAssignable(
                    range,
                    row.id !== editingAppointmentId
                  )
                ) {
                  return;
                }
                if (unavailableRangesForEdit.length > 0) {
                  for (const ur of unavailableRangesForEdit) {
                    if (
                      skipUnavailableAfterBenchInheritedEnd &&
                      typeof benchInheritedCoverageEndForUnavailableSkip === "number" &&
                      !Number.isNaN(benchInheritedCoverageEndForUnavailableSkip) &&
                      ur.start >= benchInheritedCoverageEndForUnavailableSkip
                    ) {
                      continue;
                    }
                    if (rangeOverlapsMinutesForEdit(range, ur)) {
                      setEditFormValidationMessage(unavailableOverlapEditMessage(range));
                      return;
                    }
                  }
                }
                const blocker = getFirstBlockingConfirmedOrCompletedForRange(
                  range.start,
                  range.end
                );
                if (blocker) {
                  setEditFormValidationMessage(
                    getEditDurationConflictWithAnchoredMessage(blocker)
                  );
                  return;
                }
              }
            }

            let currentEndTime = newEnd;
            for (let ci = cascadeIdx + 1; ci < cascadeSorted.length; ci += 1) {
              const nextAppt = cascadeSorted[ci];
              if (!isCompressiblePendingStatus(nextAppt.status)) break;
              const nextStart = timeToMinutes(nextAppt.startTime || nextAppt.time);
              const nextDuration = nextAppt.duration || 30;
              const nextEnd = nextAppt.endTime
                ? timeToMinutes(nextAppt.endTime)
                : nextStart + nextDuration;
              let adjustedStart;
              let adjustedEnd;
              if (nextStart < currentEndTime) {
                adjustedStart = currentEndTime;
                adjustedEnd = adjustedStart + nextDuration;
              } else if (nextStart > currentEndTime) {
                adjustedStart = currentEndTime;
                adjustedEnd = adjustedStart + nextDuration;
              } else {
                currentEndTime = nextEnd;
                continue;
              }
              if (adjustedEnd > LAST_MINUTE_OF_CALENDAR_DAY) {
                setPastDayEndValidationMessage();
                return;
              }
              if (
                !rosterChairEditRangeMustStayAssignable(
                  {
                    start: adjustedStart,
                    end: adjustedEnd,
                  },
                  true
                )
              ) {
                return;
              }
              for (let ur = 0; ur < unavailableRangesForEdit.length; ur += 1) {
                if (
                  rangeOverlapsMinutesForEdit(
                    { start: adjustedStart, end: adjustedEnd },
                    unavailableRangesForEdit[ur]
                  )
                ) {
                  setEditFormValidationMessage(
                    unavailableOverlapEditMessage({
                      start: adjustedStart,
                      end: adjustedEnd,
                    })
                  );
                  return;
                }
              }
              const blocker = getFirstBlockingConfirmedOrCompletedForRange(
                adjustedStart,
                adjustedEnd
              );
              if (blocker) {
                setEditFormValidationMessage(
                  getEditDurationConflictWithAnchoredMessage(blocker)
                );
                return;
              }
              currentEndTime = adjustedEnd;
            }

            if (
              isChairCapacityConfigured(chairSettings) &&
              dentistId &&
              !isDentistOnRoster(dentistId, chairSettings)
            ) {
              let swapsBySlotForRosterCascade = {};
              if (editedDateKey === formattedDate) {
                swapsBySlotForRosterCascade = chairSwapsBySlotForMainDate || {};
              } else {
                const swapsSnapRoster = await getDoc(
                  doc(db, CHAIR_SWAPS_COLLECTION, editedDateKey)
                );
                swapsBySlotForRosterCascade = swapsSnapRoster.exists()
                  ? normalizeChairSwaps(swapsSnapRoster.data() || {})
                  : {};
              }
              const mergedBenchForRosterCascade = getBenchSwapCoverageRanges({
                swapsBySlot: swapsBySlotForRosterCascade,
                benchUid: dentistId,
                timeToMinutes,
              });
              const maxBenchInheritedEndRoster =
                mergedBenchForRosterCascade.length > 0
                  ? Math.max(...mergedBenchForRosterCascade.map((r) => r.end))
                  : null;
              const SLOT_GRID_ROSTER = 30;
              const OFFICE_GRID_END_ROSTER = 18 * 60;
              const allowPastAssignedWindowRoster =
                typeof maxBenchInheritedEndRoster === "number" &&
                !Number.isNaN(maxBenchInheritedEndRoster) &&
                maxBenchInheritedEndRoster >= OFFICE_GRID_END_ROSTER &&
                newStart >= maxBenchInheritedEndRoster - SLOT_GRID_ROSTER &&
                newStart < maxBenchInheritedEndRoster;
              if (
                typeof maxBenchInheritedEndRoster === "number" &&
                !Number.isNaN(maxBenchInheritedEndRoster) &&
                currentEndTime > maxBenchInheritedEndRoster &&
                !allowPastAssignedWindowRoster
              ) {
                if (!canExtendPastBenchAssignedCoverageForEdit) {
                  setEditFormValidationMessage(assignedTimeslotsErr);
                  return;
                }
                if (currentEndTime > maxEndAllowedSameCalendarDay) {
                  setEditFormValidationMessage(assignedTimeslotsErr);
                  return;
                }
              }
            }
          }
        }
      }

      setIsSavingAppointmentDetails(true);
      setEditFormValidationMessage("");

      try {
        // Prepare update data
        const updateData = { ...formData };
        const resolvedCompletedServicePrice = (serviceName) => {
          const p = servicePrices[serviceName];
          if (p === "" || p == null) return "0";
          const s = String(p).trim();
          if (!/^\d+$/.test(s)) return "0";
          return s;
        };
        const totalBill =
          editFormData.services && Array.isArray(editFormData.services)
            ? editFormData.services.reduce(
                (sum, serviceObj) =>
                  sum + Number(resolvedCompletedServicePrice(serviceObj.name)),
                0
              )
            : Object.values(servicePrices).reduce((sum, price) => {
                return sum + (Number(price) || 0);
              }, 0);

        // Always update services from editFormData if available, regardless of status
        // This ensures email notifications reflect the current selected services
        if (editFormData.services && Array.isArray(editFormData.services)) {
          if (formData.status === "Completed") {
            const servicesWithPrices = editFormData.services.map((serviceObj) => ({
              name: serviceObj.name,
              price: resolvedCompletedServicePrice(serviceObj.name),
            }));
            updateData.services = servicesWithPrices;
            updateData.totalBill = totalBill;
          } else {
            // For non-completed status, use the service objects from editFormData
            // They may already have prices or just names
            updateData.services = editFormData.services.map(serviceObj => ({
              name: serviceObj.name,
              price: serviceObj.price || (servicePrices[serviceObj.name] || "0")
            }));
          }
        }
        
        // Always update date, time, dentist, and other fields from editFormData if available, regardless of status
        // This ensures email notifications reflect real-time changes
        if (editFormData.date) {
          updateData.date = editFormData.date;
        }
        const resolvedStartTimeForSave = resolveEditFormStartTimeForSave(
          editFormData,
          originalAppointment
        );
        updateData.startTime = resolvedStartTimeForSave;
        updateData.time = resolvedStartTimeForSave;
        if (editFormData.dentist) {
          updateData.dentist = editFormData.dentist;
        }
        if (editFormData.hasInsurance !== undefined) {
          updateData.hasInsurance = editFormData.hasInsurance;
        }
        if (editFormData.insuranceDetails) {
          updateData.insuranceDetails = editFormData.insuranceDetails;
        }
        
        // Ensure dentistRemarks is included in updateData
        // ALWAYS include dentistRemarks in the update, even if empty
        updateData.dentistRemarks = editFormData.dentistRemarks || "";
        
        delete updateData.bill;

        const parseDurationMinutesForSave = (value, fallback) => {
          if (value === "" || value == null) return fallback;
          const n = typeof value === "number" ? value : parseInt(String(value), 10);
          return Number.isFinite(n) && n >= 1 ? n : fallback;
        };
        const originalDurationMinutes = parseDurationMinutesForSave(
          originalAppointment?.duration,
          30
        );
        const durationFieldEmpty =
          editFormData.duration === "" || editFormData.duration == null;
        const resolvedSaveDuration = durationFieldEmpty
          ? originalDurationMinutes
          : parseDurationMinutesForSave(
              editFormData.duration,
              originalDurationMinutes
            );

        updateData.duration = resolvedSaveDuration;
        updateData.endTime = minutesToTime(
          timeToMinutes(resolvedStartTimeForSave) + resolvedSaveDuration
        );

        // Services-only changes should not run time-cascade logic (avoids spurious time notifications).
        // Time cascade runs only when start time and/or duration actually change.
        const startTimeChanged = (originalAppointment.startTime || originalAppointment.time) !== (updateData.startTime || updateData.time);
        const durationChanged = (originalAppointment.duration || 30) !== (updateData.duration || 30);
        const timeOrDurationChanged = startTimeChanged || durationChanged;

        // Appointment IDs that already received pending time-shift notifications (patient + personnel) in this save.
        // Prevents duplicate emails/in-app alerts when adjust/autoAdjust runs, then the post-save block would run again.
        let appointmentIdsNotifiedForPendingTimeChange = new Set();

        if (timeOrDurationChanged) {
          setAppointments((prev) =>
            prev.map((appt) =>
              appt.id === editingAppointmentId
                ? {
                    ...appt,
                    ...updateData,
                    id: editingAppointmentId,
                    date: updateData.date || appt.date || formattedDate,
                  }
                : appt
            )
          );
          appointmentIdsNotifiedForPendingTimeChange = await adjustAppointmentTimes(
            updateData,
            originalAppointment
          );
        } else {
          // Update the appointment in the database using dot notation (e.g. services or remarks only)
          const updateDataWithPath = {};
          Object.keys(updateData).forEach((key) => {
            updateDataWithPath[`${editingAppointmentId}.${key}`] = updateData[key];
          });

          await updateDoc(appointmentRef, updateDataWithPath);

          // Check for overlapping pending appointments after update
          const dentistId = updateData.dentistId || originalAppointment?.dentistId;
          if (dentistId) {
            appointmentIdsNotifiedForPendingTimeChange =
              await autoAdjustOverlappingPendingAppointments(
                editingAppointmentId,
                formattedDate,
                dentistId
              );
          }
        }

        // Firestore writes are done — close modal immediately; notifications run in background.
        setEditingAppointmentId(null);
        setEditFormData({ services: [] });
        setShowEditForm(false);
        savingAppointmentDetailsRef.current = false;
        setIsSavingAppointmentDetails(false);

        void (async () => {
        try {
        // Calculate start and end times for notifications from persisted update payload
        let formattedStartTime = "N/A";
        let formattedEndTime = "N/A";
        const timeSource = updateData;
        if (timeSource) {
          if (timeSource.startTime) {
            formattedStartTime = formatTimeConsistent(timeSource.startTime);
            if (timeSource.endTime) {
              formattedEndTime = formatTimeConsistent(timeSource.endTime);
            } else {
              // Calculate from start time + duration if endTime is not available
              const newStartMinutes = timeToMinutes(timeSource.startTime);
              const newDuration = timeSource.duration || updateData?.duration || formData?.duration || 30;
              const newEndMinutes = newStartMinutes + newDuration;
              formattedEndTime = formatTimeConsistent(minutesToTime(newEndMinutes));
            }
          } else if (timeSource.time) {
            formattedStartTime = formatTimeConsistent(timeSource.time);
            // Calculate from time + duration if endTime is not available
            const newStartMinutes = timeToMinutes(timeSource.time);
            const newDuration = timeSource.duration || updateData?.duration || formData?.duration || 30;
            const newEndMinutes = newStartMinutes + newDuration;
            formattedEndTime = formatTimeConsistent(minutesToTime(newEndMinutes));
          }
        }
        
        // Calculate original time values for comparison (to detect if time changed)
        let originalFormattedStartTime = "N/A";
        let originalFormattedEndTime = "N/A";
        if (originalAppointment) {
          if (originalAppointment.startTime) {
            originalFormattedStartTime = formatTimeConsistent(originalAppointment.startTime);
            if (originalAppointment.endTime) {
              originalFormattedEndTime = formatTimeConsistent(originalAppointment.endTime);
            } else {
              // Calculate from start time + duration if endTime is not available
              const originalStartMinutes = timeToMinutes(originalAppointment.startTime);
              const originalDuration = originalAppointment.duration || 30;
              const originalEndMinutes = originalStartMinutes + originalDuration;
              originalFormattedEndTime = formatTimeConsistent(minutesToTime(originalEndMinutes));
            }
          } else if (originalAppointment.time) {
            originalFormattedStartTime = formatTimeConsistent(originalAppointment.time);
            // Calculate from time + duration if endTime is not available
            const originalStartMinutes = timeToMinutes(originalAppointment.time);
            const originalDuration = originalAppointment.duration || 30;
            const originalEndMinutes = originalStartMinutes + originalDuration;
            originalFormattedEndTime = formatTimeConsistent(minutesToTime(originalEndMinutes));
          }
        }
        
        // Check if time changed (for Confirmed status notifications)
        const timeChanged = (formattedStartTime !== originalFormattedStartTime) || 
                           (formattedEndTime !== originalFormattedEndTime);
        
        // Use updated date - prioritize editFormData (most current), then updateData, then formData, finally selected date
        const dateForNotification = editFormData?.date || updateData?.date || formData?.date || formattedDate;

        // Get editor's name with appropriate title based on role and gender (used for both patient and staff notifications)
        let editorNameWithTitle = "A personnel";
        if (userDetails) {
          const userRole = userDetails.role || userDetails.userType || "";
          const fullName = `${userDetails.firstName} ${userDetails.lastName}`;
          
          if (userRole === "DentistOwner" || userRole === "AssociateDentist") {
            editorNameWithTitle = `Dr. ${fullName}`;
          } else {
            // For ClinicStaff and other roles, use name without prefix
            editorNameWithTitle = fullName;
          }
        }

        // Notify on status change
        const patientUid = originalAppointment.uid || originalAppointment.userId || originalAppointment.patientId;
        // Normalize status for comparison
        const originalStatusForPatient = originalAppointment?.status ? String(originalAppointment.status).trim() : '';
        const newStatusForPatient = formData.status ? String(formData.status).trim() : '';
        const normalizedOriginalStatusForPatient = originalStatusForPatient ? originalStatusForPatient.charAt(0).toUpperCase() + originalStatusForPatient.slice(1).toLowerCase() : '';
        const normalizedNewStatusForPatient = newStatusForPatient ? newStatusForPatient.charAt(0).toUpperCase() + newStatusForPatient.slice(1).toLowerCase() : '';
        
        if (originalAppointment && normalizedOriginalStatusForPatient !== normalizedNewStatusForPatient && normalizedNewStatusForPatient) {
          const formattedDateLong = formatDateLong(dateForNotification);
          const appointmentWindow = buildNotificationWindow(
            formattedDateLong,
            formattedStartTime,
            formattedEndTime
          );
          
          // Use updated data - prioritize editFormData (most current) for all statuses
          const hasInsurance = editFormData?.hasInsurance ?? updateData?.hasInsurance ?? formData?.hasInsurance ?? originalAppointment?.hasInsurance ?? false;
          const insuranceCompany =
            hasInsurance
              ? (editFormData?.insuranceDetails?.companyName ||
                  updateData?.insuranceDetails?.companyName ||
                  formData?.insuranceDetails?.companyName ||
                  originalAppointment?.insuranceDetails?.companyName ||
                  "Yes")
              : "None";
          const resolvedDentistRemarks =
            typeof editFormData?.dentistRemarks === "string" && editFormData.dentistRemarks.trim()
              ? editFormData.dentistRemarks.trim()
              : (typeof updateData?.dentistRemarks === "string" && updateData.dentistRemarks.trim()
                  ? updateData.dentistRemarks.trim()
                  : (typeof formData?.dentistRemarks === "string" && formData.dentistRemarks.trim()
                      ? formData.dentistRemarks.trim()
                      : (typeof originalAppointment?.dentistRemarks === "string"
                          ? originalAppointment.dentistRemarks.trim()
                          : "")));
          const dentistRemarksText = resolvedDentistRemarks || "None";
          
          // Use updated dentist name - prioritize editFormData (most current) for all statuses
          const dentistNameForEmail = editFormData?.dentist || updateData?.dentist || formData?.dentist || originalAppointment?.dentist || 'N/A';

          let message = "";
          if (formData.status === "Confirmed") {
            if (timeChanged && originalFormattedStartTime !== "N/A" && originalFormattedEndTime !== "N/A") {
              // Time was changed - use dynamic message
              const formattedDateLong = formatDateLong(dateForNotification);
              const originalTimeWindow = `${originalFormattedStartTime} – ${originalFormattedEndTime}`;
              const newTimeWindow = `${formattedStartTime} – ${formattedEndTime}`;
              message = `Your appointment on ${formattedDateLong} has been confirmed. The scheduled time has been updated from ${originalTimeWindow} to ${newTimeWindow}.\n\nIf you are unable to attend at the updated time, you may reschedule or cancel the appointment through the system.`;
            } else {
              // No time change - use original message
            message = `Your appointment on ${appointmentWindow} has been confirmed.`;
            }
          } else if (formData.status === "Completed") {
            message = `Your appointment on ${appointmentWindow} has been marked as completed. Thank you for visiting!`;
          } else if (formData.status === "Pending") {
            const previousStatus = originalAppointment.status
              ? originalAppointment.status.toLowerCase()
              : "confirmed";
            message = `Your appointment on ${appointmentWindow} has been updated from ${previousStatus} to pending while our clinic verifies your booking details.`;
          }

          if (message && patientUid) {
            if (formData.status === "Completed") {
              message += `\n\nInsurance: ${insuranceCompany}\nDentist Remarks: ${dentistRemarksText}`;
            } else {
              message += `\n\nInsurance: ${insuranceCompany}`;
            }
            await addNotification(patientUid, message);

            // Send email notification to patient if their email is verified
            const patientDoc = await getDoc(doc(db, "users", patientUid));
            if (patientDoc.exists()) {
              const patientData = patientDoc.data();
              const patientEmailVerified = patientData.emailVerified === "Yes" || patientData.emailVerified === true;
              const patientEmail = patientData.email || patientData.authEmail;
              if (patientEmailVerified && patientEmail && !patientEmail.includes("@mobileuser.dentalapp.com")) {
                const patientNameForEmail = originalAppointment.patientDisplay || originalAppointment.patientName || "Patient";
                // Format services - extract names if they're objects
                // Prefer non-empty arrays only so empty edit/update payloads still fall back to snapshot data for emails
                const servicesRaw = pickFirstNonEmptyServicesArray(
                  editFormData.services,
                  updateData.services,
                  formData.services,
                  originalAppointment.services
                );
                const services = Array.isArray(servicesRaw) 
                  ? servicesRaw.map(service => {
                      if (typeof service === 'string') {
                        return service;
                      } else if (service && typeof service === 'object') {
                        return service.name || service.serviceName || JSON.stringify(service);
                      }
                      return String(service);
                    })
                  : [];
                const sendEmailStatusChange = async () => {
                  try {
                    const apiUrl = process.env.REACT_APP_API_URL || '';
                    const response = await fetch(`${apiUrl}/api/send-appointment-status-change`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        type: 'patient',
                        recipientEmail: patientEmail,
                        patientName: patientNameForEmail,
                        appointmentDate: formattedDateLong,
                        startTime: formattedStartTime,
                        endTime: formattedEndTime !== "N/A" ? formattedEndTime : formattedStartTime,
                        previousStartTime: (formData.status === "Confirmed" && timeChanged && originalFormattedStartTime !== "N/A") ? originalFormattedStartTime : null,
                        previousEndTime: (formData.status === "Confirmed" && timeChanged && originalFormattedEndTime !== "N/A") ? originalFormattedEndTime : null,
                        dentistName: dentistNameForEmail,
                        services,
                        status: formData.status,
                        actorName: editorNameWithTitle,
                        insuranceText: insuranceCompany,
                        dentistRemarks: formData.status === "Completed" ? dentistRemarksText : null,
                        clinicName: 'VSDiamond Dental Clinic'
                      })
                    });
                    const result = await response.json();
                    if (!result.success) {
                      console.error('Failed to send status change email notification to patient:', result.message);
                    }
                  } catch (error) {
                    console.error('Error sending status change email notification to patient:', error);
                  }
                };
                sendEmailStatusChange();
              }
            }
          }
        }

        // Notify on dentist remarks change
        if (
          originalAppointment &&
          originalAppointment.status !== formData.status &&
          formData.status !== "Completed" &&
          originalAppointment.dentistRemarks !== formData.dentistRemarks
        ) {
          // Format date as "October 31, 2025"
          const formattedDateLong = formatDateLong(formattedDate);
          const remarksMessage = `Your dentist has updated the remarks for your appointment on ${formattedDateLong}, from ${formattedStartTime} to ${formattedEndTime}: '${formData.dentistRemarks || "No remarks"}'`;
          if (patientUid) {
            await addNotification(patientUid, remarksMessage);
          }
        }

        // --- Notify assigned dentist and all ClinicStaff on status change ---
        // Only notify if status was actually changed
        // Use updateData.status if available, otherwise fall back to formData.status
        const statusToCheck = updateData.status || formData.status;
        const originalStatus = originalAppointment?.status ? String(originalAppointment.status).trim() : '';
        const newStatus = statusToCheck ? String(statusToCheck).trim() : '';
        // Normalize status values for comparison (case-insensitive)
        const normalizedOriginalStatus = originalStatus ? originalStatus.charAt(0).toUpperCase() + originalStatus.slice(1).toLowerCase() : '';
        const normalizedNewStatus = newStatus ? newStatus.charAt(0).toUpperCase() + newStatus.slice(1).toLowerCase() : '';
        
        if (originalAppointment && normalizedOriginalStatus !== normalizedNewStatus && normalizedNewStatus) {
          const usersRef = collection(db, "users");
          const usersSnapshot = await getDocs(usersRef);
          
          // Format date as "October 31, 2025" - use updated date if available
          const formattedDateLong = formatDateLong(dateForNotification);
          const appointmentWindow = buildNotificationWindow(
            formattedDateLong,
            formattedStartTime,
            formattedEndTime
          );
          
          // Use updated data - prioritize editFormData (most current) for all statuses
          const hasInsurance = editFormData?.hasInsurance ?? updateData?.hasInsurance ?? formData?.hasInsurance ?? originalAppointment?.hasInsurance ?? false;
          const insuranceCompany =
            hasInsurance
              ? (editFormData?.insuranceDetails?.companyName ||
                  updateData?.insuranceDetails?.companyName ||
                  formData?.insuranceDetails?.companyName ||
                  originalAppointment?.insuranceDetails?.companyName ||
                  "Yes")
              : "None";
          const resolvedDentistRemarks =
            typeof editFormData?.dentistRemarks === "string" && editFormData.dentistRemarks.trim()
              ? editFormData.dentistRemarks.trim()
              : (typeof updateData?.dentistRemarks === "string" && updateData.dentistRemarks.trim()
                  ? updateData.dentistRemarks.trim()
                  : (typeof formData?.dentistRemarks === "string" && formData.dentistRemarks.trim()
                      ? formData.dentistRemarks.trim()
                      : (typeof originalAppointment?.dentistRemarks === "string"
                          ? originalAppointment.dentistRemarks.trim()
                          : "")));
          const dentistRemarksText = resolvedDentistRemarks || "None";
          
          // Use updated dentist name - prioritize editFormData (most current) for all statuses
          const dentistNameForEmail = editFormData?.dentist || updateData?.dentist || formData?.dentist || originalAppointment?.dentist || 'N/A';
          
          // Get assigned dentist UID to ensure they're notified
          const assignedDentistUid = resolveAppointmentDentistUid(originalAppointment);
          const notifiedUserIds = new Set(); // Track notified users to avoid duplicates

          usersSnapshot.forEach((docu) => {
            const data = docu.data();
            const userId = docu.id;
            const isAssignedDentist = assignedDentistUid && userId === assignedDentistUid;
            
            // Assigned dentist + all ClinicStaff only (not every dentist in the clinic)
            if (
              isAssignedDentist ||
              data.userType === "ClinicStaff" || data.role === "ClinicStaff"
            ) {
              // Skip if already notified (avoid duplicates)
              if (notifiedUserIds.has(userId)) {
                return;
              }
              notifiedUserIds.add(userId);
              const patientName = originalAppointment.patientDisplay || originalAppointment.patientName || "a patient";
              let personnelMessage = "";
              const statusForMessage = normalizedNewStatus || statusToCheck || formData.status;
              if (statusForMessage === "Completed" || statusForMessage?.toLowerCase() === "completed") {
                personnelMessage = `The appointment for ${patientName} on ${appointmentWindow} has been marked as completed by ${editorNameWithTitle}.`;
              } else if (statusForMessage === "Pending" || statusForMessage?.toLowerCase() === "pending") {
                personnelMessage = `The appointment for ${patientName} on ${appointmentWindow} has been set to pending by ${editorNameWithTitle}.`;
              } else if (statusForMessage === "Confirmed" || statusForMessage?.toLowerCase() === "confirmed") {
                if (timeChanged && originalFormattedStartTime !== "N/A" && originalFormattedEndTime !== "N/A") {
                  // Time was changed - use dynamic message
                  const formattedDateLong = formatDateLong(dateForNotification);
                  const originalTimeWindow = `${originalFormattedStartTime} – ${originalFormattedEndTime}`;
                  const newTimeWindow = `${formattedStartTime} – ${formattedEndTime}`;
                  personnelMessage = `The appointment for ${patientName} on ${formattedDateLong} has been confirmed by ${editorNameWithTitle}. The scheduled time has been updated from ${originalTimeWindow} to ${newTimeWindow}.`;
                } else {
                  // No time change - use original message
                personnelMessage = `The appointment for ${patientName} on ${appointmentWindow} has been confirmed by ${editorNameWithTitle}.`;
                }
              }

              if (personnelMessage) {
                if (statusForMessage === "Completed" || statusForMessage?.toLowerCase() === "completed") {
                  personnelMessage += `\n\nInsurance: ${insuranceCompany}\nDentist Remarks: ${dentistRemarksText}`;
                } else {
                  personnelMessage += `\n\nInsurance: ${insuranceCompany}`;
                }
                addNotification(docu.id, personnelMessage);

                // Send email notification if personnel's email is verified
                const personnelEmailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
                const personnelEmail = data.email || data.authEmail;
                if (personnelEmailVerified && personnelEmail && !personnelEmail.includes("@mobileuser.dentalapp.com")) {
                  const personnelName = data.firstName && data.lastName 
                    ? `${data.firstName} ${data.lastName}` 
                    : (data.name || "Staff");
                  const personnelRole = data.userType || data.role;
                  // Format services - extract names if they're objects
                  const servicesRaw = pickFirstNonEmptyServicesArray(
                    editFormData.services,
                    updateData.services,
                    formData.services,
                    originalAppointment.services
                  );
                  const services = Array.isArray(servicesRaw) 
                    ? servicesRaw.map(service => {
                        if (typeof service === 'string') {
                          return service;
                        } else if (service && typeof service === 'object') {
                          return service.name || service.serviceName || JSON.stringify(service);
                        }
                        return String(service);
                      })
                    : [];
                  const sendEmailStatusChange = async () => {
                    try {
                      const apiUrl = process.env.REACT_APP_API_URL || '';
                      // Normalize status to ensure it matches expected values
                      const normalizedStatus = statusForMessage?.charAt(0).toUpperCase() + statusForMessage?.slice(1).toLowerCase();
                      const response = await fetch(`${apiUrl}/api/send-appointment-status-change`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          type: 'dentist',
                          recipientEmail: personnelEmail,
                          recipientName: personnelName,
                          recipientRole: personnelRole,
                          patientName: patientName,
                          appointmentDate: formattedDateLong,
                          startTime: formattedStartTime,
                          endTime: formattedEndTime !== "N/A" ? formattedEndTime : formattedStartTime,
                          previousStartTime: (normalizedStatus === "Confirmed" && timeChanged && originalFormattedStartTime !== "N/A") ? originalFormattedStartTime : null,
                          previousEndTime: (normalizedStatus === "Confirmed" && timeChanged && originalFormattedEndTime !== "N/A") ? originalFormattedEndTime : null,
                          dentistName: dentistNameForEmail,
                          services,
                          status: normalizedStatus,
                          actorName: editorNameWithTitle,
                          insuranceText: insuranceCompany,
                          dentistRemarks: (statusForMessage === "Completed" || statusForMessage?.toLowerCase() === "completed") ? dentistRemarksText : null,
                          clinicName: 'VSDiamond Dental Clinic'
                        })
                      });
                      const result = await response.json();
                      if (!result.success) {
                        console.error(`Failed to send status change email notification to ${personnelRole}:`, result.message);
                      }
                    } catch (error) {
                      console.error(`Error sending status change email notification to ${personnelRole}:`, error);
                    }
                  };
                  sendEmailStatusChange();
                }
              }
            }
          });
        }
        // ------------------------------------------------------------------------

        // Notify when duration/time is updated but status remains "Pending"
        const statusToCheckForPendingUpdate = updateData.status || formData.status;
        const originalStatusForPendingUpdate = originalAppointment?.status ? String(originalAppointment.status).trim() : '';
        const newStatusForPendingUpdate = statusToCheckForPendingUpdate ? String(statusToCheckForPendingUpdate).trim() : '';
        const normalizedOriginalStatusForPendingUpdate = originalStatusForPendingUpdate ? originalStatusForPendingUpdate.charAt(0).toUpperCase() + originalStatusForPendingUpdate.slice(1).toLowerCase() : '';
        const normalizedNewStatusForPendingUpdate = newStatusForPendingUpdate ? newStatusForPendingUpdate.charAt(0).toUpperCase() + newStatusForPendingUpdate.slice(1).toLowerCase() : '';
        
        // Only notify if status is still "Pending" (didn't change) AND start time and/or duration changed.
        // Skip if adjustAppointmentTimes / autoAdjust already sent time notifications for this appointment id.
        if (
          originalAppointment &&
          normalizedOriginalStatusForPendingUpdate === "Pending" &&
          normalizedNewStatusForPendingUpdate === "Pending" &&
          timeOrDurationChanged &&
          !appointmentIdsNotifiedForPendingTimeChange.has(editingAppointmentId)
        ) {
          const formattedDateLong = formatDateLong(dateForNotification);
          const appointmentWindow = buildNotificationWindow(
            formattedDateLong,
            originalFormattedStartTime,
            originalFormattedEndTime
          );
          
          // Get insurance info
          const hasInsurance = editFormData?.hasInsurance ?? updateData?.hasInsurance ?? formData?.hasInsurance ?? originalAppointment?.hasInsurance ?? false;
          const insuranceCompany =
            hasInsurance
              ? (editFormData?.insuranceDetails?.companyName ||
                  updateData?.insuranceDetails?.companyName ||
                  formData?.insuranceDetails?.companyName ||
                  originalAppointment?.insuranceDetails?.companyName ||
                  "Yes")
              : "None";
          const servicesRawForTimeUpdate = pickFirstNonEmptyServicesArray(
            editFormData.services,
            updateData.services,
            formData.services,
            originalAppointment.services
          );
          const servicesForTimeUpdate = Array.isArray(servicesRawForTimeUpdate)
            ? servicesRawForTimeUpdate.map((service) => {
                if (typeof service === "string") {
                  return service;
                }
                if (service && typeof service === "object") {
                  return service.name || service.serviceName || JSON.stringify(service);
                }
                return String(service);
              })
            : [];
          const dentistNameForEmail =
            editFormData?.dentist ||
            updateData?.dentist ||
            formData?.dentist ||
            originalAppointment?.dentist ||
            "N/A";
          
          // Notify patient
          if (patientUid) {
            // Use appointment window format: "December 24, 2025 (2:00 PM - 4:10 PM)"
            const patientAppointmentWindow = buildNotificationWindow(formattedDateLong, formattedStartTime, formattedEndTime);
            const patientMessage = `Your appointment on ${patientAppointmentWindow} has been updated. The time has been updated from ${originalFormattedStartTime} - ${originalFormattedEndTime} to ${formattedStartTime} - ${formattedEndTime}.\n\nIf you think you will not be able to attend the time, then we suggest to reschedule a new appointment or cancel this appointment.\n\nInsurance: ${insuranceCompany}\nStatus: Pending`;
            await addNotification(patientUid, patientMessage);
            
            // Send email notification to patient
            try {
              const patientDoc = await getDoc(doc(db, "users", patientUid));
              if (patientDoc.exists()) {
                const patientData = patientDoc.data();
                const patientEmail = patientData.email || patientData.authEmail;
                const patientEmailVerified = patientData.emailVerified === "Yes" || patientData.emailVerified === true;
                const patientNameForEmail = originalAppointment.patientDisplay || originalAppointment.patientName || "Patient";
                
                if (patientEmail && patientEmailVerified && !patientEmail.includes("@mobileuser.dentalapp.com")) {
                  try {
                    const apiUrl = process.env.REACT_APP_API_URL || '';
                    const response = await fetch(`${apiUrl}/api/send-appointment-status-change`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        isAutoAdjustment: true,
                        type: 'patient',
                        recipientEmail: patientEmail,
                        recipientName: patientNameForEmail,
                        patientName: patientNameForEmail,
                        appointmentDate: formattedDateLong,
                        originalStartTime: originalFormattedStartTime,
                        originalEndTime: originalFormattedEndTime,
                        adjustedStartTime: formattedStartTime,
                        adjustedEndTime: formattedEndTime,
                        startTime: formattedStartTime,
                        endTime: formattedEndTime,
                        dentistName: dentistNameForEmail,
                        services: servicesForTimeUpdate,
                        insuranceText: insuranceCompany,
                        clinicName: 'VSDiamond Dental Clinic',
                        appointmentId: originalAppointment.id || originalAppointment.appointmentId,
                        appointmentDateKey: originalAppointment.date || originalAppointment._dateKey,
                      })
                    });
                    
                    const result = await response.json();
                    if (!result.success) {
                      console.error(`Failed to send time update email to patient:`, result.message);
                    }
                  } catch (fetchError) {
                    console.error(`Error sending time update email to patient:`, fetchError);
                  }
                }
              }
            } catch (emailError) {
              console.error(`Error sending time update email to patient:`, emailError);
            }
          }
          
          // Notify assigned dentist and all ClinicStaff (manual pending time update)
          const usersRef = collection(db, "users");
          const usersSnapshot = await getDocs(usersRef);
          const assignedDentistUid = resolveAppointmentDentistUid(originalAppointment);
          const notifiedUserIds = new Set();
          const actorName = getCurrentUserDisplayNameForSystemNotification() || "Clinic Personnel";
          const patientName = originalAppointment.patientDisplay || originalAppointment.patientName || "a patient";
          
          // Use for...of loop instead of forEach to properly handle async operations
          for (const docu of usersSnapshot.docs) {
            const data = docu.data();
            const userId = docu.id;
            const isAssignedDentist = assignedDentistUid && userId === assignedDentistUid;
            
            if (
              isAssignedDentist ||
              data.userType === "ClinicStaff" || data.role === "ClinicStaff"
            ) {
              // Skip if already notified (avoid duplicates)
              if (notifiedUserIds.has(userId)) {
                continue;
              }
              notifiedUserIds.add(userId);
              
              // Manual update: include actor name, no appointment window format
              const personnelMessage = `The appointment for ${patientName} on ${formattedDateLong} has been updated by ${actorName}. The scheduled time has been updated from ${originalFormattedStartTime} - ${originalFormattedEndTime} to ${formattedStartTime} - ${formattedEndTime}.\n\nInsurance: ${insuranceCompany}\nStatus: Pending`;
              addNotification(userId, personnelMessage);
              
              // Send email notification to personnel
              const personnelEmailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
              const personnelEmail = data.email || data.authEmail;
              if (personnelEmailVerified && personnelEmail && !personnelEmail.includes("@mobileuser.dentalapp.com")) {
                const personnelName = data.firstName && data.lastName 
                  ? `${data.firstName} ${data.lastName}` 
                  : (data.name || "Staff");
                const personnelRole = data.userType || data.role;
                
                try {
                  const apiUrl = process.env.REACT_APP_API_URL || '';
                  const response = await fetch(`${apiUrl}/api/send-appointment-status-change`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      isAutoAdjustment: true,
                      type: 'personnel',
                      recipientEmail: personnelEmail,
                      recipientName: personnelName,
                      recipientRole: personnelRole,
                      patientName: patientName,
                      appointmentDate: formattedDateLong,
                      originalStartTime: originalFormattedStartTime,
                      originalEndTime: originalFormattedEndTime,
                      adjustedStartTime: formattedStartTime,
                      adjustedEndTime: formattedEndTime,
                      startTime: formattedStartTime,
                      endTime: formattedEndTime,
                      actorName: actorName,
                      dentistName: dentistNameForEmail,
                      services: servicesForTimeUpdate,
                      insuranceText: insuranceCompany,
                      clinicName: 'VSDiamond Dental Clinic',
                        appointmentId: originalAppointment.id || originalAppointment.appointmentId,
                        appointmentDateKey: originalAppointment.date || originalAppointment._dateKey,
                    })
                  });
                  
                  if (!response.ok) {
                    const errorText = await response.text();
                    console.error(`HTTP error sending time update email to personnel (${personnelEmail}):`, response.status, errorText);
                    continue;
                  }
                  
                  const result = await response.json();
                  if (!result.success) {
                    console.error(`Failed to send time update email to personnel (${personnelEmail}):`, result.message);
                  }
                } catch (error) {
                  console.error(`Error sending time update email to personnel (${personnelEmail}):`, error);
                }
              }
            }
          }
        }

        } catch (notifyError) {
          console.error("Error sending appointment update notifications:", notifyError);
        }
        })();

        return;
      } catch (error) {
        console.error("Error updating appointment:", error);
      }
    } finally {
      savingAppointmentDetailsRef.current = false;
      setIsSavingAppointmentDetails(false);
    }
  };

  // Handle closing the edit form
  const handleEditClose = () => {
    if (isSavingAppointmentDetails) return;
    setShowEditForm(false);
    setEditingAppointmentId(null);
    setEditFormData({ services: [] });
    setServicePrices({});
    setShowSuccessMessage(false);
    setEditFormValidationMessage("");
  };

  // Handle service price change (digits only, non-negative integers; empty clears)
  const handleServicePriceChange = (serviceName, price) => {
    const statusNorm = (editFormData.status || "").trim().toLowerCase();
    if (statusNorm !== "completed") return;
    if (price === "") {
      setServicePrices((prev) => ({ ...prev, [serviceName]: "" }));
      return;
    }
    const trimmed = String(price).trim();
    if (!/^\d+$/.test(trimmed)) return;
    setServicePrices((prev) => ({ ...prev, [serviceName]: trimmed }));
  };

  // Calculate total bill from service prices
  const calculateTotalBill = () => {
    return Object.values(servicePrices).reduce((sum, price) => {
      return sum + (Number(price) || 0);
    }, 0);
  };

  // Format number with commas
  const formatNumberWithCommas = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Toggle service selection
  const toggleService = (service) => {
    setEditFormData((prevData) => {
      const serviceExists = prevData.services.some(s => s.name === service);
      let updatedServices;
      
      if (serviceExists) {
        updatedServices = prevData.services.filter((s) => s.name !== service);
      } else {
        const newService = { name: service, price: "" };
        updatedServices = [...prevData.services, newService];
      }

      // Keep existing duration, only recalculate end time
      const startTime = prevData.startTime || prevData.time;
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = startMinutes + (prevData.duration || 30);
      const newEndTime = minutesToTime(endMinutes);

      return { 
        ...prevData, 
        services: updatedServices,
        endTime: newEndTime
      };
    });
    
    if (!editFormData.services.some(s => s.name === service)) {
      setServicePrices(prev => ({
        ...prev,
        [service]: ""
      }));
    } else {
      setServicePrices(prev => {
        const newPrices = { ...prev };
        delete newPrices[service];
        return newPrices;
      });
    }
  };

  // Format time in minutes to HH:MM AM/PM format (minute counts may exceed one day)
  const formatTime = (minutes) => formatMinutesOfDayTo12h(minutes);



  // Convert time string to minutes (handles both 12-hour and 24-hour formats)
  function timeToMinutes(timeStr) {
    if (!timeStr) return 0;
    
    // If in 12-hour format (H:MM AM/PM), convert to 24-hour first
    if (timeStr.match(/^\d{1,2}:\d{2}\s?(AM|PM)$/i)) {
      const [time, period] = timeStr.split(/\s?(AM|PM)$/i);
      const [hours, minutes] = time.split(':').map(Number);
      let hour24 = hours;
      
      if (period.toUpperCase() === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      
      return hour24 * 60 + minutes;
    }
    
    // If in 24-hour format (HH:MM), convert directly
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }
  // Convert minutes to time string
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  /** Cleared, placeholder (--), or incomplete times keep the appointment's existing start on save. */
  const resolveEditFormStartTimeForSave = (editState, originalAppt) => {
    const fallback = String(originalAppt?.startTime || originalAppt?.time || "").trim();
    const raw = String(editState?.startTime ?? editState?.time ?? "")
      .trim()
      .replace(/\s+/g, " ");
    if (!raw) return fallback;
    if (/--/.test(raw)) return fallback;
    if (!/^\d{1,2}:\d{2}(\s*(AM|PM))?$/i.test(raw)) return fallback;
    const m = timeToMinutes(raw);
    if (!Number.isFinite(m)) return fallback;
    return raw;
  };

  /** Start time must be fully specified when editing (no placeholders, incomplete segments). */
  const isEditFormStartTimeFieldComplete = (editState) => {
    let raw = String(editState?.startTime ?? editState?.time ?? "").trim();
    if (!raw) return false;
    raw = raw.replace(/\s*:\s*/, ":");
    raw = raw.replace(/\s+/g, " ");
    if (/--/.test(raw)) return false;
    if (/^\d{1,2}:\d{2}$/.test(raw)) {
      const [h, m] = raw.split(":").map(Number);
      return (
        Number.isFinite(h) &&
        Number.isFinite(m) &&
        h >= 0 &&
        h <= 23 &&
        m >= 0 &&
        m <= 59
      );
    }
    if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(raw)) {
      const mins = timeToMinutes(raw);
      return Number.isFinite(mins);
    }
    return false;
  };

  const isEditFormDurationFieldComplete = (editState) => {
    const d = editState?.duration;
    if (d === "" || d == null) return false;
    const n = typeof d === "number" ? d : parseInt(String(d).trim(), 10);
    return Number.isFinite(n) && n >= 1;
  };

  const isEditFormEndBeyondCalendarMidnight = (editState, originalAppt) => {
    if (!editState || !originalAppt) return false;
    const startTime = resolveEditFormStartTimeForSave(editState, originalAppt);
    const startMin = timeToMinutes(startTime);
    if (!Number.isFinite(startMin)) return false;
    const durationFieldEmpty = editState.duration === "" || editState.duration == null;
    let resolvedDur;
    if (durationFieldEmpty) {
      const od = originalAppt.duration;
      resolvedDur = typeof od === "number" ? od : parseInt(String(od ?? 30), 10);
    } else {
      resolvedDur =
        typeof editState.duration === "number"
          ? editState.duration
          : parseInt(String(editState.duration).trim(), 10);
    }
    if (!Number.isFinite(resolvedDur) || resolvedDur < 1) return false;
    const maxDur = maxDurationUntilMidnightFromSlotStart(startMin);
    return resolvedDur > maxDur;
  };

  const computeEditExtendsPastCalendarMidnight = () => {
    if (!showEditForm || !editingAppointmentId) return false;
    const orig = appointments.find((appt) => appt.id === editingAppointmentId);
    if (!orig) return false;
    return isEditFormEndBeyondCalendarMidnight(editFormData, orig);
  };

  const getDurationBeyondAssignedTimeslotsMessageForEdit = useCallback(
    (dentistUid) => {
      const dentistName = dentistUid
        ? getDentistDisplayNameFromUID(dentistUid)
        : "This dentist";
      return `Failed to save changes. The duration goes beyond ${dentistName}'s assigned timeslots.`;
    },
    [getDentistDisplayNameFromUID]
  );

  const computeEditViolatesPlottedScheduleBoundaries = useCallback(() => {
    if (!showEditForm || !editingAppointmentId) {
      return { violated: false, dentistId: null, hasPlottedSchedule: false };
    }
    const orig = appointments.find((appt) => appt.id === editingAppointmentId);
    if (!orig) return { violated: false, dentistId: null, hasPlottedSchedule: false };

    const dentistId = editFormData.dentistId || orig.dentistId;
    if (!dentistId) return { violated: false, dentistId: null, hasPlottedSchedule: false };

    const normalizeEditedDateKey = (value) => {
      if (value == null || value === "") return null;
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
        if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
          const [mm, dd, yyyy] = trimmed.split("-");
          return `${yyyy}-${mm}-${dd}`;
        }
        const fromIso = normalizeAppointmentDateKey(trimmed);
        return fromIso || null;
      }
      try {
        return getLocalDateKey(value);
      } catch {
        return null;
      }
    };

    const editDateKey = normalizeEditedDateKey(editFormData.date || orig.date);
    let selectedDateKey = null;
    try {
      const selectedDateObj =
        selectedDateAppointment &&
        typeof selectedDateAppointment.toDate === "function"
          ? selectedDateAppointment.toDate()
          : selectedDateAppointment;
      if (selectedDateObj) {
        selectedDateKey = getLocalDateKey(selectedDateObj);
      }
    } catch {
      selectedDateKey = null;
    }

    if (!editDateKey || !selectedDateKey || editDateKey !== selectedDateKey) {
      return { violated: false, dentistId, hasPlottedSchedule: false };
    }

    const hasPlottedSchedule = dentistHasPlottedSchedule(dentistSchedulesForDate, dentistId);
    if (!hasPlottedSchedule) {
      return { violated: false, dentistId, hasPlottedSchedule: false };
    }

    const startTime = resolveEditFormStartTimeForSave(editFormData, orig);
    const startMin = timeToMinutes(startTime);
    if (!Number.isFinite(startMin)) {
      return { violated: false, dentistId, hasPlottedSchedule: true };
    }

    let resolvedDur;
    if (editFormData.duration === "" || editFormData.duration == null) {
      const od = orig.duration;
      resolvedDur = typeof od === "number" ? od : parseInt(String(od ?? 30), 10);
    } else {
      resolvedDur =
        typeof editFormData.duration === "number"
          ? editFormData.duration
          : parseInt(String(editFormData.duration).trim(), 10);
    }
    if (!Number.isFinite(resolvedDur) || resolvedDur < 1) {
      return { violated: false, dentistId, hasPlottedSchedule: true };
    }

    const endMin = startMin + resolvedDur;
    const scheduleUnavailableRanges =
      getDentistScheduleUnavailableRanges(dentistSchedulesForDate, dentistId) || [];
    const violated = scheduleUnavailableRanges.some(
      (range) => startMin < range.end && range.start < endMin
    );
    return { violated, dentistId, hasPlottedSchedule: true };
  }, [
    showEditForm,
    editingAppointmentId,
    appointments,
    editFormData,
    selectedDateAppointment,
    dentistSchedulesForDate,
  ]);

  /** Live preview: same direct overlap check as Save (confirmed/completed), before plot-boundary message. */
  const computeEditOverlapsAnchoredAppointment = useCallback(() => {
    if (!showEditForm || !editingAppointmentId) return null;
    const orig = appointments.find((appt) => appt.id === editingAppointmentId);
    if (!orig) return null;

    const dentistId = editFormData.dentistId || orig.dentistId;
    if (!dentistId) return null;

    const normalizeEditedDateKey = (value) => {
      if (value == null || value === "") return null;
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
        if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
          const [mm, dd, yyyy] = trimmed.split("-");
          return `${yyyy}-${mm}-${dd}`;
        }
        const fromIso = normalizeAppointmentDateKey(trimmed);
        return fromIso || null;
      }
      try {
        return getLocalDateKey(value);
      } catch {
        return null;
      }
    };

    let selectedDateKey = null;
    try {
      const selectedDateObj =
        selectedDateAppointment &&
        typeof selectedDateAppointment.toDate === "function"
          ? selectedDateAppointment.toDate()
          : selectedDateAppointment;
      if (selectedDateObj) {
        selectedDateKey = getLocalDateKey(selectedDateObj);
      }
    } catch {
      selectedDateKey = null;
    }

    const editedDateKey =
      normalizeEditedDateKey(editFormData.date) ||
      normalizeEditedDateKey(orig.date) ||
      selectedDateKey;
    if (!editedDateKey) return null;

    const newStartTime = resolveEditFormStartTimeForSave(editFormData, orig);
    const newStart = timeToMinutes(newStartTime);
    if (!Number.isFinite(newStart)) return null;

    let newDuration;
    if (editFormData.duration === "" || editFormData.duration == null) {
      const od = orig.duration;
      newDuration = typeof od === "number" ? od : parseInt(String(od ?? 30), 10);
    } else {
      newDuration =
        typeof editFormData.duration === "number"
          ? editFormData.duration
          : parseInt(String(editFormData.duration).trim(), 10);
    }
    if (!Number.isFinite(newDuration) || newDuration < 1) return null;

    const newEnd = newStart + newDuration;

    const editStatus = (editFormData.status || orig.status || "").toLowerCase();
    if (editStatus !== "pending" && editStatus !== "new") return null;

    let allDayForDate = (Array.isArray(allAppointments) ? allAppointments : []).filter(
      (a) => a && a.date === editedDateKey
    );
    if (allDayForDate.length === 0 && selectedDateKey && editedDateKey === selectedDateKey) {
      allDayForDate = (Array.isArray(appointments) ? appointments : [])
        .map((a) => ({ ...a, date: a.date || editedDateKey }))
        .filter(Boolean);
    }

    const scheduleDoc =
      editedDateKey === selectedDateKey && dentistSchedulesForDate
        ? sanitizeDentistPlotDocData(dentistSchedulesForDate)
        : {};

    const configuredChairCount = isChairCapacityConfigured(chairSettings)
      ? resolveChairCountForDateKey(editedDateKey, null, chairSettings)
      : 0;

    return findPendingEditImmovableConflict({
      editingAppointmentId,
      editingDentistId: dentistId,
      newStartMin: newStart,
      newEndMin: newEnd,
      allDayAppointments: allDayForDate,
      scheduleDocData: scheduleDoc,
      chairCount: configuredChairCount,
    });
  }, [
    showEditForm,
    editingAppointmentId,
    appointments,
    editFormData,
    allAppointments,
    selectedDateAppointment,
    dentistSchedulesForDate,
    chairSettings,
  ]);

  // Auto-adjust pending appointments to fill gaps (runs on page load and real-time updates)
  const autoAdjustPendingAppointments = async (appointmentsArray, formattedDate) => {
    const appointmentRef = doc(db, "appointments", formattedDate);
    
    // Sort appointments by start time
    const sortedAppointments = [...appointmentsArray].sort((a, b) => {
      const aStart = timeToMinutes(a.startTime || a.time);
      const bStart = timeToMinutes(b.startTime || b.time);
      return aStart - bStart;
    });

    // Start from clinic opening time (9:00 AM = 540 minutes)
    let currentEndMinutes = 540; // 9:00 AM
    const updates = {};
    let hasChanges = false;

    // Process each appointment
    for (let i = 0; i < sortedAppointments.length; i++) {
      const appointment = sortedAppointments[i];
      const appointmentStartMinutes = timeToMinutes(appointment.startTime || appointment.time);
      const appointmentDuration = appointment.duration || 30;
      const appointmentEndMinutes = appointmentStartMinutes + appointmentDuration;

      // Skip pending appointments - they should remain at their selected time
      if (appointment.status === "Pending" || appointment.status === "New") {
        // Just update the current end time to account for this appointment's duration
        currentEndMinutes = Math.max(currentEndMinutes, appointmentEndMinutes);
        continue;
      }

      // For confirmed/completed appointments, just update the current end time
      currentEndMinutes = appointmentEndMinutes;
    }

    // Apply updates if there were changes
    if (hasChanges && Object.keys(updates).length > 0) {
      try {
        await updateDoc(appointmentRef, updates);
      } catch (error) {
        console.error("Error auto-adjusting appointments:", error);
      }
    }
  };

  // Helper function to auto-adjust overlapping pending appointments after any update
  const autoAdjustOverlappingPendingAppointments = async (appointmentId, formattedDate, dentistId) => {
    const notifiedAppointmentIdsForPendingTimeChange = new Set();
    try {
      const appointmentRef = doc(db, "appointments", formattedDate);
      const appointmentDoc = await getDoc(appointmentRef);
      if (!appointmentDoc.exists()) return notifiedAppointmentIdsForPendingTimeChange;
      
      const allAppointments = appointmentDoc.data() || {};
      const appointmentsArray = Object.entries(allAppointments)
        .map(([firestoreDocId, appt]) =>
          withAppointmentDocDate({ ...appt, id: firestoreDocId }, formattedDate)
        )
        .filter(appt => appt && typeof appt === 'object');
      
      // Find the updated appointment
      const updatedAppt = appointmentsArray.find(appt => appt.id === appointmentId);
      if (!updatedAppt) return notifiedAppointmentIdsForPendingTimeChange;
      
      if (!isCompressiblePendingStatus(updatedAppt.status)) {
        return notifiedAppointmentIdsForPendingTimeChange;
      }
      
      const updatedStart = timeToMinutes(updatedAppt.startTime || updatedAppt.time);
      const updatedDuration = updatedAppt.duration || 30;
      const updatedEnd = updatedAppt.endTime 
        ? timeToMinutes(updatedAppt.endTime)
        : (updatedStart + updatedDuration);
      
      const appointmentsToAdjust = [];
      const updates = {};
      
      // Sort appointments by start time to process them in order
      const sortedAppointments = appointmentsArray
        .filter(appt => {
          if (!appt || typeof appt !== 'object') return false;
          if (appt.dentistId !== dentistId || !appointmentBelongsToDateDoc(appt, formattedDate))
            return false;
          const status = (appt.status || "").toLowerCase();
          return status !== "cancelled" && status !== "completed";
        })
        .sort((a, b) => {
          const aStart = timeToMinutes(a.startTime || a.time);
          const bStart = timeToMinutes(b.startTime || b.time);
          return aStart - bStart;
        });
      
      // Find the updated appointment's position
      const updatedIndex = sortedAppointments.findIndex(appt => appt.id === appointmentId);
      if (updatedIndex === -1) return notifiedAppointmentIdsForPendingTimeChange;
      
      // Process succeeding appointments (only pending ones) - cascade forward
      let currentEndTime = updatedEnd;
      
      for (let i = updatedIndex + 1; i < sortedAppointments.length; i++) {
        const nextAppt = sortedAppointments[i];
        
        if (!isCompressiblePendingStatus(nextAppt.status)) {
          break;
        }
        
        const nextStart = timeToMinutes(nextAppt.startTime || nextAppt.time);
        const nextEnd = nextAppt.endTime 
          ? timeToMinutes(nextAppt.endTime)
          : (nextStart + (nextAppt.duration || 30));
        const nextSpanMinutes = Math.max(1, nextEnd - nextStart);
        
        if (nextStart < currentEndTime) {
          // Overlap - adjust to start at currentEndTime
          const adjustedStart = currentEndTime;
          const adjustedEnd = adjustedStart + nextSpanMinutes;
          
          appointmentsToAdjust.push({
            id: nextAppt.id,
            startTime: formatTime(adjustedStart),
            endTime: formatTime(adjustedEnd),
            originalStart: nextAppt.startTime || nextAppt.time,
            originalEnd: nextAppt.endTime || formatTime(nextEnd),
            patientId: nextAppt.patientId || nextAppt.uid || nextAppt.userId,
            date: nextAppt.date || formattedDate,
            insuranceDetails: nextAppt.insuranceDetails,
            hasInsurance: nextAppt.hasInsurance,
            status: nextAppt.status || "Pending"
          });
          
          currentEndTime = adjustedEnd;
        } else {
          // No overlap (gap or back-to-back) - keep appointment in place, just track end time
          currentEndTime = nextEnd;
        }
      }
      
      // Also check for appointments that start before the updated one (in case it was moved earlier)
      for (let i = updatedIndex - 1; i >= 0; i--) {
        const prevAppt = sortedAppointments[i];
        
        if (!isCompressiblePendingStatus(prevAppt.status)) break;
        
        const prevStart = timeToMinutes(prevAppt.startTime || prevAppt.time);
        const prevDuration = prevAppt.duration || 30;
        const prevEnd = prevAppt.endTime 
          ? timeToMinutes(prevAppt.endTime)
          : (prevStart + prevDuration);
        
        // Check if updated appointment actually overlaps with previous one
        // Both conditions must be true for a real overlap:
        // 1. Updated appointment starts before previous ends (updatedStart < prevEnd)
        // 2. Previous appointment starts before updated ends (prevStart < updatedEnd)
        if (updatedStart < prevEnd && prevStart < updatedEnd) {
          // Actual overlap - adjust the updated appointment to start after previous one
          const adjustedStart = prevEnd;
          const adjustedEnd = adjustedStart + (updatedEnd - updatedStart);
          
          // Update the updated appointment
          updates[`${updatedAppt.id}.startTime`] = formatTime(adjustedStart);
          updates[`${updatedAppt.id}.endTime`] = formatTime(adjustedEnd);
          updates[`${updatedAppt.id}.time`] = formatTime(adjustedStart);
          
          // Track for notification
          appointmentsToAdjust.push({
            id: updatedAppt.id,
            startTime: formatTime(adjustedStart),
            endTime: formatTime(adjustedEnd),
            originalStart: updatedAppt.startTime || updatedAppt.time,
            originalEnd: updatedAppt.endTime || formatTime(updatedEnd),
            patientId: updatedAppt.patientId || updatedAppt.uid || updatedAppt.userId,
            date: formattedDate,
            insuranceDetails: updatedAppt.insuranceDetails,
            hasInsurance: updatedAppt.hasInsurance,
            status: updatedAppt.status || "Pending"
          });
          
          break; // Only adjust once for the immediately previous appointment
        } else {
          break; // No overlap, stop checking
        }
      }
      
      // Apply adjustments
      if (appointmentsToAdjust.length > 0) {
        for (const adjustment of appointmentsToAdjust) {
          updates[`${adjustment.id}.startTime`] = adjustment.startTime;
          updates[`${adjustment.id}.endTime`] = adjustment.endTime;
          updates[`${adjustment.id}.time`] = adjustment.startTime;
          const adjStart = timeToMinutes(adjustment.startTime);
          const adjEnd = timeToMinutes(adjustment.endTime);
          if (adjEnd > adjStart) {
            updates[`${adjustment.id}.duration`] = adjEnd - adjStart;
          }
        }
      }
      
      
      // Apply updates if there are any (either from adjustments or backward check)
      if (Object.keys(updates).length > 0) {
        await updateDoc(appointmentRef, updates);
        markTimelineCompressHandledByStaffSave();

        // Send notifications (affected patient + assigned dentist + Clinic Staff; app + email)
        const uniqueAdjustments = [];
        const seenIds = new Set();
        for (const adjustment of appointmentsToAdjust) {
          if (!seenIds.has(adjustment.id)) {
            seenIds.add(adjustment.id);
            uniqueAdjustments.push(adjustment);
          }
        }

        const enrichedAdjustments = uniqueAdjustments.map((adjustment) => {
          const apptSource = appointmentsArray.find((a) => a.id === adjustment.id) || {};
          return {
            ...adjustment,
            dentistId:
              adjustment.dentistId ||
              resolveAppointmentDentistUid(apptSource) ||
              dentistId,
            dentistName: dentistDisplayFromAppointmentSnapshot(apptSource, adjustment.dentistName),
            services: pickServicesFromAppointmentSnapshot(apptSource),
            patientDisplay:
              adjustment.patientDisplay ||
              apptSource.patientDisplay ||
              apptSource.patientName ||
              apptSource.name,
          };
        });
        await sendPendingAppointmentTimeAdjustmentNotificationsBatch(enrichedAdjustments);
        for (const adjustment of enrichedAdjustments) {
          notifiedAppointmentIdsForPendingTimeChange.add(adjustment.id);
        }
      }

    return notifiedAppointmentIdsForPendingTimeChange;
    } catch (error) {
      console.error("Error auto-adjusting overlapping pending appointments:", error);
      return new Set();
    }
  };
  const adjustAppointmentTimes = async (updatedAppointment, originalAppointment) => {
    /** Firestore field ids for which time-update notifications were sent in this run. */
    const notifiedAppointmentIdsForPendingTimeChange = new Set();

    const formattedDate = new Date(selectedDateAppointment.toDate().getTime() - selectedDateAppointment.toDate().getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];
    
    const appointmentRef = doc(db, "appointments", formattedDate);
    
    // Get all appointments for the date
    const appointmentDoc = await getDoc(appointmentRef);
    if (!appointmentDoc.exists()) return notifiedAppointmentIdsForPendingTimeChange;

    const allAppointments = appointmentDoc.data();
    const appointmentsArray = Object.entries(allAppointments).map(([firestoreDocId, appt]) =>
      withAppointmentDocDate({ ...appt, id: firestoreDocId }, formattedDate)
    );

    // Sort appointments by start time
    appointmentsArray.sort((a, b) => {
      const aStart = timeToMinutes(a.startTime || a.time);
      const bStart = timeToMinutes(b.startTime || b.time);
      return aStart - bStart;
    });

    const effectiveId = updatedAppointment.id || originalAppointment?.id;
    if (!effectiveId) return notifiedAppointmentIdsForPendingTimeChange;

    // Find the index of the updated appointment
    const updatedIndex = appointmentsArray.findIndex((appt) => appt.id === effectiveId);
    if (updatedIndex === -1) return notifiedAppointmentIdsForPendingTimeChange;

    // Use the manually entered duration from the form
    const newDuration = updatedAppointment.duration || 30;
    const startMinutes = timeToMinutes(updatedAppointment.startTime || updatedAppointment.time);
    const newEndMinutes = startMinutes + newDuration;
    
    // Update the appointment with new duration and end time
    const updatedAppointmentData = {
      ...updatedAppointment,
      duration: newDuration,
      endTime: minutesToTime(newEndMinutes),
      // Also update the time field for backward compatibility
      time: updatedAppointment.startTime || updatedAppointment.time
    };

    // Add the updated appointment to the updates object
    const updates = {};
    Object.keys(updatedAppointmentData).forEach((key) => {
      updates[`${effectiveId}.${key}`] = updatedAppointmentData[key];
    });

    const updatedStart = timeToMinutes(
      updatedAppointmentData.startTime || updatedAppointmentData.time
    );
    const updatedEnd = timeToMinutes(
      updatedAppointmentData.endTime || minutesToTime(newEndMinutes)
    );

    let backwardBatchDone = false;
    let chairPlotCascadeActive = false;
    const appointmentsToAdjust = [];

    // Chair-plot cascade: same simulation as edit validation (cross-dentist, original chair).
    // Use resolveChairCountForDateKey directly (not isChairCapacityConfigured) so that the
    // chair-plot path activates whenever a chair count is configured, even if rosterDentistIds
    // is empty — consistent with the validation path in the save handler.
    const chairSchedSnapForCascade = await getDoc(doc(db, ...CHAIR_SCHEDULES_DOC_PATH));
    const configuredChairCount = resolveChairCountForDateKey(
      formattedDate,
      chairSchedSnapForCascade.exists() ? chairSchedSnapForCascade.data() : null,
      chairSettings
    );
    if (
      isCompressiblePendingStatus(updatedAppointmentData.status) &&
      originalAppointment &&
      configuredChairCount > 0
    ) {
      {
        chairPlotCascadeActive = true;
        let scheduleDocForCascade = dentistSchedulesForDate;
        if (!scheduleDocForCascade || Object.keys(scheduleDocForCascade).length === 0) {
          const plotSnap = await getDoc(
            doc(db, CLINIC_SCHEDULING_COLLECTION, `${DENTIST_PLOT_DOC_PREFIX}${formattedDate}`)
          );
          scheduleDocForCascade = plotSnap.exists()
            ? sanitizeDentistPlotDocData(plotSnap.data() || {})
            : {};
        } else {
          scheduleDocForCascade = sanitizeDentistPlotDocData(scheduleDocForCascade);
        }

        const originalStartMin = timeToMinutes(
          originalAppointment.startTime || originalAppointment.time
        );
        const { violation, updatesById } = simulateEditChairCascade({
          editingAppointmentId: effectiveId,
          editingDentistId: updatedAppointmentData.dentistId,
          newStartMin: updatedStart,
          newEndMin: updatedEnd,
          originalStartMin,
          allDayAppointments: appointmentsArray,
          scheduleDocData: scheduleDocForCascade || {},
          chairCount: configuredChairCount,
        });

        if (!violation) {
          const batchUpdates = {};
          Object.keys(updatedAppointmentData).forEach((key) => {
            batchUpdates[`${effectiveId}.${key}`] = updatedAppointmentData[key];
          });

          const originalById = new Map(appointmentsArray.map((a) => [a.id, a]));

          updatesById.forEach((timeUpdate, apptId) => {
            batchUpdates[`${apptId}.startTime`] = timeUpdate.startTime;
            batchUpdates[`${apptId}.time`] = timeUpdate.startTime;
            batchUpdates[`${apptId}.endTime`] = timeUpdate.endTime;
            batchUpdates[`${apptId}.duration`] = timeUpdate.duration;

            const origAp = originalById.get(apptId);
            const origStartStr = origAp?.startTime || origAp?.time || "";
            const origDur = origAp?.duration || 30;
            const origEndStr =
              origAp?.endTime ||
              formatTime(timeToMinutes(origStartStr) + origDur);
            if (origStartStr !== timeUpdate.startTime || origEndStr !== timeUpdate.endTime) {
              const origSource = origAp || {};
              appointmentsToAdjust.push({
                id: apptId,
                startTime: timeUpdate.startTime,
                endTime: timeUpdate.endTime,
                originalStart: origStartStr,
                originalEnd: origEndStr,
                patientId:
                  origSource.patientId || origSource.uid || origSource.userId,
                date: origSource.date || formattedDate,
                insuranceDetails: origSource.insuranceDetails,
                hasInsurance: origSource.hasInsurance,
                status: origSource.status || "Pending",
              });
            }
          });

          await updateDoc(appointmentRef, batchUpdates);
          markTimelineCompressHandledByStaffSave();
          backwardBatchDone = true;
        }
      }
    }

    // Legacy dentist-scoped cascade when chair plots are not configured (chairCount = 0).
    if (
      !chairPlotCascadeActive &&
      !backwardBatchDone &&
      isCompressiblePendingStatus(updatedAppointmentData.status) &&
      originalAppointment
    ) {
      const chainSorted = [...appointmentsArray]
        .map((appt) => withAppointmentDocDate(appt, formattedDate))
        .filter((appt) => {
          if (!appt || typeof appt !== "object") return false;
          if (
            appt.dentistId !== updatedAppointmentData.dentistId ||
            !appointmentBelongsToDateDoc(appt, formattedDate)
          )
            return false;
          const status = (appt.status || "").toLowerCase();
          return status !== "cancelled" && status !== "canceled";
        })
        .sort(
          (a, b) =>
            timeToMinutes(a.startTime || a.time) - timeToMinutes(b.startTime || b.time)
        );

      const cascadeIdx = chainSorted.findIndex((ap) => ap.id === effectiveId);
      const originalStartMin = timeToMinutes(
        originalAppointment.startTime || originalAppointment.time
      );

      if (cascadeIdx !== -1 && originalStartMin > updatedStart) {
        let slackMinutesBeforeEdited = 0;
        if (cascadeIdx > 0) {
          const prevAp = chainSorted[cascadeIdx - 1];
          const prevStart = timeToMinutes(prevAp.startTime || prevAp.time);
          const prevEndMin = prevAp.endTime
            ? timeToMinutes(prevAp.endTime)
            : prevStart + (prevAp.duration || 30);
          slackMinutesBeforeEdited = Math.max(0, originalStartMin - prevEndMin);
        }
        const requestedEarlierMinutes = Math.max(0, originalStartMin - updatedStart);
        const deltaEarlier =
          cascadeIdx > 0
            ? Math.max(0, requestedEarlierMinutes - slackMinutesBeforeEdited)
            : requestedEarlierMinutes;
        const syntheticSorted = chainSorted
          .map((ap, j) => {
            const clone = { ...ap };
            if (clone.id === effectiveId) {
              clone.startTime = updatedAppointmentData.startTime || updatedAppointmentData.time;
              clone.time = clone.startTime;
              clone.duration = newDuration;
              clone.endTime = updatedAppointmentData.endTime;
              return clone;
            }
            const stLow = (clone.status || "").toLowerCase();
            if (j < cascadeIdx && (stLow === "pending" || stLow === "new")) {
              const sm = timeToMinutes(clone.startTime || clone.time);
              const dur = clone.duration || 30;
              const em = clone.endTime ? timeToMinutes(clone.endTime) : sm + dur;
              clone.startTime = formatTime(sm - deltaEarlier);
              clone.time = clone.startTime;
              clone.endTime = formatTime(em - deltaEarlier);
            }
            return clone;
          })
          .sort(
            (a, b) =>
              timeToMinutes(a.startTime || a.time) - timeToMinutes(b.startTime || b.time)
          );

        const { updates: compressBackwardUpdates, exceedsDayEnd } =
          computeCompressibleTimelineShiftUpdates(syntheticSorted, {
            isCompressible: (apt, st) => st === "pending" || st === "new",
          });

        if (!exceedsDayEnd) {
          const finalById = new Map(syntheticSorted.map((a) => [a.id, { ...a }]));
          for (const u of compressBackwardUpdates) {
            const row = finalById.get(u.id);
            if (!row) continue;
            row.startTime = u.startTime;
            row.time = u.startTime;
            row.endTime = u.endTime;
          }

          const batchUpdates = {};
          Object.keys(updatedAppointmentData).forEach((key) => {
            batchUpdates[`${effectiveId}.${key}`] = updatedAppointmentData[key];
          });

          const chainOriginalById = new Map(chainSorted.map((a) => [a.id, a]));

          for (const ap of chainSorted) {
            const stLow = (ap.status || "").toLowerCase();
            if (stLow !== "pending" && stLow !== "new" && ap.id !== effectiveId) continue;
            const fin = finalById.get(ap.id);
            if (!fin) continue;
            const origAp = chainOriginalById.get(ap.id);
            const origStartStr = origAp?.startTime || origAp?.time || "";
            const origDur = origAp?.duration || 30;
            const origEndStr =
              origAp?.endTime ||
              formatTime(timeToMinutes(origStartStr) + origDur);
            const nextStart = fin.startTime || fin.time || "";
            const nextEnd = fin.endTime || "";
            if (!nextStart || !nextEnd) continue;
            const nextDur = Math.max(1, timeToMinutes(nextEnd) - timeToMinutes(nextStart));

            batchUpdates[`${ap.id}.startTime`] = nextStart;
            batchUpdates[`${ap.id}.time`] = nextStart;
            batchUpdates[`${ap.id}.endTime`] = nextEnd;
            batchUpdates[`${ap.id}.duration`] = nextDur;

            if (origStartStr !== nextStart || origEndStr !== nextEnd) {
              appointmentsToAdjust.push({
                id: ap.id,
                startTime: nextStart,
                endTime: nextEnd,
                originalStart: origStartStr,
                originalEnd: origEndStr,
                patientId: ap.patientId || ap.uid || ap.userId,
                date: ap.date || formattedDate,
                insuranceDetails: ap.insuranceDetails,
                hasInsurance: ap.hasInsurance,
                status: ap.status || "Pending",
              });
            }
          }

          await updateDoc(appointmentRef, batchUpdates);
          markTimelineCompressHandledByStaffSave();
          backwardBatchDone = true;
        }
      }
    }

    if (!backwardBatchDone) {
      // Apply the updated appointment first
      if (Object.keys(updates).length > 0) {
        await updateDoc(appointmentRef, updates);
        markTimelineCompressHandledByStaffSave();
      }
    }

    if (
      isCompressiblePendingStatus(updatedAppointmentData.status) &&
      !backwardBatchDone &&
      !chairPlotCascadeActive
    ) {
      
      // Sort appointments by start time to process them in order (exclude completed — legacy cascade)
      const sortedAppointments = [...appointmentsArray]
        .map((appt) => withAppointmentDocDate(appt, formattedDate))
        .filter(appt => {
          if (!appt || typeof appt !== 'object') return false;
          if (
            appt.dentistId !== updatedAppointmentData.dentistId ||
            !appointmentBelongsToDateDoc(appt, formattedDate)
          )
            return false;
          const status = (appt.status || "").toLowerCase();
          return status !== "cancelled" && status !== "completed";
        })
        .sort((a, b) => {
          const aStart = timeToMinutes(a.startTime || a.time);
          const bStart = timeToMinutes(b.startTime || b.time);
          return aStart - bStart;
        });
      
      // Find the updated appointment's position
      const sortedPendingIdx = sortedAppointments.findIndex(appt => appt.id === effectiveId);
      if (sortedPendingIdx !== -1) {
      
      // Process succeeding appointments (only pending ones)
      let currentEndTime = updatedEnd;
      
      for (let i = sortedPendingIdx + 1; i < sortedAppointments.length; i++) {
        const nextAppt = sortedAppointments[i];

        if (!isCompressiblePendingStatus(nextAppt.status)) {
          break;
        }

        const nextStart = timeToMinutes(nextAppt.startTime || nextAppt.time);
        const nextEnd = nextAppt.endTime
          ? timeToMinutes(nextAppt.endTime)
          : nextStart + (nextAppt.duration || 30);
        const shiftSpanMinutes = Math.max(1, nextEnd - nextStart);

        if (nextStart < currentEndTime) {
          const adjustedStart = currentEndTime;
          const adjustedEnd = adjustedStart + shiftSpanMinutes;

          appointmentsToAdjust.push({
            id: nextAppt.id,
            startTime: formatTime(adjustedStart),
            endTime: formatTime(adjustedEnd),
            originalStart: nextAppt.startTime || nextAppt.time,
            originalEnd: nextAppt.endTime || formatTime(nextEnd),
            patientId: nextAppt.patientId || nextAppt.uid || nextAppt.userId,
            date: nextAppt.date || formattedDate,
            insuranceDetails: nextAppt.insuranceDetails,
            hasInsurance: nextAppt.hasInsurance,
            status: nextAppt.status || "Pending",
          });

          currentEndTime = adjustedEnd;
        } else {
          // No overlap (gap or back-to-back) — keep in place, just advance the cursor.
          currentEndTime = nextEnd;
        }
      }

      for (let i = sortedPendingIdx - 1; i >= 0; i--) {
        const prevAppt = sortedAppointments[i];

        if (!isCompressiblePendingStatus(prevAppt.status)) break;

        const prevStart = timeToMinutes(prevAppt.startTime || prevAppt.time);
        const prevDuration = prevAppt.duration || 30;
        const prevEnd = prevAppt.endTime 
          ? timeToMinutes(prevAppt.endTime)
          : (prevStart + prevDuration);
        
        if (updatedStart < prevEnd && prevStart < updatedEnd) {
          const adjustedStart = prevEnd;
          const adjustedEnd = adjustedStart + (updatedEnd - updatedStart);
          
          updates[`${effectiveId}.startTime`] = formatTime(adjustedStart);
          updates[`${effectiveId}.endTime`] = formatTime(adjustedEnd);
          updates[`${effectiveId}.time`] = formatTime(adjustedStart);
          
          appointmentsToAdjust.push({
            id: effectiveId,
            startTime: formatTime(adjustedStart),
            endTime: formatTime(adjustedEnd),
            originalStart: updatedAppointmentData.startTime || updatedAppointmentData.time,
            originalEnd: updatedAppointmentData.endTime || formatTime(updatedEnd),
            patientId: updatedAppointmentData.patientId || updatedAppointmentData.uid || updatedAppointmentData.userId,
            date: formattedDate,
            insuranceDetails: updatedAppointmentData.insuranceDetails,
            hasInsurance: updatedAppointmentData.hasInsurance,
            status: updatedAppointmentData.status || "Pending"
          });
          
          break;
        } else {
          break;
        }
      }
      }
    }
    
    // Apply adjustments to overlapping appointments
    if (appointmentsToAdjust.length > 0) {
      if (!backwardBatchDone) {
        const adjustmentUpdates = {};
        for (const adjustment of appointmentsToAdjust) {
          adjustmentUpdates[`${adjustment.id}.startTime`] = adjustment.startTime;
          adjustmentUpdates[`${adjustment.id}.endTime`] = adjustment.endTime;
          adjustmentUpdates[`${adjustment.id}.time`] = adjustment.startTime;
          const adjStart = timeToMinutes(adjustment.startTime);
          const adjEnd = timeToMinutes(adjustment.endTime);
          if (adjEnd > adjStart) {
            adjustmentUpdates[`${adjustment.id}.duration`] = adjEnd - adjStart;
          }
        }

        // Merge with existing updates
        Object.assign(updates, adjustmentUpdates);

        // Apply all updates
        if (Object.keys(updates).length > 0) {
          await updateDoc(appointmentRef, updates);
          markTimelineCompressHandledByStaffSave();
        }
      }

      // Send notifications (affected patient + assigned dentist + Clinic Staff; app + email)
      const uniqueAdjustments = [];
      const seenIds = new Set();
      for (const adjustment of appointmentsToAdjust) {
        if (!seenIds.has(adjustment.id)) {
          seenIds.add(adjustment.id);
          uniqueAdjustments.push(adjustment);
        }
      }

      const enrichedAdjustments = uniqueAdjustments.map((adjustment) => {
        const apptSource = appointmentsArray.find((a) => a.id === adjustment.id) || {};
        return {
          ...adjustment,
          dentistId:
            adjustment.dentistId ||
            resolveAppointmentDentistUid(apptSource) ||
            updatedAppointmentData.dentistId,
          dentistName: dentistDisplayFromAppointmentSnapshot(apptSource, adjustment.dentistName),
          services: pickServicesFromAppointmentSnapshot(apptSource),
          patientDisplay:
            adjustment.patientDisplay ||
            apptSource.patientDisplay ||
            apptSource.patientName ||
            apptSource.name,
        };
      });
      await sendPendingAppointmentTimeAdjustmentNotificationsBatch(enrichedAdjustments);
      for (const adjustment of enrichedAdjustments) {
        notifiedAppointmentIdsForPendingTimeChange.add(adjustment.id);
      }
    }

    return notifiedAppointmentIdsForPendingTimeChange;
  };

useEffect(() => {
  if (
    !showPendingAppointments &&
    !showConfirmedAppointments &&
    !showAllBookedAppointments
  ) {
    setShowNone(true);
  }
}, [showPendingAppointments, showConfirmedAppointments, showAllBookedAppointments]);

  // Handle start time change and recalculate end time
  const handleStartTimeChange = (newStartTime) => {
    setEditFormValidationMessage("");
    setEditFormData((prevData) => {
      const raw = String(newStartTime ?? "").trim();
      if (!raw) {
        return {
          ...prevData,
          startTime: "",
          time: "",
          endTime: prevData.endTime,
        };
      }
      const startMinutes = timeToMinutes(raw);
      const endMinutes = startMinutes + (prevData.duration || 30);
      const newEndTime = minutesToTime(endMinutes);

      return {
        ...prevData,
        startTime: newStartTime,
        time: newStartTime,
        endTime: newEndTime,
      };
    });
  };

  const handleDurationChange = (newDuration) => {
    setEditFormValidationMessage("");
    setEditFormData((prevData) => {
      const startTime = prevData.startTime || prevData.time;
      const startMinutes = timeToMinutes(startTime);

      if (newDuration === "") {
        const endMinutes = startMinutes + 30;
        return {
          ...prevData,
          duration: "",
          endTime: minutesToTime(endMinutes),
        };
      }

      const trimmed = newDuration.trim();
      // Do not accept "0", "01", etc. — ignore so nothing is entered/clamped from a leading zero.
      if (/^0/.test(trimmed)) {
        return prevData;
      }

      const parsed = parseInt(newDuration, 10);
      if (Number.isNaN(parsed) || parsed < 1) {
        return prevData;
      }

      const duration = parsed;
      const endMinutes = startMinutes + duration;
      const newEndTime = minutesToTime(endMinutes);

      return {
        ...prevData,
        duration,
        endTime: newEndTime,
      };
    });
  };

  // Fetch user details for sidebar profile (search all personnel types)
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const snapshot = await getDoc(userRef);
        if (snapshot.exists()) {
          const userData = snapshot.data();
          setUserDetails(userData);
          
          // Auto-select dentist for DentistOwner and AssociateDentist
          const userRole = userData.role || userData.userType;
          if (userRole === "DentistOwner" || userRole === "AssociateDentist") {
            setSelectedDentist(user.uid);
          }
        }
      }
      setTimeout(() => setShowSplash(false), 1200);
    });
    return () => {
      unsubscribeAuth();
    };
  }, [auth]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('[data-menu-container]')) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  // Function to handle logout
  const handleLogout = () => {
    signOut(auth).then(() => {
      navigate('/', { replace: true });
    });
  };

  // Function to handle menu toggle
  const toggleMenu = (appointmentId) => {
    setOpenMenuId(openMenuId === appointmentId ? null : appointmentId);
  };

  // Function to close menu when clicking outside
  const closeMenu = () => {
    setOpenMenuId(null);
  };

  // Check if any dentist has pending/new appointments
  const anyDentistHasPending = Object.values(pendingCounts).some((count) => count > 0);

  // Get symbol for the selected dentist if they have pending/new appointments
  const selectedDentistHasPending = pendingCounts[selectedDentist] > 0;

  // Sort appointments by time (ascending) - removed unavailable slots from left panel
  const sortedAppointments = [...filteredAppointments].sort((a, b) => {
    // Get the time field to use for sorting (prefer startTime, fallback to time)
    const aTime = a.startTime || a.time;
    const bTime = b.startTime || b.time;
    
    if (!aTime) return 1;
    if (!bTime) return -1;
    
    const aMinutes = timeToMinutes(aTime);
    const bMinutes = timeToMinutes(bTime);
    return aMinutes - bMinutes;
  });

  const boxStylesWrapper = {
    // width: "100%",
    backgroundColor: "#FDF9F3",
    justifyContent: "center",
    alignItems: "center",
  }

  const boxStylesContent = {
    width: "100%",
    maxWidth: "1740px",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "calc(100vh - 61px)",
    backgroundColor: "#FDF9F3",
  }

  const styleText = {
    color: '#2C251E',
    fontWeight: {
      xs : '600',
      sm : '600',
      md : '100',
      lg : '100',
      xl : '100',
    },
    fontSize: '14px',
    padding: '5px 0',
  };

  const accordionStylesFaqs = {
    margin: "10px auto",
    width: "100%",
    transition:"0.2s ease-in-out",
    // backgroundColor: "transparent",
    "&:hover": {
        // padding: "10px 10px",
        backgroundColor: "transparent",
    },               
  }

  // ===== Appointment Chair Conflict Prevention =====
  // All helpers below layer chair-capacity rules (Rule 1), replacement
  // pairing (Rule 2) and appointment-conflict prevention (Rule 3) on top of
  // existing slot-toggle behavior. Triggered only when chair settings have
  // been configured (Staff sets chairCount + roster); otherwise we no-op.
  const isStaffUser = (() => {
    if (!userDetails) return false;
    const role = userDetails.role || userDetails.userType;
    return role === "ClinicStaff";
  })();

  const dentistDisplayLabel = useCallback(
    (uid) => {
      if (!uid) return "Unknown Dentist";
      return getDentistDisplayNameFromUID(uid);
    },
    [getDentistDisplayNameFromUID]
  );

  const dentistUidsKnown = useMemo(
    () => (Array.isArray(dentists) ? dentists.map((d) => d.uid).filter(Boolean) : []),
    [dentists]
  );
  const maxChairCount = dentistUidsKnown.length;
  const clampedChairCount = useMemo(() => {
    const current = Number(chairSettings?.chairCount) || 0;
    if (maxChairCount <= 0) return 0;
    if (current <= 0) return 0;
    return Math.min(current, maxChairCount);
  }, [chairSettings?.chairCount, maxChairCount]);

  const formattedSelectedDateStr = useMemo(() => {
    if (!selectedModalDate) return "";
    const y = selectedModalDate.getFullYear();
    const m = String(selectedModalDate.getMonth() + 1).padStart(2, "0");
    const d = String(selectedModalDate.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [selectedModalDate]);

  const appointmentsForSelectedDate = useMemo(() => {
    if (!formattedSelectedDateStr || !Array.isArray(allAppointments)) return [];
    return allAppointments.filter((a) => a && a.date === formattedSelectedDateStr);
  }, [allAppointments, formattedSelectedDateStr]);

  const secondaryScheduleBlockOptions = useMemo(
    () => [
      { label: "9:00 AM - 6:00 PM", start: 9 * 60, end: 18 * 60 },
      { label: "9:00 AM - 1:30 PM", start: 9 * 60, end: 13 * 60 + 30 },
      { label: "1:30 PM - 6:00 PM", start: 13 * 60 + 30, end: 18 * 60 },
    ],
    []
  );

  const standardHalfHourLabels = useMemo(() => buildStandardHalfHourSlotLabels({}), []);

  const getSecondaryBlockByLabel = useCallback(
    (label) => secondaryScheduleBlockOptions.find((block) => block.label === label) || null,
    [secondaryScheduleBlockOptions]
  );

  const getSlotsForSecondaryBlock = useCallback(
    (label) => {
      const block = getSecondaryBlockByLabel(label);
      if (!block) return [];
      return standardHalfHourLabels.filter((slotLabel) => {
        const range = parseChairSlotLabelToRange(slotLabel, timeToMinutes);
        if (!range) return false;
        return range.start >= block.start && range.end <= block.end;
      });
    },
    [getSecondaryBlockByLabel, standardHalfHourLabels]
  );

  const normalizedChairSwapsForDate = useMemo(
    () => normalizeChairSwaps(chairSwapsBySlotForDate),
    [chairSwapsBySlotForDate]
  );

  const rosterSecondaryAssignmentSummaryForDate = useMemo(() => {
    const byRoster = {};
    const bySecondary = {};
    const bySecondarySlots = {};
    Object.entries(normalizedChairSwapsForDate || {}).forEach(([slotKey, entry]) => {
      const rosterUid = entry?.fromRosterUid;
      const secondaryUid = entry?.toBenchUid;
      const slotLabel = entry?.slot || slotKey;
      if (!rosterUid || !secondaryUid) return;
      if (!slotLabel || typeof slotLabel !== "string") return;
      if (!byRoster[rosterUid]) {
        byRoster[rosterUid] = { secondaryUids: new Set(), slotsBySecondary: {} };
      }
      byRoster[rosterUid].secondaryUids.add(secondaryUid);
      if (!byRoster[rosterUid].slotsBySecondary[secondaryUid]) {
        byRoster[rosterUid].slotsBySecondary[secondaryUid] = new Set();
      }
      byRoster[rosterUid].slotsBySecondary[secondaryUid].add(slotLabel);
      if (!bySecondary[secondaryUid]) {
        bySecondary[secondaryUid] = new Set();
      }
      bySecondary[secondaryUid].add(rosterUid);
      if (!bySecondarySlots[secondaryUid]) {
        bySecondarySlots[secondaryUid] = new Set();
      }
      bySecondarySlots[secondaryUid].add(slotLabel);
    });
    return { byRoster, bySecondary, bySecondarySlots };
  }, [normalizedChairSwapsForDate]);

  const getRosterLinkedSecondaryUid = useCallback(
    (rosterUid) => {
      const rosterSummary = rosterSecondaryAssignmentSummaryForDate.byRoster?.[rosterUid];
      if (!rosterSummary) return null;
      const linked = Array.from(rosterSummary.secondaryUids || []);
      return linked.length === 1 ? linked[0] : null;
    },
    [rosterSecondaryAssignmentSummaryForDate]
  );

  const getRosterSecondarySlots = useCallback(
    (rosterUid, secondaryUid = null) => {
      const rosterSummary = rosterSecondaryAssignmentSummaryForDate.byRoster?.[rosterUid];
      if (!rosterSummary) return [];
      const targetSecondary = secondaryUid || getRosterLinkedSecondaryUid(rosterUid);
      if (!targetSecondary) return [];
      const slots = rosterSummary.slotsBySecondary?.[targetSecondary];
      return Array.from(slots || []);
    },
    [rosterSecondaryAssignmentSummaryForDate, getRosterLinkedSecondaryUid]
  );

  const detectBlockLabelFromSlots = useCallback(
    (slots = []) => {
      const parsedRanges = (Array.isArray(slots) ? slots : [])
        .map((slot) => parseChairSlotLabelToRange(slot, timeToMinutes))
        .filter(Boolean);
      if (parsedRanges.length === 0) return "";
      const minStart = Math.min(...parsedRanges.map((r) => r.start));
      const maxEnd = Math.max(...parsedRanges.map((r) => r.end));
      const hasMorningCoverage = minStart <= 9 * 60 && maxEnd >= 13 * 60 + 30;
      const hasAfternoonCoverage = minStart <= 13 * 60 + 30 && maxEnd >= 18 * 60;
      if (hasMorningCoverage && hasAfternoonCoverage) return "9:00 AM - 6:00 PM";
      if (hasMorningCoverage && maxEnd <= 13 * 60 + 30) return "9:00 AM - 1:30 PM";
      if (minStart >= 13 * 60 + 30 && hasAfternoonCoverage) return "1:30 PM - 6:00 PM";
      const incoming = new Set(slots);
      const exact = secondaryScheduleBlockOptions.find((block) => {
        const expected = getSlotsForSecondaryBlock(block.label);
        if (expected.length !== incoming.size) return false;
        return expected.every((slot) => incoming.has(slot));
      });
      return exact ? exact.label : "";
    },
    [
      parseChairSlotLabelToRange,
      timeToMinutes,
      secondaryScheduleBlockOptions,
      getSlotsForSecondaryBlock,
    ]
  );

  const getActiveAppointmentOverlapsForDentist = useCallback(
    ({ dentistUid, slots = [] }) => {
      if (!dentistUid || !Array.isArray(slots) || slots.length === 0) return [];
      const slotRanges = slots
        .map((slot) => parseChairSlotLabelToRange(slot, timeToMinutes))
        .filter(Boolean);
      if (slotRanges.length === 0) return [];
      return (appointmentsForSelectedDate || []).filter((appointment) => {
        if (!appointment) return false;
        if (!isBlockingAppointmentStatus(appointment.status)) {
          return false;
        }
        const uid = resolveAppointmentDentistUid(appointment);
        if (uid !== dentistUid) return false;
        const apptRange = parseAppointmentRangeMinutes(appointment, timeToMinutes);
        if (!apptRange) return false;
        return slotRanges.some((slotRange) => slotRange.start < apptRange.end && slotRange.end > apptRange.start);
      });
    },
    [
      appointmentsForSelectedDate,
      parseChairSlotLabelToRange,
      resolveAppointmentDentistUid,
      timeToMinutes,
    ]
  );

  const currentAssignedSecondaryUidForSelectedRoster = useMemo(
    () => getRosterLinkedSecondaryUid(selectedModalDentist),
    [getRosterLinkedSecondaryUid, selectedModalDentist]
  );
  const linkedRosterUidForSelectedSecondary = useMemo(() => {
    if (!isChairCapacityConfigured(chairSettings)) return null;
    if (!selectedModalDentist) return null;
    if (isDentistOnRoster(selectedModalDentist, chairSettings)) return null;
    const linkedRosterSet =
      rosterSecondaryAssignmentSummaryForDate.bySecondary?.[selectedModalDentist];
    const linkedRosterUids = Array.from(linkedRosterSet || []);
    return linkedRosterUids.length === 1 ? linkedRosterUids[0] : null;
  }, [
    chairSettings,
    selectedModalDentist,
    rosterSecondaryAssignmentSummaryForDate,
  ]);

  const assignSecondaryDentistOptions = useMemo(() => {
    if (!isChairCapacityConfigured(chairSettings)) return [];
    if (!selectedModalDentist) return [];
    const rosterSet = new Set(
      Array.isArray(chairSettings?.rosterDentistIds) ? chairSettings.rosterDentistIds : []
    );
    const linkedCurrentSecondaryUid = getRosterLinkedSecondaryUid(selectedModalDentist);
    if (linkedCurrentSecondaryUid) {
      return (Array.isArray(dentists) ? dentists : []).filter(
        (dentist) => dentist?.uid === linkedCurrentSecondaryUid
      );
    }
    const effectiveBlockLabel =
      assignSecondaryBlockLabel ||
      (secondaryScheduleBlockOptions.length > 0
        ? secondaryScheduleBlockOptions[0].label
        : "");
    const targetBlockRanges = getSlotsForSecondaryBlock(effectiveBlockLabel)
      .map((slot) => parseChairSlotLabelToRange(slot, timeToMinutes))
      .filter(Boolean);
    return (Array.isArray(dentists) ? dentists : []).filter((dentist) => {
      if (!dentist?.uid) return false;
      if (dentist.uid === selectedModalDentist) return false;
      if (rosterSet.has(dentist.uid)) return false;
      const linkedRosterSet = rosterSecondaryAssignmentSummaryForDate.bySecondary?.[dentist.uid];
      const linkedSlotsSet =
        rosterSecondaryAssignmentSummaryForDate.bySecondarySlots?.[dentist.uid];
      if (!linkedRosterSet || linkedRosterSet.size === 0 || !linkedSlotsSet) return true;
      const linkedSlotRanges = Array.from(linkedSlotsSet)
        .map((slot) => parseChairSlotLabelToRange(slot, timeToMinutes))
        .filter(Boolean);
      if (targetBlockRanges.length === 0 || linkedSlotRanges.length === 0) {
        return false;
      }
      const hasOverlap = linkedSlotRanges.some((linkedRange) =>
        targetBlockRanges.some(
          (targetRange) =>
            linkedRange.start < targetRange.end && linkedRange.end > targetRange.start
        )
      );
      return !hasOverlap;
    });
  }, [
    chairSettings,
    selectedModalDentist,
    dentists,
    assignSecondaryBlockLabel,
    secondaryScheduleBlockOptions,
    getSlotsForSecondaryBlock,
    parseChairSlotLabelToRange,
    timeToMinutes,
    getRosterLinkedSecondaryUid,
    rosterSecondaryAssignmentSummaryForDate,
  ]);

  const openRosterModal = () => {
    const activeSet = new Set(dentistUidsKnown);
    const sanitizedRoster = (Array.isArray(chairSettings.rosterDentistIds) ? chairSettings.rosterDentistIds : []).filter(
      (uid) => activeSet.has(uid)
    );
    const count = clampedChairCount || maxChairCount || 0;
    setRosterDraftCount(count);
    setRosterDraftIds(sanitizedRoster.slice(0, count));
    setRosterSavingError(null);
    setShowRosterModal(true);
  };

  // Auto-clamp chair settings when dentist accounts are deleted so the
  // selectable chair range always matches active dentists (e.g. 1-4 only when
  // 4 dentists remain). This is Staff-only because rules enforce Staff writes.
  useEffect(() => {
    if (!isStaffUser) return;
    const activeSet = new Set(dentistUidsKnown);
    const currentCount = Number(chairSettings?.chairCount) || 0;
    const currentRoster = Array.isArray(chairSettings?.rosterDentistIds)
      ? chairSettings.rosterDentistIds
      : [];
    const filteredRoster = currentRoster.filter((uid) => activeSet.has(uid));

    let nextCount = currentCount;
    if (maxChairCount <= 0) {
      nextCount = 0;
    } else if (currentCount > maxChairCount) {
      nextCount = maxChairCount;
    }
    const nextRoster = filteredRoster.slice(0, Math.max(0, nextCount));

    const rosterChanged =
      nextRoster.length !== currentRoster.length ||
      nextRoster.some((uid, idx) => uid !== currentRoster[idx]);
    const countChanged = nextCount !== currentCount;
    if (!rosterChanged && !countChanged) return;

    const ref = doc(db, ...CHAIR_SETTINGS_DOC_PATH);
    setDoc(
      ref,
      {
        chairCount: nextCount,
        rosterDentistIds: nextRoster,
        updatedAt: new Date().toISOString(),
        updatedByUid: auth.currentUser ? auth.currentUser.uid : null,
      },
      { merge: true }
    ).catch((err) => {
      console.error("Error auto-syncing chair settings after dentist updates:", err);
    });
  }, [
    isStaffUser,
    dentistUidsKnown,
    maxChairCount,
    chairSettings?.chairCount,
    chairSettings?.rosterDentistIds,
    auth,
  ]);

  const toggleRosterDraft = (uid) => {
    setRosterDraftIds((prev) => {
      if (prev.includes(uid)) {
        return prev.filter((x) => x !== uid);
      }
      if (rosterDraftCount > 0 && prev.length >= rosterDraftCount) {
        return prev;
      }
      return [...prev, uid];
    });
  };

  const handleSaveRoster = async () => {
    const count = Number(rosterDraftCount) || 0;
    if (count <= 0) {
      setRosterSavingError("Please choose a valid number of chairs.");
      return;
    }
    if (rosterDraftIds.length !== count) {
      setRosterSavingError(`Please select exactly ${count} dentist${count === 1 ? "" : "s"} for the chair roster.`);
      return;
    }
    const previousRoster = Array.isArray(chairSettings.rosterDentistIds)
      ? chairSettings.rosterDentistIds
      : [];
    const removedFromRoster = previousRoster.filter((uid) => !rosterDraftIds.includes(uid));

    // Run appointment checks before isSavingRoster so Save does not flicker (matches count validation UX).
    if (removedFromRoster.length > 0) {
      try {
        const appointmentsSnap = await getDocs(collection(db, "appointments"));
        const todayStr = getLocalDateKey(new Date());
        const removalBlocks = computeRosterRemovalAppointmentBlocks(
          removedFromRoster,
          appointmentsSnap,
          todayStr,
          resolveAppointmentDentistUid
        );
        if (removalBlocks.length > 0) {
          setRosterSavingError({ removalBlocks });
          return;
        }
      } catch (readErr) {
        console.error("Error loading appointments for roster validation:", readErr);
        setRosterSavingError(
          "Could not verify existing appointments. Check your network connection and try again."
        );
        return;
      }
    }

    setRosterSavingError(null);
    setIsSavingRoster(true);
    try {
      const ref = doc(db, ...CHAIR_SETTINGS_DOC_PATH);
      await setDoc(
        ref,
        {
          chairCount: count,
          rosterDentistIds: rosterDraftIds,
          updatedAt: new Date().toISOString(),
          updatedByUid: auth.currentUser ? auth.currentUser.uid : null,
        },
        { merge: true }
      );

      // One-time consistency sync: dentists newly included in the active chair
      // roster should not remain flagged as "new / unavailable by default".
      const previousSet = new Set(previousRoster);
      const newlyIncluded = rosterDraftIds.filter((uid) => !previousSet.has(uid));
      if (newlyIncluded.length > 0) {
        try {
          const unavailableSnap = await getDocs(collection(db, "unavailableTimeSlots"));
          let docsTouched = 0;
          for (const unavailableDoc of unavailableSnap.docs) {
            const dateId = unavailableDoc.id;
            const [year, month, day] = dateId.split("-").map((n) => Number(n));
            if (!year || !month || !day) continue;
            const dateObj = new Date(year, month - 1, day);
            const expectedFullDaySlots = generateTimeSlotsForDate(dateObj);
            const unavailableData = unavailableDoc.data() || {};
            let dateAppointmentsData = null;
            const updates = {};
            for (const uid of newlyIncluded) {
              const existingSlots = unavailableData[uid];
              if (!Array.isArray(existingSlots)) continue;
              const isFullDayFallback =
                existingSlots.length === expectedFullDaySlots.length &&
                expectedFullDaySlots.every((slot) => existingSlots.includes(slot));
              if (!isFullDayFallback) continue;
              if (dateAppointmentsData === null) {
                const appointmentsSnap = await getDoc(doc(db, "appointments", dateId));
                dateAppointmentsData = appointmentsSnap.exists() ? appointmentsSnap.data() : {};
              }
              const hasAppointmentsForDentist = Object.values(dateAppointmentsData || {}).some(
                (appointment) => resolveAppointmentDentistUid(appointment) === uid
              );
              if (!hasAppointmentsForDentist) {
                updates[uid] = deleteField();
              }
            }
            if (Object.keys(updates).length > 0) {
              await updateDoc(doc(db, "unavailableTimeSlots", dateId), updates);
              docsTouched += 1;
            }
          }
        } catch (cleanupErr) {
          console.warn("Non-blocking stale unavailable slot cleanup warning:", cleanupErr);
        }
      }
      if (newlyIncluded.length > 0) {
        try {
          await Promise.all(
            newlyIncluded.map((uid) =>
              setDoc(
                doc(db, "users", uid),
                { hasScheduleSetup: true },
                { merge: true }
              )
            )
          );
        } catch (syncErr) {
          // Do not fail chair roster save when this optional sync is blocked by
          // Firestore rules for ClinicStaff. Chair config is already persisted.
          console.warn("Non-blocking roster sync warning:", syncErr);
        }
      }
      setRosterSavingError(null);
      setShowRosterModal(false);
      setChairSnackbar({
        open: true,
        message: `Chair roster saved (${count} chair${count === 1 ? "" : "s"}).`,
        severity: "success",
      });
    } catch (err) {
      console.error("Error saving chair roster:", err);
      const code = err?.code || "";
      if (code === "permission-denied") {
        setRosterSavingError(
          "Firestore denied this save (permission denied). Publish the clinicScheduling rules from your repo’s firestore.rules to Firebase (Firestore → Rules → Publish). Until that block exists, writes to clinicScheduling/settings are blocked. Only Clinic Staff users may save the roster."
        );
      } else if (code === "unavailable") {
        setRosterSavingError("Could not reach Firebase. Check your network connection and try again.");
      } else {
        setRosterSavingError(
          `Could not save the chair roster${code ? ` (${code})` : ""}. Check the browser console for details.`
        );
      }
    } finally {
      setIsSavingRoster(false);
    }
  };

  // Build a context object describing per-slot dropdown options for the
  // Replacement modal (Scenarios 1, 2, 3, 5).
  const getChairSlotUniverseForFlow = useCallback(
    (primaryDentistUid, slots = []) => {
      const fromDentistTimeline = buildSelectedDateSlotUniverseForDentist(primaryDentistUid);
      return Array.from(new Set([...(fromDentistTimeline || []), ...(slots || [])]));
    },
    [buildSelectedDateSlotUniverseForDentist]
  );

  const parseSlotLabelToRange = useCallback(
    (slotLabel) => {
      if (!slotLabel || typeof slotLabel !== "string") return null;
      const [startRaw, endRaw] = slotLabel.split(" - ").map((part) => part.trim());
      if (!startRaw || !endRaw) return null;
      const start = timeToMinutes(startRaw);
      const end = timeToMinutes(endRaw);
      if (
        typeof start !== "number" ||
        typeof end !== "number" ||
        Number.isNaN(start) ||
        Number.isNaN(end) ||
        end <= start
      ) {
        return null;
      }
      return { start, end };
    },
    [timeToMinutes]
  );

  const doRangesOverlap = useCallback((a, b) => {
    if (!a || !b) return false;
    return a.start < b.end && a.end > b.start;
  }, []);

  const getBenchLinkedRosterUidsForSlotRange = useCallback(
    ({ benchUid, slot }) => {
      if (!benchUid || !slot) return [];
      const sourceRange = parseSlotLabelToRange(slot);
      if (!sourceRange) return [];
      return Array.from(
        new Set(
          Object.entries(chairSwapsBySlotForDate || {})
            .filter(([, entry]) => entry?.toBenchUid === benchUid && entry?.fromRosterUid)
            .filter(([, entry]) =>
              doRangesOverlap(sourceRange, parseSlotLabelToRange(entry?.slot || ""))
            )
            .map(([, entry]) => entry.fromRosterUid)
        )
      );
    },
    [chairSwapsBySlotForDate, doRangesOverlap, parseSlotLabelToRange]
  );

  const getBenchLinkedSwapSlotsForSlotRange = useCallback(
    ({ benchUid, slot }) => {
      if (!benchUid || !slot) return [];
      const sourceRange = parseSlotLabelToRange(slot);
      if (!sourceRange) return [];
      return Object.entries(chairSwapsBySlotForDate || {})
        .filter(([, entry]) => entry?.toBenchUid === benchUid)
        .map(([, entry]) => entry?.slot)
        .filter(Boolean)
        .filter((swapSlot) =>
          doRangesOverlap(sourceRange, parseSlotLabelToRange(swapSlot))
        );
    },
    [chairSwapsBySlotForDate, doRangesOverlap, parseSlotLabelToRange]
  );

  // Resolve a dentist's effective unavailability for a given row, treating
  // bench dentists with no record as blocked (chair-mode default) and trusting
  // the persisted array for known records.
  const isDentistEffectivelyUnavailableForRow = useCallback(
    ({ uid, slot, lookup }) => {
      const entry = lookup?.[uid];
      const hasRecord = Array.isArray(entry);
      const onRoster = isDentistOnRoster(uid, chairSettings);
      if (!isChairCapacityConfigured(chairSettings)) {
        return hasRecord ? entry.includes(slot) : false;
      }
      if (onRoster) {
        return hasRecord ? entry.includes(slot) : false;
      }
      if (!hasRecord) return true;
      return entry.includes(slot);
    },
    [chairSettings]
  );

  const buildReplacementContext = ({ dentistUid, slots, dateStr }) => {
    if (!dentistUid || !Array.isArray(slots) || !dateStr) return null;
    const lookup = unavailableSlotsByDentistForDate;
    const actingIsRoster = isDentistOnRoster(dentistUid, chairSettings);
    const isBenchLinkedToOtherRosterForDate = (benchUid, rosterUid) => {
      if (!benchUid || !rosterUid) return false;
      const links = Object.values(chairSwapsBySlotForDate || {}).filter(
        (entry) => entry?.toBenchUid === benchUid && entry?.fromRosterUid
      );
      if (links.length === 0) return false;
      return links.some((entry) => entry.fromRosterUid !== rosterUid);
    };
    const benchDentists = dentistUidsKnown.filter(
      (uid) => uid !== dentistUid && !isDentistOnRoster(uid, chairSettings)
    );
    const linkedBenchUidsForRosterDate = actingIsRoster
      ? Array.from(
          new Set(
            Object.values(chairSwapsBySlotForDate || {})
              .filter((entry) => entry?.fromRosterUid === dentistUid && entry?.toBenchUid)
              .map((entry) => entry.toBenchUid)
          )
        )
      : [];
    const rosterDentists = dentistUidsKnown.filter(
      (uid) => uid !== dentistUid && isDentistOnRoster(uid, chairSettings)
    );
    const rows = slots.map((slot) => {
      const linkedBenchCandidates = actingIsRoster
        ? Array.from(
            new Set(
              Object.entries(chairSwapsBySlotForDate || {})
                .filter(([, entry]) => entry?.fromRosterUid === dentistUid && entry?.toBenchUid)
                .filter(([, entry]) =>
                  doRangesOverlap(
                    parseSlotLabelToRange(slot),
                    parseSlotLabelToRange(entry?.slot || "")
                  )
                )
                .map(([, entry]) => entry.toBenchUid)
            )
          )
        : [];
      const linkedBenchUid =
        linkedBenchCandidates.length === 1
          ? linkedBenchCandidates[0]
          : getLinkedBenchForRosterSlot({
              swapsBySlot: chairSwapsBySlotForDate,
              rosterUid: dentistUid,
              slot,
            });
      const linkedRosterCandidates = !actingIsRoster
        ? getBenchLinkedRosterUidsForSlotRange({ benchUid: dentistUid, slot })
        : [];
      const linkedRosterUid =
        linkedRosterCandidates.length === 1
          ? linkedRosterCandidates[0]
          : getLinkedRosterForBenchSlot({
              swapsBySlot: chairSwapsBySlotForDate,
              benchUid: dentistUid,
              slot,
            });
      const affectedSwapSlots = !actingIsRoster
        ? getBenchLinkedSwapSlotsForSlotRange({ benchUid: dentistUid, slot })
        : [];
      const compatibleBench = benchDentists.filter((uid) => {
        if (
          actingIsRoster &&
          uid !== linkedBenchUid &&
          isBenchLinkedToOtherRosterForDate(uid, dentistUid)
        ) {
          return false;
        }
        if (uid === linkedBenchUid) return true;
        // Full-day linked roster↔bench: a bench dentist linked via chairSwaps for
        // this roster dentist on this date remains a valid replacement even when
        // that bench row appears “available” here (opposite timeline), since swap
        // rows may not overlap arbitrary generated labels (e.g. 10:16–10:46).
        if (
          actingIsRoster &&
          linkedBenchUidsForRosterDate.includes(uid)
        ) {
          return true;
        }
        return isDentistEffectivelyUnavailableForRow({ uid, slot, lookup });
      });
      let replacementOptions = [];
      if (actingIsRoster) {
        if (linkedBenchUidsForRosterDate.length > 0) {
          replacementOptions = compatibleBench.filter((uid) =>
            linkedBenchUidsForRosterDate.includes(uid)
          );
        } else {
          replacementOptions = compatibleBench;
        }
      } else {
        const reciprocalRoster = linkedRosterUid ? [linkedRosterUid] : [];
        const compatibleRoster = rosterDentists.filter((uid) =>
          isDentistEffectivelyUnavailableForRow({ uid, slot, lookup })
        );
        // Bench dentist flow (vice-versa):
        // if this bench slot is already linked, only the linked roster dentist
        // should be offered as replacement candidate.
        replacementOptions =
          reciprocalRoster.length > 0
            ? reciprocalRoster
            : Array.from(new Set([...compatibleRoster]));
      }
      return {
        slot,
        replacementOptions,
        affectedSwapSlots,
        allowNone: true,
      };
    });
    return { dentistUid, dateStr, rows };
  };

  const buildRemovalContext = ({ targetDentistUid, slots, dateStr, projectedLookup }) => {
    if (!targetDentistUid || !Array.isArray(slots) || !dateStr) return null;
    const lookup = projectedLookup || unavailableSlotsByDentistForDate;
    const targetIsRoster = isDentistOnRoster(targetDentistUid, chairSettings);
    // Bench dentists mirror a roster counterpart's day via swaps; counterparty is
    // the set of roster UIDs appearing in any swap for this bench that date —
    // not every roster dentist who happens to be available for this row (which
    // incorrectly listed unrelated roster dentists when swap keys did not
    // overlap the clicked slot, e.g. 9:00–9:30 vs 9:30–10:00).
    const benchPartnerRosterUids = !targetIsRoster
      ? Array.from(
          new Set(
            Object.values(chairSwapsBySlotForDate || {})
              .filter((entry) => entry?.toBenchUid === targetDentistUid && entry?.fromRosterUid)
              .map((entry) => entry.fromRosterUid)
          )
        )
      : [];
    const rows = slots.map((slot) => {
      const linkedBenchCandidates = targetIsRoster
        ? Array.from(
            new Set(
              Object.entries(chairSwapsBySlotForDate || {})
                .filter(([, entry]) => entry?.fromRosterUid === targetDentistUid && entry?.toBenchUid)
                .filter(([, entry]) =>
                  doRangesOverlap(
                    parseSlotLabelToRange(slot),
                    parseSlotLabelToRange(entry?.slot || "")
                  )
                )
                .map(([, entry]) => entry.toBenchUid)
            )
          )
        : [];
      const linkedBenchUid =
        linkedBenchCandidates.length === 1
          ? linkedBenchCandidates[0]
          : getLinkedBenchForRosterSlot({
              swapsBySlot: chairSwapsBySlotForDate,
              rosterUid: targetDentistUid,
              slot,
            });
      const linkedRosterCandidates = !targetIsRoster
        ? getBenchLinkedRosterUidsForSlotRange({ benchUid: targetDentistUid, slot })
        : [];
      const linkedRosterUid =
        linkedRosterCandidates.length === 1
          ? linkedRosterCandidates[0]
          : getLinkedRosterForBenchSlot({
              swapsBySlot: chairSwapsBySlotForDate,
              benchUid: targetDentistUid,
              slot,
            });
      let removableOptions = [];
      if (targetIsRoster && linkedBenchUid) {
        removableOptions = [linkedBenchUid];
      } else if (!targetIsRoster) {
        if (linkedRosterUid) {
          removableOptions = [linkedRosterUid];
        } else if (benchPartnerRosterUids.length === 1) {
          removableOptions = [...benchPartnerRosterUids];
        } else if (benchPartnerRosterUids.length > 1) {
          const overlapLinked = getBenchLinkedRosterUidsForSlotRange({
            benchUid: targetDentistUid,
            slot,
          });
          const narrowed = overlapLinked.filter((uid) => benchPartnerRosterUids.includes(uid));
          removableOptions = narrowed.length > 0 ? narrowed : [...benchPartnerRosterUids];
        } else {
          removableOptions = dentistUidsKnown.filter((uid) => {
            if (uid === targetDentistUid) return false;
            return !isDentistEffectivelyUnavailableForRow({
              uid,
              slot,
              lookup,
            });
          });
        }
      } else {
        removableOptions = dentistUidsKnown.filter((uid) => {
          if (uid === targetDentistUid) return false;
          return !isDentistEffectivelyUnavailableForRow({
            uid,
            slot,
            lookup,
          });
        });
      }
      return { slot, removableOptions };
    });
    return { targetDentistUid, dateStr, rows };
  };

  const writeChairSwapsForDate = async ({ dateStr, swapsBySlot }) => {
    const normalized = normalizeChairSwaps(swapsBySlot || {});
    const unavailableRef = doc(db, "unavailableTimeSlots", dateStr);
    const swapsRef = doc(db, CHAIR_SWAPS_COLLECTION, dateStr);
    const batch = writeBatch(db);
    // Replace `slots` map (not merge) so removed swap links are actually deleted.
    // Using merge here keeps stale slot keys forever.
    batch.set(
      swapsRef,
      {
        slots: normalized,
        updatedAt: new Date().toISOString(),
      },
      { merge: false }
    );
    return { batch, unavailableRef };
  };

  const persistChairSchedulingForDate = async ({
    dateStr,
    projectedLookup,
    swapsBySlot,
    touchedDentistUids = [],
    settings,
  }) => {
    const { batch, unavailableRef } = await writeChairSwapsForDate({
      dateStr,
      swapsBySlot,
    });
    const payload = {};
    const inChairMode = isChairCapacityConfigured(settings);
    const touchedSet = new Set(Array.isArray(touchedDentistUids) ? touchedDentistUids : []);
    Object.entries(projectedLookup || {}).forEach(([uid, value]) => {
      if (!Array.isArray(value)) return;
      // In chair mode, avoid persisting non-roster blocked-baseline arrays.
      // Bench availability is represented by chairSwaps source-of-truth.
      if (inChairMode && !isDentistOnRoster(uid, settings)) {
        if (touchedSet.has(uid)) {
          payload[uid] = deleteField();
        }
        return;
      }
      payload[uid] = value;
    });
    batch.set(unavailableRef, payload, { merge: true });
    await batch.commit();
  };

  const buildAppointmentConflictRowErrors = (conflicts = []) => {
    const next = {};
    conflicts.forEach((record) => {
      if (!record?.slot || next[record.slot]) return;
      const conflictDentist = dentistDisplayLabel(record.dentistUid);
      next[record.slot] = `${conflictDentist} already has a patient appointment that conflicts with the selected timeslot. Please cancel the patient appointment first.`;
    });
    return next;
  };

  // Swap docs use discrete keys (often half-hour). UI may show irregular gaps
  // (e.g. 11:16–11:46). Mutate every overlapping swap key, but apply Rule 1 / Rule 3
  // using the UI slot string only — otherwise findAppointmentConflicts blocks saves
  // when a broader swap row (e.g. 11:00–11:30) overlaps an earlier appointment end.
  const resolveChairSwapKeysOverlappingSlot = useCallback(
    (dentistUid, slotLabel) => {
      if (!dentistUid || !slotLabel) return [slotLabel];
      const range = parseSlotLabelToRange(slotLabel);
      if (!range) return [slotLabel];
      if (isDentistOnRoster(dentistUid, chairSettings)) {
        const keys = Object.entries(chairSwapsBySlotForDate || {})
          .filter(([, e]) => e?.fromRosterUid === dentistUid && e?.toBenchUid)
          .filter(([, entry]) => {
            const sr = parseSlotLabelToRange(entry?.slot || "");
            return sr && doRangesOverlap(range, sr);
          })
          .map(([, entry]) => entry?.slot)
          .filter(Boolean);
        return keys.length > 0 ? Array.from(new Set(keys)) : [slotLabel];
      }
      const keys = getBenchLinkedSwapSlotsForSlotRange({
        benchUid: dentistUid,
        slot: slotLabel,
      });
      return keys.length > 0 ? keys : [slotLabel];
    },
    [
      chairSettings,
      chairSwapsBySlotForDate,
      parseSlotLabelToRange,
      doRangesOverlap,
      getBenchLinkedSwapSlotsForSlotRange,
    ]
  );

  const handleSaveReplacement = async () => {
    if (!replacementContext) return;
    const { dentistUid, dateStr, rows } = replacementContext;
    const rowSlotTargets = rows.map((row) => ({
      rowSlot: row.slot,
      swapTargetsEffective: resolveChairSwapKeysOverlappingSlot(dentistUid, row.slot),
      availabilityTargets: [row.slot],
    }));
    const capacitySlotsToValidate = Array.from(new Set(rows.map((r) => r.slot)));
    const slotUniverseForFlow = Array.from(
      new Set([
        ...capacitySlotsToValidate,
        ...rowSlotTargets.flatMap((e) => e.swapTargetsEffective),
      ])
    );
    const recognizedSlots = getChairSlotUniverseForFlow(
      dentistUid,
      slotUniverseForFlow
    );
    setReplacementRowErrors({});
    const invalidSelections = rows.filter((r) => {
      const replacement = replacementSelections[r.slot];
      if (!replacement || replacement === "__none__") return false;
      return !r.replacementOptions.includes(replacement);
    });
    if (invalidSelections.length > 0) {
      setChairSnackbar({
        open: true,
        message:
          "One or more selected replacements are no longer valid for the selected timeslots. Please review your selections and try again.",
        severity: "error",
      });
      return;
    }
    const proposedChanges = {};
    proposedChanges[dentistUid] = [];
    rowSlotTargets.forEach(({ rowSlot, availabilityTargets }) => {
      const replacement = replacementSelections[rowSlot];
      availabilityTargets.forEach((slot) => {
        proposedChanges[dentistUid].push({ slot, willBeAvailable: false });
      });
      if (replacement && replacement !== "__none__") {
        if (!proposedChanges[replacement]) proposedChanges[replacement] = [];
        availabilityTargets.forEach((slot) => {
          proposedChanges[replacement].push({ slot, willBeAvailable: true });
        });
      }
    });

    // Rule 1
    const capacityErrors = validateChairCapacity({
      slots: capacitySlotsToValidate,
      unavailableTimeSlotsByDentist: unavailableSlotsByDentistForDate,
      knownDentistUids: dentistUidsKnown,
      proposedChanges,
      settings: chairSettings,
      recognizedSlots,
      appointments: appointmentsForSelectedDate,
      swapsBySlot: chairSwapsBySlotForDate,
      timeToMinutes,
    });
    // Rule 3
    const conflicts = findAppointmentConflicts({
      proposedChanges,
      appointmentsForDate: appointmentsForSelectedDate,
      timeToMinutes,
    });

    if (conflicts.length > 0) {
      setReplacementRowErrors(buildAppointmentConflictRowErrors(conflicts));
      return;
    }
    if (capacityErrors.length > 0) {
      const combined = capacityErrors.join(" \n");
      setChairSnackbar({ open: true, message: combined, severity: "error" });
      return;
    }

    // Apply changes using the same chair-aware projection semantics as validation.
    const projected = applyProposedChangesWithChairSemantics({
      unavailableTimeSlotsByDentist: unavailableSlotsByDentistForDate,
      proposedChanges,
      settings: chairSettings,
      slotUniverse: recognizedSlots,
      forceBenchReseed: true,
    });
    let nextSwapsBySlot = normalizeChairSwaps(chairSwapsBySlotForDate);
    rowSlotTargets.forEach(({ rowSlot, swapTargetsEffective }) => {
      const replacement = replacementSelections[rowSlot];
      const replacementIsBench =
        replacement &&
        replacement !== "__none__" &&
        !isDentistOnRoster(replacement, chairSettings);
      swapTargetsEffective.forEach((slot) => {
        if (replacementIsBench) {
          if (isDentistOnRoster(dentistUid, chairSettings)) {
            nextSwapsBySlot = upsertSwapForSlot({
              swapsBySlot: nextSwapsBySlot,
              slot,
              fromRosterUid: dentistUid,
              toBenchUid: replacement,
            });
            return;
          }
          const linkedRosterUid = getLinkedRosterForBenchSlot({
            swapsBySlot: nextSwapsBySlot,
            benchUid: dentistUid,
            slot,
          });
          if (linkedRosterUid) {
            nextSwapsBySlot = upsertSwapForSlot({
              swapsBySlot: nextSwapsBySlot,
              slot,
              fromRosterUid: linkedRosterUid,
              toBenchUid: replacement,
            });
            return;
          }
        }
        nextSwapsBySlot = removeSwapForSlot({
          swapsBySlot: nextSwapsBySlot,
          slot,
          fromRosterUid: isDentistOnRoster(dentistUid, chairSettings) ? dentistUid : null,
          toBenchUid: !isDentistOnRoster(dentistUid, chairSettings) ? dentistUid : null,
        });
      });
    });

    try {
      await persistChairSchedulingForDate({
        dateStr,
        projectedLookup: projected,
        swapsBySlot: nextSwapsBySlot,
        touchedDentistUids: Object.keys(proposedChanges || {}),
        settings: chairSettings,
      });
      setReplacementRowErrors({});
      setChairSnackbar({
        open: true,
        message: "Replacement saved successfully.",
        severity: "success",
      });
    } catch (err) {
      console.error("Error saving replacement:", err);
      const code = err?.code || "";
      setChairSnackbar({
        open: true,
        message:
          code === "permission-denied"
            ? "Could not save replacement because Firestore denied write access to chairSwaps. Publish the firestore.rules update, then try again."
            : "Could not save replacement. Please try again.",
        severity: "error",
      });
    } finally {
      setShowReplacementModal(false);
      setReplacementSelections({});
      setReplacementContext(null);
      setReplacementRowErrors({});
    }
  };

  const handleSaveRemoval = async () => {
    if (!removalContext) return;
    const { targetDentistUid, dateStr, rows } = removalContext;
    const removalSwapKeys = Array.from(
      new Set(
        rows.flatMap(({ slot }) =>
          resolveChairSwapKeysOverlappingSlot(targetDentistUid, slot)
        )
      )
    );
    const recognizedSlots = getChairSlotUniverseForFlow(targetDentistUid, [
      ...rows.map((row) => row.slot),
      ...removalSwapKeys,
    ]);
    setRemovalRowErrors({});
    // Removal mode: every row must have a dentist selected for forced unavailability.
    const missing = rows.filter((r) => !removalSelections[r.slot]);
    if (missing.length > 0) {
      setChairSnackbar({
        open: true,
        message:
          "Please pick a dentist to mark unavailable for every timeslot. 'None' is not allowed because it would exceed chair capacity.",
        severity: "error",
      });
      return;
    }
    const invalidSelections = rows.filter(
      (r) => !r.removableOptions.includes(removalSelections[r.slot])
    );
    if (invalidSelections.length > 0) {
      setChairSnackbar({
        open: true,
        message:
          "One or more selected dentists are no longer available for the selected timeslots. Please review your selections and try again.",
        severity: "error",
      });
      return;
    }

    const proposedChanges = {};
    proposedChanges[targetDentistUid] = [];
    rows.forEach(({ slot }) => {
      proposedChanges[targetDentistUid].push({ slot, willBeAvailable: true });
      const removeUid = removalSelections[slot];
      if (!proposedChanges[removeUid]) proposedChanges[removeUid] = [];
      proposedChanges[removeUid].push({ slot, willBeAvailable: false });
    });

    const capacityErrors = validateChairCapacity({
      slots: rows.map((r) => r.slot),
      unavailableTimeSlotsByDentist: unavailableSlotsByDentistForDate,
      knownDentistUids: dentistUidsKnown,
      proposedChanges,
      settings: chairSettings,
      recognizedSlots,
      appointments: appointmentsForSelectedDate,
      swapsBySlot: chairSwapsBySlotForDate,
      timeToMinutes,
    });
    const conflicts = findAppointmentConflicts({
      proposedChanges,
      appointmentsForDate: appointmentsForSelectedDate,
      timeToMinutes,
    });

    if (conflicts.length > 0) {
      setRemovalRowErrors(buildAppointmentConflictRowErrors(conflicts));
      return;
    }
    if (capacityErrors.length > 0) {
      const combined = capacityErrors.join(" \n");
      setChairSnackbar({ open: true, message: combined, severity: "error" });
      return;
    }

    const projected = applyProposedChangesWithChairSemantics({
      unavailableTimeSlotsByDentist: unavailableSlotsByDentistForDate,
      proposedChanges,
      settings: chairSettings,
      slotUniverse: recognizedSlots,
      forceBenchReseed: true,
    });
    let nextSwapsBySlot = normalizeChairSwaps(chairSwapsBySlotForDate);
    rows.forEach(({ slot }) => {
      const removeUid = removalSelections[slot];
      const targetIsBench = !isDentistOnRoster(targetDentistUid, chairSettings);
      const removeIsRoster = isDentistOnRoster(removeUid, chairSettings);
      const swapKeys = resolveChairSwapKeysOverlappingSlot(targetDentistUid, slot);
      swapKeys.forEach((swapSlot) => {
        if (targetIsBench && removeIsRoster) {
          nextSwapsBySlot = upsertSwapForSlot({
            swapsBySlot: nextSwapsBySlot,
            slot: swapSlot,
            fromRosterUid: removeUid,
            toBenchUid: targetDentistUid,
          });
          return;
        }
        nextSwapsBySlot = removeSwapForSlot({
          swapsBySlot: nextSwapsBySlot,
          slot: swapSlot,
          fromRosterUid: targetIsBench ? null : targetDentistUid,
          toBenchUid: targetIsBench ? targetDentistUid : null,
        });
      });
    });

    try {
      await persistChairSchedulingForDate({
        dateStr,
        projectedLookup: projected,
        swapsBySlot: nextSwapsBySlot,
        touchedDentistUids: Object.keys(proposedChanges || {}),
        settings: chairSettings,
      });
      setRemovalRowErrors({});
      setChairSnackbar({
        open: true,
        message: "Removal saved successfully.",
        severity: "success",
      });
    } catch (err) {
      console.error("Error saving removal:", err);
      setChairSnackbar({
        open: true,
        message: "Could not save replacement. Please try again.",
        severity: "error",
      });
    } finally {
      setShowRemovalModal(false);
      setRemovalSelections({});
      setRemovalContext(null);
      setRemovalRowErrors({});
    }
  };

  const closeAssignSecondaryModal = useCallback(() => {
    setShowAssignSecondaryModal(false);
    setAssignSecondaryDentistUid("");
    setAssignSecondaryBlockLabel("");
    setAssignSecondaryValidationMessages([]);
  }, []);
  const closeUnassignSecondaryModal = useCallback(() => {
    setShowUnassignSecondaryModal(false);
    setUnassignSecondaryValidationMessage("");
    setIsUnassigningSecondary(false);
  }, []);

  const openAssignSecondaryModal = useCallback(() => {
    if (!isClinicStaffUser) return;
    if (!selectedModalDentist || !formattedSelectedDateStr) return;
    if (!isChairCapacityConfigured(chairSettings)) return;
    if (!isDentistOnRoster(selectedModalDentist, chairSettings)) return;

    const linkedSecondaryUid = getRosterLinkedSecondaryUid(selectedModalDentist);
    const linkedSlots = linkedSecondaryUid
      ? getRosterSecondarySlots(selectedModalDentist, linkedSecondaryUid)
      : [];
    const detectedBlock = detectBlockLabelFromSlots(linkedSlots);

    const defaultSecondary =
      linkedSecondaryUid ||
      (assignSecondaryDentistOptions.length > 0
        ? assignSecondaryDentistOptions[0].uid
        : "");
    const defaultBlock = detectedBlock || "";

    setAssignSecondaryDentistUid(defaultSecondary);
    setAssignSecondaryBlockLabel(defaultBlock);
    setAssignSecondaryValidationMessages([]);
    setShowAssignSecondaryModal(true);
  }, [
    selectedModalDentist,
    formattedSelectedDateStr,
    chairSettings,
    getRosterLinkedSecondaryUid,
    getRosterSecondarySlots,
    detectBlockLabelFromSlots,
    assignSecondaryDentistOptions,
    secondaryScheduleBlockOptions,
    isClinicStaffUser,
  ]);
  const openUnassignSecondaryModal = useCallback(() => {
    if (!isClinicStaffUser) return;
    if (!selectedModalDentist || !formattedSelectedDateStr) return;
    if (!isChairCapacityConfigured(chairSettings)) return;
    if (isDentistOnRoster(selectedModalDentist, chairSettings)) return;
    if (!linkedRosterUidForSelectedSecondary) return;
    setUnassignSecondaryValidationMessage("");
    setShowUnassignSecondaryModal(true);
  }, [
    selectedModalDentist,
    formattedSelectedDateStr,
    chairSettings,
    linkedRosterUidForSelectedSecondary,
    isClinicStaffUser,
  ]);

  const handleSaveAssignSecondaryDentist = useCallback(async () => {
    if (!isClinicStaffUser) return;
    if (!selectedModalDentist || !formattedSelectedDateStr) return;
    if (!assignSecondaryDentistUid || !assignSecondaryBlockLabel) {
      setAssignSecondaryValidationMessages([
        "Please select both a secondary dentist and an inherited schedule block.",
      ]);
      return;
    }

    const blockSlots = getSlotsForSecondaryBlock(assignSecondaryBlockLabel);
    if (blockSlots.length === 0) {
      setAssignSecondaryValidationMessages([
        "The selected schedule block has no valid timeslots. Please select another block.",
      ]);
      return;
    }

    const existingSecondaryUid = getRosterLinkedSecondaryUid(selectedModalDentist);
    const existingSlots = existingSecondaryUid
      ? getRosterSecondarySlots(selectedModalDentist, existingSecondaryUid)
      : [];
    const nextBlockSlotSet = new Set(blockSlots);
    const existingSlotSet = new Set(existingSlots);
    const blockChanged =
      blockSlots.length !== existingSlots.length ||
      blockSlots.some((slot) => !existingSlotSet.has(slot));

    const validationMessages = [];
    const secondaryAssignments =
      rosterSecondaryAssignmentSummaryForDate.bySecondary?.[assignSecondaryDentistUid];
    if (secondaryAssignments) {
      const linkedToOtherRoster = Array.from(secondaryAssignments).some(
        (rosterUid) => rosterUid !== selectedModalDentist
      );
      if (linkedToOtherRoster) {
        validationMessages.push(
          `${dentistDisplayLabel(assignSecondaryDentistUid)} is already assigned as a secondary dentist for this date.`
        );
      }
    }

    if (
      existingSecondaryUid &&
      (blockChanged || existingSecondaryUid !== assignSecondaryDentistUid)
    ) {
      const existingSecondaryConflicts = getActiveAppointmentOverlapsForDentist({
        dentistUid: existingSecondaryUid,
        slots: existingSlots,
      });
      if (existingSecondaryConflicts.length > 0) {
        validationMessages.push(
          `${dentistDisplayLabel(existingSecondaryUid)}'s existing assigned timeslots has patient appointments. Please cancel the patient appointments first.`
        );
      }
    }

    const primaryTargetConflicts = getActiveAppointmentOverlapsForDentist({
      dentistUid: selectedModalDentist,
      slots: blockSlots,
    });
    if (primaryTargetConflicts.length > 0) {
      validationMessages.push(
        `${dentistDisplayLabel(selectedModalDentist)} has patient appointments within the selected timeslots (${assignSecondaryBlockLabel}). Please cancel the patient appointments first.`
      );
    }

    if (validationMessages.length > 0) {
      setAssignSecondaryValidationMessages(validationMessages);
      return;
    }

    const affectedSlots = Array.from(new Set([...existingSlots, ...blockSlots]));
    const recognizedSlots = getChairSlotUniverseForFlow(selectedModalDentist, affectedSlots);
    const proposedChanges = {
      [selectedModalDentist]: affectedSlots.map((slot) => ({
        slot,
        willBeAvailable: !nextBlockSlotSet.has(slot),
      })),
      [assignSecondaryDentistUid]: affectedSlots.map((slot) => ({
        slot,
        willBeAvailable: nextBlockSlotSet.has(slot),
      })),
    };
    if (existingSecondaryUid && existingSecondaryUid !== assignSecondaryDentistUid) {
      proposedChanges[existingSecondaryUid] = existingSlots.map((slot) => ({
        slot,
        willBeAvailable: false,
      }));
    }

    const capacityErrors = validateChairCapacity({
      slots: affectedSlots,
      unavailableTimeSlotsByDentist: unavailableSlotsByDentistForDate,
      knownDentistUids: dentistUidsKnown,
      proposedChanges,
      settings: chairSettings,
      recognizedSlots,
      appointments: appointmentsForSelectedDate,
      swapsBySlot: chairSwapsBySlotForDate,
      timeToMinutes,
    });
    if (capacityErrors.length > 0) {
      setAssignSecondaryValidationMessages(capacityErrors);
      return;
    }

    const appointmentConflicts = findAppointmentConflicts({
      proposedChanges,
      appointmentsForDate: appointmentsForSelectedDate,
      timeToMinutes,
    });
    if (appointmentConflicts.length > 0) {
      const distinctMessages = Array.from(
        new Set(
          appointmentConflicts.map((record) => {
            const conflictDentist = dentistDisplayLabel(record?.dentistUid);
            return `${conflictDentist} has patient appointments within the assigned timeslots. Please cancel the patient appointments first.`;
          })
        )
      );
      setAssignSecondaryValidationMessages(distinctMessages);
      return;
    }

    const projectedLookup = applyProposedChangesWithChairSemantics({
      unavailableTimeSlotsByDentist: unavailableSlotsByDentistForDate,
      proposedChanges,
      settings: chairSettings,
      slotUniverse: recognizedSlots,
      forceBenchReseed: true,
    });

    let nextSwaps = normalizeChairSwaps(chairSwapsBySlotForDate);
    Object.entries(nextSwaps).forEach(([, entry]) => {
      const swapSlot = entry?.slot;
      if (!swapSlot) return;
      if (entry?.fromRosterUid === selectedModalDentist) {
        nextSwaps = removeSwapForSlot({
          swapsBySlot: nextSwaps,
          slot: swapSlot,
          fromRosterUid: selectedModalDentist,
        });
      }
    });
    blockSlots.forEach((slot) => {
      nextSwaps = upsertSwapForSlot({
        swapsBySlot: nextSwaps,
        slot,
        fromRosterUid: selectedModalDentist,
        toBenchUid: assignSecondaryDentistUid,
      });
    });

    try {
      await persistChairSchedulingForDate({
        dateStr: formattedSelectedDateStr,
        projectedLookup,
        swapsBySlot: nextSwaps,
        touchedDentistUids: Object.keys(proposedChanges),
        settings: chairSettings,
      });
      const actorName =
        getCurrentUserDisplayNameForSystemNotification() || "Clinic Personnel";
      const primaryDentistName = dentistDisplayLabel(selectedModalDentist);
      const secondaryDentistName = dentistDisplayLabel(assignSecondaryDentistUid);
      if (
        existingSecondaryUid &&
        existingSecondaryUid !== assignSecondaryDentistUid
      ) {
        const oldBlockLabel =
          detectBlockLabelFromSlots(existingSlots) || assignSecondaryBlockLabel;
        void sendSecondaryDentistRemovalNotifications({
          primaryDentistUid: selectedModalDentist,
          secondaryDentistUid: existingSecondaryUid,
          dateStr: formattedSelectedDateStr,
          timeBlockLabel: oldBlockLabel,
          actorName,
          primaryDentistName,
          secondaryDentistName: dentistDisplayLabel(existingSecondaryUid),
        });
      }
      void sendSecondaryDentistAssignmentNotifications({
        primaryDentistUid: selectedModalDentist,
        secondaryDentistUid: assignSecondaryDentistUid,
        dateStr: formattedSelectedDateStr,
        timeBlockLabel: assignSecondaryBlockLabel,
        actorName,
        primaryDentistName,
        secondaryDentistName,
      });
      setChairSnackbar({
        open: true,
        message: "Secondary dentist assignment saved successfully.",
        severity: "success",
      });
      closeAssignSecondaryModal();
    } catch (error) {
      console.error("Error saving secondary dentist assignment:", error);
      setAssignSecondaryValidationMessages([
        "Could not save the secondary dentist assignment. Please try again.",
      ]);
    }
  }, [
    selectedModalDentist,
    formattedSelectedDateStr,
    assignSecondaryDentistUid,
    assignSecondaryBlockLabel,
    getSlotsForSecondaryBlock,
    getRosterLinkedSecondaryUid,
    getRosterSecondarySlots,
    rosterSecondaryAssignmentSummaryForDate,
    dentistDisplayLabel,
    getActiveAppointmentOverlapsForDentist,
    getChairSlotUniverseForFlow,
    unavailableSlotsByDentistForDate,
    dentistUidsKnown,
    chairSettings,
    appointmentsForSelectedDate,
    chairSwapsBySlotForDate,
    timeToMinutes,
    closeAssignSecondaryModal,
    isClinicStaffUser,
    detectBlockLabelFromSlots,
  ]);
  const handleConfirmUnassignSecondaryDentist = useCallback(async () => {
    if (!isClinicStaffUser) return;
    if (!selectedModalDentist || !formattedSelectedDateStr) return;
    if (!linkedRosterUidForSelectedSecondary) {
      setUnassignSecondaryValidationMessage(
        "Could not find the linked primary dentist for this secondary assignment."
      );
      return;
    }
    const inheritedSlots = getRosterSecondarySlots(
      linkedRosterUidForSelectedSecondary,
      selectedModalDentist
    );
    if (inheritedSlots.length === 0) {
      closeUnassignSecondaryModal();
      return;
    }

    const secondaryConflicts = getActiveAppointmentOverlapsForDentist({
      dentistUid: selectedModalDentist,
      slots: inheritedSlots,
    });
    if (secondaryConflicts.length > 0) {
      const secondaryShortLabel =
        (dentistDisplayLabel(selectedModalDentist).match(/^Dr\.\s+\S+/) || [])[0] ||
        dentistDisplayLabel(selectedModalDentist);
      setUnassignSecondaryValidationMessage(
        `${secondaryShortLabel} already has patient appointments within the assigned timeslots. Please cancel the patient appointments first.`
      );
      return;
    }

    const recognizedSlots = getChairSlotUniverseForFlow(
      linkedRosterUidForSelectedSecondary,
      inheritedSlots
    );
    const proposedChanges = {
      [selectedModalDentist]: inheritedSlots.map((slot) => ({
        slot,
        willBeAvailable: false,
      })),
      [linkedRosterUidForSelectedSecondary]: inheritedSlots.map((slot) => ({
        slot,
        willBeAvailable: true,
      })),
    };
    const capacityErrors = validateChairCapacity({
      slots: inheritedSlots,
      unavailableTimeSlotsByDentist: unavailableSlotsByDentistForDate,
      knownDentistUids: dentistUidsKnown,
      proposedChanges,
      settings: chairSettings,
      recognizedSlots,
      appointments: appointmentsForSelectedDate,
      swapsBySlot: chairSwapsBySlotForDate,
      timeToMinutes,
    });
    if (capacityErrors.length > 0) {
      setUnassignSecondaryValidationMessage(capacityErrors[0]);
      return;
    }

    setIsUnassigningSecondary(true);
    setUnassignSecondaryValidationMessage("");
    try {
      const projectedLookup = applyProposedChangesWithChairSemantics({
        unavailableTimeSlotsByDentist: unavailableSlotsByDentistForDate,
        proposedChanges,
        settings: chairSettings,
        slotUniverse: recognizedSlots,
        forceBenchReseed: true,
      });

      let nextSwaps = normalizeChairSwaps(chairSwapsBySlotForDate);
      Object.entries(nextSwaps).forEach(([, entry]) => {
        const swapSlot = entry?.slot;
        if (!swapSlot) return;
        if (
          entry?.fromRosterUid === linkedRosterUidForSelectedSecondary &&
          entry?.toBenchUid === selectedModalDentist
        ) {
          nextSwaps = removeSwapForSlot({
            swapsBySlot: nextSwaps,
            slot: swapSlot,
            fromRosterUid: linkedRosterUidForSelectedSecondary,
            toBenchUid: selectedModalDentist,
          });
        }
      });

      await persistChairSchedulingForDate({
        dateStr: formattedSelectedDateStr,
        projectedLookup,
        swapsBySlot: nextSwaps,
        touchedDentistUids: Object.keys(proposedChanges),
        settings: chairSettings,
      });

      const unassignBlockLabel = detectBlockLabelFromSlots(inheritedSlots);
      if (unassignBlockLabel) {
        void sendSecondaryDentistRemovalNotifications({
          primaryDentistUid: linkedRosterUidForSelectedSecondary,
          secondaryDentistUid: selectedModalDentist,
          dateStr: formattedSelectedDateStr,
          timeBlockLabel: unassignBlockLabel,
          actorName:
            getCurrentUserDisplayNameForSystemNotification() || "Clinic Personnel",
          primaryDentistName: dentistDisplayLabel(linkedRosterUidForSelectedSecondary),
          secondaryDentistName: dentistDisplayLabel(selectedModalDentist),
        });
      }

      setChairSnackbar({
        open: true,
        message: "Secondary dentist unassigned successfully.",
        severity: "success",
      });
      closeUnassignSecondaryModal();
    } catch (error) {
      console.error("Error unassigning secondary dentist:", error);
      setUnassignSecondaryValidationMessage(
        "Could not unassign the secondary dentist. Please try again."
      );
    } finally {
      setIsUnassigningSecondary(false);
    }
  }, [
    selectedModalDentist,
    formattedSelectedDateStr,
    linkedRosterUidForSelectedSecondary,
    getRosterSecondarySlots,
    getActiveAppointmentOverlapsForDentist,
    dentistDisplayLabel,
    getChairSlotUniverseForFlow,
    unavailableSlotsByDentistForDate,
    dentistUidsKnown,
    chairSettings,
    appointmentsForSelectedDate,
    chairSwapsBySlotForDate,
    timeToMinutes,
    closeUnassignSecondaryModal,
    isClinicStaffUser,
    detectBlockLabelFromSlots,
  ]);

  useEffect(() => {
    if (!isClinicStaffUser) {
      closeAssignSecondaryModal();
      closeUnassignSecondaryModal();
    }
  }, [isClinicStaffUser, closeAssignSecondaryModal, closeUnassignSecondaryModal]);

  // Hook called by the existing slot toggle flow to decide whether the
  // chair-aware Replacement / Removal modal should pre-empt the normal save.
  const maybeOpenChairAwareModal = ({ dentistUid, slots, isMakingUnavailable }) => {
    if (!isChairCapacityConfigured(chairSettings)) return false;
    if (!Array.isArray(slots) || slots.length === 0) return false;
    if (!formattedSelectedDateStr) return false;
    const isRosterDentist = isDentistOnRoster(dentistUid, chairSettings);

    if (isMakingUnavailable) {
      // Marking a dentist unavailable should not force ownership transfer.
      // This keeps inherited ownership links intact and only updates
      // unavailable state for the selected dentist.
      return false;
    }

    if (!isRosterDentist) {
      // Bench/inherited dentists making owned slots available again should not
      // trigger removal/replacement ownership flow; only unavailability toggles.
      return false;
    }

    const rosterHasLinkedBenchOnTargetSlots = slots.some((slot) => {
      if (
        getLinkedBenchForRosterSlot({
          swapsBySlot: chairSwapsBySlotForDate,
          rosterUid: dentistUid,
          slot,
        })
      ) {
        return true;
      }
      const slotRange = parseSlotLabelToRange(slot);
      if (!slotRange) return false;
      return Object.entries(chairSwapsBySlotForDate || {}).some(
        ([, entry]) =>
          entry?.fromRosterUid === dentistUid &&
          entry?.toBenchUid &&
          doRangesOverlap(slotRange, parseSlotLabelToRange(entry?.slot || ""))
      );
    });
    if (!rosterHasLinkedBenchOnTargetSlots) {
      // Roster dentist toggling their own non-inherited block (e.g. 1:30-6:00)
      // should only update unavailable state; ownership stays unchanged.
      return false;
    }

    if (!isMakingUnavailable) {
      // Any dentist becoming available - check if any slot would exceed cap.
      // Bench rows often reflect swap containment (`getTimeSlotStatus`) instead of
      // a literal row label in `unavailableTimeSlots`; naive splice misses that and
      // never credits the bench as newly available, so the modal never opens.
      const recognizedSlots = getChairSlotUniverseForFlow(dentistUid, slots);
      const projected = applyProposedChangesWithChairSemantics({
        unavailableTimeSlotsByDentist: unavailableSlotsByDentistForDate,
        proposedChanges: {
          [dentistUid]: slots.map((slot) => ({ slot, willBeAvailable: true })),
        },
        settings: chairSettings,
        slotUniverse: recognizedSlots,
        forceBenchReseed: true,
      });
      const overcapSlots = slots.filter((slot) => {
        const count = countAvailableDentistsForSlot({
          slot,
          unavailableTimeSlotsByDentist: projected,
          knownDentistUids: dentistUidsKnown,
          settings: chairSettings,
          recognizedSlots,
          appointments: appointmentsForSelectedDate,
          swapsBySlot: chairSwapsBySlotForDate,
          timeToMinutes,
        });
        return count > chairSettings.chairCount;
      });
      const linkedSlots = slots.filter((slot) => {
        if (isDentistOnRoster(dentistUid, chairSettings)) {
          if (
            getLinkedBenchForRosterSlot({
              swapsBySlot: chairSwapsBySlotForDate,
              rosterUid: dentistUid,
              slot,
            })
          ) {
            return true;
          }
          const rosterBenchOverlap = Array.from(
            new Set(
              Object.entries(chairSwapsBySlotForDate || {})
                .filter(([, entry]) => entry?.fromRosterUid === dentistUid && entry?.toBenchUid)
                .filter(([, entry]) =>
                  doRangesOverlap(
                    parseSlotLabelToRange(slot),
                    parseSlotLabelToRange(entry?.slot || "")
                  )
                )
                .map(([, entry]) => entry.toBenchUid)
            )
          );
          return rosterBenchOverlap.length > 0;
        }
        const benchRosterOverlap = getBenchLinkedRosterUidsForSlotRange({
          benchUid: dentistUid,
          slot,
        });
        if (benchRosterOverlap.length > 0) return true;
        return !!getLinkedRosterForBenchSlot({
          swapsBySlot: chairSwapsBySlotForDate,
          benchUid: dentistUid,
          slot,
        });
      });
      const slotsNeedingRemovalContext = Array.from(
        new Set([...(overcapSlots || []), ...(linkedSlots || [])])
      );
      // Even when projected over-cap check does not trigger (e.g. bench records
      // are intentionally not persisted), restoring a linked swapped slot must
      // still open Removal modal to pick which counterpart becomes unavailable.
      if (slotsNeedingRemovalContext.length > 0) {
        const ctx = buildRemovalContext({
          targetDentistUid: dentistUid,
          slots: slotsNeedingRemovalContext,
          dateStr: formattedSelectedDateStr,
          projectedLookup: projected,
        });
        if (ctx) {
          setRemovalContext(ctx);
          const initial = {};
          ctx.rows.forEach((row) => {
            initial[row.slot] = row.removableOptions[0] || "";
          });
          setRemovalSelections(initial);
          setRemovalRowErrors({});
          setShowRemovalModal(true);
          return true;
        }
      }
    }
    return false;
  };
  // ===== End Appointment Chair Conflict Prevention helpers =====

  return (
    <div>
      {/* CSS for closed days and calendar badges */}
      <style>
        {`
          .closed-day {
            background-color: #ff6b6b !important;
            color: white !important;
            font-weight: bold !important;
            border-radius: 8px !important;
            border: 2px solid #ff6b6b !important;
          }
          .react-calendar__tile.closed-day {
            background-color: #ff6b6b !important;
            color: white !important;
            font-weight: bold !important;
            border-radius: 8px !important;
            border: 2px solid #ff6b6b !important;
          }
          .react-calendar__tile {
            position: relative !important;
          }
          .react-calendar__tile > div {
            position: relative !important;
            width: 100% !important;
            height: 100% !important;
          }
          .react-calendar__tile > div > div {
            position: relative !important;
          }
        `}
      </style>

      <Box
        sx={boxStylesWrapper}
      >

        {/* Header Container */}
        <Header
          count={1}
          page="Appointment Page"
        />

      {/* All Content */}
      <Box 
        sx={boxStylesContent}
      >
        {/* Main Grids */}
        <Grid
          container
          rowSpacing={4}
          columnSpacing={6}
          sx={{
          // backgroundColor: "mediumaquamarine",
          justifyContent: "end",
          justifyItems: "center",
          alignItems: "center",
          alignContent: "center",
          width: "100%",
          height: "100vh",
          overflow: "auto",
          // backgroundColor: "blue",
          "& .MuiGrid-item": {
            display: "flex",
            justifyContent: "center",
            // backgroundColor: "blue",
          },
        }}>
            {/* Main Content */}
            <Grid 
              size={{ 
                xs: 12, 
                sm: 12, 
                md: 12, 
                lg: 12, 
                xl: 12 
              }} 
              item
            >
              {/* Main Content */}
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  // backgroundColor: "#FDF9F3",
                  // backgroundColor: "#a0844dff",
              }}>
                {/* Grid Main Content */}
                <Grid
                  container
                  sx={{
                    width: "100%",
                    // backgroundColor: "blue",
                    "& .MuiGrid-item": {
                      display: "flex",
                      justifyContent: "center",
                      // backgroundColor: "blue",
                    },
                }}>
                  {/* Main Content */}
                  <Grid 
                    size={{ 
                      xs: 12, 
                      sm: 12, 
                      md: 12, 
                      lg: 12, 
                      xl: 12 
                    }} 
                    item
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    // boxShadow: "-1px 5px 10px rgba(0,0,0,0.25)",
                      height: "calc(100vh - 61px)", // 👈 fills height below the header (adjust 80px if header height changes)
                      overflow: "auto",
                      mt: "0px",
                      maxHeight: "calc(100vh - 61px)",
                  }}>
                    <Box
                      sx={{
                        background: "#FEFEFE",
                        overflow: "auto",
                        // margin: "30px",
                        boxShadow: {
                          xs : "none",
                          sm : "0 4px 20px rgba(0,0,0,0.30)",
                          md : "0 4px 20px rgba(0,0,0,0.30)",
                          lg : "0 4px 20px rgba(0,0,0,0.30)",
                          xl : "0 4px 20px rgba(0,0,0,0.30)",
                        },
                        // border: "1px solid black",
                        margin: {
                          xs : 0,
                          sm : "40px",
                          md : "40px",
                          lg : "40px",
                          xl : "40px",
                        },
                        borderRadius: {
                          xs : 0,
                          sm : "15px",
                          md : "15px",
                          lg : "15px",
                          xl : "15px",
                        },
                        minHeight: 0,

                        display: "flex",
                        flexDirection: "column",
                        padding:  {
                          xs : "20px",
                          sm : "20px 30px",
                          md : "20px 30px",
                          lg : "20px 30px",
                          xl : "20px 30px",
                        },
                      }}>
                      {/* Title */}
                      <Box
                        sx={{
                        // background: "#905656ff",
                        padding: {
                          xs : "10px 0",
                          sm : "10px",
                          md : "10px",
                          lg : "10px",
                          xl : "10px",
                        },
                        justifyItems: "center",
                        textAlign: "center"
                        }}>                     
                        <Text
                          text="Manage Appointment"
                          variant="h2"
                        />
                      </Box>

                      <Stack
                        direction="row"
                        // spacing={5}
                        justifyContent={{
                          xs: "center",
                          sm: "center",
                          md: "center",
                          lg: "end",
                          xl: "end",
                        }}
                        gap={{
                          xs: 1,
                          sm: 2,
                          md: 1,
                          lg: 3,
                          xl: 3,
                        }}
                        // width="calc(100% - 40px)"
                        display="flex"
                        flexWrap="wrap"
                        margin="10px 0"
                        >
                        {/* Confirmed */}
                        <Stack
                          direction="row"
                          sx={{
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#2E2D2D",
                            borderRadius: "20px"
                          }}>
                          <Icon
                            src={require("../../assets/approve.png")}
                            size="small"
                          />
                          <Text
                            text="Confirmed"
                            variant="appointmentLabels"
                          />
                        </Stack>

                        {/* Pending */}
                        <Stack
                          direction="row"
                          sx={{
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#2E2D2D",
                            borderRadius: "20px"
                          }}>
                          <Icon
                            src={require("../../assets/clock_variant2.png")}
                            size="small"
                          />
                          <Text
                            text="Pending"
                            variant="appointmentLabels"
                          />
                        </Stack>

                        {/* Unavailable */}
                        <Stack
                          direction="row"
                          sx={{
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#2E2D2D",
                            borderRadius: "20px"
                          }}>
                            <Icon
                              src={require("../../assets/icons/cancel_light.png")}
                              size="unavailable"
                            />     
                            <Text
                              text="Unavailable"
                              variant="appointmentLabels"
                            />
                        </Stack>
                      </Stack>

                      <Box
                        display={{
                          xs : "block",
                          sm : "block",
                          md : "none",
                          lg : "none",
                          xl : "none"
                        }}>
                        <Divider/>
                      </Box>


                      <Stack 
                        direction={{
                          xs: "column",
                          sm: "column",
                          md: "row",
                          lg: "row",
                          xl: "row",
                        }}
                        spacing={2}>
                        <Box>
                          {/* Selects & Calendar & Summary*/}
                          <Grid
                            container
                            rowSpacing={2}
                            columnSpacing={2}
                            display={{
                              xs: "flex",
                              sm: "flex",
                              md: "flex",
                              lg: "flex",
                              xl: "flex",
                            }} 
                            sx={{
                              // mt: "40px",
                              // width: "100%",
                              justifyContent: "center",
                              "& .MuiGrid-item": {
                                display: "flex",
                                justifyContent: "center",
                                // backgroundColor: "blue",
                              },
                            }}>
                              {/* Filters Select */}
                              <Grid 
                                size={{ 
                                  xs: 12, 
                                  sm: 12, 
                                  md: 12, 
                                  lg: 12, 
                                  xl: 12 
                                }} 
                                item
                                sx={{
                                  // backgroundColor: "maroon" 
                                  // padding: "0 20px"
                                }}>     
                                  {/* Filter Checkboxes */}
                                  <Typography
                                    variant="h6"
                                    sx={{ marginBottom: "10px", fontWeight: "bold", color: "#333" }}
                                    >
                                    Filter Appointments
                                  </Typography>

                                  {/* Selects & Calendar & Summary*/}
                                  <Grid
                                    container
                                    columnSpacing={1}
                                    sx={{
                                      borderRadius: "15px",
                                      // gap: "10px",
                                      backgroundColor: "rgba(46, 45, 45, .1)",
                                      justifyContent: "space-evenly",
                                      "& .MuiGrid-item": {
                                        display: "flex",
                                        justifyContent: "center",
                                      },
                                    }}>

                                    {/* None Filter */}
                                    <Grid 
                                      item
                                      size={{
                                        xs: 3, 
                                        sm: 3, 
                                        md: 3, 
                                        lg: 3, 
                                        xl: 3 
                                      }}
                                      sx={{
                                        textAlign: "center",
                                      }}>
                                      <Tooltip
                                          title={showNone ? null : "None"}
                                        placement="top"
                                        arrow
                                        >
                                        {/* All Dentists (Pending + Confirmed) */}
                                        <Chip
                                          avatar={
                                            <Avatar 
                                              sx={{
                                                opacity: showNone ? 1 : .6
                                              }} 
                                              src={require("../../assets/icons/filter_off.png")}>
                                            </Avatar>
                                          }
                                          // label={showAllBookedAppointments ? "All Appointments" : "All"}
                                          label="None"
                                          clickable
                                          variant={showNone ? "filled" : "filled"}
                                          sx={{
                                            width: "100%",
                                            // borderColor: "#FFA500",
                                            boxShadow: showNone ? '0 2px 5px rgba(0,0,0,0.5)' : null,
                                            color: showNone ? "#2C251E" : "rgba(89, 84, 74, .7)",
                                            backgroundColor: showNone ? "#FEFEFE" : "transparent",
                                          }}
                                          onClick={() => {
                                            const newValue = !showNone;
                                            if (
                                              !showPendingAppointments &&
                                              !showConfirmedAppointments &&
                                              !showAllBookedAppointments
                                            ) {
                                              setShowNone(true);
                                            } else {
                                              setShowPendingAppointments(false);
                                              setShowConfirmedAppointments(false);
                                              setShowAllBookedAppointments(false);
                                            }
                                          }}
                                        />
                                      </Tooltip>
                                    </Grid>

                                    {/* Pending Filter */}
                                    <Grid 
                                      item
                                      size={{
                                        xs: 3, 
                                        sm: 3, 
                                        md: 3, 
                                        lg: 3, 
                                        xl: 3 
                                      }}
                                      sx={{
                                        textAlign: "center",
                                        // padding: "0 20px"
                                      }}
                                      >
                                      <Tooltip
                                        title={showPendingAppointments ? null : "Pending"}
                                        placement="top"
                                        arrow
                                      >
                                        {/* Pending */}
                                        <Chip
                                          avatar={<Avatar sx={{
                                            opacity: showPendingAppointments ? 1 : .8
                                          }} src={require("../../assets/clock_variant2.png")}></Avatar>}
                                          // label={showPendingAppointments ? "Pending Appointment" : "Pending"}
                                          label="Pending"
                                          clickable
                                          variant={showPendingAppointments ? "filled" : "filled"}
                                          sx={{
                                            width: "100%",
                                            transition: "all 0.5s ease",
                                            // borderColor: "#FFA500",
                                            boxShadow: showPendingAppointments ? '0 2px 5px rgba(0,0,0,0.5)' : null,
                                            color: showPendingAppointments ? "#2C251E" : "rgba(89, 84, 74, .9)",
                                            backgroundColor: showPendingAppointments ? "#FEFEFE" : "transparent",
                                          }}
                                          onClick={() => {
                                            const newValue = !showPendingAppointments;

                                            setShowPendingAppointments(newValue);
                                            if (newValue) {
                                              setShowNone(false)
                                              setShowAllBookedAppointments(false);
                                              setShowConfirmedAppointments(false);
                                            }
                                          }}
                                        />
                                      </Tooltip>
                                    </Grid>

                                    {/* Confirmed Filter*/}
                                    <Grid 
                                      item
                                      size={{
                                        xs: 3, 
                                        sm: 3, 
                                        md: 3, 
                                        lg: 3, 
                                        xl: 3 
                                      }}
                                      sx={{
                                        textAlign: "center",
                                        // backgroundColor: "maroon" 
                                        // padding: "0 20px"
                                      }}
                                    >
                                      <Tooltip
                                        title={showConfirmedAppointments ? null : "Confirmed"}
                                        placement="top"
                                        arrow
                                      >
                                        {/* Confirmed */}
                                        <Chip
                                          avatar={<Avatar sx={{
                                            opacity: showConfirmedAppointments ? 1 : .6
                                          }} src={require("../../assets/approve.png")}></Avatar>}
                                          // label={showConfirmedAppointments ? "Confirmed Appointments" : "Confirmed"}
                                          label="Confirmed"
                                          clickable
                                          variant={showConfirmedAppointments ? "filled" : "filled"}
                                          sx={{
                                            width: "100%",
                                            // borderColor: "#FFA500",
                                            boxShadow: showConfirmedAppointments ? '0 2px 5px rgba(0,0,0,0.5)' : null,
                                            color: showConfirmedAppointments ? "#2C251E" : "rgba(89, 84, 74, .7)",
                                            backgroundColor: showConfirmedAppointments ? "#FEFEFE" : "transparent",
                                          }}
                                          onClick={() => {
                                            const newValue = !showConfirmedAppointments;

                                            setShowConfirmedAppointments(newValue);
                                            if (newValue) {
                                              setShowNone(false)
                                              setShowAllBookedAppointments(false);
                                              setShowPendingAppointments(false);
                                            }
                                          }}
                                        />
                                      </Tooltip>
                                    </Grid>

                                    {/* All Filter */}
                                    <Grid 

                                      item
                                      sx={{
                                        textAlign: "center",
                                        // backgroundColor: "maroon" 
                                        // padding: "0 20px"
                                      }}
                                    >
                                      <Tooltip
                                        title={showAllBookedAppointments ? null : "All"}
                                        placement="top"
                                        arrow
                                      >
                                        {/* All Dentists (Pending + Confirmed) */}
                                        <Chip
                                          avatar={<Avatar sx={{
                                            opacity: showAllBookedAppointments ? 1 : .6
                                          }} src={require("../../assets/icons/all.png")}></Avatar>}
                                          // label={showAllBookedAppointments ? "All Appointments" : "All"}
                                          label="All"
                                          clickable
                                          variant={showAllBookedAppointments ? "filled" : "filled"}
                                          sx={{
                                            width: "100%",
                                            // borderColor: "#FFA500",
                                            boxShadow: showAllBookedAppointments ? '0 2px 5px rgba(0,0,0,0.5)' : null,
                                            color: showAllBookedAppointments ? "#2C251E" : "rgba(89, 84, 74, .7)",
                                            backgroundColor: showAllBookedAppointments ? "#FEFEFE" : "transparent",
                                          }}
                                          onClick={() => {
                                            const newValue = !showAllBookedAppointments;

                                            setShowAllBookedAppointments(newValue);
                                            if (newValue) {
                                              setShowNone(false)
                                              setShowPendingAppointments(false);
                                              setShowConfirmedAppointments(false);
                                            }
                                          }}
                                        />
                                      </Tooltip>
                                    </Grid>
                                  </Grid>
                              </Grid>

                              {/* Dentist Select */}
                              {(userDetails?.role === "ClinicStaff" || userDetails?.userType === "ClinicStaff") && (
                                <Grid 
                                  size={{ 
                                    xs: 12, 
                                    sm: 12, 
                                    md: 12, 
                                    lg: 12, 
                                    xl: 12 
                                  }} 
                                  item
                                  sx={{
                                    // backgroundColor: "maroon" 
                                    // padding: "0 20px"
                                  }}>
                                    <Box textAlign="start" sx={{width: "100%", }}>
                                      <Stack direction="column">
                                        {/* Text */}
                                        <Typography 
                                          variant="h3" 
                                          sx={styleText}>
                                          Select a Dentist
                                        </Typography>
                            
                                        <FormControl
                                          fullWidth
                                          sx={{
                                            minWidth: "100px",
                                            height: "53px",
                                            width: "100%"
                                          }}>
                                            <Select
                                              displayEmpty
                                              fullWidth
                                              labelId="dentistSelect"
                                              id="dentistSelect"
                                              value={selectedDentist || ""}
                                              disabled={showAllBookedAppointments || showPendingAppointments || showConfirmedAppointments}
                                              onChange={(e) => setSelectedDentist(e.target.value || null)}
                                              sx={{
                                                height: "53px",
                                                width: "100%",
                                                fontSize: "14px",
                                              "& .MuiSelect-select": {
                                                padding: "10px 15px",
                                                color: selectedDentist ? "#2C251E" : "rgba(44, 37, 30, 0.6)",
                                              },
                                              "& .MuiOutlinedInput-notchedOutline": {
                                                border:
                                                  "2px solid rgba(197,165,114,0.4)",
                                                borderRadius: "8px",
                                              },
                                              "&:hover .MuiOutlinedInput-notchedOutline":
                                                {
                                                  borderColor: "rgba(197,165,114,0.9)",
                                                },
                                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                {
                                                  borderColor: "rgba(197,165,114,1)",
                                                },
                                              "& .MuiSvgIcon-root": {
                                                color: "#2C251E",
                                              },
                                            }}
                                            MenuProps={{
                                              PaperProps: {
                                                sx: {
                                                  maxHeight: "200px",
                                                  backgroundColor: "#FEFEFE",
                                                  color: "#2C251E",
                                                  borderRadius: "10px",
                                                  marginTop: "10px",
                                                  border:　"1.5px solid rgba(197,165,114,0.6)",
                                                  "& .MuiMenuItem-root": {
                                                    fontSize: "14px",
                                                    borderRadius: "6px",
                                                    "&:hover": {
                                                      backgroundColor:
                                                        "rgba(197,165,114,0.2)",
                                                    },
                                                  },
                                                  "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover":{
                                                    backgroundColor:
                                                      "rgba(44,37,30,0.9) !important",
                                                    color: "#FEFEFE !important",
                                                    borderRadius: "0px !important",
                                                    "& .MuiListItemButton-root": {
                                                      borderRadius: "0px !important",
                                                    },
                                                  },
                                                  "& .MuiList-root .Mui-selected": {
                                                    borderRadius: "0px !important",
                                                  },
                                                  "& .MuiMenuItem-root:first-of-type": {
                                                    borderTopLeftRadius: "6px",
                                                    borderTopRightRadius: "6px",
                                                  },
                                                  "& .MuiMenuItem-root:last-of-type": {
                                                    borderBottomLeftRadius: "6px",
                                                    borderBottomRightRadius: "6px",
                                                  }
                                                }
                                              }
                                              }}>
                                                <MenuItem value="" disabled>
                                                  {showAllBookedAppointments || showPendingAppointments || showConfirmedAppointments ? "Select None Filter to Use" : "Select a Dentist"}
                                                </MenuItem>
                                                {dentists
                                                  // .filter(
                                                  //   (dentist) =>
                                                  //     dentist.name !== "Dr. Joseph Kahl Awis" &&
                                                  //     dentist.name !== "Dr. Daniel Allan Gozon"
                                                  // )
                                                  .map((dentist) => {
                                                  const displayName = getDentistDisplayName(dentist);
                                                  return (
                                                    
                                                      <MenuItem key={dentist.uid} value={dentist.uid}>
                                                      {(showAllBookedAppointments || showPendingAppointments || showConfirmedAppointments) 
                                                        ? `(clear filtered appointment to use)` 
                                                        : displayName}
                                                    </MenuItem>
                                                  );})
                                                }


                                          </Select>
                                      </FormControl>
                                    </Stack>
                                  </Box>
                              </Grid>
                              )}

                              {/* Date Picker */}
                              <Box
                                width="100%"
                              >
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <StaticDatePicker
                                    value={selectedDateAppointment}
                                    onChange={(newDate) => {
                                      handleDateChange(newDate);
                                    }}
                                    minDate={allowPastDates ? undefined : dayjs()}
                                    maxDate={null}
                                    showDaysOutsideCurrentMonth
                                    slots={{
                                      day: ManageAppointmentPickersDay,
                                    }}
                                    slotProps={{
                                      layout: {
                                        sx: {
                                          borderRadius: 2,
                                          boxShadow: "0px 0.5px 10px .6px rgba(46, 45, 45, .6)",
                                          p: 2,
                                        },
                                      },
                                      toolbar: {
                                        sx: {
                                          display: "none",
                                        },
                                      },
                                      actionBar: {
                                        actions: [],
                                      },
                                      day: {
                                        highlightedDays,
                                        closedDays,
                                        getAppointmentCountsForDate,
                                        sx: {
                                          "&.Mui-disabled": {
                                            backgroundColor: "rgba(46, 45, 45, .1)",
                                            opacity: 1,
                                            color: "white",
                                          },
                                        },
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Box>
                          </Grid>
                        </Box>
                
                        {/* Content Split */}
                        <Grid
                          container
                          columnSpacing={1}
                          sx={{
                            // padding: "0 20px",
                            width: "100%",
                            borderRadius: "10px",
                            // backgroundColor: "mediumaquamarine",
                            overflowY: "hidden",
                            // justifyContent: "end",
                            boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
                            "& .MuiGrid-item": {
                              display: "flex",
                              justifyContent: "center",
                              // backgroundColor: "blue",
                            },
                          }}>

                            {/* Header */}
                            <Grid 
                              size={{ 
                                xs: 12, 
                                sm: 12, 
                                md: 12, 
                                lg: 12, 
                                xl: 12 
                              }}
                              display="block"
                              item
                            >
                              {/* Dentist Header */}
                              <Box
                                sx={{
                                  background: "#C5A572",
                                  color: "white",
                                  padding: "10px 20px",
                                  justifyContent: "center",
                                  textAlign: "center",
                                  borderBottom: "2px solid rgba(46, 45, 45, .8)",
                                  fontSize: "16px",
                                  fontWeight: "600",
                                  letterSpacing: 0.4,
                                }}
                              >
                                <Stack
                                  direction="row"
                                  sx={{
                                    alignContent: "center",
                                    alignItems: "center",
                                    justifyItems: "center",
                                    justifyContent: "space-between",
                                    p: "10px"
                                  }}>
                                  <Box>
                                  {(() =>
                                    {
                                      const dateStr = selectedDateAppointment.toDate().toLocaleDateString('en-US', {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric'
                                      });
                                      const dayStr = selectedDateAppointment.toDate().toLocaleDateString('en-US', { weekday: 'long' });
                                      const dentistName = selectedDentist ? getDentistDisplayNameFromUID(selectedDentist) : "Select a dentist";

                                      if (!selectedDateAppointment && (!selectedDentist || selectedDentist === "") && !isCurrentUserDentist()) {
                                        return "Please select a date and dentist";
                                      } else if (!selectedDateAppointment) {
                                        return "Please select a date in the calendar";
                                      } else if (showAllBookedAppointments || showPendingAppointments || showConfirmedAppointments) {
                                        return `Date: ${dateStr} (${dayStr})`;
                                      } else if ((!selectedDentist || selectedDentist === "") && !isCurrentUserDentist()) {
                                        return `Date: ${dateStr} (${dayStr}) | Dentist: Doctor`;
                                      } else{
                                        return `Date: ${dateStr} (${dayStr}) | Dentist: ${dentistName}`;
                                      }
                                    }
                                  )()}
                                  </Box>
                                  {isCurrentUserDentist() && selectedDateAppointment && (
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        setReferenceFrom("09:00");
                                        setReferenceTo("18:00");
                                        setShowReferenceScheduleModal(true);
                                      }}
                                      sx={{
                                        color: "#C5A572",
                                        backgroundColor: "white",
                                        border: "1px solid white",
                                        borderRadius: "20px",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "12px",
                                        px: 2,
                                        py: 0.5,
                                        whiteSpace: "nowrap",
                                        flexShrink: 0,
                                        "&:hover": { backgroundColor: "rgba(255,255,255,0.85)" },
                                      }}
                                    >
                                      Set Reference Schedule
                                    </Button>
                                  )}
                                </Stack>
                              </Box>
                            </Grid>

                            {/* Content Split */}
                            <Grid
                              container
                              spacing={0}
                              sx={{
                                // padding: "0 20px",
                                width: "100%",
                                  height: showAllBookedAppointments || showPendingAppointments || showConfirmedAppointments ? "100%" : null,
                                // backgroundColor: "mediumaquamarine",
                                // height: "100%",
                                overflowY: "auto",
                                // justifyContent: "end",
                                marginTop: "0",
                                "& .MuiGrid-item": {
                                  display: "flex",
                                  justifyContent: "center",
                                  // backgroundColor: "blue",
                                },
                              }}
                            >            
                              {/* Appointments List */}
                              <Grid 
                                size={{ 
                                  xs: 12, 
                                  sm: 12, 
                                  md: 12, 
                                  lg: 12, 
                                  xl: (!selectedDentist || selectedDentist === "") && !showAllBookedAppointments && !showPendingAppointments && !showConfirmedAppointments && !isCurrentUserDentist() ? 12 : 6   
                                }}
                                display="block"
                                item
                                padding="20px"
                                id="timeslot left panel"
                              >
                                {/* Legends & Time Slot*/}
                                <Grid
                                  container
                                  id="doc timeslot 2"
                                  // rowSpacing={2}
                                  // columnSpacing={2}
                                  sx={{
                                    // padding: "10px",
                                    pl: "10px",
                                    width: "100%",
                                      height: "100%",
                                    // height: "100vh",
                                    // backgroundColor: "cadetblue",
                                    // borderRadius: "10px",
                                    justifyContent: "end",
                                    "& .MuiGrid-item": {
                                      display: "flex",
                                      justifyContent: "center",
                                      // backgroundColor: "blue",
                                    },
                                  }}>
                                    {/* Time Slot */}
                                    <Grid 
                                      id="doc timeslot 3"
                                      size={{ 
                                        xs: 12, 
                                        sm: 12, 
                                        md: 12, 
                                        lg: 12, 
                                        xl: 12 
                                      }} 
                                      item
                                      sx={{
                                        // display:"block",
                                        height: "100%",
                                        maxHeight: "550px",
                                        // overflowY: "auto",
                                        overflowX: "hidden",
                                        minHeight: 0,
                                      }}
                                    >
                                      {(() => {
                                        const filteredData = getFilteredAppointmentsByDentist();
                                        // If any filter is active, show filtered results
                                        if (filteredData) {
                                          const dentistNames = Object.keys(filteredData);

                                          if (dentistNames.length === 0) {
                                            return (
                                              <Box 
                                                sx={{
                                                  padding: "40px",
                                                  textAlign: "center",
                                                  // backgroundColor: "cadetblue",
                                                  // height: "100%",
                                                  color: "#666",
                                                  fontSize: "16px",
                                                }}>
                                                  <Stack
                                                    id="Stack me"
                                                    direction="column"
                                                    spacing={3}
                                                    sx={{
                                                      justifyContent: "center",
                                                      textAlign: "center",
                                                      justifyItems: "center",
                                                      alignContent: "center",
                                                      alignItems: "center"
                                                    }}>

                                                    <Box
                                                      sx={{
                                                        backgroundColor: "rgba(46, 45, 45, 0.1)",
                                                        borderRadius: "150px",
                                                        padding: "20px"
                                                      }}>                                                      
                                                      <Icon
                                                        src={require("../../assets/icons/no_date.png")}
                                                        size="user-image"
                                                      />
                                                    </Box>

                                                    <Stack
                                                      direction="column"
                                                      spacing={1}
                                                      >
                                                      <Text
                                                        text="Select a Different Date"
                                                        variant="historyService"
                                                      />
                                                      <Text
                                                        text="No appointments found matching the selected filters."
                                                        variant="body1"
                                                      />
                                                    </Stack>
                                                  </Stack>
                                              </Box>
                                            );
                                          }

                                          return (
                                            <div>
                                              {dentistNames.map(dentistName => (
                                                <Box 
                                                  key={dentistName}
                                                  sx={{
                                                    // borderTopRightRadius: "8px",
                                                    // borderTopLeftRadius: "8px",
                                                    borderRadius: "8px",
                                                    // backgroundColor: "#59544A",
                                                    marginY: "10px",
                                                    border: "5px solid rgba(46, 45, 45, 1)",
                                                  }}>
                                                  <Box
                                                    sx={{
                                                      padding: "10px",
                                                      backgroundColor: "#2C251E"
                                                    }}>
                                                      <Text
                                                        text={dentistName}
                                                        variant="accDentistTitle"
                                                      />
                                                  </Box>
                                                  {filteredData[dentistName].map((appointment) => (
                                                    <Box 
                                                      key={appointment.id} 
                                                      sx={{
                                                        display: "",
                                                        alignItems: "center",
                                                        // height: "100%",
                                                        transition: "all 0.2s ease",
                                                        minHeight: "44px",
                                                        background: "rgba(253, 249, 243, .9)",
                                                        justifyContent: "space-between"
                                                      }}>
                                                      <Stack
                                                        id="pending item"
                                                        direction="column"
                                                        sx={{
                                                          // width: "100%",
                                                          m: "10px 15px ",
                                                          border: appointment.isUnavailableSlot ? "2px solid #C5A572" :
                                                          appointment.status === "Pending" || appointment.status === "New" ? "2px solid rgba(162, 140, 102, 1)" : 
                                                          appointment.status === "Confirmed" ? "2px solid rgba(67, 103, 78, 1)" : 
                                                          appointment.status === "Completed" ? "2px solid rgba(44, 103, 133, 1)" : "1px solid #e0e0e0",
                                                          borderRadius: "8px",
                                                          background: "#FEFEFE",
                                                          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                                        }}>
                                                        {/* Name & Tools */}
                                                        <Stack
                                                          direction="row"
                                                          justifyContent="space-between"
                                                          sx={{
                                                            width: "100%",
                                                            padding: "5px 0",
                                                            borderTopLeftRadius: "6px",
                                                            borderTopRightRadius: "6px",
                                                            backgroundColor: appointment.status === "Pending" || appointment.status === "New" ? "rgba(162, 140, 102, .8)" :
                                                            appointment.status === "Confirmed" ? "rgba(67, 103, 78, .75)" :
                                                            appointment.status === "Completed" ? "rgba(44, 103, 133, .7)" :
                                                            "#FEFEFE"
                                                          }}>
                                                          {/* Name */}
                                                          <Box
                                                            sx={{
                                                              padding: "10px 15px",
                                                              // backgroundColor: "cadetblue"
                                                            }}>
                                                            <Text
                                                              text={appointment.patientDisplay || appointment.patientName || appointment.name}
                                                              variant="manageListName"
                                                            />
                                                          </Box>
                                                          {/* Tools */}
                                                          <Stack 
                                                            direction="row" 
                                                            spacing={1} 
                                                            sx={{ 
                                                              alignItems: "center", 
                                                              margin: "0 10px", 
                                                              backgroundColor: "rgba(253, 249, 243, .7)", 
                                                              borderRadius: "10px", 
                                                              padding: "0 10px"
                                                            }}>
                                                            {/* Edit */}
                                                            <Tooltip
                                                              title="Edit"
                                                              placement="top"
                                                              arrow
                                                            >
                                                              <Box
                                                                onClick={() => {
                                                                  handleEditAppointment(appointment);
                                                                }}
                                                                sx={{
                                                                  borderRadius: "50px",
                                                                  height: "40px",
                                                                  width: "40px",
                                                                  cursor: "pointer",
                                                                  ":hover": {
                                                                    backgroundColor: "rgba(46, 45, 45, 0.2)"
                                                                  }
                                                                }}>
                                                                  <Icon
                                                                    src={require("../../assets/icons/edit_v2.png")}
                                                                    size="small"
                                                                  />
                                                              </Box>
                                                            </Tooltip>

                                                            {/* View Medical History */}
                                                            <Tooltip
                                                              title="View Medical History"
                                                              placement="top"
                                                              arrow
                                                            >
                                                              <Box
                                                                onClick={() => handleViewMedicalHistory(appointment)}
                                                                sx={{
                                                                  borderRadius: "50px",
                                                                  height: "40px",
                                                                  width: "40px",
                                                                  cursor: "pointer",
                                                                  ":hover": {
                                                                    backgroundColor: "rgba(46, 45, 45, 0.2)"
                                                                  }
                                                                }}>
                                                                <Icon
                                                                  src={require("../../assets/icons/file_preview.png")}
                                                                  size="small"
                                                                />
                                                              </Box>
                                                            </Tooltip>

                                                            {/* View Insurance */}
                                                            {appointment.insuranceDetails && !appointment.isUnavailableSlot && (

                                                              <Tooltip
                                                                title="View Insurance"
                                                                placement="top"
                                                                arrow
                                                              >
                                                                <Box
                                                                  onClick={() => handleViewInsuranceDetails(appointment)}sx={{
                                                                  borderRadius: "50px",
                                                                  height: "40px",
                                                                  width: "40px",
                                                                  cursor: "pointer",
                                                                  ":hover": {
                                                                    backgroundColor: "rgba(46, 45, 45, 0.2)"
                                                                  }
                                                                  }}>
                                                                  <Icon
                                                                    src={require("../../assets/icons/insurance_card_green.png")}
                                                                    size="small"
                                                                  />
                                                                </Box>
                                                              </Tooltip>
                                                            )}

                                                            {/* Remove */}
                                                            {appointment.status !== "Completed" && appointment.status !== "completed" && (

                                                              <Tooltip
                                                                title="Remove"
                                                                placement="top"
                                                                arrow
                                                              >
                                                                <Box
                                                                  onClick={() => handleRemoveAppointment(appointment.id)}
                                                                  sx={{
                                                                    borderRadius: "50px",
                                                                    height: "40px",
                                                                    width: "40px",
                                                                    cursor: "pointer",
                                                                    ":hover": {
                                                                      backgroundColor: "rgba(46, 45, 45, 0.2)"
                                                                    }
                                                                  }}>
                                                                  <Icon
                                                                    src={require("../../assets/delete.png")}
                                                                    size="small"/>
                                                                </Box>
                                                              </Tooltip>
                                                            )}
                                                          </Stack> 
                                                        </Stack>

                                                        {/* Status & Time */}
                                                        <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between"}}>
                                                          {/* Status */}
                                                          <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            sx={{
                                                              padding: "5px 15px",
                                                            }}>
                                                            <Text
                                                              text="Status:"
                                                              variant="manageListBold"
                                                            />
                                                            <Text
                                                              text={appointment.status}
                                                              variant={
                                                                appointment.status === "Pending" ? "manageListPending" :
                                                                appointment.status === "Confirmed" ? "manageListConfirmed" :
                                                                appointment.status === "Completed" ? "manageListCompleted" :
                                                                "manageListBold"
                                                              }
                                                            />
                                                          </Stack>
                                                          {/* Time */}
                                                          <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            sx={{
                                                              padding: "5px 15px",
                                                            }}>
                                                            <Text
                                                              text="Time:"
                                                              variant="manageListBold"
                                                            />
                                                            <Text
                                                              text={appointment.startTime && appointment.endTime
                                                              ? `${formatTimeConsistent(appointment.startTime)} - ${formatTimeConsistent(appointment.endTime)}`
                                                              : appointment.time
                                                              ? `${formatTimeConsistent(appointment.time)} - ${formatTimeConsistent(appointment.endTime || minutesToTime(timeToMinutes(appointment.time) + (appointment.duration || 30)))}`
                                                              : ""}
                                                              variant="manageListRegular"
                                                            />
                                                          </Stack>
                                                        </Stack>
                                                        {/* Services & Dentist */}
                                                        <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between"}}>
                                                          {/* Services */}
                                                          <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            sx={{
                                                              padding: "5px 15px",
                                                            }}>
                                                            <Text
                                                              text="Service(s):"
                                                              variant="manageListBold"
                                                            />
                                                          </Stack>
                                                          {/* Dentist */}
                                                          <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            sx={{
                                                              padding: "5px 15px",
                                                            }}>
                                                            <Text
                                                              text="Dentist:"
                                                              variant="manageListBold"
                                                            />
                                                            <Text
                                                              text={appointment.dentist || "N/A"}
                                                              variant="manageListRegular"
                                                            />
                                                          </Stack>
                                                        </Stack>
                                                        {/* Service List */}
                                                        <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between", padding: "5px 15px",}}>
                                                          <Text
                                                            text={appointment.services && Array.isArray(appointment.services) 
                                                              ? appointment.services.map(service => 
                                                                  typeof service === 'object' ? service.name : service
                                                                ).join(", ") 
                                                              : ""}
                                                            variant="manageListRegular"
                                                          />
                                                        </Stack>
                                                      </Stack>
                                                    </Box>
                                                  ))}
                                                </Box>
                                              ))}
                                            </div>
                                          );
                                        }
                                        // Default view when no filters are active and no dentist is selected (only for ClinicStaff)
                                        if ((!selectedDentist || selectedDentist === "") && !showAllBookedAppointments && !showPendingAppointments && !showConfirmedAppointments && !isCurrentUserDentist()) {
                                          return (
                                            <Box 
                                              sx={{
                                                padding: "40px",
                                                textAlign: "center",
                                                color: "#666",
                                                fontSize: "16px",
                                              }}>
                                              <Stack
                                                direction="column"
                                                spacing={3}
                                                sx={{
                                                  justifyContent: "center",
                                                  textAlign: "center",
                                                  justifyItems: "center",
                                                  alignContent: "center",
                                                  alignItems: "center"
                                                }}>
                                                <Box
                                                  sx={{
                                                    backgroundColor: "rgba(46, 45, 45, 0.1)",
                                                    borderRadius: "150px",
                                                    padding: "20px"
                                                  }}>
                                                  <Icon
                                                    src={require("../../assets/icons/calendar_dark.png")}
                                                    size="user-image"
                                                  />
                                                </Box>
                                                <Stack
                                                  direction="column"
                                                  spacing={1}
                                                  >
                                                  <Text
                                                    text="Select a Dentist First"
                                                    variant="historyService"
                                                  />
                                                  <Text
                                                    text="View appointments by selecting a dentist first or use filters."
                                                    variant="body1"
                                                  />
                                                </Stack>
                                              </Stack>
                                            </Box>
                                          );
                                        }

                                        if (sortedAppointments.length > 0) {
                                          return sortedAppointments.map((appointment) => (
                                            <Box 
                                              key={appointment.id} 
                                              sx={{
                                                border: appointment.isUnavailableSlot ? "2px solid #C5A572" :
                                                      appointment.status === "Pending" || appointment.status === "New" ? "2px solid rgba(162, 140, 102, 1)" : 
                                                      appointment.status === "Confirmed" ? "2px solid rgba(67, 103, 78, 1)" : 
                                                      appointment.status === "Completed" ? "2px solid rgba(44, 103, 133, 1)" : "1px solid #e0e0e0",
                                                borderRadius: "8px",
                                                boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
                                                background: "#FEFEFE",
                                                transition: "all 0.2s ease",
                                                margin: "15px 10px"
                                              }}>
                                              {/* Name & Tools */}
                                              <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                sx={{
                                                  width: "100%",
                                                  padding: "5px 0",
                                                  borderTopLeftRadius: "6px",
                                                  borderTopRightRadius: "6px",
                                                  backgroundColor: appointment.status === "Pending" || appointment.status === "New" ? "rgba(162, 140, 102, .8)" :
                                                  appointment.status === "Confirmed" ? "rgba(67, 103, 78, .75)" :
                                                  appointment.status === "Completed" ? "rgba(44, 103, 133, .7)" :
                                                  "#FEFEFE"
                                                }}> 
                                                {/* Name */}
                                                <Box
                                                  sx={{
                                                    padding: "10px 15px",
                                                    // backgroundColor: "cadetblue"
                                                  }}>
                                                  <Text
                                                    text={appointment.patientDisplay}
                                                    variant="manageListName"
                                                  />
                                                </Box>
                                                {/* Tools */}
                                                <Stack 
                                                  direction="row" 
                                                  spacing={1} 
                                                  sx={{ 
                                                    alignItems: "center", 
                                                    margin: "0 10px", 
                                                    backgroundColor: "rgba(253, 249, 243, .7)", 
                                                    borderRadius: "10px", 
                                                    padding: "0 10px"
                                                  }}>
                                                  {/* Edit */}
                                                  <Tooltip
                                                    title="Edit"
                                                    placement="top"
                                                    arrow
                                                    >
                                                    <Box
                                                      onClick={() => {
                                                        handleEditAppointment(appointment);
                                                      }}
                                                      sx={{
                                                        borderRadius: "50px",
                                                        height: "40px",
                                                        width: "40px",
                                                        cursor: "pointer",
                                                        ":hover": {
                                                          backgroundColor: "rgba(46, 45, 45, 0.2)"
                                                        }
                                                      }}>
                                                      <Icon
                                                        src={require("../../assets/icons/edit_v2.png")}
                                                        size="small"/>
                                                    </Box>
                                                  </Tooltip>

                                                  {/* View Medical History */}
                                                  <Tooltip
                                                    title="View Medical History"
                                                    placement="top"
                                                    arrow
                                                    >
                                                    <Box
                                                      onClick={() => handleViewMedicalHistory(appointment)}
                                                      sx={{
                                                        borderRadius: "50px",
                                                        height: "40px",
                                                        width: "40px",
                                                        cursor: "pointer",
                                                        ":hover": {
                                                          backgroundColor: "rgba(46, 45, 45, 0.2)"
                                                        }
                                                      }}>
                                                      <Icon
                                                        src={require("../../assets/icons/file_preview.png")}
                                                        size="small"
                                                      />
                                                    </Box>
                                                  </Tooltip>

                                                  {/* View Insurance */}
                                                  {appointment.insuranceDetails && !appointment.isUnavailableSlot && (
                                                    <Tooltip
                                                      title="View Insurance"
                                                      placement="top"
                                                      arrow
                                                      >
                                                      <Box
                                                        onClick={() => handleViewInsuranceDetails(appointment)}sx={{
                                                        borderRadius: "50px",
                                                        height: "40px",
                                                        width: "40px",
                                                        cursor: "pointer",
                                                        ":hover": {
                                                          backgroundColor: "rgba(46, 45, 45, 0.2)"
                                                        }
                                                        }}>
                                                        <Icon
                                                          src={require("../../assets/icons/insurance_card_green.png")}
                                                          size="small"
                                                        />
                                                      </Box>
                                                    </Tooltip>    
                                                  )}

                                                  {/* Remove */}
                                                  {appointment.status !== "Completed" && appointment.status !== "completed" && (
                                                    <Tooltip
                                                      title="Remove"
                                                      placement="top"
                                                      arrow
                                                      >
                                                      <Box
                                                        onClick={() => handleRemoveAppointment(appointment.id)}
                                                        sx={{
                                                          borderRadius: "50px",
                                                          height: "40px",
                                                          width: "40px",
                                                          cursor: "pointer",
                                                          ":hover": {
                                                            backgroundColor: "rgba(46, 45, 45, 0.2)"
                                                          }
                                                        }}>
                                                        <Icon
                                                          src={require("../../assets/delete.png")}
                                                          size="small"
                                                        />
                                                      </Box>
                                                    </Tooltip>
                                                  )}
                                                </Stack>
                                              </Stack>

                                              {/* Status & Time */}
                                              <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between"}}>
                                                {/* Status */}
                                                <Stack
                                                  direction="row"
                                                  spacing={1}
                                                  sx={{
                                                    padding: "5px 15px",
                                                  }}>
                                                  <Text
                                                    text="Status:"
                                                    variant="manageListBold"
                                                  />
                                                  <Text
                                                    text={appointment.status}
                                                    variant={
                                                      appointment.status === "Pending" ? "manageListPending" :
                                                      appointment.status === "Confirmed" ? "manageListConfirmed" :
                                                      appointment.status === "Completed" ? "manageListCompleted" :
                                                      "manageListBold"
                                                    }
                                                  />
                                                </Stack>
                                                {/* Time */}
                                                <Stack
                                                  direction="row"
                                                  spacing={1}
                                                  sx={{
                                                    padding: "5px 15px",
                                                  }}>
                                                  <Text
                                                    text="Time:"
                                                    variant="manageListBold"
                                                  />
                                                  <Text
                                                    text={appointment.startTime && appointment.endTime
                                                      ? `${formatTimeConsistent(appointment.startTime)} - ${formatTimeConsistent(appointment.endTime)}`
                                                      : appointment.time
                                                      ? `${formatTimeConsistent(appointment.time)} - ${formatTimeConsistent(appointment.endTime || minutesToTime(timeToMinutes(appointment.time) + (appointment.duration || 30)))}`
                                                      : ""}
                                                    variant="manageListRegular"/>
                                                </Stack>
                                              </Stack>

                                              {/* Services & Dentist */}
                                              <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between"}}>
                                                {/* Services */}
                                                <Stack
                                                  direction="row"
                                                  spacing={1}
                                                  sx={{
                                                    padding: "5px 15px",
                                                  }}>
                                                  <Text
                                                    text="Service(s):"
                                                    variant="manageListBold"
                                                    />
                                                </Stack>

                                                {/* Dentist */}
                                                <Stack
                                                  direction="row"
                                                  spacing={1}
                                                  sx={{
                                                    padding: "5px 15px",
                                                  }}>
                                                  <Text
                                                    text="Dentist:"
                                                    variant="manageListBold"
                                                  />
                                                  <Text
                                                    text={appointment.dentist || "N/A"}
                                                    variant="manageListRegular"
                                                  />
                                                </Stack>
                                              </Stack>

                                              {/* Service List */}
                                              <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between", padding: "5px 15px",}}>
                                                <Text
                                                  text={appointment.services && Array.isArray(appointment.services) 
                                                    ? appointment.services.map(service => 
                                                        typeof service === 'object' ? service.name : service
                                                      ).join(", ") 
                                                    : ""}
                                                  variant="manageListRegular"
                                                />
                                              </Stack>
                                            </Box>
                                          ));
                                        }

                                        return (
                                          <div style={{ 
                                            textAlign: "center", 
                                            padding: "40px", 
                                            color: "#333" 
                                          }}>
                                            {selectedDentist &&
                                            isDentistSchedulePlottingEnabled(chairSettings) &&
                                            !dentistHasPlottedSchedule(
                                              dentistSchedulesForDate,
                                              selectedDentist
                                            )
                                              ? `There are no available timeslots for ${getDentistDisplayNameFromUID(selectedDentist)} on this date.`
                                              : `No appointments found for ${selectedDentist ? getDentistDisplayNameFromUID(selectedDentist) : "this dentist"} on this date.`}
                                          </div>
                                        );
                                      })()}
                                    </Grid>
                                </Grid>
                              </Grid>
                              {/* Time Slots */}
                              <Grid
                                id="timeslot right panel"
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 12,
                                  lg: 12,
                                  xl: 6,
                                }}
                                display={{
                                  xs: (!selectedDentist || selectedDentist === "") && !showAllBookedAppointments && !showPendingAppointments && !showConfirmedAppointments && !isCurrentUserDentist() ? "none" : "block",
                                  sm: (!selectedDentist || selectedDentist === "") && !showAllBookedAppointments && !showPendingAppointments && !showConfirmedAppointments && !isCurrentUserDentist() ? "none" : "block",
                                  md: (!selectedDentist || selectedDentist === "") && !showAllBookedAppointments && !showPendingAppointments && !showConfirmedAppointments && !isCurrentUserDentist() ? "none" : "block",
                                  lg: (!selectedDentist || selectedDentist === "") && !showAllBookedAppointments && !showPendingAppointments && !showConfirmedAppointments && !isCurrentUserDentist() ? "none" : "block",
                                  xl: (!selectedDentist || selectedDentist === "") && !showAllBookedAppointments && !showPendingAppointments && !showConfirmedAppointments && !isCurrentUserDentist() ? "none" : "block",
                                }} 
                                item
                                sx={{
                                  marginTop: "0",
                                  paddingTop: "0",
                                }}
                                >
                                {/* Legends & Time Slot*/}
                                <Grid
                                  container
                                  sx={{
                                    // padding: "10px",
                                    width: "100%",
                                    height: "100%",
                                    maxHeight: "550px",
                                    // height: "100vh",
                                    // backgroundColor: "blue",
                                    justifyContent: "start",
                                    alignItems: "flex-start",
                                    "& .MuiGrid-item": {
                                      display: "flex",
                                      justifyContent: "center",
                                      // backgroundColor: "blue",
                                    },
                                  }}>
                                  {/* Time Slot */}
                                  <Grid 
                                    size={{ 
                                      xs: 12, 
                                      sm: 12, 
                                      md: 12, 
                                      lg: 12, 
                                      xl: 12 
                                    }} 
                                    item
                                    sx={{
                                      boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
                                    }}
                                    >
                                    {/* Time Slots Schedule */}
                                    <div 
                                      id="right panel"
                                      style={{ 
                                        padding: showAllBookedAppointments || showPendingAppointments || showConfirmedAppointments ? "20px" : "0", 
                                        paddingTop: "0",
                                        maxHeight: "520px", 
                                        overflowY: "auto",
                                        backgroundColor: "#FEFEFE",
                                      }}>

                                      {(() => {
                                        // Check if filters are active first
                                        const filteredData = getFilteredAppointmentsByDentist();
                                        if (filteredData) {
                                          const dentistNames = Object.keys(filteredData);
                                          if (dentistNames.length === 0) {
                                            return (
                                              null
                                            );
                                          }
                                          // Show filtered appointments in the main table
                                          return (
                                            <div>
                                              {dentistNames.map(dentistName => (
                                                <Box 
                                                  key={dentistName}
                                                  sx={{
                                                    borderTopLeftRadius: "8px",
                                                    borderTopRightRadius: "8px",
                                                    backgroundColor: "rgba(46, 45, 45, 1)",
                                                    marginY: "10px",
                                                    border: "2px solid rgba(89, 84, 74, .20)",
                                                  }}>
                                                  <Box
                                                    id="doc name"
                                                    sx={{
                                                      padding: "10px"
                                                    }}>
                                                    <Text
                                                      text={dentistName}
                                                      variant="accDentistTitle"
                                                    />
                                                  </Box>
                                                  {filteredData[dentistName].map((appointment) => (
                                                    <Box
                                                      key={appointment.id} 
                                                      sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        height: "100%",
                                                        borderBottom: "1px solid #585858",
                                                        background: 
                                                          appointment.status === "Pending" || appointment.status === "New" ? "#b4996cff" : 
                                                          appointment.status === "Confirmed" ? "#43674eff" : 
                                                          appointment.status === "Completed" ? "rgba(44, 103, 133, .9)" : "#DBEAFE",
                                                        transition: "all 0.2s ease",
                                                        minHeight: "44px",
                                                        pr: "20px",
                                                        justifyContent: "space-between"
                                                      }}
                                                      >
                                                      <Text
                                                        text={appointment.startTime && appointment.endTime
                                                          ? `${formatTimeConsistent(appointment.startTime)} - ${formatTimeConsistent(appointment.endTime)}`
                                                          : appointment.time
                                                          ? `${formatTimeConsistent(appointment.time)} - ${formatTimeConsistent(appointment.endTime || minutesToTime(timeToMinutes(appointment.time) + (appointment.duration || 30)))}`
                                                          : ""}
                                                        variant="timeSlotReserved"
                                                      />
                                                      <Text
                                                        text={appointment.patientDisplay || appointment.patientName || appointment.name}
                                                        variant="timeSlotReserved"
                                                      />
                                                      <div style={{ 
                                                        width: "80px", 
                                                        fontSize: "16px", 
                                                        textAlign: "center",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                        }}>
                                                        <Box 
                                                          sx={{ 
                                                          textAlign: "center",
                                                          }}
                                                          >
                                                          {appointment.status === "Confirmed" ? (
                                                              <Icon
                                                                src={require("../../assets/icons/check_light.png")}
                                                                size="small"
                                                              />
                                                            ) : appointment.status === "Completed" ? (
                                                              <span style={{ color: "#1e3a8a" }}>📋✓</span>
                                                            ) : appointment.status === "Pending" || appointment.status === "New" ? (
                                                              <Icon
                                                                src={require("../../assets/icons/clock_filled.png")}
                                                                size="small"
                                                              />
                                                            ) : (
                                                              <span style={{ color: "orange" }}>!</span>
                                                            )
                                                          }
                                                        </Box>
                                                      </div>
                                                    </Box>
                                                  ))}
                                                </Box>
                                              ))}
                                            </div>
                                          );
                                        }

                                        // Only show time slots if both date and dentist are selected (when no filters are active)
                                        if (!selectedDateAppointment || !selectedDentist || selectedDentist === "") {
                                          return (
                                            <div style={{
                                              padding: "40px",
                                              textAlign: "center",
                                              color: "#666",
                                              fontSize: "16px"
                                              }}>
                                              {!selectedDateAppointment && (!selectedDentist || selectedDentist === "") && !isCurrentUserDentist() ? "Please select a date and dentist" :
                                              !selectedDateAppointment ? "Please select a date in the calendar" :
                                              (!selectedDentist || selectedDentist === "") && !isCurrentUserDentist() ? "Please select a dentist" :
                                              !selectedDateAppointment ? "Please select a date" : "Add Appointment"}
                                            </div>
                                          );
                                        }

                                        if (
                                          isDentistSchedulePlottingEnabled(chairSettings) &&
                                          selectedDentist &&
                                          !dentistHasPlottedSchedule(
                                            dentistSchedulesForDate,
                                            selectedDentist
                                          )
                                        ) {
                                          return null;
                                        }
                                        // For individual dentist view, use the original logic
                                        let slotsForRender;
                                        const officeStartTime = 9 * 60; // 9:00 AM
                                        const officeEndTime = 18 * 60; // 6:00 PM
                                        const slotInterval = 30; // 30-minute intervals

                                        // In chair mode, bench dentists mirror the union of roster appointments
                                        // for the selected date so the timeline matches the roster baseline.
                                        const inChairModeMain = isChairCapacityConfigured(chairSettings);
                                        const isBenchSelectedDentistMain =
                                          inChairModeMain &&
                                          selectedDentist &&
                                          !isDentistOnRoster(selectedDentist, chairSettings);
                                        const mainDateBenchLinks = isBenchSelectedDentistMain
                                          ? Object.values(chairSwapsBySlotForMainDate || {}).filter(
                                              (entry) =>
                                                entry?.toBenchUid === selectedDentist &&
                                                entry?.fromRosterUid
                                            )
                                          : [];
                                        const linkedBenchUidsForRosterMain = !isBenchSelectedDentistMain
                                          ? getLinkedBenchUidsForRosterFromSwaps({
                                              rosterUid: selectedDentist,
                                              swapsBySlot: chairSwapsBySlotForMainDate,
                                            })
                                          : [];
                                        const inheritedSwapSlotsForRosterMain =
                                          !isBenchSelectedDentistMain && selectedDentist
                                            ? Object.values(chairSwapsBySlotForMainDate || {})
                                                .filter(
                                                  (entry) =>
                                                    entry?.fromRosterUid === selectedDentist &&
                                                    entry?.toBenchUid
                                                )
                                                .map((entry) => entry?.slot)
                                                .filter(Boolean)
                                            : [];
                                        const alignedRosterUidForMain = isBenchSelectedDentistMain
                                          ? (mainDateBenchLinks[0]?.fromRosterUid || null)
                                          : null;
                                        const benchSwapSlotsForMain = isBenchSelectedDentistMain
                                          ? Object.entries(chairSwapsBySlotForMainDate || {})
                                              .filter(([, entry]) => entry?.toBenchUid === selectedDentist)
                                              .map(([, entry]) => entry?.slot)
                                              .filter(Boolean)
                                          : [];
                                        if (isBenchSelectedDentistMain && !alignedRosterUidForMain) {
                                          return null;
                                        }
                                        const dateAppointmentsForMain = (() => {
                                          if (isBenchSelectedDentistMain && !alignedRosterUidForMain) return [];
                                          if (!selectedDateAppointment) return [];
                                          const yearMain = selectedDateAppointment.toDate().getFullYear();
                                          const monthMain = String(selectedDateAppointment.toDate().getMonth() + 1).padStart(2, '0');
                                          const dayMain = String(selectedDateAppointment.toDate().getDate()).padStart(2, '0');
                                          const dateKeyMain = `${yearMain}-${monthMain}-${dayMain}`;
                                          return (Array.isArray(timelineDisplayAppointments)
                                            ? timelineDisplayAppointments
                                            : []
                                          ).filter((appt) => {
                                            if (!appt) return false;
                                            if (!appointmentBelongsToDateDoc(appt, dateKeyMain))
                                              return false;
                                            if (appt.status === 'Cancelled' || appt.status === 'cancelled') return false;
                                            const apptDentistUid = appt.dentistId || null;
                                            if (isBenchSelectedDentistMain) {
                                              return apptDentistUid === selectedDentist;
                                            }
                                            if (apptDentistUid) {
                                              return (
                                                apptDentistUid === selectedDentist ||
                                                linkedBenchUidsForRosterMain.includes(apptDentistUid)
                                              );
                                            }
                                            return false;
                                          });
                                        })();

                                        // Sort appointments by start time
                                        const sortedAppointmentsByTime = [...dateAppointmentsForMain].sort((a, b) => {
                                          const aStart = timeToMinutes(a.startTime || a.time);
                                          const bStart = timeToMinutes(b.startTime || b.time);
                                          return aStart - bStart;
                                        });

                                        // Always truth-based slots for roster (including when a secondary is assigned part-day).
                                        const useStandardGridForRosterMain = false;

                                        if (useStandardGridForRosterMain) {
                                          slotsForRender = buildStandardHalfHourSlotLabels({})
                                            .map((label) => {
                                              const range = parseChairSlotLabelToRange(
                                                label,
                                                timeToMinutes
                                              );
                                              if (!range) return null;
                                              const overlapping = sortedAppointmentsByTime.filter(
                                                (appt) => {
                                                  const aStart = timeToMinutes(
                                                    appt.startTime || appt.time
                                                  );
                                                  const aEnd = appt.endTime
                                                    ? timeToMinutes(appt.endTime)
                                                    : aStart + (appt.duration || 30);
                                                  return aStart < range.end && aEnd > range.start;
                                                }
                                              );
                                              let appointment = null;
                                              for (const appt of overlapping) {
                                                if ((appt.dentistId || "") === selectedDentist) {
                                                  appointment = appt;
                                                  break;
                                                }
                                              }
                                              if (!appointment) {
                                                appointment =
                                                  overlapping.find((appt) =>
                                                    linkedBenchUidsForRosterMain.includes(
                                                      appt.dentistId
                                                    )
                                                  ) || null;
                                              }
                                              return {
                                                start: range.start,
                                                end: range.end,
                                                timeStr: label,
                                                appointment,
                                              };
                                            })
                                            .filter(Boolean);
                                        } else {
                                          const timeSlots = [];

                                        // First, create slots for all appointments at their original times
                                        const appointmentSlots = [];
                                        sortedAppointmentsByTime.forEach(appointment => {
                                          const apptStart = timeToMinutes(appointment.startTime || appointment.time);
                                          // Use appointment.endTime if available, otherwise calculate from duration
                                          const apptEnd = appointment.endTime 
                                            ? timeToMinutes(appointment.endTime)
                                            : apptStart + (appointment.duration || 30);

                                          const apptDentistUid = appointment.dentistId || null;
                                          const isRosterMirrorOfBenchAppointment =
                                            !isBenchSelectedDentistMain &&
                                            apptDentistUid &&
                                            apptDentistUid !== selectedDentist &&
                                            linkedBenchUidsForRosterMain.includes(apptDentistUid);

                                          const pushAppointmentSlotRange = (rangeStart, rangeEnd) => {
                                            const startHours = Math.floor(rangeStart / 60);
                                            const startMinutes = rangeStart % 60;
                                            const startAmpm = startHours >= 12 ? 'PM' : 'AM';
                                            const startDisplayHours = startHours % 12 || 12;
                                            const startTimeStr = `${startDisplayHours}:${startMinutes.toString().padStart(2, '0')} ${startAmpm}`;
                                            const endHours = Math.floor(rangeEnd / 60);
                                            const endMinutes = rangeEnd % 60;
                                            const endAmpm = endHours >= 12 ? 'PM' : 'AM';
                                            const endDisplayHours = endHours % 12 || 12;
                                            const endTimeStr = `${endDisplayHours}:${endMinutes.toString().padStart(2, '0')} ${endAmpm}`;
                                            const timeStr = `${startTimeStr} - ${endTimeStr}`;
                                            appointmentSlots.push({
                                              start: rangeStart,
                                              end: rangeEnd,
                                              timeStr,
                                              appointment: appointment,
                                            });
                                          };

                                          if (isRosterMirrorOfBenchAppointment) {
                                            const capEnd = Math.min(apptEnd, officeEndTime);
                                            let seg = apptStart;
                                            while (seg < capEnd) {
                                              const segEnd = Math.min(seg + slotInterval, capEnd);
                                              pushAppointmentSlotRange(seg, segEnd);
                                              seg = segEnd;
                                            }
                                          } else {
                                            pushAppointmentSlotRange(apptStart, apptEnd);
                                          }
                                        });

                                        // Sort appointment slots by start time
                                        appointmentSlots.sort((a, b) => a.start - b.start);
                                        const coalescedAppointmentSlotsMain =
                                          coalesceAppointmentSlotsByStableId(appointmentSlots);

                                        // Merge overlapping appointments into continuous blocks
                                        const mergedBlocks = [];
                                        let currentBlock = null;

                                        coalescedAppointmentSlotsMain.forEach((slot) => {
                                          if (!currentBlock) {
                                            // Start a new block
                                            currentBlock = {
                                              start: slot.start,
                                              end: slot.end,
                                              appointments: [slot],
                                            };
                                          } else if (slot.start < currentBlock.end) {
                                            // Overlaps the current block (strict < so back-to-back stays separate)
                                            currentBlock.end = Math.max(currentBlock.end, slot.end);
                                            currentBlock.appointments.push(slot);
                                          } else {
                                            // No overlap, finalize current block and start new one
                                            mergedBlocks.push(currentBlock);
                                            currentBlock = {
                                              start: slot.start,
                                              end: slot.end,
                                              appointments: [slot],
                                            };
                                          }
                                        });
                                        
                                        // Don't forget the last block
                                        if (currentBlock) {
                                          mergedBlocks.push(currentBlock);
                                        }

                                        // Now create time slots from merged blocks
                                        mergedBlocks.forEach(block => {
                                          // Add all appointments in this block
                                          block.appointments.forEach(appointmentSlot => {
                                            timeSlots.push(appointmentSlot);
                                          });
                                        });

                                        // Sort all slots by start time
                                        timeSlots.sort((a, b) => a.start - b.start);

                                        const parseSlotLabelRangeMain = (label) => {
                                          if (!label || typeof label !== "string") return null;
                                          const [startRaw, endRaw] = label
                                            .split(" - ")
                                            .map((part) => part.trim());
                                          if (!startRaw || !endRaw) return null;
                                          const start = timeToMinutes(startRaw);
                                          const end = timeToMinutes(endRaw);
                                          if (
                                            typeof start !== "number" ||
                                            typeof end !== "number" ||
                                            Number.isNaN(start) ||
                                            Number.isNaN(end) ||
                                            end <= start
                                          ) {
                                            return null;
                                          }
                                          return { start, end };
                                        };
                                        const inheritedBoundaryMinutesMain = (() => {
                                          const sourceSlots = isBenchSelectedDentistMain
                                            ? benchSwapSlotsForMain
                                            : inheritedSwapSlotsForRosterMain;
                                          return Array.from(
                                            new Set(
                                              (Array.isArray(sourceSlots) ? sourceSlots : [])
                                                .map((slotLabel) => parseSlotLabelRangeMain(slotLabel))
                                                .filter(Boolean)
                                                .flatMap((range) => [range.start, range.end])
                                            )
                                          ).sort((a, b) => a - b);
                                        })();
                                        const clampEndToInheritedBoundaryMain = (
                                          startMinutes,
                                          proposedEndMinutes
                                        ) => {
                                          const boundary = inheritedBoundaryMinutesMain.find(
                                            (minute) =>
                                              minute > startMinutes &&
                                              minute < proposedEndMinutes
                                          );
                                          return boundary || proposedEndMinutes;
                                        };

                                        const unavailableRangesMain =
                                          parseUnavailableLabelsToMinuteRanges(
                                            mainAppointmentUnavailableSlots,
                                            timeToMinutes
                                          );
                                        const pushGapSlotsMain = (gapStart, gapEnd) => {
                                          forEachTimelineGapSlot({
                                            gapStart,
                                            gapEnd,
                                            unavailableRanges: unavailableRangesMain,
                                            slotInterval,
                                            officeEndTime,
                                            minSlotMinutes: 20,
                                            clampEnd: clampEndToInheritedBoundaryMain,
                                            formatRangeLabel: formatSlotRangeLabelFromMinutes,
                                            onSlot: ({ start, end, timeStr }) => {
                                              timeSlots.push({
                                                start,
                                                end,
                                                timeStr,
                                                appointment: null,
                                              });
                                            },
                                          });
                                        };

                                        let currentTime = officeStartTime;
                                        mergedBlocks.forEach((block) => {
                                          if (
                                            currentTime < block.start &&
                                            currentTime < officeEndTime
                                          ) {
                                            pushGapSlotsMain(
                                              currentTime,
                                              Math.min(block.start, officeEndTime)
                                            );
                                          }
                                          currentTime = block.end;
                                        });
                                        if (currentTime < officeEndTime) {
                                          pushGapSlotsMain(currentTime, officeEndTime);
                                        }

                                        // Sort all slots by start time again (after adding available slots)
                                        timeSlots.sort((a, b) => a.start - b.start);
                                        slotsForRender = timeSlots;
                                        }

                                        const skipMergeMirrorOrOtherOwner =
                                          selectedDentist &&
                                          ((slot) => {
                                            const appt = slot?.appointment;
                                            if (!appt) return false;
                                            const owner = appt.dentistId;
                                            return !!(
                                              owner && owner !== selectedDentist
                                            );
                                          });
                                        slotsForRender = mergeContiguousBookedTimelineRows(
                                          slotsForRender,
                                          {
                                            skipMergeForSlot:
                                              skipMergeMirrorOrOtherOwner || undefined,
                                          }
                                        );

                                        // Show all slots (appointments and valid available slots)
                                        // Note: available slot fragments shorter than 20 minutes are filtered out.
                                        return slotsForRender.map((slot, index) => {
                                          const slotAppointmentDentistUid = slot.appointment
                                            ? resolveAppointmentDentistUid(slot.appointment)
                                            : null;
                                          const isBenchOwnedAppointmentMain =
                                            !!slot.appointment &&
                                            slotAppointmentDentistUid === selectedDentist;
                                          const isLinkedBenchOwnedAppointmentMain =
                                            !!slot.appointment &&
                                            !isBenchSelectedDentistMain &&
                                            linkedBenchUidsForRosterMain.includes(slotAppointmentDentistUid);
                                          const isLinkedRosterOwnedAppointmentMain =
                                            !!slot.appointment &&
                                            isBenchSelectedDentistMain &&
                                            alignedRosterUidForMain &&
                                            slotAppointmentDentistUid === alignedRosterUidForMain;
                                          const isBenchSlotWithinInheritedMain =
                                            isBenchSelectedDentistMain &&
                                            (slot.appointment
                                              ? doesSlotRangeOverlapSwapSlots(
                                                  slot.timeStr,
                                                  benchSwapSlotsForMain
                                                ) ||
                                                slotAppointmentDentistUid === selectedDentist
                                              : doesSlotOverlapSwapSlots(
                                                  slot.timeStr,
                                                  benchSwapSlotsForMain
                                                ));
                                          // Check if this time slot is unavailable for the selected dentist
                                          const hasBenchOwnedSwapSlotsForMain =
                                            isBenchSelectedDentistMain &&
                                            benchSwapSlotsForMain.length > 0;
                                          const baseUnavailable = isBenchSelectedDentistMain
                                            ? !isBenchSlotWithinInheritedMain ||
                                              isSlotEffectivelyUnavailableForDentist({
                                                slot: slot.timeStr,
                                                dentistUid: selectedDentist,
                                                dentistUnavailableSlots: mainAppointmentUnavailableSlots,
                                                hasUnavailabilityRecord:
                                                  hasMainUnavailableRecord ||
                                                  hasBenchOwnedSwapSlotsForMain,
                                                settings: chairSettings,
                                                timeToMinutes,
                                              })
                                            : isSlotEffectivelyUnavailableForDentist({
                                                slot: slot.timeStr,
                                                dentistUid: selectedDentist,
                                                dentistUnavailableSlots: mainAppointmentUnavailableSlots,
                                                hasUnavailabilityRecord: hasMainUnavailableRecord,
                                                settings: chairSettings,
                                                timeToMinutes,
                                              });
                                          let rosterInheritedMain = false;
                                          if (
                                            !slot.appointment &&
                                            !isBenchSelectedDentistMain &&
                                            selectedDentist &&
                                            isDentistOnRoster(selectedDentist, chairSettings)
                                          ) {
                                            const slotRangeForRoster = parseChairSlotLabelToRange(
                                              slot.timeStr,
                                              timeToMinutes
                                            );
                                            rosterInheritedMain =
                                              !!slotRangeForRoster &&
                                              inheritedSwapSlotsForRosterMain.some((swapSlot) => {
                                                const swapRange = parseChairSlotLabelToRange(
                                                  swapSlot,
                                                  timeToMinutes
                                                );
                                                return (
                                                  !!swapRange &&
                                                  slotRangeForRoster.start < swapRange.end &&
                                                  slotRangeForRoster.end > swapRange.start
                                                );
                                              });
                                          }
                                          const isUnavailable = slot.appointment
                                            ? isBenchSelectedDentistMain
                                              ? !isBenchSlotWithinInheritedMain
                                              : (isLinkedBenchOwnedAppointmentMain ||
                                                 isLinkedRosterOwnedAppointmentMain)
                                            : baseUnavailable || rosterInheritedMain;
                                          
                                          // Determine background and border colors based on appointment status
                                          let backgroundColor = "white";
                                          let borderColor = "transparent";
                                          let borderRadius = "0px";

                                          if (isUnavailable) {
                                            backgroundColor = "#f5f5f5"; // Light gray background
                                            borderColor = "#666"; // Dark grey border
                                            borderRadius = "8px";
                                          } else if (slot.appointment) {
                                            if (slot.appointment.status === "Pending" || slot.appointment.status === "New") {
                                              backgroundColor = "#b4996cff";
                                            } else if (slot.appointment.status === "Confirmed") {
                                              backgroundColor = "#43674eff";
                                            } else if (slot.appointment.status === "Completed") {
                                              backgroundColor = "#rgba(44, 103, 133, .9)";
                                            } else {
                                              backgroundColor = "#f5f5f5"; // Light gray background for other statuses
                                              borderColor = "#999"; // Gray border
                                              borderRadius = "8px";
                                            }
                                          }
                                          return (
                                            <Box 
                                              key={index} 
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                borderBottom: "1px solid #585858",
                                                background: isUnavailable 
                                                  ? "rgba(46, 45, 45, 0.2)" // Grey background for unavailable slots (matching PatientAppointmentBooking)
                                                  : slot.appointment 
                                                    ? (slot.appointment.status === "Pending" ? "#b4996cff" : slot.appointment.status === "Confirmed" ? "#43674eff" : "rgba(44, 103, 133, .9)") 
                                                    : "#FEFEFE",
                                                transition: "all 0.2s ease",
                                                minHeight: "44px",
                                                pr: "20px",
                                                justifyContent: "space-between",
                                                opacity: isUnavailable ? 0.8 : 1
                                              }}
                                            >
                                            <Box
                                              sx={{
                                                opacity: isUnavailable ? 0.5 : 1,
                                                color: isUnavailable ? "#333" : undefined,
                                              }}
                                            >
                                              <Text
                                                text={slot.timeStr}
                                                variant={
                                                  slot.appointment && !isUnavailable
                                                    ? "timeSlotReserved"
                                                    : "historyDateTime"
                                                }
                                              />
                                            </Box>
                        
                      <div style={{ 
                        flex: 1, 
                        fontSize: "14px",
                         color: isUnavailable ? "#999" : (slot.appointment ? "#333" : "#999"),
                        textAlign: "center"
                      }}>
                        {isUnavailable ? (
                          ""
                        ) : slot.appointment ? (
                                  <Text
                                    text={slot.appointment.patientDisplay}
                                    variant="timeSlotReserved"/>
                        ) : (
                          ""
                        )}
                      </div>

                      <div style={{ 
                        width: "80px", 
                        fontSize: "14px",
                        textAlign: "center",
                        opacity: isUnavailable ? 0.5 : 1
                      }}>
                        {isUnavailable ? (
                               <Icon
                                src={require("../../assets/icons/cancel_light.png")}
                                size="unavailable"/>
                        ) : slot.appointment ? (
                          <>
                            {slot.appointment.status === "Confirmed" ? (
                               <Icon
                                 src={require("../../assets/icons/check_light.png")}
                                 size="small"/>
                            ) : slot.appointment.status === "Completed" ? (
                               <Icon
                                src={require("../../assets/icons/complete.png")}
                                size="small"/>
                            ) : slot.appointment.status === "Pending" || slot.appointment.status === "New" ? (
                               <Icon
                                 src={require("../../assets/icons/clock_filled.png")}
                                 size="small"/>
                            ) : null}
                          </>
                        ) : ( null )}
                      </div>
                      
                      </Box>
                  );
                                        });
                                      })()}
                                    </div>
                                  </Grid>


            </Grid>

            </Grid>

            </Grid>


          </Grid>
          </Stack>

              </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>

      </Box>

      {/* Edit Appointment Modal */}
      <Dialog
        open={showEditForm}
        onClose={(event, reason) => {
          if (isSavingAppointmentDetails) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            handleEditClose();
          }
        }}
        maxWidth="md"
        fullWidth={true}
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: "#FDF9F3",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            width: "600px",
            maxWidth: "90vw"
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#BA9A65",
            color: "white",
            padding: "20px 24px",
            paddingRight: "64px",
            borderRadius: "16px 16px 0 0",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 700,
              fontSize: "1.25rem",
              lineHeight: 1.1,
              maxWidth: "100%",
              whiteSpace: "nowrap",
              textAlign: "center",
              color: "#FFFFFF",
            }}
            noWrap
          >
            Appointment Details
          </Typography>
          <IconButton
            onClick={handleEditClose}
            disabled={isSavingAppointmentDetails}
            sx={{
              position: "absolute",
              right: "16px",
              top: "16px",
              backgroundColor: "transparent",
              border: "1px solid rgba(253, 249, 243, 0.8)",
              color: "#FDF9F3",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              "&:hover": {
                backgroundColor: "white",
                color: "#C5A572",
                borderColor: "white",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "24px",
            backgroundColor: "#FDF9F3",
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            scrollbarGutter: "stable",
          }}
        >

          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (isSavingAppointmentDetails) return;
              handleEditFormSubmit(editFormData);
            }}
            sx={{ 
              display: "flex", 
              flexDirection: "column", 
              gap: "24px"
            }}
          >
                         {/* Date and Status Row */}
             <div style={{ display: "flex", gap: "40px", width: "100%", boxSizing: "border-box" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ display: "block", marginBottom: "12px", fontWeight: "600", color: "#333", fontSize: "16px" }}>
                  Date:
                </label>
                <input
                  type="text"
                  readOnly
                  aria-readonly="true"
                  title="Date matches the calendar; change the day on the schedule if needed."
                  value={editFormData.date || ""}
                  style={{ 
                    width: "100%", 
                    padding: "16px", 
                    border: "2px solid #C7A76C", 
                    borderRadius: "12px",
                    fontSize: "16px",
                    height: "52px",
                    boxSizing: "border-box",
                    backgroundColor: "#f5f5f5",
                    cursor: "default",
                    color: "#333",
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ display: "block", marginBottom: "12px", fontWeight: "600", color: "#333", fontSize: "16px" }}>
                  Status:
                </label>
                <select
                  value={editFormData.status || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                  style={{ 
                    width: "100%", 
                    padding: "16px", 
                    border: "2px solid #C7A76C", 
                    borderRadius: "12px",
                    fontSize: "16px",
                    height: "52px",
                    boxSizing: "border-box",
                    backgroundColor: "white"
                  }}
                >
                  <option value="Pending">Pending</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
            </div>

                         {/* Start Time and Duration Row */}
             <div style={{ display: "flex", gap: "40px", width: "100%", boxSizing: "border-box" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ display: "block", marginBottom: "12px", fontWeight: "600", color: "#333", fontSize: "16px" }}>
                  Start Time:
                </label>
                <input
                  type="time"
                  value={editFormData.startTime || editFormData.time || ""}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                  disabled={editFormData.status === "Confirmed" || editFormData.status === "Completed"}
                  style={{ 
                    width: "100%", 
                    padding: "16px", 
                    border: "2px solid #C7A76C", 
                    borderRadius: "12px",
                    fontSize: "16px",
                    height: "52px",
                    boxSizing: "border-box",
                    backgroundColor: (editFormData.status === "Confirmed" || editFormData.status === "Completed") ? "#f5f5f5" : "white",
                    cursor: (editFormData.status === "Confirmed" || editFormData.status === "Completed") ? "not-allowed" : "text",
                    opacity: (editFormData.status === "Confirmed" || editFormData.status === "Completed") ? 0.6 : 1
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ display: "block", marginBottom: "12px", fontWeight: "600", color: "#333", fontSize: "16px" }}>
                  Duration (minutes):
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={editFormData.duration || ""}
                  placeholder="30"
                  onChange={(e) => handleDurationChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "+" || e.key === "e" || e.key === "E") {
                      e.preventDefault();
                    }
                  }}
                  disabled={editFormData.status === "Confirmed" || editFormData.status === "Completed"}
                  style={{ 
                    width: "100%", 
                    padding: "16px", 
                    border: "2px solid #C7A76C", 
                    borderRadius: "12px",
                    fontSize: "16px",
                    height: "52px",
                    boxSizing: "border-box",
                    backgroundColor: (editFormData.status === "Confirmed" || editFormData.status === "Completed") ? "#f5f5f5" : "white",
                    cursor: (editFormData.status === "Confirmed" || editFormData.status === "Completed") ? "not-allowed" : "text",
                    opacity: (editFormData.status === "Confirmed" || editFormData.status === "Completed") ? 0.6 : 1
                  }}
                />
                <small style={{ color: "#333", fontSize: "14px", marginTop: "8px", display: "block", fontWeight: "500" }}>
                  End Time: {editFormData.endTime ? formatTimeConsistent(editFormData.endTime) : ""}
                </small>
              </div>
            </div>

            {/* Services */}
            <div style={{ width: "100%", boxSizing: "border-box" }}>
              <label style={{ display: "block", marginBottom: "12px", fontWeight: "600", color: "#333", fontSize: "16px" }}>
                Services:
              </label>
              <ServicesList
                selectedServices={editFormData.services.map(s => s.name)}
                toggleService={toggleService}
                isEditMode={true}
                servicePrices={servicePrices}
                onServicePriceChange={handleServicePriceChange}
                dentistUID={resolveAppointmentDentistUid(editFormData)}
                allowServicePriceEdit={(editFormData.status || "").trim().toLowerCase() === "completed"}
              />
            </div>

            {/* Total Bill (only show when status is Completed) */}
            {editFormData.status === "Completed" && (
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#333" }}>
                  Total Bill: ₱{formatNumberWithCommas(calculateTotalBill())}
                </label>
                <div style={{ 
                  fontSize: "14px", 
                  color: "#333", 
                  padding: "16px",
                  background: "#f8f9fa",
                  borderRadius: "8px",
                  border: "1px solid #e9ecef"
                }}>
                  <strong>Service Breakdown:</strong><br />
                  {editFormData.services.map(serviceObj => (
                    <div key={serviceObj.name} style={{ marginTop: "8px" }}>
                      {serviceObj.name}: ₱{formatNumberWithCommas(Number(servicePrices[serviceObj.name]) || 0)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Remarks */}
            <div>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#333" }}>
                Remarks:
              </label>
              <input
                type="text"
                value={editFormData.dentistRemarks || ""}
                onChange={(e) => setEditFormData({ ...editFormData, dentistRemarks: e.target.value })}
                disabled={editFormData.status === "Confirmed"}
                style={{ 
                  width: "100%", 
                  padding: "12px", 
                  border: "1px solid #C7A76C", 
                  borderRadius: "8px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                  backgroundColor: editFormData.status === "Confirmed" ? "#f5f5f5" : "white",
                  cursor: editFormData.status === "Confirmed" ? "not-allowed" : "text",
                  opacity: editFormData.status === "Confirmed" ? 0.6 : 1
                }}
                placeholder="Enter dentist remarks..."
              />
            </div>
          </Box>
        </DialogContent>

        <DialogActions
          disableSpacing
          sx={{
          padding: "16px 24px",
          backgroundColor: "#FDF9F3",
          borderRadius: "0 0 16px 16px",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "stretch",
          gap: "12px",
          // MUI DialogActions normally adds margin-left between siblings for
          // horizontal button rows; disableSpacing clears it — needed here so a
          // second banner does not shift the yellow info strip sideways.
          "& > :not(style) ~ :not(style)": { marginLeft: 0 },
        }}
        >
          {(() => {
            const {
              violated: scheduleBoundaryViolated,
              dentistId: scheduleBoundaryDentistId,
              hasPlottedSchedule,
            } = computeEditViolatesPlottedScheduleBoundaries();
            const midnightExceeded = computeEditExtendsPastCalendarMidnight();
            const anchoredOverlapAppt = computeEditOverlapsAnchoredAppointment();
            const displayedEditValidation = editFormValidationMessage
              ? editFormValidationMessage
              : hasPlottedSchedule && (scheduleBoundaryViolated || midnightExceeded)
                ? getDurationBeyondAssignedTimeslotsMessageForEdit(scheduleBoundaryDentistId)
                : anchoredOverlapAppt
                  ? getEditDurationConflictWithAnchoredMessage(anchoredOverlapAppt)
                  : midnightExceeded
                    ? EDIT_APPOINTMENT_PAST_MIDNIGHT_MESSAGE
                    : "";
            return displayedEditValidation ? (
            <Box
              sx={{
                width: "100%",
                minHeight: "48px",
                padding: "12px 16px",
                backgroundColor: "#fdeff1",
                border: "none",
                borderRadius: "8px",
                textAlign: "center",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#c62828",
                  fontWeight: "500",
                  lineHeight: "1.5",
                }}
              >
                {displayedEditValidation}
              </Typography>
            </Box>
            ) : null;
          })()}
          {editFormData.status === "Confirmed" && (
            <Box
              sx={{
                width: "100%",
                padding: "12px 16px",
                backgroundColor: "#FFFFB7",
                border: "none",
                borderRadius: "8px",
                textAlign: "center",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#333",
                  fontWeight: "500",
                  lineHeight: "1.5"
                }}
              >
                Set the appointment status to pending to update necessary changes to the appointment details (e.g., date, start time, duration).
              </Typography>
            </Box>
          )}
          <Button
            onClick={(e) => {
              e.preventDefault();
              if (isSavingAppointmentDetails) return;
              handleEditFormSubmit(editFormData);
            }}
            disabled={
              isSavingAppointmentDetails ||
              (editFormData.status !== "Confirmed" &&
                editFormData.status !== "Completed" &&
                (!isEditFormStartTimeFieldComplete(editFormData) ||
                  !isEditFormDurationFieldComplete(editFormData)))
            }
            variant="contained"
            sx={{
              alignSelf: "center",
              backgroundColor: "#C5A572",
              color: "white",
              fontWeight: "600",
              padding: "12px 32px",
              borderRadius: "25px",
              textTransform: "none",
              fontSize: "16px",
              minWidth: "160px",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#B8955A"
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(197, 165, 114, 0.45)",
                color: "rgba(255, 255, 255, 0.85)",
              },
            }}
          >
            {isSavingAppointmentDetails ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {showMedicalHistoryModal && medicalHistoryPatientId && (
        <Modal
          isOpen={showMedicalHistoryModal}
          onRequestClose={() => setShowMedicalHistoryModal(false)}
          shouldCloseOnOverlayClick
          shouldCloseOnEsc
          style={{
            overlay: {
              zIndex: 1100,
              backgroundColor: "rgba(0,0,0,0.7)",
            },
            content: {
              // width: "80%",
              // margin: "0 auto",
              position: "absolute",
              padding: "0",
              maxHeight: "calc(100vh - 100px)",
              overflow: "hidden",
              top: "20px",
              borderRadius: "16px",
              backgroundColor: "rgb(241, 241, 241)",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {/* Fixed Header */}
          <div
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "rgb(241, 241, 241)",
              padding: "16px",
              paddingRight: "64px",
              zIndex: 10,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            {/* Close button - top right, circular */}
            <button
              onClick={() => setShowMedicalHistoryModal(false)}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "1px solid rgba(44,37,30,0.06)",
                background: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                fontWeight: "bold",
                color: "#2E2D2D",
                lineHeight: 1,
                transition: "all 0.2s ease",
                boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#C5A572";
                e.target.style.borderColor = "#C5A572";
                e.target.style.color = "#FEFEFE";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                e.target.style.borderColor = "rgba(44,37,30,0.06)";
                e.target.style.color = "#2E2D2D";
              }}
            >
              ✕
            </button>

            {/* Centered title */}
            <h3
              style={{
                textAlign: "center",
                marginBottom: "0px",
                marginTop: "0px",
                fontSize: "24px",
                fontWeight: "bold",
                color: "#3d342b",
              }}
            >
              Medical History for {medicalHistoryPatientName}
            </h3>
          </div>

          {/* Scrollable Content */}
          <div
            style={{
              flex: 1,
              overflow: "auto",
              padding: "16px",
              paddingRight: "8px",
              paddingBottom: "16px",
              minHeight: 0,
            }}
          >
            <MedicalHistory
              ref={medicalHistoryRef}
              patientId={medicalHistoryPatientId}
            />
          </div>

          {/* Fixed Footer with Save Button */}
          <div
            style={{
              flexShrink: 0,
              backgroundColor: "rgb(241, 241, 241)",
              padding: "16px",
              textAlign: "center",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <button
              onClick={async () => {
                if (medicalHistoryRef.current) {
                  await medicalHistoryRef.current.save();
                  // Close the modal after saving
                  setShowMedicalHistoryModal(false);
                }
              }}
              style={{
                background: "rgb(197, 165, 114)",
                color: "#fff",
                border: "none",
                padding: "10px 28px",
                borderRadius: "6px",
                fontSize: "1.08rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgb(185, 153, 102)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "rgb(197, 165, 114)";
              }}
            >
              Save Medical History
            </button>
          </div>
        </Modal>
      )}

      {/* Insurance Details Modal */}
      <Modal
        isOpen={showInsuranceForm}
        onRequestClose={handleInsuranceClose}
        contentLabel="Insurance Details Modal"
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
          },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1001,
          },
        }}
      >
        <ViewInsurance
          isOpen={showInsuranceForm}
          onClose={handleInsuranceClose}
          insuranceDetails={insuranceDetails}
        />
      </Modal>

      {/* Remove Appointment Confirmation Modal */}
      {appointmentToRemove !== null && (
      <Dialog
        open={showRemoveConfirmation && appointmentToRemove !== null && !isClosingRemoveDialog.current}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            cancelRemoveAppointment();
          }
        }}
        maxWidth="sm"
        fullWidth={true}
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: "#FDF9F3",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column"
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#BA9A65",
            color: "white",
            fontWeight: "bold",
            textAlign: "center",
            padding: "20px 24px",
            position: "relative",
            borderRadius: "16px 16px 0 0"
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: "bold",
              fontSize: "1.25rem",
              textTransform: "none"
            }}
          >
            Confirm Appointment Removal
          </Typography>
          <IconButton
            onClick={cancelRemoveAppointment}
            sx={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              "&:hover": {
                backgroundColor: "white",
                color: "#C5A572",
                borderColor: "white"
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers={false}
          sx={{
            backgroundColor: "#FDF9F3",
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "0"
          }}
        >
          <Box sx={{ paddingTop: "24px", paddingRight: "24px", paddingLeft: "24px" }}>
          {appointmentToRemove && (() => {
            const status = appointmentToRemove.status || "";
            const isConfirmed = status === "Confirmed" || status === "confirmed";
            const isPending = status === "Pending" || status === "pending" || status === "New" || status === "new";
            
            // Determine colors based on status
            let backgroundColor = "#fff";
            let borderColor = "#ddd";
            
            if (isConfirmed) {
              backgroundColor = "#e8f5e8";
              borderColor = "#4CAF50";
            } else if (isPending) {
              backgroundColor = "#fff8e1";
              borderColor = "#FFA500";
            }

            return (
              <Box sx={{ marginBottom: "20px" }}>
                <Box
                  sx={{
                    padding: "12px",
                    backgroundColor,
                    border: `2px solid ${borderColor}`,
                    borderRadius: "8px",
                    textAlign: "left"
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#000", fontWeight: "bold", marginBottom: 2, fontSize: "14px" }}
                  >
                    Appointment Details:
                  </Typography>

                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ color: "#000", marginBottom: 0.5, fontSize: "13px" }}>
                        <strong>Name:</strong> {appointmentToRemove.patientDisplay || appointmentToRemove.patientName || appointmentToRemove.name || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#000", marginBottom: 0.5, fontSize: "13px" }}>
                        <strong>Services:</strong> {appointmentToRemove.services && Array.isArray(appointmentToRemove.services)
                          ? appointmentToRemove.services
                              .map((service) => (typeof service === "object" ? service.name : service))
                              .join(", ")
                          : appointmentToRemove.services || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#000", fontSize: "13px" }}>
                        <strong>Time:</strong> {appointmentToRemove.startTime && appointmentToRemove.endTime
                          ? `${formatTimeConsistent(appointmentToRemove.startTime)} - ${formatTimeConsistent(appointmentToRemove.endTime)}`
                          : appointmentToRemove.time
                          ? formatTimeConsistent(appointmentToRemove.time)
                          : "N/A"}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: "right" }}>
                      <Typography variant="body2" sx={{ color: "#000", marginBottom: 0.5, fontSize: "13px" }}>
                        <strong>Dentist:</strong> {appointmentToRemove.dentist || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#000", fontSize: "13px" }}>
                        <strong>Status:</strong> {appointmentToRemove.status || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })()}

          <Box>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              color: "#333",
              fontSize: "14px",
              fontWeight: "500",
              textAlign: "left"
            }}>
              Reason for removing (optional):
            </label>
            <textarea
              value={removalReason}
              onChange={(e) => setRemovalReason(e.target.value)}
              placeholder="Enter reason for removing this appointment..."
              style={{
                width: "100%",
                minHeight: "80px",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ddd",
                fontSize: "14px",
                fontFamily: "inherit",
                resize: "vertical",
                boxSizing: "border-box"
              }}
            />
          </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          padding: "16px 24px", 
          backgroundColor: "#FDF9F3",
          borderRadius: "0 0 16px 16px",
          justifyContent: "center"
        }}>
          <Button
            onClick={confirmRemoveAppointment}
            variant="contained"
            sx={{
              backgroundColor: "#dc3545",
              color: "white",
              fontWeight: "600",
              padding: "12px 32px",
              borderRadius: "25px",
              textTransform: "none",
              fontSize: "16px",
              minWidth: "200px",
              "&:hover": {
                backgroundColor: "#c82333"
              }
            }}
          >
            Yes, Remove
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Closed Days Management Modal */}
      <Dialog
        open={showClosedDaysModal}
        onClose={() => {
          // When closing the modal, update the main calendar to show the modal's selected date
          if (selectedModalDate) {
            setSelectedDate(selectedModalDate);
          }
          setShowClosedDaysModal(false);
        }}
        maxWidth="lg"
        fullWidth={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: "#f8f5ef",
            maxHeight: "90vh",
            width: "1200px",
            margin: "auto"
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: "#C5A572", 
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px 24px",
          position: "relative"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventBusyIcon sx={{ color: "white" }} />
            <div style={{ fontWeight: "bold", color: "white", fontSize: "1.5rem", lineHeight: "1.2" }}>
              Manage Calendar
            </div>
          </Box>
          <IconButton
            onClick={() => {
              // When closing the modal, update the main calendar to show the modal's selected date
              if (selectedModalDate) {
                setSelectedDate(selectedModalDate);
              }
              setShowClosedDaysModal(false);
            }}
            sx={{
              position: "absolute",
              right: "24px",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              "&:hover": {
                backgroundColor: "white",
                color: "#C5A572",
                borderColor: "white"
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: 0 }}>
          <div style={{ padding: "16px" }}>
            {/* CSS for closed days and calendar badges */}
            <style>
              {`
                .closed-day {
                  background-color: #ff6b6b !important;
                  color: white !important;
                  font-weight: bold !important;
                  border-radius: 8px !important;
                  border: 2px solid #ff6b6b !important;
                }
                .react-calendar__tile.closed-day {
                  background-color: #ff6b6b !important;
                  color: white !important;
                  font-weight: bold !important;
                  border-radius: 8px !important;
                  border: 2px solid #ff6b6b !important;
                }
                .selected-date {
                  background-color: #C5A572 !important;
                  color: white !important;
                  font-weight: bold !important;
                  border-radius: 8px !important;
                  border: 2px solid #C5A572 !important;
                }
                .react-calendar__tile.selected-date {
                  background-color: #C5A572 !important;
                  color: white !important;
                  font-weight: bold !important;
                  border-radius: 8px !important;
                  border: 2px solid #C5A572 !important;
                }
                .react-calendar__tile {
                  position: relative !important;
                }
                .react-calendar__tile > div {
                  position: relative !important;
                  width: 100% !important;
                  height: 100% !important;
                }
                .react-calendar__tile > div > div {
                  position: relative !important;
                }
              `}
            </style>
            
            {/* Top Row: Calendar and Currently Closed Days - Only show for ClinicStaff */}
            {userDetails && (userDetails.role || userDetails.userType) === "ClinicStaff" && (
              <Box 
                sx={{ 
                  display: {
                    xs: "list-item",
                    sm: "list-item",
                    md: "list-item",
                    lg: "flex",
                    xl: "flex",
                  }, 
                  gap: 2, 
                  marginBottom: 2 
                }}>
                {/* Calendar Card (for Closed Days) */}
                <Card sx={{ flex: 1, backgroundColor: "white" }}>
                <CardContent sx={{ padding: "12px", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Typography variant="h6" gutterBottom sx={{ color: "#333", fontWeight: "600", fontSize: "16px", marginBottom: 1 }}>
                    Select Unavailable Date(s)
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#333", lineHeight: 1.5, fontSize: "13px", marginBottom: 2, textAlign: "center" }}>
                    Click on any date to mark it as unavailable, click again to mark it as available. Closed days will be highlighted in red.
                  </Typography>
                  <Calendar
                    key={closedDays.join(',')} // Force re-render when closedDays changes
                    onChange={(date) => {
                      // Only ClinicStaff can mark dates as unavailable
                      // DentistOwner and AssociateDentist cannot mark dates as unavailable
                      if (userDetails) {
                        const userRole = userDetails.role || userDetails.userType;
                        if (userRole === "DentistOwner" || userRole === "AssociateDentist") {
                          return; // Disable date marking for DentistOwner and AssociateDentist
                        }
                      }
                      // Only ClinicStaff can mark dates
                      toggleDayClosed(date);
                    }}
                    value={null}
                    onActiveStartDateChange={({ activeStartDate }) => {
                      setModalCalendarDate(activeStartDate);
                    }}
                    tileClassName={({ date }) => {
                      // Use local date formatting instead of UTC to avoid timezone issues
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const dateStr = `${year}-${month}-${day}`;
                      return closedDays.includes(dateStr) ? 'closed-day' : '';
                    }}
                    tileStyle={({ date }) => {
                      // Use local date formatting instead of UTC to avoid timezone issues
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const dateStr = `${year}-${month}-${day}`;
                      
                      const isClosed = closedDays.includes(dateStr);
                      
                      if (isClosed) {
                        return {
                          backgroundColor: '#ff6b6b',
                          color: 'white',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          border: '2px solid #ff6b6b',
                          zIndex: 10
                        };
                      }
                      return {};
                    }}
                    tileContent={({ date, view }) => renderCalendarTile(date, view)}
                    style={{
                      width: "100%",
                      border: "none",
                      fontFamily: "Arial, sans-serif",
                      fontSize: "12px"
                    }}
                  />
                </CardContent>
              </Card>

              {/* Currently Closed Days Card */}
              <Card sx={{ flex: 1, backgroundColor: "white" }}>
                <CardContent sx={{ padding: "16px", textAlign: "center" }}>
                  <Typography variant="h6" gutterBottom sx={{ color: "#333", fontWeight: "600", fontSize: "16px" }}>
                    Currently Closed Days ({modalCalendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})
                  </Typography>
                  {getClosedDaysForCurrentMonth().length === 0 ? (
                    <Typography variant="body2" sx={{ color: "#333", fontStyle: "italic" }}>
                      No days are currently marked as closed for this month.
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
                      {getClosedDaysForCurrentMonth().map((dateStr) => (
                        <Chip
                          key={dateStr}
                          label={new Date(dateStr).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                          sx={{
                            backgroundColor: "#ff6b6b",
                            color: "white",
                            fontWeight: "bold",
                            borderRadius: "20px",
                            padding: "8px 16px",
                            fontSize: "14px",
                            minWidth: "120px",
                            height: "40px",
                            "&:hover": {
                              backgroundColor: "#ff5252"
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
            )}

            {/* Middle Row: Time Slots Calendar and Time Slots Management */}
            <Box               
              sx={{ 
                display: {
                  xs: "list-item",
                  sm: "list-item",
                  md: "list-item",
                  lg: "flex",
                  xl: "flex",
                }, 
                gap: 2, 
                marginBottom: 2 
              }}>
              {/* Time Slots Calendar Card */}
              <Card sx={{ flex: 1, backgroundColor: "white" }}>
                <CardContent sx={{ padding: "12px", display: "flex", justifyContent: "center" }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h6" gutterBottom sx={{ color: "#333", fontWeight: "600", fontSize: "16px", marginBottom: 1 }}>
                      Select Date for Time Slot Management
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#333", lineHeight: 1.5, fontSize: "13px", marginBottom: 2 }}>
                      Select a date to manage time slots for that specific day.
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                      <Calendar
                        onChange={(date) => {
                          setSelectedModalDate(date);
                          // Also update the main calendar to show the same date
                          setSelectedDate(date);
                        }}
                        value={selectedModalDate}
                        tileClassName={({ date }) => {
                          // Highlight selected date
                          if (selectedModalDate && 
                              date.getDate() === selectedModalDate.getDate() &&
                              date.getMonth() === selectedModalDate.getMonth() &&
                              date.getFullYear() === selectedModalDate.getFullYear()) {
                            return 'selected-date';
                          }
                          return '';
                        }}
                        tileStyle={({ date }) => {
                          // Style selected date
                          if (selectedModalDate && 
                              date.getDate() === selectedModalDate.getDate() &&
                              date.getMonth() === selectedModalDate.getMonth() &&
                              date.getFullYear() === selectedModalDate.getFullYear()) {
                            return {
                              backgroundColor: '#C5A572',
                              color: 'white',
                              fontWeight: 'bold',
                              borderRadius: '8px',
                              border: '2px solid #C5A572'
                            };
                          }
                          return {};
                        }}
                        tileContent={({ date, view }) => renderCalendarTile(date, view)}
                        style={{
                          width: "100%",
                          maxWidth: "350px",
                          border: "none",
                          fontFamily: "Arial, sans-serif",
                          fontSize: "12px"
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Time Slots Management Card */}
              <Card sx={{ flex: 1, backgroundColor: "white" }}>
                <CardContent sx={{ padding: "16px", textAlign: "center" }}>
                  {selectedModalDate ? (
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: "#333", fontWeight: "600", marginBottom: 1 }}>
                        Time slots for: {selectedModalDate.toLocaleDateString('en-US', { 
                          month: 'long', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })} ({selectedModalDate.toLocaleDateString('en-US', { weekday: 'long' })}){selectedModalDentist ? ` | ${getDentistDisplayNameFromUID(selectedModalDentist)}` : ' | Select a dentist'}
                      </Typography>
                      
                      {/* Dentist Selection Dropdown - Only show for ClinicStaff */}
                      {isClinicStaffUser && (
                        <Box sx={{ marginBottom: 2 }}>
                          <Typography variant="body2" sx={{ color: "#333", fontWeight: "500", marginBottom: 1 }}>
                            Select Dentist/Clinic:
                          </Typography>
                          <select
                            value={selectedModalDentist || ""}
                            onChange={(e) => setSelectedModalDentist(e.target.value || null)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              border: "1px solid #C5A572",
                              borderRadius: "8px",
                              fontSize: "14px",
                              backgroundColor: "white",
                              color: "#333",
                              textAlign: "center",
                              cursor: "pointer"
                            }}
                          >
                            <option value="" disabled>Select a dentist</option>
                            {timeSlotDropdownDentists.map((dentist) => {
                              const displayName = getDentistDisplayName(dentist);
                              return (
                                <option key={dentist.uid} value={dentist.uid}>
                                  {displayName}
                                </option>
                              );
                            })}
                          </select>
                        </Box>
                      )}
                      
                      <Typography variant="body2" sx={{ color: "#333", lineHeight: 1.5, fontSize: "13px", marginBottom: 2 }}>
                        Select any time slot below by clicking on it to mark it as unavailable, click again to make it available.
                      </Typography>
                      
                      {/* Legend */}
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, marginBottom: 2, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: "12px", height: "12px", backgroundColor: "transparent", border: "2px solid #C5A572", borderRadius: "4px" }}></Box>
                          <Typography variant="caption" sx={{ color: "#333", fontSize: "12px" }}>
                            Available
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: "12px", height: "12px", backgroundColor: "#C5A572", borderRadius: "4px", opacity: 0.5 }}></Box>
                          <Typography variant="caption" sx={{ color: "#333", fontSize: "12px" }}>
                            Unavailable
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: "12px", height: "12px", backgroundColor: "#fff8e1", border: "1px solid #ffb74d", borderRadius: "4px" }}></Box>
                          <Typography variant="caption" sx={{ color: "#333", fontSize: "12px" }}>
                            Pending Appointment
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: "12px", height: "12px", backgroundColor: "#e8f5e8", border: "1px solid #4CAF50", borderRadius: "4px" }}></Box>
                          <Typography variant="caption" sx={{ color: "#333", fontSize: "12px" }}>
                            Confirmed Appointment
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: "12px", height: "12px", backgroundColor: "#e3f2fd", border: "1px solid #2196F3", borderRadius: "4px" }}></Box>
                          <Typography variant="caption" sx={{ color: "#333", fontSize: "12px" }}>
                            Completed Appointment
                          </Typography>
                        </Box>
                      </Box>
                      {!selectedModalDentist ? (
                        <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic", marginTop: 2 }}>
                          Select a dentist
                        </Typography>
                      ) : isDentistSchedulePlottingEnabled(chairSettings) &&
                        modalRenderableTimeSlots.length === 0 ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#333",
                            fontSize: "16px",
                            marginTop: 2,
                            padding: "24px",
                          }}
                        >
                          {`There are no available timeslots for ${getDentistDisplayNameFromUID(selectedModalDentist)} on this date.`}
                        </Typography>
                      ) : (
                        <>
                      {/* Select All / Deselect All Checkbox */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1.5,
                          marginBottom: 2,
                          marginTop: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={(() => {
                                if (!selectedModalDate || !selectedModalDentist) return false;
                                const totalSelectableSlots = getModalSelectableTimeSlots().length;
                                return totalSelectableSlots > 0 && selectedTimeSlots.length === totalSelectableSlots;
                              })()}
                              onChange={handleSelectAllTimeSlots}
                              sx={{
                                color: '#C5A572',
                                '&.Mui-checked': {
                                  color: '#C5A572',
                                },
                              }}
                            />
                          }
                          label={(() => {
                            if (!selectedModalDate || !selectedModalDentist) return "Select All";
                            const totalSelectableSlots = getModalSelectableTimeSlots().length;
                            return totalSelectableSlots > 0 && selectedTimeSlots.length === totalSelectableSlots ? "Deselect All" : "Select All";
                          })()}
                          sx={{
                            margin: 0,
                            '& .MuiFormControlLabel-label': {
                              color: '#333',
                              fontSize: '14px',
                            },
                          }}
                        />
                        {isClinicStaffUser &&
                        isChairCapacityConfigured(chairSettings) &&
                          selectedModalDentist &&
                          isDentistOnRoster(selectedModalDentist, chairSettings) && (
                            <Button
                              onClick={openAssignSecondaryModal}
                              variant="outlined"
                              sx={{
                                color: "#C5A572",
                                borderColor: "#C5A572",
                                borderRadius: "8px",
                                fontWeight: "bold",
                                textTransform: "none",
                                padding: "6px 14px",
                                "&:hover": {
                                  backgroundColor: "#C5A572",
                                  borderColor: "#C5A572",
                                  color: "white",
                                },
                              }}
                            >
                              Assign Secondary Dentist
                            </Button>
                          )}
                        {isClinicStaffUser &&
                        isChairCapacityConfigured(chairSettings) &&
                          selectedModalDentist &&
                          !isDentistOnRoster(selectedModalDentist, chairSettings) &&
                          linkedRosterUidForSelectedSecondary && (
                            <Button
                              onClick={openUnassignSecondaryModal}
                              variant="outlined"
                              sx={{
                                color: "#C5A572",
                                borderColor: "#C5A572",
                                borderRadius: "8px",
                                fontWeight: "bold",
                                textTransform: "none",
                                padding: "6px 14px",
                                "&:hover": {
                                  backgroundColor: "#C5A572",
                                  borderColor: "#C5A572",
                                  color: "white",
                                },
                              }}
                            >
                              Unassign Dentist
                            </Button>
                          )}
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
                          {modalRenderableTimeSlots.map((timeSlotData, index) => {
                            const timeSlot = timeSlotData.timeStr;
                            const segmentKeys =
                              Array.isArray(timeSlotData.modalSegmentTimeStrs) &&
                              timeSlotData.modalSegmentTimeStrs.length > 0
                                ? timeSlotData.modalSegmentTimeStrs
                                : [timeSlot];
                          // Check if this time slot has a booked appointment
                          const bookedAppointment = checkTimeSlotForBookedAppointment(timeSlot, selectedModalDate, selectedModalDentist !== "clinic" ? selectedModalDentist : null);
                          
                          // Check if the date is globally unavailable (closed day)
                          const year = selectedModalDate.getFullYear();
                          const month = String(selectedModalDate.getMonth() + 1).padStart(2, '0');
                          const day = String(selectedModalDate.getDate()).padStart(2, '0');
                          const dateStrForClosed = `${year}-${month}-${day}`;
                          const isDateClosed = closedDays.includes(dateStrForClosed);
                          const isBenchSelectedForSlot =
                            isChairCapacityConfigured(chairSettings) &&
                            selectedModalDentist &&
                            !isDentistOnRoster(selectedModalDentist, chairSettings);
                          const linkedBenchUidsForRosterForSlot = !isBenchSelectedForSlot
                            ? getLinkedBenchUidsForRosterFromSwaps({
                                rosterUid: selectedModalDentist,
                                swapsBySlot: chairSwapsBySlotForDate,
                              })
                            : [];
                          const benchSwapSlotsForSlot = isBenchSelectedForSlot
                            ? getBenchSwapSlots({
                                swapsBySlot: chairSwapsBySlotForDate,
                                benchUid: selectedModalDentist,
                              })
                            : [];
                          const hasBenchOwnedSwapSlotsForSlot =
                            isBenchSelectedForSlot && benchSwapSlotsForSlot.length > 0;
                          const overlapsBenchSwapForModal =
                            isBenchSelectedForSlot &&
                            doesSlotRangeOverlapSwapSlots(timeSlot, benchSwapSlotsForSlot);
                          const isBooked = bookedAppointment !== null;
                          const bookedAppointmentDentistUid = bookedAppointment
                            ? resolveAppointmentDentistUid(bookedAppointment)
                            : null;
                          const benchOutsideSwapButOwnsBooking =
                            isBenchSelectedForSlot &&
                            isBooked &&
                            bookedAppointmentDentistUid === selectedModalDentist;

                          // Time slot is unavailable if it is effectively blocked for the dentist OR if the date is globally closed
                          const baseUnavailable =
                            isDateClosed ||
                            (isBenchSelectedForSlot
                              ? (!overlapsBenchSwapForModal && !benchOutsideSwapButOwnsBooking) ||
                                isSlotEffectivelyUnavailableForDentist({
                                  slot: timeSlot,
                                  dentistUid: selectedModalDentist,
                                  dentistUnavailableSlots: unavailableTimeSlots,
                                  hasUnavailabilityRecord:
                                    hasModalUnavailableRecord ||
                                    hasBenchOwnedSwapSlotsForSlot,
                                  settings: chairSettings,
                                  timeToMinutes,
                                })
                              : isSlotEffectivelyUnavailableForDentist({
                                  slot: timeSlot,
                                  dentistUid: selectedModalDentist,
                                  dentistUnavailableSlots: unavailableTimeSlots,
                                  hasUnavailabilityRecord: hasModalUnavailableRecord,
                                  settings: chairSettings,
                                  timeToMinutes,
                                }));
                          const isUnavailable = isBooked
                            ? (isBenchSelectedForSlot
                                ? bookedAppointmentDentistUid !== selectedModalDentist
                                : linkedBenchUidsForRosterForSlot.includes(bookedAppointmentDentistUid))
                            : baseUnavailable ||
                                computeRosterBenchInheritedUnavailableForModal(timeSlot);
                          
                          
                          // Determine the button style based on state.
                          // When a roster dentist sees a linked bench patient's booking (or vice versa),
                          // both isBooked and isUnavailable are true — use unavailable styling like "Currently Unavailable".
                          const showAppointmentChipStyle = isBooked && !isUnavailable;

                          const slotLabelForDisplay = showAppointmentChipStyle
                            ? getModalSlotBookedDisplayLabel(timeSlot, bookedAppointment)
                            : timeSlot;

                          let backgroundColor, color, borderColor, hoverBg, hoverColor, hoverBorder;

                          if (showAppointmentChipStyle) {
                            // Check appointment status and apply appropriate styling
                            if (bookedAppointment.status === "Completed" || bookedAppointment.status === "completed") {
                              // Completed appointment - blue styling
                              backgroundColor = "#e3f2fd";
                              color = "#333";
                              borderColor = "#2196F3";
                              hoverBg = "#bbdefb";
                              hoverColor = "#333";
                              hoverBorder = "#1976D2";
                            } else if (bookedAppointment.status === "Confirmed" || bookedAppointment.status === "confirmed") {
                              // Confirmed appointment - green styling
                              backgroundColor = "#e8f5e8";
                              color = "#333";
                              borderColor = "#4CAF50";
                              hoverBg = "#d4edda";
                              hoverColor = "#333";
                              hoverBorder = "#45a049";
                            } else {
                              // Pending appointment - orange styling
                              backgroundColor = "#fff8e1";
                              color = "#333";
                              borderColor = "#FFA500";
                              hoverBg = "#fff3c4";
                              hoverColor = "#333";
                              hoverBorder = "#ff9800";
                            }
                          } else if (isUnavailable) {
                            // Unavailable - contained styling (solid gold background, white text) with fade effect
                            backgroundColor = "#C5A572";
                            color = "white";
                            borderColor = "transparent";
                            hoverBg = "#B8955A";
                            hoverColor = "white";
                            hoverBorder = "transparent";
                          } else {
                            // Available - outlined styling (transparent background, gold border)
                            backgroundColor = "transparent";
                            color = "#C5A572";
                            borderColor = "#C5A572";
                            hoverBg = "#C5A572";
                            hoverColor = "white";
                            hoverBorder = "#C5A572";
                          }
                          
                          // Completed bookings can still be selected; marking unavailable runs the
                          // same conflict flow as pending/confirmed (see multi-slot validation).
                          const isOwnedBySelectedDentist = segmentKeys.every((s) =>
                            modalSelectableSlotSet.has(s)
                          );
                          const isSlotInteractive = isOwnedBySelectedDentist;

                          // Check if this time slot is selected
                          const isSelected = segmentKeys.every((s) =>
                            selectedTimeSlots.includes(s)
                          );

                          return (
                            <Box
                              key={`${segmentKeys.join("|")}-${index}`}
                              onClick={
                                isSlotInteractive
                                  ? () =>
                                      handleTimeSlotSelection(
                                        timeSlot,
                                        timeSlotData.modalSegmentTimeStrs
                                      )
                                  : undefined
                              }
                              sx={{
                                position: "relative",
                                cursor: isSlotInteractive ? "pointer" : "not-allowed",
                                "&:hover": isSlotInteractive ? {
                                  transform: "scale(1.05)",
                                  transition: "transform 0.2s ease"
                                } : {}
                              }}
                            >
                              <Box
                                sx={{
                                  backgroundColor: backgroundColor,
                                  color: color,
                                  borderColor: borderColor,
                                  border: showAppointmentChipStyle ? `3px solid ${borderColor} !important` : `2px solid ${borderColor}`,
                                  fontWeight: 700,
                                  borderRadius: "20px",
                                  padding: "8px 16px",
                                  fontSize: "14px",
                                  textTransform: "none",
                                  minWidth: "120px",
                                  opacity: isUnavailable ? 0.5 : 1,
                                  position: "relative",
                                  "&:hover": isSlotInteractive ? {
                                    backgroundColor: hoverBg,
                                    color: hoverColor,
                                    borderColor: hoverBorder,
                                    opacity: isUnavailable ? 0.8 : 1
                                  } : {}
                                }}
                              >
                                {slotLabelForDisplay}
                                {/* Checkbox indicator */}
                                {isSelected && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: "-8px",
                                      right: "-8px",
                                      width: "24px",
                                      height: "24px",
                                      backgroundColor: "#C5A572",
                                      borderRadius: "50%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      border: "2px solid white",
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                                    }}
                                  >
                                    <Typography sx={{ color: "white", fontSize: "14px", fontWeight: "bold" }}>
                                      ✓
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                          {modalRenderableTimeSlots.length === 0 && (
                        <Typography variant="body2" sx={{ color: "#333", fontStyle: "italic", marginTop: 2 }}>
                          No time slots available for this date.
                        </Typography>
                          )}
                        </>
                      )}
                      
                      {/* Save/Cancel buttons for multiple selection */}
                      {selectedTimeSlots.length > 0 && (
                        <Box sx={{ 
                          display: "flex", 
                          gap: 2, 
                          justifyContent: "center", 
                          marginTop: 3,
                          padding: "16px",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "12px",
                          border: "1px solid #e9ecef"
                        }}>
                          <Button
                            onClick={handleCancelMultipleSlots}
                            variant="contained"
                            sx={{
                              backgroundColor: "#C5A572",
                              color: "white",
                              borderRadius: "8px",
                              padding: "10px 24px",
                              fontWeight: "bold",
                              "&:hover": {
                                backgroundColor: "#B8955A"
                              }
                            }}
                          >
                            Cancel ({selectedTimeSlots.length})
                          </Button>
                          <Button
                            onClick={handleSaveMultipleSlots}
                            variant="outlined"
                            sx={{
                              backgroundColor: "transparent",
                              color: "#C5A572",
                              borderColor: "#C5A572",
                              borderRadius: "8px",
                              padding: "10px 24px",
                              fontWeight: "bold",
                              border: "2px solid #C5A572",
                              "&:hover": {
                                backgroundColor: "#C5A572",
                                color: "white",
                                borderColor: "#C5A572"
                              }
                            }}
                          >
                            Save ({selectedTimeSlots.length})
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: "#333", fontStyle: "italic" }}>
                      Please select a date in the calendar above to manage time slots.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </div>
        </DialogContent>
      </Dialog>

      {/* Conflict Confirmation Dialog */}
      <Dialog
        open={showConflictDialog}
        onClose={(event, reason) => {
          // Only allow closing with close button or ESC key
          if (reason === 'backdropClick') {
            return;
          }
          cancelConflictDialog();
        }}
        maxWidth="sm"
        fullWidth={false}
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: "#f8f5ef",
            maxHeight: "90vh",
            width: "500px",
            margin: "auto"
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: "#C5A572", 
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px 24px",
          position: "relative"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventBusyIcon />
            <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
              Confirm Appointment Cancellation
            </Typography>
          </Box>
          <IconButton
            onClick={cancelConflictDialog}
            sx={{
              position: "absolute",
              right: "24px",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              "&:hover": {
                backgroundColor: "white",
                color: "#C5A572",
                borderColor: "white"
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "24px" }}>
          <Box sx={{ textAlign: "center", marginBottom: "20px" }}>
            <Typography variant="h6" sx={{ color: "#333", fontWeight: "600", marginBottom: "16px" }}>
              This time slot has a booked appointment
            </Typography>
            
            {conflictAppointment && conflictTimeSlot && (
              <Box sx={{ 
                backgroundColor: (() => {
                  switch (conflictAppointment.status) {
                    case "Pending":
                    case "pending":
                      return "#fff8e1"; // Light orange background
                    case "Confirmed":
                    case "confirmed":
                      return "#e8f5e8"; // Light green background
                    case "Completed":
                    case "completed":
                      return "#e3f2fd"; // Light blue background
                    default:
                      return "#fff8e1"; // Default to pending styling
                  }
                })(),
                padding: "20px",
                borderRadius: "12px",
                border: (() => {
                  switch (conflictAppointment.status) {
                    case "Pending":
                    case "pending":
                      return "3px solid #FFA500"; // Orange border
                    case "Confirmed":
                    case "confirmed":
                      return "3px solid #4CAF50"; // Green border
                    case "Completed":
                    case "completed":
                      return "3px solid #2196F3"; // Blue border
                    default:
                      return "3px solid #FFA500"; // Default to pending styling
                  }
                })(),
                marginBottom: "20px"
              }}>
                <Typography variant="body1" sx={{ color: "#333", fontWeight: "500", marginBottom: "12px" }}>
                  <strong>Appointment Details:</strong>
                </Typography>
                
                <Box sx={{ textAlign: "left", marginBottom: "12px" }}>
                  <Typography variant="body2" sx={{ color: "#333", marginBottom: "4px" }}>
                    <strong>Patient:</strong> {conflictAppointment.patientDisplay || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#333", marginBottom: "4px" }}>
                    <strong>Dentist:</strong> {conflictAppointment.dentist || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#333", marginBottom: "4px" }}>
                    <strong>Time Slot:</strong> {conflictTimeSlot}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#333", marginBottom: "4px" }}>
                    <strong>Services:</strong> {conflictAppointment.services && Array.isArray(conflictAppointment.services) 
                      ? conflictAppointment.services.map(service => 
                          typeof service === 'object' ? service.name : service
                        ).join(", ") 
                      : "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#333", marginBottom: "4px" }}>
                    <strong>Status:</strong> {conflictAppointment.status || "N/A"}
                  </Typography>
                </Box>
              </Box>
            )}
            
            <Typography variant="body2" sx={{ color: "#333", lineHeight: 1.6 }}>
              Marking this time slot as unavailable will automatically cancel the existing appointment and send notifications to both the patient and clinic personnel.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: "16px 24px", justifyContent: "center" }}>
          <Button
            onClick={cancelAppointmentAndMarkUnavailable}
            variant="contained"
            sx={{
              backgroundColor: "#C5A572",
              color: "white",
              fontWeight: "600",
              padding: "12px 32px",
              borderRadius: "25px",
              textTransform: "none",
              fontSize: "16px",
              minWidth: "280px",
              "&:hover": {
                backgroundColor: "#B8955A"
              }
            }}
          >
            Cancel Appointment & Mark Unavailable
          </Button>
        </DialogActions>
      </Dialog>

      {/* Date Unavailable Confirmation Dialog */}
      {dateToToggle !== null && (
      <Dialog
        open={showDateUnavailableDialog && !isClosingDateDialog.current && !isMarkingDateUnavailable}
        onClose={(event, reason) => {
          // Prevent closing if already closing or processing
          if (isMarkingDateUnavailable || isClosingDateDialog.current) {
            return;
          }
          cancelMarkDateUnavailable();
        }}
        maxWidth="sm"
        fullWidth={false}
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: "#f8f5ef",
            maxHeight: "90vh",
            width: "500px",
            margin: "auto"
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#BA9A65",
            color: "#FFFFFF",
            padding: "20px 24px",
            paddingRight: "64px",
            borderRadius: "16px 16px 0 0",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 700,
              fontSize: dateUnavailableFontSize,
              lineHeight: 1.1,
              maxWidth: "100%",
              whiteSpace: "nowrap",
              textAlign: "center",
              color: "#FFFFFF",
            }}
            title={dateUnavailableTitle}
            noWrap
          >
            {dateUnavailableTitle}
          </Typography>
          <IconButton
            onClick={cancelMarkDateUnavailable}
            sx={{
              position: "absolute",
              right: "16px",
              top: "16px",
              backgroundColor: "transparent",
              border: "1px solid rgba(253, 249, 243, 0.8)",
              color: "#FDF9F3",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              "&:hover": {
                backgroundColor: "white",
                color: "#C5A572",
                borderColor: "white",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: 0,
          }}
        >
          <Box sx={{ padding: "24px 24px 0", textAlign: "center", marginBottom: 0 }}>
            {dateToToggle && (
              <Box sx={{ 
                backgroundColor: "white",
                padding: "16px",
                borderRadius: "12px",
                border: "2px solid #C5A572",
                marginBottom: 0
              }}>
                
                {(() => {
                  const year = dateToToggle.getFullYear();
                  const month = String(dateToToggle.getMonth() + 1).padStart(2, '0');
                  const day = String(dateToToggle.getDate()).padStart(2, '0');
                  const dateStr = `${year}-${month}-${day}`;
                  const isCurrentlyUnavailable = closedDays.includes(dateStr);
                  
                  if (isCurrentlyUnavailable) {
                    return null;
                  }

                  if (dateAppointments.length === 0) {
                    return null;
                  }

                  const actionableAppointments = dateAppointments
                    .filter((appointment) =>
                      appointment.status === "Confirmed" ||
                      appointment.status === "confirmed" ||
                      appointment.status === "Pending" ||
                      appointment.status === "pending" ||
                      appointment.status === "New" ||
                      appointment.status === "new"
                    )
                    .sort((a, b) => {
                      const getTimeInMinutes = (appointment) => {
                        if (appointment.startTime) {
                          return timeToMinutes(appointment.startTime);
                        }
                        if (appointment.time) {
                          return timeToMinutes(appointment.time);
                        }
                        return 0;
                      };
                      return getTimeInMinutes(a) - getTimeInMinutes(b);
                    });

                  if (actionableAppointments.length === 0) {
                    return null;
                  }

                  const confirmedAppointments = actionableAppointments.filter(
                    (appointment) => appointment.status === "Confirmed" || appointment.status === "confirmed"
                  );
                  const pendingAppointments = actionableAppointments.filter(
                    (appointment) =>
                      appointment.status === "Pending" ||
                      appointment.status === "pending" ||
                      appointment.status === "New" ||
                      appointment.status === "new"
                  );

                  const renderAppointmentCard = (appointment, isConfirmed) => {
                    const backgroundColor = isConfirmed ? "#E8F5E8" : "#FFF8E1";
                    const borderColor = isConfirmed ? "#4CAF50" : "#FFB74D";

                    return (
                      <Box
                        key={appointment.id}
                        sx={{
                          marginBottom: "8px",
                          padding: "12px",
                          backgroundColor,
                          border: `2px solid ${borderColor}`,
                          borderRadius: "8px",
                          textAlign: "left",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "#000", fontWeight: "bold", marginBottom: 2, fontSize: "14px" }}
                        >
                          Appointment Details:
                        </Typography>

                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ color: "#000", marginBottom: 0.5, fontSize: "13px" }}>
                              <strong>Name:</strong> {appointment.patientDisplay || appointment.patientName || appointment.name || "N/A"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#000", marginBottom: 0.5, fontSize: "13px" }}>
                              <strong>Services:</strong> {appointment.services && Array.isArray(appointment.services)
                                ? appointment.services
                                    .map((service) => (typeof service === "object" ? service.name : service))
                                    .join(", ")
                                : "N/A"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#000", fontSize: "13px" }}>
                              <strong>Time:</strong> {appointment.startTime && appointment.endTime
                                ? `${formatTimeConsistent(appointment.startTime)} - ${formatTimeConsistent(appointment.endTime)}`
                                : appointment.time
                                ? formatTimeConsistent(appointment.time)
                                : "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ color: "#000", marginBottom: 0.5, fontSize: "13px" }}>
                              <strong>Dentist:</strong> {appointment.dentist || "N/A"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#000", fontSize: "13px" }}>
                              <strong>Status:</strong> {appointment.status || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  };

                  return (
                    <Box sx={{ textAlign: "left" }}>
                      {confirmedAppointments.length > 0 && (
                        <Box sx={{ marginBottom: confirmedAppointments.length > 0 && pendingAppointments.length > 0 ? "16px" : 0 }}>
                          <Typography variant="body2" sx={{ color: "#333", marginBottom: "8px" }}>
                            <strong>{confirmedAppointments.length === 1 ? "Confirmed appointment:" : "Confirmed appointments:"}</strong>
                          </Typography>
                          {confirmedAppointments.map((appointment) => renderAppointmentCard(appointment, true))}
                        </Box>
                      )}

                      {pendingAppointments.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ color: "#333", marginBottom: "8px" }}>
                            <strong>{pendingAppointments.length === 1 ? "Pending appointment:" : "Pending appointments:"}</strong>
                          </Typography>
                          {pendingAppointments.map((appointment) => renderAppointmentCard(appointment, false))}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            )}
            
            <Typography variant="body2" sx={{ color: "#333", lineHeight: 1.6 }}>
              {(() => {
                if (!dateToToggle) return null;
                const year = dateToToggle.getFullYear();
                const month = String(dateToToggle.getMonth() + 1).padStart(2, '0');
                const day = String(dateToToggle.getDate()).padStart(2, '0');
                const dateStr = `${year}-${month}-${day}`;
                const isCurrentlyUnavailable = closedDays.includes(dateStr);
                
                if (isCurrentlyUnavailable) {
                  return null;
                } else {
                  const hasAppointmentsToCancel = dateAppointments.some(appointment => 
                    appointment.status === "Confirmed" || appointment.status === "confirmed" ||
                    appointment.status === "Pending" || appointment.status === "New" ||
                    appointment.status === "pending" || appointment.status === "new"
                  );
                  
                  return hasAppointmentsToCancel 
                    //? "Mark Unavailable & Cancel Appointments"
                    //: "Mark Date As Unavailable";
                }
              })()}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: "16px 24px", justifyContent: "center" }}>
          <Button
            onClick={confirmMarkDateUnavailable}
            disabled={isMarkingDateUnavailable}
            variant="contained"
            sx={{
              backgroundColor: "#C5A572",
              color: "white",
              fontWeight: "600",
              padding: "12px 32px",
              borderRadius: "25px",
              textTransform: "none",
              fontSize: "16px",
              minWidth: "280px",
              cursor: isMarkingDateUnavailable ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: isMarkingDateUnavailable ? "#C5A572" : "#B8955A"
              },
              "&:disabled": {
                backgroundColor: "#C5A572",
                opacity: 0.7
              }
            }}
          >
            {isMarkingDateUnavailable ? (
              <>
                <CircularProgress size={20} sx={{ color: "white", marginRight: "8px" }} />
                Mark Date As Unavailable...
              </>
            ) : (
              (() => {
                if (!dateToToggle) return "Confirm";
                const year = dateToToggle.getFullYear();
                const month = String(dateToToggle.getMonth() + 1).padStart(2, '0');
                const day = String(dateToToggle.getDate()).padStart(2, '0');
                const dateStr = `${year}-${month}-${day}`;
                const isCurrentlyUnavailable = closedDays.includes(dateStr);
                
                if (isCurrentlyUnavailable) {
                  return "Mark Date As Available";
                } else {
                  const hasAppointmentsToCancel = dateAppointments.some(appointment => 
                    appointment.status === "Confirmed" || appointment.status === "confirmed" ||
                    appointment.status === "Pending" || appointment.status === "New" ||
                    appointment.status === "pending" || appointment.status === "new"
                  );
                  
                  return hasAppointmentsToCancel 
                    ? "Mark Unavailable & Cancel Appointments"
                    : "Mark Date As Unavailable";
                }
              })()
            )}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Time Slot Confirmation Dialog */}
      {timeSlotToToggle !== null && (
      <Dialog
        open={showTimeSlotDialog && !isClosingTimeSlotDialog.current && !isMarkingTimeSlotUnavailable}
        onClose={(event, reason) => {
          // Prevent closing if already closing or processing
          if (isMarkingTimeSlotUnavailable || isClosingTimeSlotDialog.current) {
            return;
          }
          cancelTimeSlotToggle();
        }}
        maxWidth="sm"
        fullWidth={false}
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: "#f8f5ef",
            maxHeight: "90vh",
            width: "500px",
            margin: "auto"
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: "#C5A572", 
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px 24px",
          position: "relative"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EventBusyIcon />
            <Typography variant="h5" component="div" sx={{ fontWeight: "bold", color: "white" }}>
              {(() => {
                if (!timeSlotToToggle) return "Time Slot Management";
                const isCurrentlyUnavailable =
                  getTimeSlotStatus(timeSlotToToggle).status === "Unavailable";
                return isCurrentlyUnavailable ? "Make Time Slot Available" : "Mark Time Slot as Unavailable";
              })()}
            </Typography>
          </Box>
          <IconButton
            onClick={cancelTimeSlotToggle}
            sx={{
              position: "absolute",
              right: "24px",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              "&:hover": {
                backgroundColor: "white",
                color: "#C5A572",
                borderColor: "white"
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "24px" }}>
          <Box sx={{ textAlign: "center", marginBottom: "20px" }}>
            <Typography variant="h6" sx={{ color: "#333", fontWeight: "600", marginBottom: "16px" }}>
              {(() => {
                if (!timeSlotToToggle) return "Confirm time slot change";
                const isCurrentlyUnavailable =
                  getTimeSlotStatus(timeSlotToToggle).status === "Unavailable";
                return isCurrentlyUnavailable ? "Confirm making time slot available" : "Confirm marking time slot as unavailable";
              })()}
            </Typography>
            
            {timeSlotToToggle && (
              <Box sx={{ 
                backgroundColor: "white",
                padding: "16px",
                borderRadius: "12px",
                border: "2px solid #C5A572",
                marginBottom: "20px"
              }}>
                <Typography variant="body1" sx={{ color: "#333", fontWeight: "500", marginBottom: "12px" }}>
                  <strong>Time Slot:</strong> {timeSlotToToggle}
                </Typography>
                
                {selectedModalDate && (
                  <Typography variant="body1" sx={{ color: "#333", fontWeight: "500", marginBottom: "12px" }}>
                    <strong>Date:</strong> {selectedModalDate.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </Typography>
                )}
              </Box>
            )}
            
            <Typography variant="body2" sx={{ color: "#333", lineHeight: 1.6 }}>
              {(() => {
                if (!timeSlotToToggle) return null;
                const isCurrentlyUnavailable =
                  getTimeSlotStatus(timeSlotToToggle).status === "Unavailable";

                if (isCurrentlyUnavailable) {
                  return "This will make the time slot available for new appointments.";
                } else {
                  return "This will mark the time slot as unavailable for appointments.";
                }
              })()}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: "16px 24px",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {timeSlotDialogValidationMessage && (
            <Alert
              severity="error"
              icon={false}
              sx={{
                width: "100%",
                maxWidth: "460px",
                backgroundColor: "#FDE8E8",
                color: "#9B2C2C",
                border: "1px solid #F04848",
                "& .MuiAlert-message": {
                  color: "#9B2C2C",
                  width: "100%",
                },
                "& .MuiTypography-root": {
                  color: "#9B2C2C",
                },
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 400 }}>
                {timeSlotDialogValidationMessage}
              </Typography>
            </Alert>
          )}
          <Button
            onClick={confirmTimeSlotToggle}
            disabled={isMarkingTimeSlotUnavailable}
            variant="contained"
            sx={{
              backgroundColor: "#C5A572",
              color: "white",
              fontWeight: "600",
              padding: "12px 32px",
              borderRadius: "25px",
              textTransform: "none",
              fontSize: "16px",
              minWidth: "280px",
              cursor: isMarkingTimeSlotUnavailable ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: isMarkingTimeSlotUnavailable ? "#C5A572" : "#B8955A"
              },
              "&:disabled": {
                backgroundColor: "#C5A572",
                opacity: 0.7
              }
            }}
          >
            {isMarkingTimeSlotUnavailable ? (
              <>
                <CircularProgress size={20} sx={{ color: "white", marginRight: "8px" }} />
                Processing...
              </>
            ) : (
              (() => {
                if (!timeSlotToToggle) return "Confirm";
                const isCurrentlyUnavailable =
                  getTimeSlotStatus(timeSlotToToggle).status === "Unavailable";

                if (isCurrentlyUnavailable) {
                  return "Make Time Slot Available";
                } else {
                  return "Mark Time Slot as Unavailable";
                }
              })()
            )}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Clinic-Wide Time Slot Confirmation Dialog */}
      <Dialog
        open={showClinicWideDialog && (clinicWideTimeSlot !== null || selectedTimeSlots.length > 0)}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          setShowClinicWideDialog(false);
          setClinicWideTimeSlot(null);
          setDentistStatuses([]);
          setSelectedTimeSlots([]);
        }}
        maxWidth="md"
        fullWidth={true}
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: "#FDF9F3",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)"
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: "#C5A572", 
          color: "white", 
          padding: "20px 24px",
          borderRadius: "16px 16px 0 0",
          position: "relative"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, width: "100%" }}>
            <EventBusyIcon sx={{ color: "white" }} />
            <div style={{ fontWeight: "bold", color: "white", fontSize: "1.5rem", lineHeight: "1.2" }}>
              {`Time Slot Management: ${getDentistDisplayNameFromUID(selectedModalDentist)}`}
            </div>
          </Box>
          <IconButton
            onClick={() => {
              setShowClinicWideDialog(false);
              setClinicWideTimeSlot(null);
              setDentistStatuses([]);
            }}
            sx={{
              position: "absolute",
              right: "16px",
              top: "16px",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              "&:hover": {
                backgroundColor: "white",
                color: "#C5A572",
                borderColor: "white"
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "24px" }}>
          <Typography variant="h6" sx={{ color: "#333", fontWeight: "600", marginBottom: 2, marginTop: 2, textAlign: "center" }}>
            {selectedTimeSlots.length > 1 
              ? `Selected Time Slots (${consolidatedSelectedSlotsForConfirm.length})` 
              : `Time Slot: ${clinicWideTimeSlot}`
            }
          </Typography>
          {selectedTimeSlots.length > 1 &&
            consolidatedSelectedSlotsForConfirm.length !== selectedTimeSlots.length && (
              <Typography
                variant="body2"
                sx={{ color: "#59544A", textAlign: "center", marginBottom: 1 }}
              >
                {`${selectedTimeSlots.length} half-hour intervals selected — shown as ${consolidatedSelectedSlotsForConfirm.length} booking block(s).`}
              </Typography>
            )}
          
          <Typography variant="body1" sx={{ color: "#333", marginBottom: 2, textAlign: "center" }}>
            {(() => {
              const clinicSlots = unavailableTimeSlots || [];
              const isCurrentlyUnavailable = clinicSlots.includes(clinicWideTimeSlot);
              
              // Check if this is a single dentist operation
              const isSingleDentistOp = dentistStatuses.length === 1;
              const dentistName = isSingleDentistOp ? getDentistDisplayNameFromUID(dentistStatuses[0].dentistId) : null;
              
              if (isCurrentlyUnavailable) {
                if (isSingleDentistOp) {
                  return selectedTimeSlots.length > 1 
                    ? `You are about to mark these time slots as available for ${dentistName}. Below is the current status:`
                    : `You are about to mark this time slot as available for ${dentistName}. Below is the current status:`;
                } else {
                  return selectedTimeSlots.length > 1 
                    ? "You are about to mark these time slots as available for the entire clinic. Below is the current status of all dentists for the first time slot:"
                    : "You are about to mark this time slot as available for the entire clinic. Below is the current status of all dentists for this time slot:";
                }
              } else {
                if (isSingleDentistOp) {
                  return selectedTimeSlots.length > 1 
                    ? `You are about to mark these time slots as unavailable and will cancel all appointments for ${dentistName}. Below is the current status:`
                    : `You are about to mark this time slot as unavailable and will cancel an appointment for ${dentistName}. Below is the current status:`;
                } else {
                  return selectedTimeSlots.length > 1 
                    ? "You are about to mark these time slots as unavailable and will cancel all appointments (time slots that are already marked as unavailable will remain unavailable) for the entire clinic. Below are the current status of all dentist(s) for all selected time slots:"
                    : "You are about to mark this time slot as unavailable and will cancel all appointments for the entire clinic. Below is the current status of all dentist(s) for this time slot:";
                }
              }
            })()}
          </Typography>
          
          {selectedTimeSlots.length > 1 && (
            <Box sx={{ marginBottom: 2, display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
              {consolidatedSelectedSlotsForConfirm
                .sort((a, b) => {
                  const timeToMinutes = (timeStr) => {
                    const [time, period] = timeStr.split(' - ')[0].split(' ');
                    const [hours, minutes] = time.split(':').map(Number);
                    const totalMinutes = hours * 60 + minutes;
                    return period === 'PM' && hours !== 12 ? totalMinutes + 12 * 60 : totalMinutes;
                  };
                  return timeToMinutes(a.displayLabel) - timeToMinutes(b.displayLabel);
                })
                .map((group, index) => {
                  return (
                <Box
                  key={`${group.segments.join("|")}-${index}`}
                  sx={{
                    backgroundColor: "transparent",
                    color: "#C5A572",
                    border: "2px solid #C5A572",
                    fontWeight: "bold",
                    borderRadius: "20px",
                    padding: "8px 16px",
                    fontSize: "16px",
                    textTransform: "none",
                    minWidth: "120px",
                    textAlign: "center",
                    display: "inline-block",
                    
                  }}
                >
                  <Typography variant="body1" sx={{ 
                    color: "#C5A572", 
                    fontWeight: "bold",
                    fontSize: "18px",
                    textAlign: "center"
                  }}>
                    {group.displayLabel}
                  </Typography>
                </Box>
              );
                })}
            </Box>
          )}

          <Box sx={{ marginBottom: 3 }}>
            {(comprehensiveDentistStatuses.length > 0 ? comprehensiveDentistStatuses : dentistStatuses).map((dentistStatus, index) => {
              // Determine card styling - keep outer card neutral
              let cardBorderColor = "#e0e0e0";
              let cardBackgroundColor = "white";
              
              // Determine appointment details styling based on appointment status
              let appointmentDetailsBorderColor = "#e0e0e0";
              let appointmentDetailsBackgroundColor = "white";
              
              if (dentistStatus.appointment) {
                switch (dentistStatus.appointment.status) {
                  case "Pending":
                  case "pending":
                    appointmentDetailsBorderColor = "#FFA500";
                    appointmentDetailsBackgroundColor = "#FFF8E1";
                    break;
                  case "Confirmed":
                  case "confirmed":
                    appointmentDetailsBorderColor = "#4CAF50";
                    appointmentDetailsBackgroundColor = "#E8F5E8";
                    break;
                  case "Completed":
                  case "completed":
                    appointmentDetailsBorderColor = "#2196F3";
                    appointmentDetailsBackgroundColor = "#E3F2FD";
                    break;
                  default:
                    appointmentDetailsBorderColor = "#FFA500";
                    appointmentDetailsBackgroundColor = "#FFF8E1";
                }
              } else if (dentistStatus.status === "Unavailable") {
                appointmentDetailsBorderColor = "#F44336";
                appointmentDetailsBackgroundColor = "#FFEBEE";
              }
              
              return (
                <Card key={index} sx={{ 
                  marginBottom: 2, 
                  backgroundColor: cardBackgroundColor,
                  border: `2px solid ${cardBorderColor}`,
                  borderRadius: "8px"
                }}>
                  <CardContent sx={{ padding: "16px" }}>
                    <Typography variant="h6" sx={{ fontWeight: "600", color: "#333", marginBottom: 2, fontSize: "20px" }}>
                      {dentistStatus.displayName}
                    </Typography>
                    
                    {/* Handle comprehensive data structure (multiple slots) */}
                    {dentistStatus.timeSlotDetails ? (
                      <Box>
                        {dentistStatus.timeSlotDetails.map((slotDetail, slotIndex) => (
                          <Box key={slotIndex} sx={{ marginBottom: 2, padding: "12px", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #e9ecef" }}>
                            <Typography variant="body1" sx={{ fontWeight: "600", color: "#000", marginBottom: 1, fontSize: "18px" }}>
                              <strong>{slotDetail.timeSlot}</strong>
                            </Typography>
                            
                            <Box sx={{ marginBottom: 1 }}>
                              {slotDetail.status === "Unavailable" ? (
                                <Box sx={{
                                  display: "inline-block",
                                  padding: "4px 8px",
                                  borderRadius: "12px",
                                  backgroundColor: "#FFEBEE",
                                  border: "2px solid #F44336"
                                }}>
                                  <Typography variant="body2" sx={{ 
                                    color: "#C62828 !important",
                                    fontWeight: "bold",
                                    fontSize: "16px"
                                  }}>
                                    Status: Unavailable
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={{
                                  display: "inline-block",
                                  padding: "4px 8px",
                                  borderRadius: "12px",
                                  backgroundColor: "#E8F5E8",
                                  border: "2px solid #4CAF50"
                                }}>
                                  <Typography variant="body2" sx={{ 
                                    color: "#2E7D32 !important",
                                    fontWeight: "bold",
                                    fontSize: "16px"
                                  }}>
                                    Status: Available
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            
                            {slotDetail.appointment && (
                              <Box>
                                
                                <Box sx={{ 
                                  backgroundColor: (() => {
                                    switch (slotDetail.appointment.status) {
                                      case "Pending":
                                      case "pending":
                                        return "#FFF8E1";
                                      case "Confirmed":
                                      case "confirmed":
                                        return "#E8F5E8";
                                      case "Completed":
                                      case "completed":
                                        return "#E3F2FD";
                                      default:
                                        return "#FFF8E1";
                                    }
                                  })(),
                                  padding: "16px", 
                                  borderRadius: "4px",
                                  border: `2px solid ${(() => {
                                    switch (slotDetail.appointment.status) {
                                      case "Pending":
                                      case "pending":
                                        return "#FFA500";
                                      case "Confirmed":
                                      case "confirmed":
                                        return "#4CAF50";
                                      case "Completed":
                                      case "completed":
                                        return "#2196F3";
                                      default:
                                        return "#FFA500";
                                    }
                                  })()}`
                                }}>
                                  <Typography variant="body1" sx={{ color: "#000", fontWeight: "500", marginBottom: "12px", display: "block", fontSize: "16px" }}>
                                    <strong>Appointment Details:</strong>
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: "#000", marginBottom: "8px", display: "block", fontSize: "15px" }}>
                                    <strong>Patient:</strong> {slotDetail.appointment.patientName || slotDetail.appointment.patientDisplay || "N/A"}
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: "#000", marginBottom: "8px", display: "block", fontSize: "15px" }}>
                                    <strong>Services:</strong> {slotDetail.appointment.services && Array.isArray(slotDetail.appointment.services) 
                                      ? slotDetail.appointment.services.map(service => 
                                          typeof service === 'object' ? service.name : service
                                        ).join(", ") 
                                      : "N/A"}
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: "#000", marginBottom: "8px", display: "block", fontSize: "15px" }}>
                                    <strong>Status:</strong> {slotDetail.appointment.status || "N/A"}
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: "#000", display: "block", fontSize: "15px" }}>
                                    <strong>Time:</strong> {slotDetail.appointment.startTime || slotDetail.appointment.time || "N/A"} - {slotDetail.appointment.endTime || "N/A"}
                                  </Typography>
                                </Box>
                                <Typography variant="body1" sx={{
                                  color: "#000",
                                  fontWeight: "bold",
                                  marginTop: "8px",
                                  display: "block",
                                  fontStyle: "italic",
                                  fontSize: "14px"
                                }}>
                                  ⚠️ Note: Marking this time slot ({slotDetail.timeSlot}) as unavailable will cancel this patient's appointment automatically.
                                </Typography>
                              </Box>
                            )}
                            
                            {!slotDetail.appointment && (
                              <Typography variant="body1" sx={{ color: "#000", fontSize: "15px", fontWeight: "500" }}>
                                No Appointment Found
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      /* Handle single-slot data structure (backward compatibility) */
                      <Box>
                        <Box sx={{ marginBottom: 2 }}>
                          {dentistStatus.status === "Unavailable" ? (
                            <Box sx={{
                              display: "inline-block",
                              padding: "6px 12px",
                              borderRadius: "20px",
                              backgroundColor: "#FFEBEE",
                              border: "2px solid #F44336"
                            }}>
                              <Typography variant="body2" sx={{ 
                                color: "#C62828 !important",
                                fontWeight: "bold",
                                fontSize: "16px"
                              }}>
                                Status: Unavailable
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{
                              display: "inline-block",
                              padding: "6px 12px",
                              borderRadius: "20px",
                              backgroundColor: "#E8F5E8",
                              border: "2px solid #4CAF50"
                            }}>
                              <Typography variant="body2" sx={{ 
                                color: "#2E7D32 !important",
                                fontWeight: "bold",
                                fontSize: "16px"
                              }}>
                                Status: Available
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        
                        
                        {dentistStatus.appointment ? (
                          <Box>
                            <Box sx={{ 
                              backgroundColor: appointmentDetailsBackgroundColor, 
                              padding: "16px", 
                              borderRadius: "6px",
                              border: `2px solid ${appointmentDetailsBorderColor}`
                            }}>
                              <Typography variant="body1" sx={{ color: "#000", fontWeight: "500", marginBottom: "12px", fontSize: "16px" }}>
                                <strong>Appointment Details:</strong>
                              </Typography>
                              <Typography variant="body1" sx={{ color: "#000", marginBottom: "8px", fontSize: "15px" }}>
                                <strong>Patient:</strong> {dentistStatus.appointment.patientName || dentistStatus.appointment.patientDisplay || "N/A"}
                              </Typography>
                              <Typography variant="body1" sx={{ color: "#000", marginBottom: "8px", fontSize: "15px" }}>
                                <strong>Services:</strong> {dentistStatus.appointment.services && Array.isArray(dentistStatus.appointment.services) 
                                  ? dentistStatus.appointment.services.map(service => 
                                      typeof service === 'object' ? service.name : service
                                    ).join(", ") 
                                  : "N/A"}
                              </Typography>
                              <Typography variant="body1" sx={{ color: "#000", marginBottom: "8px", fontSize: "15px" }}>
                                <strong>Status:</strong> {dentistStatus.appointment.status || "N/A"}
                              </Typography>
                              <Typography variant="body1" sx={{ color: "#000", fontSize: "15px" }}>
                                <strong>Time:</strong> {dentistStatus.appointment.startTime || dentistStatus.appointment.time || "N/A"} - {dentistStatus.appointment.endTime || "N/A"}
                              </Typography>
                            </Box>
                            <Typography variant="body1" sx={{
                              color: "#000",
                              fontWeight: "bold",
                              marginTop: "8px",
                              display: "block",
                              fontStyle: "italic",
                              fontSize: "14px"
                            }}>
                              ⚠️ Note: Marking this time slot ({clinicWideTimeSlot}) as unavailable will cancel this patient's appointment automatically.
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body1" sx={{ color: "#000", fontSize: "15px", fontWeight: "500" }}>
                            No Appointment(s) Found
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic" }}>
            {(() => {
              // Check if any dentist has appointments or is individually marked unavailable
              const hasProtectedDentists = dentistStatuses.some(status => 
                status.appointment || (status.status === "Unavailable" && !status.appointment)
              );
              
              // Check if any dentist is currently unavailable due to clinic-wide settings
              const hasUnavailableDentists = dentistStatuses.some(status => 
                status.status === "Unavailable" && !status.appointment
              );
              
              // Check if any dentist is currently available (for protection message)
              const hasAvailableDentists = dentistStatuses.some(status => 
                status.status === "Available" && !status.appointment
              );
              
              if (hasUnavailableDentists) {
                // Making unavailable slots available
                if (hasProtectedDentists && hasAvailableDentists) {
                  return "Note: Dentist(s) who already marked this time slot as available will remain available. This action will make the time slot available for all dentist(s).";
                } else if (hasProtectedDentists) {
                  return "Note: This action will make the time slot available for all dentist(s).";
                } else {
                  return "Note: This action will make the time slot available for dentists who currently have it unavailable due to clinic-wide settings.";
                }
              } else {
                // Making available slots unavailable
                if (hasProtectedDentists) {
                  return "Note: Dentist(s) who already marked this time slot as unavailable will remain unavailable.";
                } else {
                  return null;
                }
              }
            })()}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ padding: "16px 24px", backgroundColor: "#f5f5f5", borderRadius: "0 0 16px 16px", justifyContent: "center" }}>
          <Button
            onClick={async () => {
              if (clinicWideTimeSlot) {
                // Directly execute clinic-wide action without showing redundant modal
                setIsMarkingTimeSlotUnavailable(true);
                
                if (!selectedModalDate) return;
                const year = selectedModalDate.getFullYear();
                const month = String(selectedModalDate.getMonth() + 1).padStart(2, '0');
                const day = String(selectedModalDate.getDate()).padStart(2, '0');
                const dateStr = `${year}-${month}-${day}`;
                
                try {
                  // Get current clinic-wide status by checking if any dentist has this slot
                  const timeSlotsRef = doc(db, "unavailableTimeSlots", dateStr);
                  const timeSlotsSnap = await getDoc(timeSlotsRef);
                  const currentData = timeSlotsSnap.exists() ? timeSlotsSnap.data() : {};
                  
                  // Check if any dentist currently has this slot unavailable
                  const isCurrentlyUnavailable = dentists.some(dentist => {
                    const dentistSlots = currentData[dentist.uid] || [];
                    return dentistSlots.includes(clinicWideTimeSlot);
                  });
                  
                  // Update dentist UIDs for selected slots
                  const updateData = {};
                  
                  // Update only the selected dentist
                  const dentistsToUpdate = dentists.filter(dentist => dentist.uid === selectedModalDentist);
                  
                  for (const dentist of dentistsToUpdate) {
                    const currentSlots = currentData[dentist.uid] || [];
                    let updatedSlots = [...currentSlots];
                    
                    // Process all selected time slots
                    const slotsToProcess = selectedTimeSlots.length > 0 ? selectedTimeSlots : [clinicWideTimeSlot];
                    
                    slotsToProcess.forEach(timeSlot => {
                      if (isCurrentlyUnavailable) {
                        // Make slot available
                        updatedSlots = updatedSlots.filter(slot => slot !== timeSlot);
                      } else {
                        // Make slot unavailable
                        if (!updatedSlots.includes(timeSlot)) {
                          updatedSlots.push(timeSlot);
                        }
                      }
                    });
                    
                    updateData[dentist.uid] = updatedSlots;
                  }
                  
                  await setDoc(timeSlotsRef, updateData, { merge: true });
                  
                  const slotsForNotify =
                    selectedTimeSlots.length > 0 ? selectedTimeSlots : [clinicWideTimeSlot];
                  if (selectedModalDentist && slotsForNotify.length > 0) {
                    await notifyClinicPersonnelForTimeSlots({
                      dentistId: selectedModalDentist,
                      dateStr,
                      slots: slotsForNotify.filter(Boolean),
                      action: isCurrentlyUnavailable ? "available" : "unavailable",
                    });
                  }
                  
                  // Update local state - for clinic view, aggregate all dentist slots
                  const allSlots = new Set();
                  Object.values(updateData).forEach(slots => {
                    slots.forEach(slot => allSlots.add(slot));
                  });
                  setUnavailableTimeSlots(Array.from(allSlots));
                  
                } catch (error) {
                  console.error("Error updating clinic-wide time slot availability:", error);
                } finally {
                  setIsMarkingTimeSlotUnavailable(false);
                  setShowClinicWideDialog(false);
                  setClinicWideTimeSlot(null);
                  setDentistStatuses([]);
                  setComprehensiveDentistStatuses([]); // Clear comprehensive statuses
                  setSelectedTimeSlots([]); // Clear selected slots after processing
                }
              }
            }}
            disabled={isMarkingTimeSlotUnavailable}
            sx={{
              backgroundColor: "#C5A572",
              color: "white",
              borderRadius: "8px",
              padding: "10px 20px",
              fontWeight: "bold",
              minWidth: "200px",
              "&:hover": {
                backgroundColor: "#B8955A"
              },
              "&:disabled": {
                backgroundColor: "#C5A572",
                opacity: 0.7
              }
            }}
          >
            {isMarkingTimeSlotUnavailable ? (
              <>
                <CircularProgress size={20} sx={{ color: "white", marginRight: "8px" }} />
                Processing...
              </>
            ) : (
              (() => {
                const clinicSlots = unavailableTimeSlots || [];
                const isCurrentlyUnavailable = clinicSlots.includes(clinicWideTimeSlot);
                const dentistName = getDentistDisplayNameFromUID(selectedModalDentist);
                
                    if (isCurrentlyUnavailable) {
                      return `Mark As Available For ${dentistName}`;
                    } else {
                      return `Mark As Unavailable For ${dentistName}`;
                }
              })()
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Time Slots Confirmation Dialog (Multiple or Single) - Shows appointment details when slots are booked */}
      {selectedTimeSlots.length > 0 && multiConfirmationAction !== null && (
      <Dialog
        open={showMultiConfirmationDialog}
        onClose={() => {
          setShowMultiConfirmationDialog(false);
          setSelectedTimeSlots([]);
          setMultiConfirmationAction(null);
          setMultiSlotValidationMessage("");
          setMultiSlotValidationSlots([]);
          setMultiSlotConflictingAppointments([]);
        }}
        maxWidth="sm"
        fullWidth={true}
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: "#FDF9F3",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)"
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#BA9A65",
            color: "white",
            padding: "20px 24px",
            paddingRight: "64px",
            borderRadius: "16px 16px 0 0",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 700,
              fontSize: timeSlotManagementFontSize,
              lineHeight: 1.1,
              maxWidth: "100%",
              whiteSpace: "nowrap",
              textAlign: "center",
              color: "#FFFFFF",
            }}
            title={timeSlotManagementTitle}
            noWrap
          >
            {timeSlotManagementTitle}
          </Typography>
          <IconButton
            onClick={() => {
              setShowMultiConfirmationDialog(false);
              setSelectedTimeSlots([]);
              setMultiConfirmationAction(null);
              setMultiSlotValidationMessage("");
              setMultiSlotValidationSlots([]);
              setMultiSlotConflictingAppointments([]);
            }}
            sx={{
              position: "absolute",
              right: "16px",
              top: "16px",
              backgroundColor: "transparent",
              border: "1px solid rgba(253, 249, 243, 0.8)",
              color: "#FDF9F3",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              "&:hover": {
                backgroundColor: "white",
                color: "#C5A572",
                borderColor: "white",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "24px",
            backgroundColor: "#FDF9F3",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#2C251E",
              fontWeight: 600,
              marginBottom: "5px",
              marginTop: "5px",
            }}
          >
            {`Selected Time Slots (${consolidatedSelectedSlotsForConfirm.length})`}
          </Typography>
          {consolidatedSelectedSlotsForConfirm.length !== selectedTimeSlots.length && (
            <Typography variant="body2" sx={{ color: "#59544A", marginBottom: 2 }}>
              {`${selectedTimeSlots.length} half-hour intervals will be updated; one row per booking below.`}
            </Typography>
          )}

          <Box sx={{ marginBottom: 3 }}>
            {consolidatedSelectedSlotsForConfirm
              .sort((a, b) => {
                // Convert time slots to minutes for comparison
                const timeToMinutes = (timeStr) => {
                  const [time, period] = timeStr.split(' - ')[0].split(' ');
                  const [hours, minutes] = time.split(':').map(Number);
                  const totalMinutes = hours * 60 + minutes;
                  return period === 'PM' && hours !== 12 ? totalMinutes + 12 * 60 : totalMinutes;
                };
                return timeToMinutes(a.displayLabel) - timeToMinutes(b.displayLabel);
              })
              .map((group, index) => {
                const slotStatus = getTimeSlotStatus(group.statusProbeSlot);
                const bookedAppointment = group.bookedAppointment;

                return (
                  <Box
                    key={`${group.segments.join("|")}-${index}`}
                    sx={{
                      backgroundColor: "#F1F1F1",
                      padding: "16px",
                      borderRadius: "12px",
                      marginBottom: 2,
                      border: "1px solid rgba(59, 43, 31, 0.2)",
                      boxShadow: "0 4px 10px rgba(44, 37, 30, 0.08)",
                    }}
                  >
                    <Typography variant="body1" sx={{ color: "#000", fontWeight: "600", marginBottom: 1, fontSize: "16px" }}>
                      {group.displayLabel}
                    </Typography>
                    
                    <Box sx={{ marginBottom: 1 }}>
                      {slotStatus.status === "Available" ? (
                        <Box sx={{
                          display: "inline-block",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          backgroundColor: "#E8F5E8",
                          border: "2px solid #4CAF50"
                        }}>
                          <Typography variant="body2" sx={{ 
                            color: "#2E7D32 !important",
                            fontWeight: "bold",
                            fontSize: "14px"
                          }}>
                            Status: Available
                          </Typography>
                        </Box>
                      ) : slotStatus.status === "Unavailable" ? (
                        <Box sx={{
                          display: "inline-block",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          backgroundColor: "#FFEBEE",
                          border: "2px solid #F44336"
                        }}>
                          <Typography variant="body2" sx={{ 
                            color: "#C62828 !important",
                            fontWeight: "bold",
                            fontSize: "14px"
                          }}>
                            Status: Unavailable
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{
                          display: "inline-block",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          backgroundColor: "#FFF3E0",
                          border: "2px solid #FF9800"
                        }}>
                          <Typography variant="body2" sx={{ 
                            color: "#E65100 !important",
                            fontWeight: "bold",
                            fontSize: "14px"
                          }}>
                            Status: Mixed
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    
                    {/* Show appointment details if there's a booked appointment */}
                    {bookedAppointment && (
                      <Box sx={{
                        backgroundColor: bookedAppointment.status === "Confirmed" ? "#E8F5E8" : 
                                        bookedAppointment.status === "Pending" ? "#fff8e1" : "#E3F2FD",
                        border: bookedAppointment.status === "Confirmed" ? "2px solid #4CAF50" : 
                               bookedAppointment.status === "Pending" ? "2px solid #FFA500" : "2px solid #2196F3",
                        borderRadius: "8px",
                        padding: "12px",
                        marginTop: "8px"
                      }}>
                        <Typography variant="body2" sx={{ 
                          color: "#000",
                          fontWeight: "bold",
                          marginBottom: 2,
                          fontSize: "14px"
                        }}>
                          Appointment Details:
                    </Typography>
                        
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                          {/* Left Column - Patient Info */}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ color: "#000", marginBottom: 0.5, fontSize: "13px" }}>
                              <strong>Name:</strong> {bookedAppointment.patientName || bookedAppointment.name || "N/A"}
                            </Typography>
                            
                            <Typography variant="body2" sx={{ color: "#000", marginBottom: 0.5, fontSize: "13px" }}>
                              <strong>Services:</strong> {bookedAppointment.services && Array.isArray(bookedAppointment.services) 
                                ? bookedAppointment.services.map(service => 
                                    typeof service === 'object' ? service.name : service
                                  ).join(", ") 
                                : "N/A"}
          </Typography>

                            <Typography variant="body2" sx={{ color: "#000", fontSize: "13px" }}>
                              <strong>Time:</strong> {group.displayLabel}
                    </Typography>
                          </Box>
                          
                          {/* Right Column - Dentist & Status */}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ color: "#000", marginBottom: 0.5, fontSize: "13px" }}>
                              <strong>Dentist:</strong>{" "}
                              {bookedAppointment.dentist ||
                                getDentistDisplayNameFromUID(
                                  resolveAppointmentDentistUid(bookedAppointment)
                                ) ||
                                "N/A"}
                            </Typography>
                            
                            <Typography variant="body2" sx={{ color: "#000", fontSize: "13px" }}>
                              <strong>Status:</strong> {bookedAppointment.status || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
          </Box>

          {/* Date unavailability warning message */}
            {(() => {
            if (!selectedModalDate) return null;
            const year = selectedModalDate.getFullYear();
            const month = String(selectedModalDate.getMonth() + 1).padStart(2, '0');
            const day = String(selectedModalDate.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            
            if (multiConfirmationAction === 'available' && closedDays.includes(dateStr)) {
              return (
                <Box sx={{
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffeaa7",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  marginBottom: "16px",
                  textAlign: "center"
                }}>
                  <Typography variant="body2" sx={{ 
                    color: "#856404", 
                    fontWeight: "500",
                    fontSize: "14px",
                    lineHeight: 1.4
                  }}>
                    ⚠️ Cannot mark time slots as available because this date is marked as unavailable.<br/>
                    Please make the date available first in the <strong>"Select Unavailable Date(s)"</strong> section.
          </Typography>
                </Box>
              );
            }
            return null;
          })()}
        </DialogContent>

        <DialogActions
          sx={{
            padding: "16px 24px",
            backgroundColor: "#FDF9F3",
            borderRadius: "0 0 16px 16px",
            justifyContent: "center",
            borderTop: "1px solid rgba(44, 37, 30, 0.08)",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {multiSlotValidationMessage && (
            <Alert
              severity="error"
              icon={false}
              sx={{
                width: "calc(100% - 48px)",
                marginX: "24px",
                backgroundColor: "#FDE8E8",
                color: "#9B2C2C",
                border: "1px solid #F04848",
                "& .MuiAlert-message": {
                  color: "#9B2C2C",
                  width: "100%",
                },
                "& .MuiTypography-root": {
                  color: "#9B2C2C",
                },
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 400 }}>
                {multiSlotValidationMessage}
              </Typography>
            </Alert>
          )}
          {(() => {
            // Determine button visibility from the same status source shown in rows.
            const unavailableSlots = selectedTimeSlots.filter(
              (slot) => getTimeSlotStatus(slot).status === "Unavailable"
            );
            const availableSlots = selectedTimeSlots.filter(
              (slot) => getTimeSlotStatus(slot).status === "Available"
            );
            const hasAvailable = availableSlots.length > 0;
            const hasUnavailable = unavailableSlots.length > 0;
            const isMixed = hasAvailable && hasUnavailable;
            
            // Check if date is closed
            const year = selectedModalDate?.getFullYear();
            const month = selectedModalDate ? String(selectedModalDate.getMonth() + 1).padStart(2, '0') : '';
            const day = selectedModalDate ? String(selectedModalDate.getDate()).padStart(2, '0') : '';
            const dateStr = year && month && day ? `${year}-${month}-${day}` : '';
            const isDateClosed = dateStr ? closedDays.includes(dateStr) : false;
            
            // Show both buttons if mixed, otherwise show the appropriate single button
            return (
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center", width: "100%" }}>
                {(isMixed || hasAvailable) && (
                  <Button
                    onClick={() => confirmMultipleSlotsAction('unavailable')}
                    disabled={isMarkingTimeSlotUnavailable}
                    sx={{
                      backgroundColor: "#C5A572",
                      color: "white",
                      borderRadius: "8px",
                      padding: "10px 20px",
                      fontWeight: "bold",
                      minWidth: "150px",
                      flex: isMixed ? 1 : "none",
                      "&:hover": {
                        backgroundColor: "#B8955A"
                      },
                      "&:disabled": {
                        backgroundColor: "#C5A572",
                        opacity: 0.7
                      }
                    }}
                  >
                    {isMarkingTimeSlotUnavailable ? (
                      <>
                        <CircularProgress size={20} sx={{ color: "white", marginRight: "8px" }} />
                        Processing...
                      </>
                    ) : (
                      "Mark as Unavailable"
                    )}
                  </Button>
                )}
                {(isMixed || hasUnavailable) && (
                  <Button
                    onClick={() => confirmMultipleSlotsAction('available')}
                    disabled={isMarkingTimeSlotUnavailable || isDateClosed}
                    sx={{
                      backgroundColor: "#C5A572",
                      color: "white",
                      borderRadius: "8px",
                      padding: "10px 20px",
                      fontWeight: "bold",
                      minWidth: "150px",
                      flex: isMixed ? 1 : "none",
                      "&:hover": {
                        backgroundColor: isDateClosed ? "#C5A572" : "#B8955A"
                      },
                      "&:disabled": {
                        backgroundColor: "#C5A572",
                        opacity: 0.7
                      }
                    }}
                  >
                    {isMarkingTimeSlotUnavailable ? (
                      <>
                        <CircularProgress size={20} sx={{ color: "white", marginRight: "8px" }} />
                        Processing...
                      </>
                    ) : (
                      "Mark as Available"
                    )}
                  </Button>
                )}
              </Box>
            );
          })()}
        </DialogActions>
      </Dialog>
      )}

      <Dialog
        open={showMultiSlotCancellationDialog}
        onClose={() => {
          setShowMultiSlotCancellationDialog(false);
          setMultiSlotValidationMessage("");
          setMultiSlotValidationSlots([]);
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: "#FDF9F3",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#BA9A65",
            color: "#FDF9F3",
            padding: "20px 24px",
            paddingRight: "64px",
            borderRadius: "16px 16px 0 0",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 700,
              fontSize: cancellationDialogFontSize,
              lineHeight: 1.1,
              maxWidth: "100%",
              whiteSpace: "nowrap",
              textAlign: "center",
              color: "#FFFFFF",
            }}
            title={cancellationDialogTitle}
            noWrap
          >
            {cancellationDialogTitle}
          </Typography>
          <IconButton
            onClick={() => {
              setShowMultiSlotCancellationDialog(false);
              setMultiSlotValidationMessage("");
              setMultiSlotValidationSlots([]);
            }}
            sx={{
              position: "absolute",
              right: "16px",
              top: "16px",
              backgroundColor: "transparent",
              border: "1px solid rgba(253, 249, 243, 0.8)",
              color: "#FDF9F3",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              "&:hover": {
                backgroundColor: "white",
                color: "#C5A572",
                borderColor: "white",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "24px",
            backgroundColor: "#FDF9F3",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#2C251E",
              fontWeight: 600,
              marginBottom: "5px",
              marginTop: "5px",
            }}
          >
            {`Selected Time Slots (${uniqueConflictingSlotCount})`}
          </Typography>

          <Box sx={{ marginBottom: 3 }}>
            {conflictsGroupedBySlot.size === 0 ? (
              <Typography variant="body2" sx={{ color: "#59544A", fontStyle: "italic" }}>
                No appointments detected for cancellation.
              </Typography>
            ) : (
              Array.from(conflictsGroupedBySlot.entries())
                .sort((a, b) => {
                  const [slotA] = a;
                  const [slotB] = b;
                  const toMinutes = (slot) => {
                    const [start] = (slot || "").split(" - ");
                    return timeToMinutes(start);
                  };
                  return toMinutes(slotA) - toMinutes(slotB);
                })
                .map(([slotKey, records], index) => {
                  const filteredRecords = records.filter((record) => record.slot === slotKey);
                  if (filteredRecords.length === 0) {
                    return null;
                  }

                  return (
                    <Box
                      key={`${slotKey || index}-slot-cancel`}
                      sx={{
                        backgroundColor: "#F1F1F1",
                        padding: "16px",
                        borderRadius: "12px",
                        marginBottom: 2,
                        border: "1px solid rgba(59, 43, 31, 0.2)",
                        boxShadow: "0 4px 10px rgba(44, 37, 30, 0.08)",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#1F1F1F",
                          fontWeight: 600,
                          marginBottom: 1,
                          fontSize: "16px",
                        }}
                      >
                        Time Slot: {slotKey}
                      </Typography>

                      {filteredRecords.map((record, recordIndex) => {
                        const appointment = record.appointment || {};
                        const patientName =
                          appointment.patientName ||
                          appointment.patientDisplay ||
                          appointment.name ||
                          "N/A";
                        const services =
                          appointment.services && Array.isArray(appointment.services)
                            ? appointment.services
                                .map((service) => (typeof service === "object" ? service.name : service))
                                .join(", ")
                            : "N/A";
                        const statusLabel = (appointment.status || "Pending").trim();
                        const appointmentWindow = `${record.appointmentStart} - ${record.appointmentEnd}`;
                        const statusVisuals = getAppointmentStatusVisuals(statusLabel);
                        const detailsTextColor = "#2C251E";

                        return (
                          <Box
                            key={`${slotKey || index}-slot-cancel-${recordIndex}`}
                            sx={{
                              backgroundColor: statusVisuals.cardBackground,
                              borderRadius: "10px",
                              padding: "16px",
                              marginTop: recordIndex === 0 ? "12px" : "16px",
                              border: statusVisuals.cardBorder,
                              boxShadow: "0 2px 6px rgba(44, 37, 30, 0.08)",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: detailsTextColor,
                                fontWeight: 600,
                                marginBottom: "12px",
                                fontSize: "15px",
                              }}
                            >
                              Appointment Details:
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                gap: 2,
                                flexWrap: "wrap",
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: "160px" }}>
                                <Typography variant="body2" sx={{ color: detailsTextColor, marginBottom: 0.5 }}>
                                  <strong>Name:</strong> {patientName}
                                </Typography>
                                <Typography variant="body2" sx={{ color: detailsTextColor, marginBottom: 0.5 }}>
                                  <strong>Services:</strong> {services}
                                </Typography>
                                <Typography variant="body2" sx={{ color: detailsTextColor }}>
                                  <strong>Time:</strong> {appointmentWindow}
                                </Typography>
                              </Box>
                              <Box sx={{ flex: 1, minWidth: "160px", textAlign: "right" }}>
                                <Typography variant="body2" sx={{ color: detailsTextColor, marginBottom: 0.5 }}>
                                  <strong>Dentist:</strong> {getDentistDisplayNameFromUID(selectedModalDentist)}
                                </Typography>
                                <Typography variant="body2" sx={{ color: detailsTextColor }}>
                                  <strong>Status:</strong> {statusLabel}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: "16px 24px",
            backgroundColor: "#FDF9F3",
            borderRadius: "0 0 16px 16px",
            justifyContent: "center",
            borderTop: "1px solid rgba(44, 37, 30, 0.08)",
          }}
        >
          <Button
            onClick={handleBulkCancelAppointments}
            disabled={isBulkCancellingAppointments || multiSlotConflictingAppointments.length === 0}
            sx={{
              backgroundColor: "#CC0000",
              color: "#FEFEFE",
              borderRadius: "8px",
              padding: "10px 20px",
              fontWeight: "bold",
              minWidth: "180px",
              "&:hover": {
                backgroundColor: "#A30000",
              },
              "&:disabled": {
                backgroundColor: "#CC0000",
                opacity: 0.7,
              },
            }}
          >
            {isBulkCancellingAppointments ? (
              <>
                <CircularProgress size={20} sx={{ color: "#FEFEFE", marginRight: "8px" }} />
                Cancelling...
              </>
            ) : (
              "Cancel Appointments"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={priceValidationSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setPriceValidationSnackbar({ open: false, message: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setPriceValidationSnackbar({ open: false, message: "" })}
          severity="error"
          icon={false}
          sx={{
            width: "100%",
            backgroundColor: "#9F4223",
            color: "#fff",
            fontWeight: 600,
            fontSize: "14px",
            letterSpacing: 0.2,
            borderRadius: "12px"
          }}
        >
          {priceValidationSnackbar.message}
        </Alert>
      </Snackbar>


      <SplashScreen isOpen={showSplash} />

      {/* ===== Appointment Chair Conflict Prevention modals ===== */}

      {/* Roster Picker (Clinic Staff only) */}
      <Dialog
        open={showRosterModal}
        onClose={() => (isSavingRoster ? null : setShowRosterModal(false))}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", backgroundColor: "#f8f5ef" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#C5A572",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px 24px",
            position: "relative",
          }}
        >
          <Box sx={{ fontWeight: "bold", fontSize: "1.25rem" }}>
            Select {rosterDraftCount || "N"} dentist{(rosterDraftCount || 0) === 1 ? "" : "s"} for each chair
          </Box>
          <IconButton
            onClick={() => (isSavingRoster ? null : setShowRosterModal(false))}
            sx={{
              position: "absolute",
              right: "24px",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              "&:hover": { backgroundColor: "white", color: "#C5A572" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "20px" }}>
          <FormControl fullWidth size="small" sx={{ marginBottom: 2 }}>
            <Typography variant="body2" sx={{ color: "#444", marginBottom: 1 }}>
              Number of dentist chairs
            </Typography>
            <Select
              value={rosterDraftCount || ""}
              disabled={maxChairCount <= 0}
              displayEmpty
              onChange={(e) => {
                const next = Number(e.target.value);
                if (!Number.isFinite(next) || next <= 0) return;
                setRosterDraftCount(next);
                setRosterDraftIds((prev) => prev.slice(0, next));
              }}
              renderValue={(value) =>
                value ? `${value} chair${value === 1 ? "" : "s"}` : (maxChairCount <= 0 ? "No dentists available" : "Set number of chairs")
              }
            >
              {Array.from({ length: maxChairCount }).map((_, idx) => {
                const value = idx + 1;
                return (
                  <MenuItem key={value} value={value}>
                    {value} chair{value === 1 ? "" : "s"}
                  </MenuItem>
                );
              })}
              {maxChairCount <= 0 && (
                <MenuItem value="" disabled>
                  No dentists available
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <Typography variant="body2" sx={{ color: "#444", marginBottom: 1 }}>
            {rosterDraftIds.length} of {rosterDraftCount || 0} selected
          </Typography>
          <Box sx={{ maxHeight: "320px", overflowY: "auto", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "8px" }}>
            {dentists.map((d) => {
              const isSelected = rosterDraftIds.includes(d.uid);
              const disable = !isSelected && rosterDraftCount > 0 && rosterDraftIds.length >= rosterDraftCount;
              return (
                <Box
                  key={d.uid}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 12px",
                    borderBottom: "1px solid rgba(0,0,0,0.05)",
                    opacity: disable ? 0.6 : 1,
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={disable}
                    onChange={() => toggleRosterDraft(d.uid)}
                    sx={{ color: "#C5A572", "&.Mui-checked": { color: "#C5A572" } }}
                  />
                  <Box sx={{ marginLeft: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: "#2C251E !important",
                        lineHeight: 1.25,
                        fontSize: "14px",
                      }}
                    >
                      {d.displayName || getDentistDisplayName(d)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        display: "block",
                        marginTop: "2px",
                        lineHeight: 1.1,
                      }}
                    >
                      {d.role === "DentistOwner"
                        ? "Dentist Owner"
                        : d.role === "AssociateDentist"
                          ? "Associate Dentist"
                          : d.role}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            {dentists.length === 0 && (
              <Box sx={{ padding: "16px", textAlign: "center", color: "#666" }}>
                No dentists found.
              </Box>
            )}
          </Box>
          {rosterSavingError != null && rosterSavingError !== "" && (
            <Alert
              severity="error"
              icon={false}
              sx={{
                marginTop: 2,
                backgroundColor: "#FDE8E8",
                color: "#9B2C2C",
                "& .MuiAlert-message": {
                  color: "#9B2C2C",
                  width: "100%",
                },
                "& .MuiTypography-root": {
                  color: "#9B2C2C",
                },
              }}
            >
              {typeof rosterSavingError === "object" &&
              rosterSavingError.removalBlocks &&
              Array.isArray(rosterSavingError.removalBlocks) ? (
                <Box>
                  {rosterSavingError.removalBlocks.map((block, idx) => (
                    <Box
                      key={block.uid}
                      sx={{
                        marginBottom:
                          idx < rosterSavingError.removalBlocks.length - 1 ? 2 : 0,
                      }}
                    >
                      {/* body2 is themed as light text site-wide; use body1 for readable alert copy */}
                      <Typography variant="body1" sx={{ fontWeight: 400, marginBottom: 1 }}>
                        {getDentistDisplayNameFromUID(block.uid)} has patient appointments on the
                        following dates:
                      </Typography>
                      <Box sx={{ pl: 0.5 }}>
                        {block.dates.map((dateKey) => (
                          <Typography key={dateKey} variant="body1" sx={{ lineHeight: 1.6 }}>
                            - {formatRosterRemovalDateKey(dateKey)}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ))}
                  <Typography variant="body1" sx={{ marginTop: 1.5, fontWeight: 400 }}>
                    Please cancel the patient appointments first.
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body1">{rosterSavingError}</Typography>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", justifyContent: "flex-end" }}>
          <Button
            onClick={() => (isSavingRoster ? null : setShowRosterModal(false))}
            sx={{ color: "#555", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRoster}
            disabled={isSavingRoster}
            variant="contained"
            sx={{
              backgroundColor: "#C5A572",
              color: "white",
              borderRadius: "20px",
              textTransform: "none",
              "&:hover": { backgroundColor: "#a87d4a" },
            }}
          >
            {isSavingRoster ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAssignSecondaryModal}
        onClose={closeAssignSecondaryModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", backgroundColor: "#f8f5ef" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#C5A572",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px 24px",
            position: "relative",
          }}
        >
          <Box sx={{ fontWeight: "bold", fontSize: "1.15rem", textAlign: "center" }}>
            Manage Calendar
          </Box>
          <IconButton
            onClick={closeAssignSecondaryModal}
            sx={{
              position: "absolute",
              right: "24px",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              "&:hover": { backgroundColor: "white", color: "#C5A572" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "20px" }}>
          <Stack spacing={2.5}>
            <FormControl fullWidth size="small">
              <Typography variant="body2" sx={{ color: "#333", marginBottom: 1 }}>
                Select Secondary Dentist
              </Typography>
              <Select
                value={assignSecondaryDentistUid}
                onChange={(event) => {
                  setAssignSecondaryDentistUid(event.target.value);
                  setAssignSecondaryValidationMessages([]);
                }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select secondary dentist
                </MenuItem>
                {assignSecondaryDentistOptions.map((dentist) => (
                  <MenuItem key={dentist.uid} value={dentist.uid}>
                    {getDentistDisplayName(dentist)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <Typography variant="body2" sx={{ color: "#333", marginBottom: 1 }}>
                Select Timeslots for Secondary Dentist
              </Typography>
              <Select
                value={assignSecondaryBlockLabel}
                onChange={(event) => {
                  setAssignSecondaryBlockLabel(event.target.value);
                  setAssignSecondaryValidationMessages([]);
                }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select schedule block
                </MenuItem>
                {secondaryScheduleBlockOptions.map((block) => (
                  <MenuItem key={block.label} value={block.label}>
                    {block.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {assignSecondaryValidationMessages.length > 0 && (
              <Alert
                severity="error"
                icon={false}
                sx={{
                  backgroundColor: "#FDE8E8",
                  color: "#9B2C2C",
                  border: "1px solid #F04848",
                  "& .MuiAlert-message": {
                    color: "#9B2C2C",
                    width: "100%",
                  },
                  "& .MuiTypography-root": {
                    color: "#9B2C2C",
                  },
                }}
              >
                <Box>
                  {assignSecondaryValidationMessages.map((message, index) => (
                    <Typography key={`${message}-${index}`} variant="body1" sx={{ fontWeight: 400 }}>
                      {message}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", justifyContent: "center" }}>
          <Button
            onClick={handleSaveAssignSecondaryDentist}
            variant="contained"
            sx={{
              backgroundColor: "#C5A572",
              color: "white",
              borderRadius: "20px",
              textTransform: "none",
              minWidth: "160px",
              "&:hover": { backgroundColor: "#a87d4a" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showUnassignSecondaryModal}
        onClose={closeUnassignSecondaryModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", backgroundColor: "#f8f5ef", maxWidth: "520px" },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#C5A572",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px 24px",
            position: "relative",
          }}
        >
          <Box sx={{ fontWeight: "bold", fontSize: "1.15rem", textAlign: "center" }}>
            Manage Calendar
          </Box>
          <IconButton
            onClick={closeUnassignSecondaryModal}
            sx={{
              position: "absolute",
              right: "24px",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              "&:hover": { backgroundColor: "white", color: "#C5A572" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "20px" }}>
          <Typography variant="body1" sx={{ color: "#333", marginBottom: 2 }}>
            Are you sure you want to unassigned {dentistDisplayLabel(selectedModalDentist)} as secondary dentist for {dentistDisplayLabel(linkedRosterUidForSelectedSecondary)}?
          </Typography>
          {unassignSecondaryValidationMessage && (
            <Alert
              severity="error"
              icon={false}
              sx={{
                backgroundColor: "#FDE8E8",
                color: "#9B2C2C",
                border: "1px solid #F04848",
                "& .MuiAlert-message": { color: "#9B2C2C", width: "100%" },
                "& .MuiTypography-root": { color: "#9B2C2C" },
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 400 }}>
                {unassignSecondaryValidationMessage}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", justifyContent: "center", gap: 2 }}>
          <Button
            onClick={closeUnassignSecondaryModal}
            variant="outlined"
            sx={{
              color: "#C5A572",
              borderColor: "#C5A572",
              borderRadius: "20px",
              textTransform: "none",
              minWidth: "120px",
            }}
          >
            No
          </Button>
          <Button
            onClick={handleConfirmUnassignSecondaryDentist}
            variant="contained"
            disabled={isUnassigningSecondary}
            sx={{
              backgroundColor: "#C5A572",
              color: "white",
              borderRadius: "20px",
              textTransform: "none",
              minWidth: "120px",
              "&:hover": { backgroundColor: "#a87d4a" },
            }}
          >
            {isUnassigningSecondary ? "Saving..." : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replacement Picker (triggered when roster dentist becomes unavailable) */}
      <Dialog
        open={showReplacementModal}
        onClose={() => {
          setShowReplacementModal(false);
          setReplacementSelections({});
          setReplacementContext(null);
          setReplacementRowErrors({});
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", backgroundColor: "#f8f5ef" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#C5A572",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px 24px",
            position: "relative",
          }}
        >
          <Box sx={{ fontWeight: "bold", fontSize: "1.15rem", textAlign: "center" }}>
            Manage Calendar
          </Box>
          <IconButton
            onClick={() => {
              setShowReplacementModal(false);
              setReplacementSelections({});
              setReplacementContext(null);
              setReplacementRowErrors({});
            }}
            sx={{
              position: "absolute",
              right: "24px",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              "&:hover": { backgroundColor: "white", color: "#C5A572" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "20px" }}>
          {replacementContext && (
            <>
              <Typography variant="body2" sx={{ color: "#333", marginBottom: 2 }}>
                Select dentist to replace {dentistDisplayLabel(replacementContext.dentistUid)} from the following timeslots:
              </Typography>
              <Stack spacing={1.5}>
                {replacementContext.rows.map((row) => (
                  <Box key={row.slot}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" sx={{ minWidth: 140, fontWeight: 600, color: "#333" }}>
                        {row.slot}
                      </Typography>
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <Select
                          value={replacementSelections[row.slot] || "__none__"}
                          onChange={(e) => {
                            setReplacementSelections((prev) => ({ ...prev, [row.slot]: e.target.value }));
                            setReplacementRowErrors((prev) => {
                              if (!prev[row.slot]) return prev;
                              const next = { ...prev };
                              delete next[row.slot];
                              return next;
                            });
                          }}
                        >
                          <MenuItem value="__none__">None</MenuItem>
                          {row.replacementOptions.map((uid) => (
                            <MenuItem key={uid} value={uid}>
                              {dentistDisplayLabel(uid)}
                            </MenuItem>
                          ))}
                          {row.replacementOptions.length === 0 && (
                            <MenuItem value="" disabled>
                              No replacement available
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Stack>
                    {replacementRowErrors[row.slot] && (
                      <Alert
                        severity="error"
                        icon={false}
                        sx={{
                          marginTop: 1,
                          backgroundColor: "#FDE8E8",
                          color: "#9B2C2C",
                          "& .MuiAlert-message": {
                            color: "#9B2C2C",
                            width: "100%",
                          },
                          "& .MuiTypography-root": {
                            color: "#9B2C2C",
                          },
                        }}
                      >
                        <Typography variant="body1">{replacementRowErrors[row.slot]}</Typography>
                      </Alert>
                    )}
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", justifyContent: "center" }}>
          <Button
            onClick={handleSaveReplacement}
            variant="contained"
            sx={{
              backgroundColor: "#C5A572",
              color: "white",
              borderRadius: "20px",
              textTransform: "none",
              minWidth: "160px",
              "&:hover": { backgroundColor: "#a87d4a" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Removal Picker (triggered when making a dentist available exceeds chairs) */}
      <Dialog
        open={showRemovalModal}
        onClose={() => {
          setShowRemovalModal(false);
          setRemovalSelections({});
          setRemovalContext(null);
          setRemovalRowErrors({});
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", backgroundColor: "#f8f5ef" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#C5A572",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px 24px",
            position: "relative",
          }}
        >
          <Box sx={{ fontWeight: "bold", fontSize: "1.15rem", textAlign: "center" }}>
            Manage Calendar
          </Box>
          <IconButton
            onClick={() => {
              setShowRemovalModal(false);
              setRemovalSelections({});
              setRemovalContext(null);
              setRemovalRowErrors({});
            }}
            sx={{
              position: "absolute",
              right: "24px",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              "&:hover": { backgroundColor: "white", color: "#C5A572" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "20px" }}>
          {removalContext && (
            <>
              <Typography variant="body2" sx={{ color: "#333", marginBottom: 2 }}>
                Select dentist to be marked unavailable on the following timeslots so {dentistDisplayLabel(removalContext.targetDentistUid)} can become available:
              </Typography>
              <Stack spacing={1.5}>
                {removalContext.rows.map((row) => (
                  <Box key={row.slot}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" sx={{ minWidth: 140, fontWeight: 600, color: "#333" }}>
                        {row.slot}
                      </Typography>
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <Select
                          value={removalSelections[row.slot] || (row.removableOptions[0] || "")}
                          onChange={(e) => {
                            setRemovalSelections((prev) => ({ ...prev, [row.slot]: e.target.value }));
                            setRemovalRowErrors((prev) => {
                              if (!prev[row.slot]) return prev;
                              const next = { ...prev };
                              delete next[row.slot];
                              return next;
                            });
                          }}
                        >
                          {row.removableOptions.map((uid) => (
                            <MenuItem key={uid} value={uid}>
                              {dentistDisplayLabel(uid)}
                            </MenuItem>
                          ))}
                          {row.removableOptions.length === 0 && (
                            <MenuItem value="" disabled>
                              No dentist available to swap for this slot
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Stack>
                    {removalRowErrors[row.slot] && (
                      <Alert
                        severity="error"
                        icon={false}
                        sx={{
                          marginTop: 1,
                          backgroundColor: "#FDE8E8",
                          color: "#9B2C2C",
                          "& .MuiAlert-message": {
                            color: "#9B2C2C",
                            width: "100%",
                          },
                          "& .MuiTypography-root": {
                            color: "#9B2C2C",
                          },
                        }}
                      >
                        <Typography variant="body1">{removalRowErrors[row.slot]}</Typography>
                      </Alert>
                    )}
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", justifyContent: "center" }}>
          <Button
            onClick={handleSaveRemoval}
            variant="contained"
            sx={{
              backgroundColor: "#C5A572",
              color: "white",
              borderRadius: "20px",
              textTransform: "none",
              minWidth: "160px",
              "&:hover": { backgroundColor: "#a87d4a" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chair-related Snackbar (success / capacity & conflict errors) */}
      <Snackbar
        open={chairSnackbar.open}
        autoHideDuration={chairSnackbar.severity === "error" ? 9000 : 4000}
        onClose={() => setChairSnackbar({ open: false, message: "", severity: "info" })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setChairSnackbar({ open: false, message: "", severity: "info" })}
          severity={chairSnackbar.severity || "info"}
          icon={false}
          sx={{ whiteSpace: "pre-line", maxWidth: "560px" }}
        >
          {chairSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Reference Schedule Modal — dentist-only */}
      <Dialog
        open={showReferenceScheduleModal}
        onClose={() => referenceScheduleSaving ? null : setShowReferenceScheduleModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", backgroundColor: "#f8f5ef" } }}
      >
        <DialogTitle sx={{ backgroundColor: "#C5A572", color: "white", fontWeight: 700, fontSize: "1.1rem", py: 2, position: "relative" }}>
          Set Reference Schedule
          <IconButton
            onClick={() => referenceScheduleSaving ? null : setShowReferenceScheduleModal(false)}
            sx={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", color: "white", border: "2px solid white", width: 32, height: 32, "&:hover": { backgroundColor: "white", color: "#C5A572" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Typography variant="body2" sx={{ color: "#59544A", mb: 2 }}>
            Set your available hours for <strong>{selectedDateAppointment?.toDate().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</strong>. Clinic staff will see this as a reference when plotting your schedule.
          </Typography>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>From</InputLabel>
              <Select
                label="From"
                value={referenceFrom}
                onChange={(e) => {
                  setReferenceFrom(e.target.value);
                  if (e.target.value >= referenceTo) {
                    const idx = REFERENCE_TIME_OPTIONS.findIndex(o => o.value === e.target.value);
                    if (idx < REFERENCE_TIME_OPTIONS.length - 1) setReferenceTo(REFERENCE_TIME_OPTIONS[idx + 1].value);
                  }
                }}
              >
                {REFERENCE_TIME_OPTIONS.filter(o => o.value < "18:00").map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>To</InputLabel>
              <Select
                label="To"
                value={referenceTo}
                onChange={(e) => setReferenceTo(e.target.value)}
              >
                {REFERENCE_TIME_OPTIONS.filter(o => o.value > referenceFrom).map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setShowReferenceScheduleModal(false)} disabled={referenceScheduleSaving} sx={{ color: "#555", textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveReferenceSchedule}
            disabled={referenceScheduleSaving}
            variant="contained"
            sx={{ backgroundColor: "#C5A572", textTransform: "none", borderRadius: "20px", px: 3, "&:hover": { backgroundColor: "#b39463" } }}
          >
            {referenceScheduleSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reference schedule snackbar */}
      <Snackbar
        open={referenceScheduleSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setReferenceScheduleSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={referenceScheduleSnackbar.severity} onClose={() => setReferenceScheduleSnackbar(prev => ({ ...prev, open: false }))} sx={{ width: "100%" }}>
          {referenceScheduleSnackbar.message}
        </Alert>
      </Snackbar>

    </div>
  );
};

export default ManageAppointment;